import React, { useEffect, useState, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster, toast } from 'sonner';
import { User, Calendar as CalendarIcon, MessageCircle, Target, LogOut, CreditCard, Menu, X, ArrowRight, CheckCircle, Check, Users, Plus, Trash2, Circle, Clock, Upload, FileText, Video, Phone, PhoneOff, TrendingUp, TrendingDown, BarChart3, PieChart, Activity, Settings, Edit, Edit2, ChevronLeft, ChevronRight, MapPin, Link as LinkIcon, Heart, ShoppingBag, Home, Music, Award, Briefcase, DollarSign, Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Download, ListMusic, Search, Shuffle, Repeat, MoreVertical, ArrowLeftRight, BookOpen, Bell, Info, Mail, MessageSquare, Sun, Moon, Package, LayoutDashboard, GraduationCap, Lock, HelpCircle, Lightbulb, PiggyBank, Church, Radio, Mic2, HandHeart, FolderOpen, Folder, Image, File, FileVideo, FileAudio, PanelLeftClose, PanelLeft, Eye, EyeOff, UserCog, Loader2, RefreshCw, UserPlus } from 'lucide-react';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import SpecFiSync from './components/SpecFiSync';
import BudgetTracker from './components/BudgetTracker';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from './components/ui/avatar';
import { Badge } from './components/ui/badge';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './components/ui/dropdown-menu';
import { Progress } from './components/ui/progress';
import { Calendar } from './components/ui/calendar';
import { Checkbox } from './components/ui/checkbox';
import { Switch } from './components/ui/switch';
import { BarChart, Bar, LineChart, Line, PieChart as RechartsPie, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Register Service Worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then((registration) => {
        console.log('SW registered:', registration.scope);
      })
      .catch((error) => {
        console.log('SW registration failed:', error);
      });
  });
}

const axiosInstance = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Tour storage helpers
const TOUR_STORAGE_KEY = 'tff_tour_completed';
const hasTourCompleted = (tourKey = 'dashboard') => {
  try {
    const completed = localStorage.getItem(TOUR_STORAGE_KEY);
    if (!completed) return false;
    const tours = JSON.parse(completed);
    return tours[tourKey] === true;
  } catch {
    return false;
  }
};

const markTourCompleted = (tourKey = 'dashboard') => {
  try {
    const completed = localStorage.getItem(TOUR_STORAGE_KEY);
    const tours = completed ? JSON.parse(completed) : {};
    tours[tourKey] = true;
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(tours));
  } catch (e) {
    console.error('Error saving tour completion:', e);
  }
};

const resetTour = (tourKey = null) => {
  try {
    if (tourKey) {
      const completed = localStorage.getItem(TOUR_STORAGE_KEY);
      const tours = completed ? JSON.parse(completed) : {};
      delete tours[tourKey];
      localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(tours));
    } else {
      localStorage.removeItem(TOUR_STORAGE_KEY);
    }
  } catch (e) {
    console.error('Error resetting tour:', e);
  }
};

// Dashboard tour steps
const dashboardTourSteps = [
  {
    title: 'Welcome to TheFeedingFaith!',
    content: 'This is your personal dashboard where you can access all platform features. Let us show you around!',
    position: 'center'
  },
  {
    title: 'Quick Actions',
    content: 'These are your one-click access to common features like check-in, AI tools, prayer requests, and more.',
    target: '[data-tour="quick-actions"]',
    position: 'bottom'
  },
  {
    title: 'Your Progress',
    content: 'Track your mentorship progress here - see active mentorships, upcoming sessions, goals, and pending requests.',
    target: '[data-tour="mentorship-stats"]',
    position: 'top'
  },
  {
    title: 'Navigation Menu',
    content: 'Use the navigation menu to access different sections: Dashboard, Courses, Calendar, Support, Store, and more.',
    target: '[data-tour="navigation"]',
    position: 'bottom'
  },
  {
    title: 'Your Account',
    content: 'Click here to access your profile, settings, purchases, and membership options.',
    target: '[data-tour="user-menu"]',
    position: 'bottom'
  },
  {
    title: 'Get Help',
    content: 'Need help? Click here anytime to restart this tour or access our knowledge base and support.',
    target: '[data-tour="help-button"]',
    position: 'left'
  }
];

// Simple Tour Tooltip Component
function TourTooltip({ step, currentStep, totalSteps, onNext, onPrev, onSkip, onFinish }) {
  const [position, setPosition] = useState({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
  
  useEffect(() => {
    if (step.target) {
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
        
        let pos = { transform: 'none' };
        switch (step.position) {
          case 'top':
            pos = { 
              top: rect.top + scrollTop - 20,
              left: rect.left + scrollLeft + rect.width / 2,
              transform: 'translate(-50%, -100%)'
            };
            break;
          case 'bottom':
            pos = { 
              top: rect.bottom + scrollTop + 20,
              left: rect.left + scrollLeft + rect.width / 2,
              transform: 'translate(-50%, 0)'
            };
            break;
          case 'left':
            pos = { 
              top: rect.top + scrollTop + rect.height / 2,
              left: rect.left + scrollLeft - 20,
              transform: 'translate(-100%, -50%)'
            };
            break;
          case 'right':
            pos = { 
              top: rect.top + scrollTop + rect.height / 2,
              left: rect.right + scrollLeft + 20,
              transform: 'translate(0, -50%)'
            };
            break;
          default:
            pos = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
        }
        setPosition(pos);
        
        // Scroll element into view
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else {
      setPosition({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
    }
  }, [step]);
  
  const isLast = currentStep === totalSteps - 1;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="fixed z-[10001] bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-sm border-2 border-primary"
      style={{ top: position.top, left: position.left, transform: position.transform }}
    >
      <div className="flex items-center gap-2 mb-2">
        <Lightbulb className="h-5 w-5 text-accent" />
        <h3 className="font-heading font-bold text-lg text-primary">{step.title}</h3>
      </div>
      <p className="text-muted-foreground mb-4">{step.content}</p>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{currentStep + 1} of {totalSteps}</span>
        <div className="flex gap-2">
          {currentStep > 0 && (
            <Button variant="ghost" size="sm" onClick={onPrev}>Back</Button>
          )}
          <Button variant="ghost" size="sm" onClick={onSkip}>Skip</Button>
          <Button size="sm" className="bg-accent hover:bg-accent/90" onClick={isLast ? onFinish : onNext}>
            {isLast ? 'Finish' : 'Next'}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

// Theme Toggle Button Component
function ThemeToggleButton() {
  const { theme, toggleTheme } = useTheme();
  
  return (
    <Button 
      variant="ghost" 
      size="icon" 
      onClick={toggleTheme}
      data-testid="theme-toggle-btn"
      className="relative"
    >
      <Sun className={`h-5 w-5 transition-all ${theme === 'dark' ? 'scale-0 rotate-90' : 'scale-100 rotate-0'}`} />
      <Moon className={`absolute h-5 w-5 transition-all ${theme === 'dark' ? 'scale-100 rotate-0' : 'scale-0 -rotate-90'}`} />
      <span className="sr-only">Toggle theme</span>
    </Button>
  );
}

// Reusable Site Logo Component
function SiteLogo({ className = "h-8", textClassName = "font-heading text-2xl font-bold text-primary" }) {
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkLogo = async () => {
      try {
        const response = await axiosInstance.get('/settings/logo-status');
        if (response.data.has_logo) {
          setLogoUrl(`${BACKEND_URL}${response.data.logo_url}?t=${Date.now()}`);
        }
      } catch (error) {
        // No custom logo, use default
      } finally {
        setLoading(false);
      }
    };
    checkLogo();
  }, []);

  if (loading) {
    return <div className={textClassName}>TheFeedingFaith</div>;
  }

  if (logoUrl) {
    return (
      <img 
        src={logoUrl} 
        alt="TheFeedingFaith" 
        className={`${className} object-contain`}
        data-testid="site-logo"
      />
    );
  }

  return <div className={textClassName} data-testid="site-logo-text">TheFeedingFaith</div>;
}

function LandingPage() {
  const navigate = useNavigate();
  const [cmsContent, setCmsContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [bibleVerse, setBibleVerse] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastorPosts, setPastorPosts] = useState([]);
  const [weeklyFocus, setWeeklyFocus] = useState(null);
  const [monthlyTheme, setMonthlyTheme] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [footerSocialLinks, setFooterSocialLinks] = useState([]);

  // Default content (fallback if CMS has no content)
  const defaultContent = {
    hero: {
      title: 'Faith. Community. Growth.',
      subtitle: "Welcome to TheFeedingFaith Church - a community where faith meets purpose. Join us for worship, mentorship, and spiritual growth as we walk together in Christ.",
      cta_text: 'Join Our Community'
    },
    features: {
      heading: 'A Church for Every Season of Life',
      items: [
        { title: 'Sunday Worship', description: 'Experience powerful worship and transformative messages every Sunday.' },
        { title: 'Mentorship Program', description: 'Connect with experienced mentors for spiritual and personal growth.' },
        { title: 'Small Groups', description: 'Build meaningful relationships in our community groups.' },
        { title: 'Prayer Ministry', description: 'Submit prayer requests and receive support from our prayer team.' },
        { title: 'Online Sermons', description: 'Watch past sermons and grow in faith anytime, anywhere.' },
        { title: 'Youth & Family', description: 'Programs designed for every age and stage of life.' }
      ]
    },
    cta: {
      heading: 'Ready to take the next step?',
      subtitle: 'Whether you want to join a small group, request prayer, or find a mentor, we are here for you.',
      button_text: 'Get Connected'
    },
    footer: {
      text: '© 2026 TheFeedingFaith Church. Building faith, community, and purpose together.'
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load CMS content
        const cmsRes = await axiosInstance.get('/cms/content/home');
        const contentMap = {};
        cmsRes.data.forEach(item => {
          if (!contentMap[item.section]) {
            contentMap[item.section] = {};
          }
          contentMap[item.section][item.key] = item.value;
        });
        setCmsContent(contentMap);
      } catch (error) {
        console.log('Using default content');
      }
      
      try {
        // Load Bible verse
        const verseRes = await axiosInstance.get('/bible-verse');
        setBibleVerse(verseRes.data);
      } catch (error) {
        setBibleVerse({ reference: "Jeremiah 29:11", text: "For I know the plans I have for you, declares the LORD..." });
      }
      
      try {
        // Load upcoming events
        const eventsRes = await axiosInstance.get('/church-events');
        setUpcomingEvents(eventsRes.data.slice(0, 3));
      } catch (error) {
        console.log('No events');
      }
      
      try {
        // Load pastor's corner
        const pastorRes = await axiosInstance.get('/pastor-corner');
        setPastorPosts(pastorRes.data.slice(0, 2));
      } catch (error) {
        console.log('No pastor posts');
      }
      
      try {
        // Load weekly focus
        const focusRes = await axiosInstance.get('/weekly-focus');
        setWeeklyFocus(focusRes.data);
      } catch (error) {
        console.log('No weekly focus');
      }
      
      try {
        // Load monthly theme
        const themeRes = await axiosInstance.get('/monthly-theme');
        setMonthlyTheme(themeRes.data);
      } catch (error) {
        console.log('No monthly theme');
      }
      
      try {
        // Load social media links for footer
        const socialRes = await axiosInstance.get('/social-links');
        setFooterSocialLinks(socialRes.data);
      } catch (error) {
        console.log('No social links');
      }
      
      setLoading(false);
    };
    loadData();
  }, []);

  // Helper to get content with fallback
  const getContent = (section, key, fallback) => {
    return cmsContent[section]?.[key] || fallback;
  };

  // Parse features from CMS or use defaults
  const getFeatures = () => {
    const cmsFeatures = cmsContent.features;
    if (cmsFeatures) {
      // Try to parse CMS features (expecting JSON array in 'items' key)
      try {
        if (cmsFeatures.items) {
          return JSON.parse(cmsFeatures.items);
        }
      } catch (e) {
        console.log('Using default features');
      }
    }
    return defaultContent.features.items;
  };

  const featureIcons = [
    <Users className="h-8 w-8" />,
    <CalendarIcon className="h-8 w-8" />,
    <Target className="h-8 w-8" />,
    <MessageCircle className="h-8 w-8" />,
    <CheckCircle className="h-8 w-8" />,
    <CreditCard className="h-8 w-8" />
  ];

  const features = getFeatures();

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div onClick={() => navigate('/')} className="cursor-pointer">
              <SiteLogo className="h-10 max-w-[200px]" />
            </div>
            {/* Desktop Navigation */}
            <div className="hidden md:flex gap-2 items-center">
              <Button variant="ghost" onClick={() => navigate('/about')} data-testid="landing-about-btn">
                <Info className="h-4 w-4 mr-2" />
                About Us
              </Button>
              <Button variant="ghost" onClick={() => navigate('/worship')} data-testid="landing-worship-btn">
                <Music className="h-4 w-4 mr-2" />
                Worship
              </Button>
              <Button variant="ghost" onClick={() => navigate('/sermons')} data-testid="landing-sermons-btn">
                Sermons
              </Button>
              <Button variant="ghost" onClick={() => navigate('/study')} data-testid="landing-study-btn">
                <GraduationCap className="h-4 w-4 mr-2" />
                Bible Study
              </Button>
              <ThemeToggleButton />
              <Button variant="ghost" onClick={() => navigate('/login')} data-testid="landing-login-btn">
                Login
              </Button>
              <Button onClick={() => navigate('/signup')} className="bg-accent hover:bg-accent/90" data-testid="landing-signup-btn">
                Join Us
              </Button>
            </div>
            {/* Mobile Menu Button */}
            <div className="flex items-center gap-2 md:hidden">
              <ThemeToggleButton />
              <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="landing-mobile-menu-btn">
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>
        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t bg-background/95 backdrop-blur-sm"
            >
              <div className="px-4 py-4 space-y-2">
                <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/about'); setMobileMenuOpen(false); }}>
                  <Info className="h-4 w-4 mr-2" />
                  About Us
                </Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/worship'); setMobileMenuOpen(false); }}>
                  <Music className="h-4 w-4 mr-2" />
                  Worship
                </Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/sermons'); setMobileMenuOpen(false); }}>
                  <BookOpen className="h-4 w-4 mr-2" />
                  Sermons
                </Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/study'); setMobileMenuOpen(false); }}>
                  <GraduationCap className="h-4 w-4 mr-2" />
                  Bible Study
                </Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/calendar'); setMobileMenuOpen(false); }}>
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  Events
                </Button>
                <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/store'); setMobileMenuOpen(false); }}>
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  Store
                </Button>
                <div className="border-t pt-2 mt-2 space-y-2">
                  <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>
                    <User className="h-4 w-4 mr-2" />
                    Login
                  </Button>
                  <Button className="w-full bg-accent hover:bg-accent/90" onClick={() => { navigate('/signup'); setMobileMenuOpen(false); }}>
                    Join Us
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Daily Bible Verse Banner */}
      {bibleVerse && (
        <div className="pt-24 pb-4 px-4 bg-primary/10">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-lg italic text-foreground">"{bibleVerse.text}"</p>
            <p className="text-sm text-primary font-semibold mt-2">— {bibleVerse.reference}</p>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className={`${bibleVerse ? 'pt-8' : 'pt-32'} pb-20 px-4`}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="md:col-span-7"
            >
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-foreground mb-6" data-testid="hero-title">
                {getContent('hero', 'title', defaultContent.hero.title).split('.').map((part, i) => 
                  i === 2 ? <span key={i} className="text-primary">{part}.</span> : part + (i < 2 ? '. ' : '')
                )}
              </h1>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed" data-testid="hero-subtitle">
                {getContent('hero', 'subtitle', defaultContent.hero.subtitle)}
              </p>
              <div className="flex gap-4 flex-wrap">
                <Button onClick={() => navigate('/signup')} size="lg" className="bg-accent hover:bg-accent/90 shadow-lg" data-testid="hero-get-started-btn">
                  {getContent('hero', 'cta_text', defaultContent.hero.cta_text)} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button onClick={() => navigate('/sermons')} size="lg" variant="outline" data-testid="hero-sermons-btn">
                  Watch Sermons
                </Button>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="md:col-span-5"
            >
              <div className="relative">
                <img
                  src={getContent('hero', 'image', 'https://images.unsplash.com/photo-1438232992991-995b7058bbb3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxjaHVyY2glMjB3b3JzaGlwfGVufDB8fHx8MTcxNzAwNzY1OXww&ixlib=rb-4.1.0&q=85')}
                  alt="Church Worship"
                  className="rounded-2xl shadow-2xl w-full"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Upcoming Events Section */}
      {upcomingEvents.length > 0 && (
        <section className="py-12 px-4 bg-accent/10">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-3xl font-bold mb-8">Upcoming Events</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {upcomingEvents.map((event, idx) => (
                <Card key={event.event_id || idx} className="glass-card hover:shadow-lg transition-shadow cursor-pointer overflow-hidden" onClick={() => navigate('/calendar')} data-testid={`event-card-${idx}`}>
                  {/* Event Thumbnail */}
                  {event.thumbnail ? (
                    <div className="h-48 w-full overflow-hidden">
                      <img 
                        src={event.thumbnail} 
                        alt={event.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ) : (
                    <div className="h-32 w-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                      <CalendarIcon className="h-12 w-12 text-primary/50" />
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-center gap-2 text-primary mb-2">
                      <CalendarIcon className="h-5 w-5" />
                      <span className="text-sm">{new Date(event.start_time).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                    </div>
                    <CardTitle>{event.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm">{event.description || 'Join us for this special event!'}</p>
                    {event.location && <p className="text-xs text-muted-foreground mt-2">📍 {event.location}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-20 px-4 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-4xl font-bold text-center mb-16" data-testid="features-heading">
            {getContent('features', 'heading', defaultContent.features.heading)}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="glass-card h-full hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/signup')}>
                  <CardHeader>
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                      {featureIcons[idx % featureIcons.length]}
                    </div>
                    <CardTitle className="font-heading">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pastor's Corner Section */}
      {pastorPosts.length > 0 && (
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-3xl font-bold mb-8">Pastor's Corner</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {pastorPosts.map((post, idx) => (
                <Card key={post.post_id || idx} className="glass-card hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/pastor-corner')}>
                  {post.image && (
                    <img src={post.image} alt={post.title} className="w-full h-48 object-cover rounded-t-lg" />
                  )}
                  <CardHeader>
                    <CardTitle>{post.title}</CardTitle>
                    <CardDescription>{new Date(post.created_at).toLocaleDateString()}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground line-clamp-3">{post.content}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Weekly Focus Section */}
      {weeklyFocus && (
        <section className="py-12 px-4 bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Card className="glass-card border-2 border-primary/20 overflow-hidden" data-testid="weekly-focus-card">
                {weeklyFocus.image_url && (
                  <div className="relative h-48 bg-gradient-to-b from-primary/20 to-transparent">
                    <img 
                      src={weeklyFocus.image_url.startsWith('/') ? `${BACKEND_URL}${weeklyFocus.image_url}` : weeklyFocus.image_url} 
                      alt={weeklyFocus.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <div className="flex justify-center mb-2">
                    <Badge className="bg-primary text-white">This Week's Focus</Badge>
                  </div>
                  <CardTitle className="font-heading text-2xl text-primary">{weeklyFocus.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Scripture */}
                  <div className="bg-primary/10 rounded-lg p-4 text-center">
                    <p className="text-lg italic mb-2">"{weeklyFocus.scripture}"</p>
                    <p className="text-sm font-semibold text-primary">— {weeklyFocus.scripture_reference}</p>
                  </div>
                  
                  {/* Message */}
                  <p className="text-muted-foreground text-center">{weeklyFocus.message}</p>
                  
                  {/* Prayer Points & Action Items */}
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    {weeklyFocus.prayer_points && weeklyFocus.prayer_points.length > 0 && (
                      <div className="bg-secondary/20 rounded-lg p-4">
                        <h4 className="font-semibold flex items-center gap-2 mb-2">
                          <Heart className="h-4 w-4 text-red-500" />
                          Prayer Points
                        </h4>
                        <ul className="text-sm space-y-1">
                          {weeklyFocus.prayer_points.map((point, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-primary">•</span>
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {weeklyFocus.action_items && weeklyFocus.action_items.length > 0 && (
                      <div className="bg-secondary/20 rounded-lg p-4">
                        <h4 className="font-semibold flex items-center gap-2 mb-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          Action Items
                        </h4>
                        <ul className="text-sm space-y-1">
                          {weeklyFocus.action_items.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-green-500">✓</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>
      )}

      {/* Monthly Theme Section */}
      {monthlyTheme && (
        <section className="py-12 px-4 bg-gradient-to-b from-background to-secondary/10">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Card className="glass-card border-2 border-accent/20 overflow-hidden" data-testid="monthly-theme-card">
                {monthlyTheme.image_url && (
                  <div className="relative h-48 bg-gradient-to-b from-accent/20 to-transparent">
                    <img 
                      src={monthlyTheme.image_url.startsWith('/') ? `${BACKEND_URL}${monthlyTheme.image_url}` : monthlyTheme.image_url} 
                      alt={monthlyTheme.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <div className="flex justify-center mb-2">
                    <Badge className="bg-accent text-white">Monthly Theme</Badge>
                  </div>
                  <CardTitle className="font-heading text-2xl text-accent">{monthlyTheme.title}</CardTitle>
                  <CardDescription>{monthlyTheme.month}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground text-center">{monthlyTheme.description}</p>
                  
                  {monthlyTheme.scripture && (
                    <div className="bg-accent/10 rounded-lg p-4 text-center">
                      <p className="text-lg italic mb-2">"{monthlyTheme.scripture}"</p>
                      <p className="text-sm font-semibold text-accent">— {monthlyTheme.scripture_reference}</p>
                    </div>
                  )}
                  
                  {monthlyTheme.key_events && monthlyTheme.key_events.length > 0 && (
                    <div className="bg-secondary/20 rounded-lg p-4">
                      <h4 className="font-semibold flex items-center gap-2 mb-2">
                        <CalendarIcon className="h-4 w-4 text-accent" />
                        Key Events This Month
                      </h4>
                      <ul className="text-sm space-y-1">
                        {monthlyTheme.key_events.map((event, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-accent">•</span>
                            {event}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl font-bold mb-6" data-testid="cta-heading">
              {getContent('cta', 'heading', defaultContent.cta.heading)}
            </h2>
            <p className="text-lg text-muted-foreground mb-8" data-testid="cta-subtitle">
              {getContent('cta', 'subtitle', defaultContent.cta.subtitle)}
            </p>
            <div className="flex justify-center gap-4 flex-wrap">
              <Button onClick={() => navigate('/signup')} size="lg" className="bg-primary hover:bg-primary/90 shadow-lg" data-testid="cta-get-started-btn">
                {getContent('cta', 'button_text', defaultContent.cta.button_text)} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button onClick={() => navigate('/prayer-requests')} size="lg" variant="outline">
                Submit Prayer Request
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="font-heading font-bold mb-4">TheFeedingFaith</h3>
              <p className="text-sm text-muted-foreground">Building faith, community, and purpose together.</p>
              {/* Social Media Links */}
              {footerSocialLinks.length > 0 && (
                <div className="flex gap-3 mt-4" data-testid="footer-social-links">
                  {footerSocialLinks.map((link) => (
                    <a 
                      key={link.link_id} 
                      href={link.url} 
                      target="_blank" 
                      rel="noreferrer"
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110 ${
                        link.platform === 'instagram' ? 'bg-gradient-to-br from-purple-600 to-pink-500 text-white' :
                        link.platform === 'youtube' ? 'bg-red-600 text-white' :
                        link.platform === 'tiktok' ? 'bg-black text-white' :
                        link.platform === 'linkedin' ? 'bg-blue-700 text-white' :
                        link.platform === 'facebook' ? 'bg-blue-600 text-white' :
                        link.platform === 'twitter' || link.platform === 'x' ? 'bg-black text-white' :
                        link.platform === 'spotify' ? 'bg-green-600 text-white' :
                        link.platform === 'pinterest' ? 'bg-red-700 text-white' :
                        'bg-gray-600 text-white'
                      }`}
                      title={link.platform}
                      data-testid={`social-link-${link.platform}`}
                    >
                      {link.platform === 'instagram' && (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                      )}
                      {link.platform === 'youtube' && (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                      )}
                      {link.platform === 'tiktok' && (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                      )}
                      {link.platform === 'linkedin' && (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                      )}
                      {link.platform === 'facebook' && (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                      )}
                      {(link.platform === 'twitter' || link.platform === 'x') && (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                      )}
                      {link.platform === 'spotify' && (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
                      )}
                      {link.platform === 'pinterest' && (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/></svg>
                      )}
                      {!['instagram', 'youtube', 'tiktok', 'linkedin', 'facebook', 'twitter', 'x', 'spotify', 'pinterest'].includes(link.platform) && (
                        <LinkIcon className="w-5 h-5" />
                      )}
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div>
              <h4 className="font-semibold mb-3">Quick Links</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="cursor-pointer hover:text-primary" onClick={() => navigate('/worship')}>Worship Music</li>
                <li className="cursor-pointer hover:text-primary" onClick={() => navigate('/sermons')}>Sermons</li>
                <li className="cursor-pointer hover:text-primary" onClick={() => navigate('/store')}>Church Store</li>
                <li className="cursor-pointer hover:text-primary" onClick={() => navigate('/giving')}>Give Online</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Connect</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="cursor-pointer hover:text-primary" onClick={() => navigate('/community')}>Community</li>
                <li className="cursor-pointer hover:text-primary" onClick={() => navigate('/prayer-requests')}>Prayer Requests</li>
                <li className="cursor-pointer hover:text-primary" onClick={() => navigate('/signup')}>Join a Group</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Resources</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="cursor-pointer hover:text-primary" onClick={() => navigate('/pastor-corner')}>Pastor's Corner</li>
                <li className="cursor-pointer hover:text-primary" onClick={() => navigate('/calendar')}>Events</li>
                <li className="cursor-pointer hover:text-primary" onClick={() => navigate('/admin/login')}>Admin Portal</li>
              </ul>
            </div>
          </div>
          <div className="text-center text-muted-foreground border-t pt-8">
            <p className="font-heading" data-testid="footer-text">
              {getContent('footer', 'text', defaultContent.footer.text)}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axiosInstance.post('/auth/login', { email, password });
      toast.success('Login successful!');
      
      // Check if user needs role selection (first time users with no role)
      const needsRoleSelection = !response.data.role || response.data.role === '';
      
      if (needsRoleSelection) {
        // First time user - needs to select member/mentee role
        navigate('/onboarding', { state: { user: response.data } });
      } else {
        // User already has a role - go to dashboard
        // Role-based onboarding (training) is handled by ProtectedRoute
        navigate('/dashboard', { state: { user: response.data } });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
      toast.error('Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <Card className="glass-card" data-testid="login-card">
          <CardHeader className="text-center">
            <div className="font-heading text-3xl font-bold text-primary mb-2">TheFeedingFaith</div>
            <CardTitle className="font-heading text-2xl">Welcome Back</CardTitle>
            <CardDescription>Sign in to continue your mentorship journey</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {error && (
                <div className="bg-destructive/10 text-destructive px-4 py-2 rounded-lg text-sm" data-testid="login-error">
                  {error}
                </div>
              )}

              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-4 text-center text-sm text-muted-foreground">
              <Link to="/forgot-password" className="text-primary hover:underline" data-testid="forgot-password-link">
                Forgot your password?
              </Link>
            </div>

            <div className="mt-4 text-center text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/signup" className="text-primary hover:underline font-semibold">
                Sign up
              </Link>
            </div>

            <div className="mt-4 text-center text-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary">
                ← Back to Home
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axiosInstance.post('/auth/forgot-password', { email });
      setSubmitted(true);
      // In production, remove this - token would be sent via email
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
      }
      toast.success('Reset instructions sent!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to process request');
      toast.error('Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <Card className="glass-card" data-testid="forgot-password-success-card">
            <CardHeader className="text-center">
              <div className="font-heading text-3xl font-bold text-primary mb-2">TheFeedingFaith</div>
              <CardTitle className="font-heading text-2xl">Check Your Email</CardTitle>
              <CardDescription>
                If an account exists for {email}, we've sent password reset instructions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-center">
                <CheckCircle className="h-16 w-16 text-green-500" />
              </div>
              
              {/* Development mode: Show reset link directly */}
              {resetToken && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="reset-token-display">
                  <p className="text-sm text-amber-800 font-medium mb-2">Development Mode:</p>
                  <p className="text-xs text-amber-700 mb-3">In production, this link would be sent via email.</p>
                  <Button 
                    onClick={() => navigate(`/reset-password/${resetToken}`)}
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    data-testid="use-reset-link-btn"
                  >
                    Reset Password Now
                  </Button>
                </div>
              )}

              <div className="text-center text-sm text-muted-foreground">
                <Link to="/login" className="text-primary hover:underline" data-testid="back-to-login-link">
                  ← Back to Login
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <Card className="glass-card" data-testid="forgot-password-card">
          <CardHeader className="text-center">
            <div className="font-heading text-3xl font-bold text-primary mb-2">TheFeedingFaith</div>
            <CardTitle className="font-heading text-2xl">Forgot Password?</CardTitle>
            <CardDescription>Enter your email and we'll send you a reset link</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-destructive/10 text-destructive px-4 py-2 rounded-lg text-sm" data-testid="forgot-password-error">
                  {error}
                </div>
              )}

              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="forgot-password-email-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={loading}
                data-testid="forgot-password-submit-btn"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>

            <div className="mt-4 text-center text-sm text-muted-foreground">
              Remember your password?{' '}
              <Link to="/login" className="text-primary hover:underline font-semibold" data-testid="back-to-login-from-forgot">
                Sign in
              </Link>
            </div>

            <div className="mt-4 text-center text-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary">
                ← Back to Home
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathParts = location.pathname.split('/');
  const token = pathParts[pathParts.length - 1];
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await axiosInstance.get(`/auth/verify-reset-token/${token}`);
        setTokenValid(true);
        setEmail(response.data.email);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired reset link');
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    if (token) {
      verifyToken();
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axiosInstance.post('/auth/reset-password', { 
        token, 
        new_password: password 
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
      toast.error('Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <Card className="glass-card" data-testid="reset-success-card">
            <CardHeader className="text-center">
              <div className="font-heading text-3xl font-bold text-primary mb-2">TheFeedingFaith</div>
              <CardTitle className="font-heading text-2xl">Password Reset!</CardTitle>
              <CardDescription>Your password has been successfully reset</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-center">
                <CheckCircle className="h-16 w-16 text-green-500" />
              </div>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-primary hover:bg-primary/90"
                data-testid="go-to-login-btn"
              >
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <Card className="glass-card" data-testid="invalid-token-card">
            <CardHeader className="text-center">
              <div className="font-heading text-3xl font-bold text-primary mb-2">TheFeedingFaith</div>
              <CardTitle className="font-heading text-2xl text-destructive">Invalid Link</CardTitle>
              <CardDescription>{error || 'This password reset link is invalid or has expired.'}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={() => navigate('/forgot-password')}
                className="w-full bg-primary hover:bg-primary/90"
                data-testid="request-new-link-btn"
              >
                Request New Reset Link
              </Button>
              <div className="text-center text-sm text-muted-foreground">
                <Link to="/login" className="text-primary hover:underline">
                  ← Back to Login
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <Card className="glass-card" data-testid="reset-password-card">
          <CardHeader className="text-center">
            <div className="font-heading text-3xl font-bold text-primary mb-2">TheFeedingFaith</div>
            <CardTitle className="font-heading text-2xl">Reset Password</CardTitle>
            <CardDescription>Enter a new password for {email}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-destructive/10 text-destructive px-4 py-2 rounded-lg text-sm" data-testid="reset-password-error">
                  {error}
                </div>
              )}

              <div>
                <Label htmlFor="password">New Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="reset-password-input"
                />
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="reset-password-confirm-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={loading}
                data-testid="reset-password-submit-btn"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>

            <div className="mt-4 text-center text-sm text-muted-foreground">
              <Link to="/login" className="text-primary hover:underline">
                ← Back to Login
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

function Signup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    user_type: '', // member, mentee, both
    selected_plan: '', // For mentee/both users
    birthday: '',
    occupation: '',
    marital_status: '',
    employment_status: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1: Type selection, 2: Plan selection (if mentee/both), 3: Details
  const [mentorshipPlans, setMentorshipPlans] = useState({});

  useEffect(() => {
    // Load mentorship plans
    axiosInstance.get('/mentorship/plans').then(res => {
      setMentorshipPlans(res.data);
    }).catch(() => {
      // Use defaults if API fails
      setMentorshipPlans({
        starter: { name: "Starter", price: 29.99, interval: "month", features: ["AI Resume Builder", "Basic career guidance"] },
        growth: { name: "Growth", price: 49.99, interval: "month", features: ["1-on-1 Mentorship", "Leadership assessment"] },
        elite: { name: "Elite", price: 99.99, interval: "month", features: ["All features", "Business plan generator"] },
        lifetime: { name: "Lifetime", original_price: 500, price: 350, features: ["All features forever"] }
      });
    });
  }, []);

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.post('/auth/signup', {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: 'member',
        user_type: formData.user_type,
        selected_plan: formData.selected_plan,
        birthday: formData.birthday,
        occupation: formData.occupation,
        marital_status: formData.marital_status,
        employment_status: formData.employment_status,
        phone: formData.phone
      });
      
      toast.success('Welcome to TheFeedingFaith Church!');
      
      // If mentee or both, redirect to payment
      if ((formData.user_type === 'mentee' || formData.user_type === 'both') && formData.selected_plan) {
        navigate('/subscribe', { state: { selectedPlan: formData.selected_plan, user: response.data } });
      } else {
        navigate('/dashboard', { state: { user: response.data } });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed');
      toast.error('Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="font-heading text-xl font-semibold mb-2">How would you like to join?</h3>
        <p className="text-sm text-muted-foreground">Choose what best fits your needs</p>
      </div>
      
      <div className="grid gap-4">
        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg ${formData.user_type === 'member' ? 'ring-2 ring-primary bg-primary/5' : ''}`}
          onClick={() => setFormData({...formData, user_type: 'member'})}
          data-testid="user-type-member"
        >
          <CardContent className="p-4 flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center shrink-0">
              <Home className="h-6 w-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold">Church Member</h4>
              <p className="text-sm text-muted-foreground">Access sermons, worship, community, events, prayer requests, and giving. Free forever.</p>
              <Badge variant="secondary" className="mt-2">Free</Badge>
            </div>
            {formData.user_type === 'member' && <CheckCircle className="h-6 w-6 text-primary shrink-0" />}
          </CardContent>
        </Card>
        
        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg ${formData.user_type === 'mentee' ? 'ring-2 ring-primary bg-primary/5' : ''}`}
          onClick={() => setFormData({...formData, user_type: 'mentee'})}
          data-testid="user-type-mentee"
        >
          <CardContent className="p-4 flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
              <Target className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold">Mentorship Program</h4>
              <p className="text-sm text-muted-foreground">AI career tools, 1-on-1 mentorship, business plan generator, budget tracking, and more.</p>
              <Badge variant="outline" className="mt-2">Paid Service</Badge>
            </div>
            {formData.user_type === 'mentee' && <CheckCircle className="h-6 w-6 text-primary shrink-0" />}
          </CardContent>
        </Card>
        
        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg ${formData.user_type === 'both' ? 'ring-2 ring-primary bg-primary/5' : ''}`}
          onClick={() => setFormData({...formData, user_type: 'both'})}
          data-testid="user-type-both"
        >
          <CardContent className="p-4 flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
              <Award className="h-6 w-6 text-purple-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold">Church Member + Mentorship</h4>
              <p className="text-sm text-muted-foreground">Full church access plus mentorship program. Best of both worlds!</p>
              <Badge className="mt-2 bg-purple-600">Recommended</Badge>
            </div>
            {formData.user_type === 'both' && <CheckCircle className="h-6 w-6 text-primary shrink-0" />}
          </CardContent>
        </Card>
      </div>
      
      <Button 
        className="w-full" 
        disabled={!formData.user_type}
        onClick={() => setStep(formData.user_type === 'member' ? 3 : 2)}
        data-testid="continue-btn"
      >
        Continue
      </Button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="mb-2">
        <ChevronLeft className="h-4 w-4 mr-1" /> Back
      </Button>
      
      <div className="text-center mb-6">
        <h3 className="font-heading text-xl font-semibold mb-2">Choose Your Mentorship Plan</h3>
        <p className="text-sm text-muted-foreground">Monthly recurring payment. Cancel anytime.</p>
      </div>
      
      <div className="grid gap-4">
        {Object.entries(mentorshipPlans).map(([key, plan]) => (
          <Card 
            key={key}
            className={`cursor-pointer transition-all hover:shadow-lg ${formData.selected_plan === key ? 'ring-2 ring-primary bg-primary/5' : ''} ${key === 'lifetime' ? 'border-amber-500' : ''}`}
            onClick={() => setFormData({...formData, selected_plan: key})}
            data-testid={`plan-${key}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <h4 className="font-semibold text-lg">{plan.name}</h4>
                  {key === 'elite' && <Badge>Popular</Badge>}
                  {key === 'lifetime' && <Badge className="bg-amber-500">Best Value</Badge>}
                </div>
                <div className="text-right">
                  {plan.original_price && (
                    <span className="text-sm text-muted-foreground line-through mr-2">${plan.original_price}</span>
                  )}
                  <span className="text-2xl font-bold">${plan.price}</span>
                  {plan.interval && <span className="text-sm text-muted-foreground">/{plan.interval}</span>}
                  {!plan.interval && <span className="text-xs text-muted-foreground block">one-time</span>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {plan.features?.slice(0, 3).map((feature, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">{feature}</Badge>
                ))}
              </div>
              {formData.selected_plan === key && (
                <div className="mt-3 flex items-center text-primary">
                  <CheckCircle className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">Selected</span>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      
      <Button 
        className="w-full" 
        disabled={!formData.selected_plan}
        onClick={() => setStep(3)}
        data-testid="continue-plan-btn"
      >
        Continue to Registration
      </Button>
    </div>
  );

  const renderStep3 = () => (
    <form onSubmit={handleSignup} className="space-y-4">
      <Button type="button" variant="ghost" size="sm" onClick={() => setStep(formData.user_type === 'member' ? 1 : 2)} className="mb-2">
        <ChevronLeft className="h-4 w-4 mr-1" /> Back
      </Button>
      
      {/* Summary of selection */}
      <div className="bg-muted/50 rounded-lg p-3 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm text-muted-foreground">Your selection:</span>
            <p className="font-semibold">
              {formData.user_type === 'member' ? 'Church Member (Free)' : 
               formData.user_type === 'mentee' ? `Mentorship - ${mentorshipPlans[formData.selected_plan]?.name}` :
               `Member + ${mentorshipPlans[formData.selected_plan]?.name} Mentorship`}
            </p>
          </div>
          {formData.selected_plan && (
            <Badge variant="outline">${mentorshipPlans[formData.selected_plan]?.price}/{mentorshipPlans[formData.selected_plan]?.interval || 'once'}</Badge>
          )}
        </div>
      </div>

      {error && <div className="text-red-500 text-sm text-center bg-red-50 p-2 rounded">{error}</div>}
      
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label htmlFor="name">Full Name *</Label>
          <Input id="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="signup-name-input" />
        </div>
        <div className="col-span-2">
          <Label htmlFor="email">Email *</Label>
          <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required data-testid="signup-email-input" />
        </div>
        <div>
          <Label htmlFor="password">Password *</Label>
          <Input id="password" type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required data-testid="signup-password-input" />
        </div>
        <div>
          <Label htmlFor="confirmPassword">Confirm Password *</Label>
          <Input id="confirmPassword" type="password" value={formData.confirmPassword} onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})} required data-testid="signup-confirm-password-input" />
        </div>
        <div>
          <Label htmlFor="phone">Phone</Label>
          <Input id="phone" type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} data-testid="signup-phone-input" />
        </div>
        <div>
          <Label htmlFor="birthday">Birthday</Label>
          <Input id="birthday" type="date" value={formData.birthday} onChange={(e) => setFormData({...formData, birthday: e.target.value})} data-testid="signup-birthday-input" />
        </div>
      </div>
      
      <Button type="submit" className="w-full" disabled={loading} data-testid="signup-submit-btn">
        {loading ? 'Creating Account...' : formData.user_type === 'member' ? 'Create Free Account' : 'Create Account & Continue to Payment'}
      </Button>
      
      <p className="text-center text-sm text-muted-foreground">
        Already have an account? <span className="text-primary cursor-pointer hover:underline" onClick={() => navigate('/login')}>Sign in</span>
      </p>
    </form>
  );

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-lg"
      >
        <Card className="glass-card" data-testid="signup-card">
          <CardHeader className="text-center">
            <div className="font-heading text-3xl font-bold text-primary mb-2 cursor-pointer" onClick={() => navigate('/')}>TheFeedingFaith</div>
            <CardTitle className="font-heading text-2xl">Join Our Community</CardTitle>
            <CardDescription>Step {step} of {formData.user_type === 'member' ? 2 : 3}</CardDescription>
          </CardHeader>
          <CardContent>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const sessionId = params.get('session_id');

    if (sessionId) {
      axiosInstance
        .post('/auth/session', { session_id: sessionId })
        .then((response) => {
          const user = response.data;
          if (!user.role) {
            navigate('/onboarding', { state: { user }, replace: true });
          } else {
            navigate('/dashboard', { state: { user }, replace: true });
          }
        })
        .catch((error) => {
          console.error('Auth error:', error);
          toast.error('Authentication failed');
          navigate('/', { replace: true });
        });
    } else {
      navigate('/', { replace: true });
    }
  }, [navigate, location]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
}

function Onboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [selectedRole, setSelectedRole] = useState('');
  const [formData, setFormData] = useState({
    bio: '',
    skills: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Handle member_mentee as mentee role (they get both church + mentorship access)
      const actualRole = selectedRole === 'member_mentee' ? 'mentee' : selectedRole;
      
      const profileData = {
        role: actualRole,
        bio: formData.bio,
        skills: formData.skills ? formData.skills.split(',').map((s) => s.trim()).filter(Boolean) : [],
        // Track if they joined as member+mentee for future reference
        membership_type: selectedRole,
      };
      await axiosInstance.put('/users/profile', profileData);
      toast.success('Welcome to TheFeedingFaith! Profile created successfully.');
      
      // If they joined mentorship, show them membership options
      if (selectedRole === 'mentee' || selectedRole === 'member_mentee') {
        navigate('/subscribe');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('Failed to create profile');
    }
  };

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Card className="glass-card" data-testid="onboarding-card">
            <CardHeader>
              <CardTitle className="font-heading text-3xl">Complete Your Profile</CardTitle>
              <CardDescription>Let's set up your account to get you started</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-8">
                <div>
                  <Label className="text-base font-semibold mb-4 block">How would you like to join our community?</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card
                      className={`cursor-pointer hover:shadow-lg transition-all ${
                        selectedRole === 'member' ? 'ring-2 ring-primary bg-primary/5' : ''
                      }`}
                      onClick={() => setSelectedRole('member')}
                      data-testid="role-member-card"
                    >
                      <CardHeader>
                        <CardTitle className="font-heading text-xl">Church Member</CardTitle>
                        <CardDescription>Join our church community, access sermons, events, and fellowship</CardDescription>
                      </CardHeader>
                    </Card>
                    <Card
                      className={`cursor-pointer hover:shadow-lg transition-all ${
                        selectedRole === 'mentee' ? 'ring-2 ring-primary bg-primary/5' : ''
                      }`}
                      onClick={() => setSelectedRole('mentee')}
                      data-testid="role-mentee-card"
                    >
                      <CardHeader>
                        <CardTitle className="font-heading text-xl">Mentee</CardTitle>
                        <CardDescription>Join the mentorship program and learn from experienced mentors</CardDescription>
                      </CardHeader>
                    </Card>
                    <Card
                      className={`cursor-pointer hover:shadow-lg transition-all ${
                        selectedRole === 'member_mentee' ? 'ring-2 ring-primary bg-primary/5' : ''
                      }`}
                      onClick={() => setSelectedRole('member_mentee')}
                      data-testid="role-member-mentee-card"
                    >
                      <CardHeader>
                        <CardTitle className="font-heading text-xl">Member & Mentee</CardTitle>
                        <CardDescription>Full access to church and mentorship program</CardDescription>
                      </CardHeader>
                    </Card>
                  </div>
                </div>

                {selectedRole && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.3 }}
                    className="space-y-6"
                  >
                    <div>
                      <Label htmlFor="bio">Bio</Label>
                      <Textarea
                        id="bio"
                        placeholder="Tell us about yourself..."
                        value={formData.bio}
                        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                        rows={4}
                        required
                        data-testid="bio-input"
                      />
                    </div>

                    {(selectedRole === 'mentee' || selectedRole === 'member_mentee') && (
                      <div>
                        <Label htmlFor="skills">Skills or Interests (comma-separated)</Label>
                        <Input
                          id="skills"
                          placeholder="e.g., Leadership, Communication, Career Growth"
                          value={formData.skills}
                          onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                          data-testid="skills-input"
                        />
                      </div>
                    )}

                    <Button type="submit" className="w-full bg-accent hover:bg-accent/90" size="lg" data-testid="complete-profile-btn">
                      Complete Profile
                    </Button>
                  </motion.div>
                )}
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}

// ==================== ROLE-BASED ONBOARDING TRAINING ====================

function RoleOnboarding() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [guide, setGuide] = useState(null);
  const [progress, setProgress] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    loadOnboarding();
  }, []);

  const loadOnboarding = async () => {
    try {
      const [statusRes, userRes] = await Promise.all([
        axiosInstance.get('/onboarding/status'),
        axiosInstance.get('/auth/me')
      ]);
      
      setUser(userRes.data);
      
      if (!statusRes.data.requires_onboarding) {
        navigate('/dashboard');
        return;
      }
      
      if (statusRes.data.onboarding_completed) {
        navigate('/dashboard');
        return;
      }
      
      const guideRes = await axiosInstance.get('/onboarding/guide');
      setGuide(guideRes.data.guide);
      setProgress(guideRes.data.progress);
      
      // Set current step based on progress
      const completedSteps = guideRes.data.progress?.completed_steps || [];
      const steps = guideRes.data.guide?.steps || [];
      const firstIncompleteIndex = steps.findIndex(s => !completedSteps.includes(s.step_id));
      setCurrentStep(firstIncompleteIndex >= 0 ? firstIncompleteIndex : 0);
      
      // Initialize quiz answers
      if (guideRes.data.guide?.quiz) {
        setQuizAnswers(new Array(guideRes.data.guide.quiz.length).fill(-1));
      }
    } catch (error) {
      console.error('Failed to load onboarding:', error);
      toast.error('Failed to load onboarding guide');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteStep = async (stepId) => {
    try {
      const res = await axiosInstance.post(`/onboarding/complete-step?step_id=${stepId}`);
      setProgress(res.data.progress);
      
      // Move to next step
      if (currentStep < guide.steps.length - 1) {
        setCurrentStep(currentStep + 1);
      }
      
      toast.success('Step completed!');
    } catch (error) {
      toast.error('Failed to save progress');
    }
  };

  const handleQuizAnswer = (questionIndex, answerIndex) => {
    const newAnswers = [...quizAnswers];
    newAnswers[questionIndex] = answerIndex;
    setQuizAnswers(newAnswers);
  };

  const handleSubmitQuiz = async () => {
    if (quizAnswers.includes(-1)) {
      toast.error('Please answer all questions');
      return;
    }
    
    setQuizSubmitting(true);
    try {
      const res = await axiosInstance.post('/onboarding/submit-quiz', { answers: quizAnswers });
      setQuizResult(res.data);
      setProgress(res.data.progress);
      
      if (res.data.passed) {
        toast.success('Congratulations! You\'ve completed the onboarding!');
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        toast.error('Quiz not passed. Please review the material and try again.');
      }
    } catch (error) {
      toast.error('Failed to submit quiz');
    } finally {
      setQuizSubmitting(false);
    }
  };

  const handleRetryQuiz = () => {
    setQuizResult(null);
    setQuizAnswers(new Array(guide.quiz.length).fill(-1));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!guide) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="glass-card max-w-md">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">No Onboarding Required</h2>
            <p className="text-muted-foreground mb-4">You're all set to use the platform!</p>
            <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentStepData = guide.steps[currentStep];
  const isQuizStep = currentStepData?.action?.type === 'quiz';
  const completedSteps = progress?.completed_steps || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <nav className="glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SiteLogo className="h-10 max-w-[180px]" />
              <Badge variant="secondary" className="ml-2">{user?.role?.toUpperCase()} Onboarding</Badge>
            </div>
            <div className="text-sm text-muted-foreground">
              Step {currentStep + 1} of {guide.steps.length}
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-4 max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl font-bold mb-2">{guide.title}</h1>
            <p className="text-muted-foreground">{guide.description}</p>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between mb-2">
              {guide.steps.map((step, idx) => (
                <div
                  key={step.step_id}
                  className={`flex-1 mx-1 h-2 rounded-full ${
                    completedSteps.includes(step.step_id)
                      ? 'bg-green-500'
                      : idx === currentStep
                      ? 'bg-primary'
                      : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              {guide.steps.map((step, idx) => (
                <span key={step.step_id} className={idx === currentStep ? 'font-bold text-primary' : ''}>
                  {idx + 1}
                </span>
              ))}
            </div>
          </div>

          {/* Step Navigation */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {guide.steps.map((step, idx) => (
              <Button
                key={step.step_id}
                variant={idx === currentStep ? "default" : "outline"}
                size="sm"
                onClick={() => setCurrentStep(idx)}
                className={`flex-shrink-0 ${completedSteps.includes(step.step_id) ? 'border-green-500' : ''}`}
                data-testid={`step-nav-${step.step_id}`}
              >
                {completedSteps.includes(step.step_id) && <CheckCircle className="h-3 w-3 mr-1 text-green-500" />}
                {step.title}
              </Button>
            ))}
          </div>

          {/* Step Content */}
          <Card className="glass-card" data-testid="onboarding-step-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  completedSteps.includes(currentStepData.step_id) 
                    ? 'bg-green-100 text-green-600' 
                    : 'bg-primary/10 text-primary'
                }`}>
                  {completedSteps.includes(currentStepData.step_id) 
                    ? <CheckCircle className="h-5 w-5" /> 
                    : <span className="font-bold">{currentStep + 1}</span>
                  }
                </div>
                <div>
                  <CardTitle className="font-heading">{currentStepData.title}</CardTitle>
                  <CardDescription>{currentStepData.description}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isQuizStep ? (
                // Quiz Content
                <div className="space-y-6">
                  {quizResult ? (
                    // Quiz Results
                    <div className="text-center py-8">
                      {quizResult.passed ? (
                        <>
                          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                            <Award className="h-10 w-10 text-green-600" />
                          </div>
                          <h3 className="text-2xl font-bold text-green-600 mb-2">Congratulations!</h3>
                          <p className="text-muted-foreground mb-4">
                            You scored {quizResult.score.toFixed(0)}% ({quizResult.correct}/{quizResult.total} correct)
                          </p>
                          <p className="text-lg">Onboarding Complete! Redirecting to dashboard...</p>
                        </>
                      ) : (
                        <>
                          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                            <X className="h-10 w-10 text-red-600" />
                          </div>
                          <h3 className="text-2xl font-bold text-red-600 mb-2">Not Passed</h3>
                          <p className="text-muted-foreground mb-4">
                            You scored {quizResult.score.toFixed(0)}% ({quizResult.correct}/{quizResult.total} correct). 
                            You need 70% to pass.
                          </p>
                          <Button onClick={handleRetryQuiz}>
                            Review Material & Retry Quiz
                          </Button>
                        </>
                      )}
                    </div>
                  ) : (
                    // Quiz Questions
                    <>
                      <div className="bg-primary/5 p-4 rounded-lg mb-6">
                        <h3 className="font-semibold mb-2">Knowledge Check</h3>
                        <p className="text-sm text-muted-foreground">
                          Answer all questions correctly to complete your onboarding. You need at least 70% to pass.
                        </p>
                      </div>
                      {guide.quiz.map((q, qIdx) => (
                        <div key={qIdx} className="p-4 border rounded-lg mb-4" data-testid={`quiz-question-${qIdx}`}>
                          <h4 className="font-semibold mb-3">{qIdx + 1}. {q.question}</h4>
                          <div className="space-y-2">
                            {q.options.map((opt, oIdx) => (
                              <div
                                key={oIdx}
                                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                                  quizAnswers[qIdx] === oIdx 
                                    ? 'border-primary bg-primary/10' 
                                    : 'border-gray-200 hover:border-primary/50'
                                }`}
                                onClick={() => handleQuizAnswer(qIdx, oIdx)}
                                data-testid={`quiz-${qIdx}-option-${oIdx}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                    quizAnswers[qIdx] === oIdx ? 'border-primary bg-primary text-white' : 'border-gray-300'
                                  }`}>
                                    {quizAnswers[qIdx] === oIdx && <CheckCircle className="h-4 w-4" />}
                                  </div>
                                  <span>{opt}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      <Button 
                        className="w-full" 
                        size="lg"
                        onClick={handleSubmitQuiz}
                        disabled={quizSubmitting || quizAnswers.includes(-1)}
                        data-testid="submit-quiz-btn"
                      >
                        {quizSubmitting ? 'Submitting...' : 'Submit Quiz'}
                      </Button>
                    </>
                  )}
                </div>
              ) : (
                // Regular Step Content
                <div>
                  {currentStepData.content && (
                    <div 
                      className="prose prose-sm max-w-none mb-6"
                      dangerouslySetInnerHTML={{ __html: currentStepData.content }}
                    />
                  )}
                  
                  <div className="flex justify-between mt-8">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                      disabled={currentStep === 0}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                    </Button>
                    
                    <div className="flex gap-2">
                      {currentStepData.action?.type === 'navigate' && (
                        <Button
                          variant="outline"
                          onClick={() => window.open(currentStepData.action.path, '_blank')}
                        >
                          <LinkIcon className="h-4 w-4 mr-2" />
                          Try It Now
                        </Button>
                      )}
                      
                      {!completedSteps.includes(currentStepData.step_id) ? (
                        <Button onClick={() => handleCompleteStep(currentStepData.step_id)} data-testid="mark-complete-btn">
                          Mark as Complete <CheckCircle className="h-4 w-4 ml-2" />
                        </Button>
                      ) : currentStep < guide.steps.length - 1 ? (
                        <Button onClick={() => setCurrentStep(currentStep + 1)}>
                          Next <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}

// Roles that require onboarding training
const ROLES_REQUIRING_ONBOARDING = ["admin", "pastor", "mentor", "support", "church_worker"];

function ProtectedRoute({ children, skipOnboardingCheck = false }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [checkingOnboarding, setCheckingOnboarding] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axiosInstance.get('/auth/me');
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/');
      }
    };
    checkAuth();
  }, [navigate, location]);

  // Check onboarding status for team roles
  useEffect(() => {
    if (!skipOnboardingCheck && isAuthenticated && user && ROLES_REQUIRING_ONBOARDING.includes(user.role)) {
      const checkOnboarding = async () => {
        setCheckingOnboarding(true);
        try {
          const res = await axiosInstance.get('/onboarding/status');
          if (res.data.requires_onboarding && !res.data.onboarding_completed) {
            // Redirect to role onboarding
            navigate('/role-onboarding', { replace: true });
          }
        } catch (error) {
          console.error('Failed to check onboarding status:', error);
        } finally {
          setCheckingOnboarding(false);
        }
      };
      checkOnboarding();
    }
  }, [isAuthenticated, user, skipOnboardingCheck, navigate]);

  if (isAuthenticated === null || checkingOnboarding) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return React.cloneElement(children, { user });
}

function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axiosInstance.post('/auth/admin/login', { email, password });
      toast.success('Login successful!');
      navigate('/admin/dashboard', { state: { user: response.data } });
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
      toast.error('Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <Card className="glass-card" data-testid="admin-login-card">
          <CardHeader className="text-center">
            <div className="font-heading text-3xl font-bold text-primary mb-2">TheFeedingFaith</div>
            <CardTitle className="font-heading text-2xl">Admin Login</CardTitle>
            <CardDescription>Sign in to access the admin dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAdminLogin} className="space-y-4">
              {error && (
                <div className="bg-destructive/10 text-destructive px-4 py-2 rounded-lg text-sm" data-testid="login-error">
                  {error}
                </div>
              )}

              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="info@thefeedingfaith.org"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="admin-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="admin-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={loading}
                data-testid="admin-login-btn"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-4 text-center text-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary">
                ← Back to Home
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

function AdminDashboard({ user: initialUser }) {
  const [user, setUser] = useState(initialUser);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [mentorships, setMentorships] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalMentors: 0,
    totalMentees: 0,
    activeMentorships: 0,
    totalRevenue: 0
  });
  // Admin Tab Navigation
  const [activeAdminTab, setActiveAdminTab] = useState('overview');
  // Admin Sidebar
  const [adminSidebarCollapsed, setAdminSidebarCollapsed] = useState(false);
  // Drill-down state for clickable charts/widgets
  const [showDrillDown, setShowDrillDown] = useState(false);
  const [drillDownData, setDrillDownData] = useState({ type: '', title: '', data: [] });
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showAddMentor, setShowAddMentor] = useState(false);
  const [showAssignMentorship, setShowAssignMentorship] = useState(false);
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [adminMobileMenuOpen, setAdminMobileMenuOpen] = useState(false);
  // Module Access Control
  const [moduleSettings, setModuleSettings] = useState({
    budget_tracker: true,
    resume_builder: true,
    business_plan: true
  });
  const [moduleInfo, setModuleInfo] = useState({});
  const [savingModules, setSavingModules] = useState(false);
  // Bulk User Selection
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [showBulkAction, setShowBulkAction] = useState(false);
  const [bulkActionType, setBulkActionType] = useState('');
  const [bulkRoleChange, setBulkRoleChange] = useState('member');
  const [bulkEmailData, setBulkEmailData] = useState({ subject: '', message: '' });
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [assignMentorshipData, setAssignMentorshipData] = useState({
    mentor_id: '',
    mentee_id: '',
    template_id: ''
  });
  const [bulkAssignData, setBulkAssignData] = useState({
    mentor_id: '',
    mentee_ids: [],
    template_id: ''
  });
  const [newTemplateData, setNewTemplateData] = useState({
    name: '',
    description: '',
    goals: [''],
    duration_weeks: 12
  });
  const [resetPasswordData, setResetPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [editUserData, setEditUserData] = useState({
    name: '',
    role: '',
    roles: [],
    bio: '',
    skills: '',
    expertise_areas: '',
    enabled_modules: [],
    disabled_modules: []
  });
  const [newMentorData, setNewMentorData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'mentor',
    bio: '',
    skills: '',
    expertise_areas: ''
  });
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'member',
    roles: ['member'],
    bio: '',
    phone: '',
    skills: '',
    expertise_areas: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [currentLogo, setCurrentLogo] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  // Church Management State
  const [ministries, setMinistries] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [prayerRequests, setPrayerRequests] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [churchStats, setChurchStats] = useState(null);
  const [showAddMinistry, setShowAddMinistry] = useState(false);
  const [showAddAnnouncement, setShowAddAnnouncement] = useState(false);
  const [showRecordAttendance, setShowRecordAttendance] = useState(false);
  const [newMinistryData, setNewMinistryData] = useState({ name: '', description: '', category: 'general', meeting_schedule: '' });
  const [newAnnouncementData, setNewAnnouncementData] = useState({ title: '', content: '', priority: 'normal' });
  const [attendanceData, setAttendanceData] = useState({ service_date: new Date().toISOString().split('T')[0], service_type: 'sunday', attendee_ids: [], visitor_count: 0 });
  // Church Events Management State
  const [churchEvents, setChurchEvents] = useState([]);
  const [churchEventsLoading, setChurchEventsLoading] = useState(false);
  const [showAddChurchEvent, setShowAddChurchEvent] = useState(false);
  const [showEditChurchEvent, setShowEditChurchEvent] = useState(false);
  const [editingChurchEvent, setEditingChurchEvent] = useState(null);
  const [savingChurchEvent, setSavingChurchEvent] = useState(false);
  const [newChurchEventData, setNewChurchEventData] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    location: '',
    event_type: 'general',
    thumbnail_url: '',
    featured_image_url: ''
  });
  // Live Streaming State
  const [livestreams, setLivestreams] = useState([]);
  const [streamPermissions, setStreamPermissions] = useState([]);
  const [showAddLivestream, setShowAddLivestream] = useState(false);
  const [showStreamSetup, setShowStreamSetup] = useState(null); // null or stream object
  const [showManagePermissions, setShowManagePermissions] = useState(false);
  const [newLivestreamData, setNewLivestreamData] = useState({ 
    title: '', 
    description: '', 
    stream_type: 'service', 
    scheduled_at: '',
    stream_destination: 'youtube',
    youtube_url: '',
    facebook_url: '',
    embed_url: '',
    custom_rtmp_url: ''
  });
  const [streamInstructions, setStreamInstructions] = useState(null);
  // Sermon Management State
  const [sermons, setSermons] = useState([]);
  const [showAddSermon, setShowAddSermon] = useState(false);
  const [showEditSermon, setShowEditSermon] = useState(false);
  const [editSermonData, setEditSermonData] = useState(null);
  // Pastor's Corner Management State
  const [pastorPosts, setPastorPosts] = useState([]);
  const [showAddPastorPost, setShowAddPastorPost] = useState(false);
  const [newPastorPost, setNewPastorPost] = useState({ title: '', content: '' });
  const [pastorPostImage, setPastorPostImage] = useState(null);
  const [savingPastorPost, setSavingPastorPost] = useState(false);
  // Resource Library State
  const [resources, setResources] = useState([]);
  const [resourceFolders, setResourceFolders] = useState([]);
  const [resourceStats, setResourceStats] = useState(null);
  const [resourceCategories, setResourceCategories] = useState([]);
  const [showUploadResource, setShowUploadResource] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [uploadingResource, setUploadingResource] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [newResourceData, setNewResourceData] = useState({ title: '', description: '', category: 'general', tags: '', is_public: true, folder_id: null });
  const [newFolderData, setNewFolderData] = useState({ name: '', description: '', is_public: true });
  const [resourceFilter, setResourceFilter] = useState({ category: '', file_type: '', search: '' });
  const [selectedResourceFile, setSelectedResourceFile] = useState(null);
  // Resource Preview State
  const [previewResource, setPreviewResource] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Weekly Focus & Monthly Theme State
  const [weeklyFocusList, setWeeklyFocusList] = useState([]);
  const [monthlyThemesList, setMonthlyThemesList] = useState([]);
  const [showAddWeeklyFocus, setShowAddWeeklyFocus] = useState(false);
  const [showAddMonthlyTheme, setShowAddMonthlyTheme] = useState(false);
  const [editingWeeklyFocus, setEditingWeeklyFocus] = useState(null);
  const [editingMonthlyTheme, setEditingMonthlyTheme] = useState(null);
  const [newWeeklyFocus, setNewWeeklyFocus] = useState({ title: '', scripture: '', scripture_reference: '', message: '', prayer_points: '', action_items: '', week_start: '', week_end: '', image_url: '' });
  const [newMonthlyTheme, setNewMonthlyTheme] = useState({ title: '', description: '', scripture: '', scripture_reference: '', month: '', image_url: '', key_events: '' });
  const [weeklyFocusImageFile, setWeeklyFocusImageFile] = useState(null);
  const [monthlyThemeImageFile, setMonthlyThemeImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  // Sermon Notes State
  const [sermonNotes, setSermonNotes] = useState([]);
  const [sermonNotesLoading, setSermonNotesLoading] = useState(false);
  const [showCreateSermonNote, setShowCreateSermonNote] = useState(false);
  const [editingSermonNote, setEditingSermonNote] = useState(null);
  const [viewingSermonNote, setViewingSermonNote] = useState(null);
  const [sermonNoteFilter, setSermonNoteFilter] = useState({ status: '', series: '', search: '' });
  const [sermonSeries, setSermonSeries] = useState([]);
  const [newSermonNote, setNewSermonNote] = useState({
    title: '',
    main_text: '',
    main_scripture_reference: '',
    supporting_scriptures: '',
    introduction: '',
    conclusion: '',
    sub_topics: [],
    sermon_date: '',
    series: '',
    tags: ''
  });
  // Admin Goals & Tasks Management State
  const [adminGoals, setAdminGoals] = useState([]);
  const [adminTasks, setAdminTasks] = useState([]);
  const [showGoalsManager, setShowGoalsManager] = useState(false);
  const [showTasksManager, setShowTasksManager] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [goalsFilter, setGoalsFilter] = useState('all');
  const [tasksFilter, setTasksFilter] = useState('all');
  // Visitors Management State
  const [visitors, setVisitors] = useState([]);
  const [visitorsLoading, setVisitorsLoading] = useState(false);
  const [visitorFilter, setVisitorFilter] = useState({ status: 'all', search: '' });
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [showVisitorDetails, setShowVisitorDetails] = useState(false);
  const [showAssignVisitor, setShowAssignVisitor] = useState(false);
  const [visitorStats, setVisitorStats] = useState(null);
  const [visitorQRCode, setVisitorQRCode] = useState(null);
  const [showSalvationEditor, setShowSalvationEditor] = useState(false);
  const [salvationContent, setSalvationContent] = useState(null);
  const [newSermonData, setNewSermonData] = useState({
    title: '',
    description: '',
    preacher: '',
    youtube_url: '',
    scripture_reference: '',
    series: ''
  });
  const [uploadingSermonMedia, setUploadingSermonMedia] = useState(null);
  // Store Products State
  const [storeProducts, setStoreProducts] = useState([]);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showEditProduct, setShowEditProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newProductData, setNewProductData] = useState({
    name: '',
    description: '',
    price: '',
    category: 'books',
    image: '',
    stock_quantity: 100
  });
  const [savingProduct, setSavingProduct] = useState(false);
  const [productImageFile, setProductImageFile] = useState(null);
  // Worship Songs State
  const [worshipSongs, setWorshipSongs] = useState([]);
  const [showAddSong, setShowAddSong] = useState(false);
  const [showEditSong, setShowEditSong] = useState(false);
  const [editingSong, setEditingSong] = useState(null);
  const [newSongData, setNewSongData] = useState({
    title: '',
    artist: '',
    album: '',
    genre: '',
    lyrics: ''
  });
  const [songAudioFile, setSongAudioFile] = useState(null);
  const [songCoverFile, setSongCoverFile] = useState(null);
  const [uploadingSong, setUploadingSong] = useState(false);
  // Advanced Courses Management State
  const [advancedCourses, setAdvancedCourses] = useState([]);
  const [advancedCoursesLoading, setAdvancedCoursesLoading] = useState(false);
  const [showEditAdvancedCourse, setShowEditAdvancedCourse] = useState(false);
  const [editingAdvancedCourse, setEditingAdvancedCourse] = useState(null);
  const [savingAdvancedCourse, setSavingAdvancedCourse] = useState(false);
  const [showCreateAdvancedCourse, setShowCreateAdvancedCourse] = useState(false);
  const [newCourseData, setNewCourseData] = useState({
    title: '',
    description: '',
    category: 'Bible Study',
    price: '0',
    thumbnail_url: '',
    lessons: []
  });
  const [creatingCourse, setCreatingCourse] = useState(false);
  const [showAddLesson, setShowAddLesson] = useState(false);
  const [newLessonData, setNewLessonData] = useState({
    title: '',
    description: '',
    duration_minutes: 15,
    content: '',
    is_free_preview: false
  });
  // AI Course Generation State
  const [showAIGenerate, setShowAIGenerate] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiGenerateForm, setAiGenerateForm] = useState({
    topic: '',
    num_lessons: 5,
    category: 'Bible Study',
    difficulty: 'intermediate',
    include_quiz: true
  });
  // Admin Enrollment State
  const [showAdminEnroll, setShowAdminEnroll] = useState(false);
  const [enrollingCourse, setEnrollingCourse] = useState(null);
  const [enrollUserSearch, setEnrollUserSearch] = useState('');
  const [enrollUserResults, setEnrollUserResults] = useState([]);
  const [enrollingUser, setEnrollingUser] = useState(false);
  const [courseEnrollments, setCourseEnrollments] = useState([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  // API Keys State
  const [apiKeysStatus, setApiKeysStatus] = useState({ stripe: {}, resend: {} });
  const [stripeKeyInput, setStripeKeyInput] = useState('');
  const [resendKeyInput, setResendKeyInput] = useState('');
  const [savingStripeKey, setSavingStripeKey] = useState(false);
  const [savingResendKey, setSavingResendKey] = useState(false);
  // Social Media Links State
  const [socialLinks, setSocialLinks] = useState([]);
  const [socialLinksLoading, setSocialLinksLoading] = useState(false);
  const [showAddSocialLink, setShowAddSocialLink] = useState(false);
  const [showEditSocialLink, setShowEditSocialLink] = useState(false);
  const [editingSocialLink, setEditingSocialLink] = useState(null);
  const [savingSocialLink, setSavingSocialLink] = useState(false);
  const [newSocialLinkData, setNewSocialLinkData] = useState({
    platform: '',
    url: '',
    icon: '',
    display_order: 0,
    is_active: true
  });
  // Homepage CMS State
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [savingHeroImage, setSavingHeroImage] = useState(false);
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [heroImagePreview, setHeroImagePreview] = useState(null);
  const navigate = useNavigate();

  const handleEditSong = (song) => {
    setEditingSong(song);
    setShowEditSong(true);
  };

  const handleUpdateSong = async () => {
    if (!editingSong) return;
    try {
      const params = new URLSearchParams();
      if (editingSong.title) params.append('title', editingSong.title);
      if (editingSong.artist) params.append('artist', editingSong.artist);
      if (editingSong.album) params.append('album', editingSong.album);
      if (editingSong.genre) params.append('genre', editingSong.genre);
      if (editingSong.lyrics) params.append('lyrics', editingSong.lyrics);
      if (editingSong.download_price !== undefined) params.append('download_price', editingSong.download_price);
      
      await axiosInstance.put(`/admin/songs/${editingSong.song_id}?${params.toString()}`);
      toast.success('Song updated successfully!');
      setShowEditSong(false);
      setEditingSong(null);
      loadWorshipSongs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update song');
    }
  };

  const handleSermonFileUpload = async (sermonId, file, mediaType) => {
    if (!file) return;
    setUploadingSermonMedia(sermonId);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axiosInstance.post(`/sermons/${sermonId}/upload?media_type=${mediaType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${mediaType === 'video' ? 'Video' : 'Audio'} uploaded successfully!`);
      loadSermons();
    } catch (error) {
      toast.error(`Failed to upload ${mediaType}`);
    }
    setUploadingSermonMedia(null);
  };

  const handleEditSermon = (sermon) => {
    setEditSermonData({
      sermon_id: sermon.sermon_id,
      title: sermon.title || '',
      description: sermon.description || '',
      preacher: sermon.preacher || '',
      youtube_url: sermon.youtube_url || '',
      scripture_reference: sermon.scripture_reference || '',
      series: sermon.series || ''
    });
    setShowEditSermon(true);
  };

  const handleUpdateSermon = async (e) => {
    e.preventDefault();
    if (!editSermonData.title || !editSermonData.preacher) {
      toast.error('Please fill in title and preacher');
      return;
    }
    try {
      await axiosInstance.put(`/sermons/${editSermonData.sermon_id}`, {
        title: editSermonData.title,
        description: editSermonData.description,
        preacher: editSermonData.preacher,
        youtube_url: editSermonData.youtube_url,
        scripture_reference: editSermonData.scripture_reference,
        series: editSermonData.series
      });
      toast.success('Sermon updated successfully!');
      setShowEditSermon(false);
      setEditSermonData(null);
      loadSermons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update sermon');
    }
  };

  // Get mentors and mentees for assignment
  const mentors = users.filter(u => u.role === 'mentor');
  const mentees = users.filter(u => u.role === 'mentee');

  useEffect(() => {
    if (!user) {
      axiosInstance.get('/auth/me').then((res) => setUser(res.data));
    }
    loadAdminData();
    checkCurrentLogo();
    loadTemplates();
    loadAnalytics();
    loadSermons();
    loadMentorshipPlans();
    loadWorshipSongs();
    loadAdvancedCourses();
    loadApiKeysStatus();
    loadSocialLinks();
    loadHeroImage();
    loadStoreProducts();
    loadPastorPosts();
    loadChurchData();
    loadChurchEvents();
    loadLivestreamData();
    loadResourcesData();
    loadWeeklyFocusData();
    loadMonthlyThemesData();
    loadSermonNotesData();
    loadVisitorsData();
    loadSalvationContent();
  }, [user]);

  const loadApiKeysStatus = async () => {
    try {
      const response = await axiosInstance.get('/admin/api-keys/status');
      setApiKeysStatus(response.data);
    } catch (error) {
      console.log('Failed to load API keys status');
    }
  };

  // Social Links Functions
  const loadSocialLinks = async () => {
    setSocialLinksLoading(true);
    try {
      const response = await axiosInstance.get('/social-links/all');
      setSocialLinks(response.data);
    } catch (error) {
      // Fallback to public endpoint
      try {
        const res = await axiosInstance.get('/social-links');
        setSocialLinks(res.data);
      } catch (e) {
        console.log('Failed to load social links');
      }
    }
    setSocialLinksLoading(false);
  };

  const handleCreateSocialLink = async () => {
    if (!newSocialLinkData.platform || !newSocialLinkData.url) {
      toast.error('Platform and URL are required');
      return;
    }
    setSavingSocialLink(true);
    try {
      await axiosInstance.post('/social-links', newSocialLinkData);
      toast.success('Social link added!');
      setShowAddSocialLink(false);
      setNewSocialLinkData({ platform: '', url: '', icon: '', display_order: 0, is_active: true });
      loadSocialLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add social link');
    }
    setSavingSocialLink(false);
  };

  const handleEditSocialLink = (link) => {
    setEditingSocialLink({ ...link });
    setShowEditSocialLink(true);
  };

  const handleUpdateSocialLink = async () => {
    if (!editingSocialLink) return;
    setSavingSocialLink(true);
    try {
      await axiosInstance.put(`/social-links/${editingSocialLink.link_id}`, {
        platform: editingSocialLink.platform,
        url: editingSocialLink.url,
        icon: editingSocialLink.icon,
        display_order: editingSocialLink.display_order,
        is_active: editingSocialLink.is_active
      });
      toast.success('Social link updated!');
      setShowEditSocialLink(false);
      setEditingSocialLink(null);
      loadSocialLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update social link');
    }
    setSavingSocialLink(false);
  };

  const handleDeleteSocialLink = async (linkId) => {
    if (!window.confirm('Delete this social link?')) return;
    try {
      await axiosInstance.delete(`/social-links/${linkId}`);
      toast.success('Social link deleted');
      loadSocialLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete social link');
    }
  };

  const handleSaveStripeKey = async () => {
    if (!stripeKeyInput) return;
    setSavingStripeKey(true);
    try {
      await axiosInstance.post('/admin/api-keys', {
        key_type: 'stripe',
        api_key: stripeKeyInput
      });
      toast.success('Stripe API key saved successfully!');
      setStripeKeyInput('');
      loadApiKeysStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Stripe key');
    } finally {
      setSavingStripeKey(false);
    }
  };

  const handleSaveResendKey = async () => {
    if (!resendKeyInput) return;
    setSavingResendKey(true);
    try {
      await axiosInstance.post('/admin/api-keys', {
        key_type: 'resend',
        api_key: resendKeyInput
      });
      toast.success('Resend API key saved successfully!');
      setResendKeyInput('');
      loadApiKeysStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Resend key');
    } finally {
      setSavingResendKey(false);
    }
  };

  // Load current hero image from CMS
  const loadHeroImage = async () => {
    try {
      const response = await axiosInstance.get('/cms/content/home');
      const heroContent = response.data.find(item => item.section === 'hero' && item.key === 'image');
      if (heroContent) {
        setHeroImageUrl(heroContent.value);
        setHeroImagePreview(heroContent.value);
      }
    } catch (error) {
      console.log('Failed to load hero image');
    }
  };

  // Handle hero image file selection
  const handleHeroImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHeroImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setHeroImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload hero image and save to CMS
  const handleSaveHeroImage = async () => {
    if (!heroImageFile && !heroImageUrl) {
      toast.error('Please select an image or enter an image URL');
      return;
    }
    
    setSavingHeroImage(true);
    try {
      let imageUrl = heroImageUrl;
      
      // If file is selected, upload it first
      if (heroImageFile) {
        const formData = new FormData();
        formData.append('file', heroImageFile);
        const uploadRes = await axiosInstance.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        imageUrl = `${BACKEND_URL}${uploadRes.data.url}`;
      }
      
      // Save to CMS
      await axiosInstance.post('/cms/content', {
        page: 'home',
        section: 'hero',
        content_type: 'image',
        key: 'image',
        value: imageUrl,
        order: 0
      });
      
      toast.success('Homepage hero image updated successfully!');
      setHeroImageFile(null);
      setHeroImageUrl(imageUrl);
      setHeroImagePreview(imageUrl);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save hero image');
    } finally {
      setSavingHeroImage(false);
    }
  };

  // Drill-down handlers for clickable charts/widgets
  const handleGoalsDrillDown = async (status) => {
    try {
      const response = await axiosInstance.get(`/admin/goals?status=${status}`);
      setDrillDownData({
        type: 'goals',
        title: `${status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')} Goals`,
        data: response.data
      });
      setShowDrillDown(true);
    } catch (error) {
      // Use analytics data as fallback
      const goals = status === 'completed' ? analytics?.completed_goals : 
                   status === 'in_progress' ? analytics?.in_progress_goals : 
                   analytics?.overdue_goals || [];
      setDrillDownData({
        type: 'goals',
        title: `${status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')} Goals`,
        data: goals
      });
      setShowDrillDown(true);
    }
  };

  const handleMentorshipsDrillDown = async (status) => {
    try {
      const response = await axiosInstance.get(`/admin/mentorships?status=${status}`);
      setDrillDownData({
        type: 'mentorships',
        title: `${status.charAt(0).toUpperCase() + status.slice(1)} Mentorships`,
        data: response.data
      });
      setShowDrillDown(true);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const handleUsersDrillDown = async (role) => {
    try {
      const response = await axiosInstance.get(`/admin/users?role=${role}`);
      setDrillDownData({
        type: 'users',
        title: `${role.charAt(0).toUpperCase() + role.slice(1)}s`,
        data: response.data
      });
      setShowDrillDown(true);
    } catch (error) {
      // Filter from existing users
      const filtered = users.filter(u => u.role === role);
      setDrillDownData({
        type: 'users',
        title: `${role.charAt(0).toUpperCase() + role.slice(1)}s`,
        data: filtered
      });
      setShowDrillDown(true);
    }
  };

  const handleRevenueDrillDown = async () => {
    try {
      const response = await axiosInstance.get('/admin/transactions');
      setDrillDownData({
        type: 'transactions',
        title: 'Revenue Transactions',
        data: response.data
      });
      setShowDrillDown(true);
    } catch (error) {
      toast.error('Failed to load transactions');
    }
  };

  // Admin Goals Management Functions
  const loadAdminGoals = async (filter = 'all') => {
    try {
      const response = await axiosInstance.get(`/admin/goals?status=${filter}`);
      setAdminGoals(response.data);
    } catch (error) {
      console.log('Failed to load goals');
    }
  };

  const handleUpdateGoal = async (goalId, updates) => {
    try {
      await axiosInstance.put(`/admin/goals/${goalId}`, updates);
      toast.success('Goal updated successfully');
      loadAdminGoals(goalsFilter);
      setEditingGoal(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update goal');
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;
    try {
      await axiosInstance.delete(`/admin/goals/${goalId}`);
      toast.success('Goal deleted');
      loadAdminGoals(goalsFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete goal');
    }
  };

  // Admin Tasks Management Functions
  const loadAdminTasks = async (filter = 'all') => {
    try {
      const response = await axiosInstance.get(`/admin/tasks?status=${filter}`);
      setAdminTasks(response.data);
    } catch (error) {
      console.log('Failed to load tasks');
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      await axiosInstance.put(`/admin/tasks/${taskId}`, updates);
      toast.success('Task updated successfully');
      loadAdminTasks(tasksFilter);
      setEditingTask(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await axiosInstance.delete(`/admin/tasks/${taskId}`);
      toast.success('Task deleted');
      loadAdminTasks(tasksFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    }
  };

  const loadSermons = async () => {
    try {
      const response = await axiosInstance.get('/sermons');
      setSermons(response.data);
    } catch (error) {
      console.log('Failed to load sermons');
    }
  };

  // Pastor's Corner Management Functions
  const loadPastorPosts = async () => {
    try {
      const response = await axiosInstance.get('/pastor-corner');
      setPastorPosts(response.data);
    } catch (error) {
      console.log('Failed to load pastor posts');
    }
  };

  const handleAddPastorPost = async (e) => {
    e.preventDefault();
    if (!newPastorPost.title || !newPastorPost.content) {
      toast.error('Please fill in title and content');
      return;
    }
    
    setSavingPastorPost(true);
    try {
      const res = await axiosInstance.post('/pastor-corner', newPastorPost);
      
      // Upload image if selected
      if (pastorPostImage) {
        const formData = new FormData();
        formData.append('file', pastorPostImage);
        await axiosInstance.post(`/pastor-corner/${res.data.post_id}/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success('Pastor\'s Corner post created!');
      setShowAddPastorPost(false);
      setNewPastorPost({ title: '', content: '' });
      setPastorPostImage(null);
      loadPastorPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    }
    setSavingPastorPost(false);
  };

  const handleDeletePastorPost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await axiosInstance.delete(`/pastor-corner/${postId}`);
      toast.success('Post deleted');
      loadPastorPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const loadWorshipSongs = async () => {
    try {
      const response = await axiosInstance.get('/songs');
      setWorshipSongs(response.data);
    } catch (error) {
      console.log('Failed to load worship songs');
    }
  };

  // Advanced Courses Management Functions
  const loadAdvancedCourses = async () => {
    setAdvancedCoursesLoading(true);
    try {
      // Use admin endpoint to get all courses including drafts
      const response = await axiosInstance.get('/advanced-courses/admin/all');
      setAdvancedCourses(response.data);
    } catch (error) {
      // Fallback to public endpoint if not admin
      try {
        const response = await axiosInstance.get('/advanced-courses');
        setAdvancedCourses(response.data);
      } catch (e) {
        console.log('Failed to load advanced courses');
      }
    }
    setAdvancedCoursesLoading(false);
  };

  const handleEditAdvancedCourse = (course) => {
    setEditingAdvancedCourse({
      ...course,
      price: course.price?.toString() || '0'
    });
    setShowEditAdvancedCourse(true);
  };

  const handleSaveAdvancedCourse = async (e) => {
    e.preventDefault();
    if (!editingAdvancedCourse) return;
    
    setSavingAdvancedCourse(true);
    try {
      await axiosInstance.put(`/advanced-courses/${editingAdvancedCourse.course_id}`, {
        title: editingAdvancedCourse.title,
        description: editingAdvancedCourse.description,
        price: parseFloat(editingAdvancedCourse.price),
        status: editingAdvancedCourse.status,
        category: editingAdvancedCourse.category,
        thumbnail_url: editingAdvancedCourse.thumbnail_url
      });
      toast.success('Course updated successfully!');
      setShowEditAdvancedCourse(false);
      setEditingAdvancedCourse(null);
      loadAdvancedCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update course');
    }
    setSavingAdvancedCourse(false);
  };

  const handleDeleteAdvancedCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course? This cannot be undone.')) return;
    try {
      await axiosInstance.delete(`/advanced-courses/${courseId}`);
      toast.success('Course deleted');
      loadAdvancedCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete course');
    }
  };

  const handlePublishAdvancedCourse = async (courseId) => {
    if (!window.confirm('Publish this course? It will become visible to all users.')) return;
    try {
      await axiosInstance.post(`/advanced-courses/${courseId}/publish`);
      toast.success('Course published successfully!');
      loadAdvancedCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish course');
    }
  };

  const handleAddLessonToNewCourse = () => {
    if (!newLessonData.title || !newLessonData.content) {
      toast.error('Please fill in lesson title and content');
      return;
    }
    
    const lessonNumber = newCourseData.lessons.length + 1;
    const lessonId = `lesson_${lessonNumber}_${newLessonData.title.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '').substring(0, 30)}`;
    
    // Convert content to slides
    const contentParagraphs = newLessonData.content.split('\n\n').filter(p => p.trim());
    const slides = contentParagraphs.map((paragraph, idx) => ({
      slide_number: idx + 1,
      title: idx === 0 ? newLessonData.title : `Slide ${idx + 1}`,
      content: paragraph.trim(),
      bullet_points: [],
      scripture_reference: null,
      image_url: null
    }));
    
    // Ensure at least one slide
    if (slides.length === 0) {
      slides.push({
        slide_number: 1,
        title: newLessonData.title,
        content: newLessonData.content || newLessonData.description,
        bullet_points: [],
        scripture_reference: null,
        image_url: null
      });
    }
    
    const newLesson = {
      lesson_id: lessonId,
      lesson_number: lessonNumber,
      title: newLessonData.title,
      description: newLessonData.description || newLessonData.title,
      duration_minutes: parseInt(newLessonData.duration_minutes) || 15,
      slides: slides,
      is_free_preview: newLessonData.is_free_preview
    };
    
    setNewCourseData(prev => ({
      ...prev,
      lessons: [...prev.lessons, newLesson]
    }));
    
    setNewLessonData({
      title: '',
      description: '',
      duration_minutes: 15,
      content: '',
      is_free_preview: false
    });
    setShowAddLesson(false);
    toast.success(`Lesson "${newLesson.title}" added`);
  };

  const handleRemoveLessonFromNewCourse = (lessonIdx) => {
    setNewCourseData(prev => ({
      ...prev,
      lessons: prev.lessons.filter((_, idx) => idx !== lessonIdx).map((l, idx) => ({
        ...l,
        lesson_number: idx + 1
      }))
    }));
  };

  const handleCreateAdvancedCourse = async () => {
    if (!newCourseData.title || !newCourseData.description) {
      toast.error('Please fill in course title and description');
      return;
    }
    if (newCourseData.lessons.length === 0) {
      toast.error('Please add at least one lesson');
      return;
    }
    
    setCreatingCourse(true);
    try {
      await axiosInstance.post('/advanced-courses', {
        title: newCourseData.title,
        description: newCourseData.description,
        category: newCourseData.category,
        price: parseFloat(newCourseData.price) || 0,
        thumbnail_url: newCourseData.thumbnail_url || null,
        lessons: newCourseData.lessons,
        question_bank: []
      });
      
      toast.success('Course created successfully!');
      setShowCreateAdvancedCourse(false);
      setNewCourseData({
        title: '',
        description: '',
        category: 'Bible Study',
        price: '0',
        thumbnail_url: '',
        lessons: []
      });
      loadAdvancedCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create course');
    }
    setCreatingCourse(false);
  };

  const handleAIGenerateCourse = async () => {
    if (!aiGenerateForm.topic.trim()) {
      toast.error('Please enter a course topic');
      return;
    }
    
    setAiGenerating(true);
    try {
      const response = await axiosInstance.post('/advanced-courses/ai-generate', {
        topic: aiGenerateForm.topic,
        num_lessons: parseInt(aiGenerateForm.num_lessons),
        category: aiGenerateForm.category,
        difficulty: aiGenerateForm.difficulty,
        include_quiz: aiGenerateForm.include_quiz
      });
      
      toast.success(`Course "${response.data.title}" generated with ${response.data.lesson_count} lessons!`);
      setShowAIGenerate(false);
      setAiGenerateForm({
        topic: '',
        num_lessons: 5,
        category: 'Bible Study',
        difficulty: 'intermediate',
        include_quiz: true
      });
      loadAdvancedCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI generation failed. Please try again.');
    }
    setAiGenerating(false);
  };

  // Admin Course Enrollment Functions
  const handleOpenAdminEnroll = async (course) => {
    setEnrollingCourse(course);
    setShowAdminEnroll(true);
    setEnrollUserSearch('');
    setEnrollUserResults([]);
    // Load current enrollments
    setLoadingEnrollments(true);
    try {
      const response = await axiosInstance.get(`/advanced-courses/${course.course_id}/enrollments`);
      setCourseEnrollments(response.data);
    } catch (error) {
      console.error('Failed to load enrollments');
      setCourseEnrollments([]);
    }
    setLoadingEnrollments(false);
  };

  const handleSearchUsersForEnroll = async () => {
    if (!enrollUserSearch.trim()) return;
    try {
      const response = await axiosInstance.get(`/admin/users?search=${encodeURIComponent(enrollUserSearch)}`);
      setEnrollUserResults(response.data.slice(0, 10));
    } catch (error) {
      toast.error('Failed to search users');
    }
  };

  const handleAdminEnrollUser = async (userId, userName) => {
    if (!enrollingCourse) return;
    setEnrollingUser(true);
    try {
      await axiosInstance.post(`/advanced-courses/${enrollingCourse.course_id}/admin-enroll`, {
        user_id: userId,
        notes: 'Admin enrolled'
      });
      toast.success(`${userName} enrolled in ${enrollingCourse.title}`);
      // Refresh enrollments
      const response = await axiosInstance.get(`/advanced-courses/${enrollingCourse.course_id}/enrollments`);
      setCourseEnrollments(response.data);
      setEnrollUserSearch('');
      setEnrollUserResults([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll user');
    }
    setEnrollingUser(false);
  };

  const handleRemoveEnrollment = async (enrollmentId) => {
    if (!enrollingCourse || !window.confirm('Remove this enrollment?')) return;
    try {
      await axiosInstance.delete(`/advanced-courses/${enrollingCourse.course_id}/enrollments/${enrollmentId}`);
      toast.success('Enrollment removed');
      setCourseEnrollments(courseEnrollments.filter(e => e.enrollment_id !== enrollmentId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove enrollment');
    }
  };

  // Store Product Management Functions
  const loadStoreProducts = async () => {
    try {
      const response = await axiosInstance.get('/store/products');
      setStoreProducts(response.data);
    } catch (error) {
      console.log('Failed to load store products');
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    if (!newProductData.name || !newProductData.price) {
      toast.error('Please fill in name and price');
      return;
    }
    
    setSavingProduct(true);
    try {
      let imageUrl = newProductData.image;
      
      // Upload image if file selected
      if (productImageFile) {
        const formData = new FormData();
        formData.append('file', productImageFile);
        const uploadRes = await axiosInstance.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        imageUrl = `${BACKEND_URL}${uploadRes.data.url}`;
      }
      
      await axiosInstance.post('/store/products', {
        ...newProductData,
        price: parseFloat(newProductData.price),
        image: imageUrl
      });
      toast.success('Product added successfully!');
      setShowAddProduct(false);
      setNewProductData({ name: '', description: '', price: '', category: 'books', image: '', stock_quantity: 100 });
      setProductImageFile(null);
      loadStoreProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add product');
    } finally {
      setSavingProduct(false);
    }
  };

  const handleEditProduct = async (e) => {
    e.preventDefault();
    if (!editingProduct.name || !editingProduct.price) {
      toast.error('Please fill in name and price');
      return;
    }
    
    setSavingProduct(true);
    try {
      let imageUrl = editingProduct.image;
      
      // Upload new image if file selected
      if (productImageFile) {
        const formData = new FormData();
        formData.append('file', productImageFile);
        const uploadRes = await axiosInstance.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        imageUrl = `${BACKEND_URL}${uploadRes.data.url}`;
      }
      
      await axiosInstance.put(`/store/products/${editingProduct.product_id}`, {
        ...editingProduct,
        price: parseFloat(editingProduct.price),
        image: imageUrl
      });
      toast.success('Product updated successfully!');
      setShowEditProduct(false);
      setEditingProduct(null);
      setProductImageFile(null);
      loadStoreProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update product');
    } finally {
      setSavingProduct(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axiosInstance.delete(`/store/products/${productId}`);
      toast.success('Product deleted');
      loadStoreProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleAddSong = async (e) => {
    e.preventDefault();
    if (!newSongData.title || !newSongData.artist) {
      toast.error('Please fill in title and artist');
      return;
    }
    if (!songAudioFile) {
      toast.error('Please select an audio file');
      return;
    }
    
    setUploadingSong(true);
    try {
      const formData = new FormData();
      formData.append('title', newSongData.title);
      formData.append('artist', newSongData.artist);
      if (newSongData.album) formData.append('album', newSongData.album);
      if (newSongData.genre) formData.append('genre', newSongData.genre);
      if (newSongData.lyrics) formData.append('lyrics', newSongData.lyrics);
      formData.append('audio_file', songAudioFile);
      if (songCoverFile) formData.append('cover_image', songCoverFile);
      
      await axiosInstance.post('/admin/songs', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Song uploaded successfully!');
      setShowAddSong(false);
      setNewSongData({ title: '', artist: '', album: '', genre: '', lyrics: '' });
      setSongAudioFile(null);
      setSongCoverFile(null);
      loadWorshipSongs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload song');
    } finally {
      setUploadingSong(false);
    }
  };

  const handleDeleteSong = async (songId) => {
    if (!window.confirm('Are you sure you want to delete this song?')) return;
    try {
      await axiosInstance.delete(`/admin/songs/${songId}`);
      toast.success('Song deleted');
      loadWorshipSongs();
    } catch (error) {
      toast.error('Failed to delete song');
    }
  };

  const handleAddSermon = async (e) => {
    e.preventDefault();
    if (!newSermonData.title || !newSermonData.preacher) {
      toast.error('Please fill in title and preacher');
      return;
    }
    try {
      await axiosInstance.post('/sermons', newSermonData);
      toast.success('Sermon added successfully!');
      setShowAddSermon(false);
      setNewSermonData({ title: '', description: '', preacher: '', youtube_url: '', scripture_reference: '', series: '' });
      loadSermons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add sermon');
    }
  };

  const handleDeleteSermon = async (sermonId) => {
    if (!window.confirm('Are you sure you want to delete this sermon?')) return;
    try {
      await axiosInstance.delete(`/sermons/${sermonId}`);
      toast.success('Sermon deleted');
      loadSermons();
    } catch (error) {
      toast.error('Failed to delete sermon');
    }
  };

  // Mentorship Plans State (New Consolidated System)
  const [mentorshipPlansAdmin, setMentorshipPlansAdmin] = useState({
    starter: { name: "Starter", price: 29.99, interval: "month", features: ["AI Resume Builder", "AI Cover Letter", "Basic career guidance", "Email support"] },
    growth: { name: "Growth", price: 49.99, interval: "month", features: ["All Starter features", "1-on-1 Mentorship matching", "Leadership assessment", "Goal tracking", "Priority support"] },
    elite: { name: "Elite", price: 99.99, interval: "month", features: ["All Growth features", "Budget tracking", "Business plan generator", "Counseling access", "Ministry team access", "VIP support"] },
    lifetime: { name: "Lifetime", original_price: 500, price: 350, interval: null, features: ["All Elite features forever", "No monthly payments", "Lifetime updates", "Founder member badge", "Priority VIP support"] }
  });
  const [showEditPricing, setShowEditPricing] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planFormData, setPlanFormData] = useState({ name: "", price: 0, original_price: null, features: [] });
  const [savingPricing, setSavingPricing] = useState(false);

  const loadMentorshipPlans = async () => {
    try {
      const response = await axiosInstance.get('/mentorship/plans');
      setMentorshipPlansAdmin(response.data);
    } catch (error) {
      console.log('Using default mentorship plans');
    }
  };

  const handleEditPlan = (planKey) => {
    const plan = mentorshipPlansAdmin[planKey];
    setEditingPlan(planKey);
    setPlanFormData({
      name: plan.name,
      price: plan.price,
      original_price: plan.original_price || null,
      features: plan.features || []
    });
    setShowEditPricing(true);
  };

  const handleSavePlan = async () => {
    setSavingPricing(true);
    try {
      const updatedPlans = {
        ...mentorshipPlansAdmin,
        [editingPlan]: {
          ...mentorshipPlansAdmin[editingPlan],
          name: planFormData.name,
          price: planFormData.price,
          original_price: planFormData.original_price,
          features: planFormData.features
        }
      };
      await axiosInstance.put('/admin/mentorship/plans', updatedPlans);
      toast.success('Plan updated successfully!');
      setShowEditPricing(false);
      setEditingPlan(null);
      loadMentorshipPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update plan');
    }
    setSavingPricing(false);
  };

  const handleAddFeature = () => {
    setPlanFormData({ ...planFormData, features: [...planFormData.features, ""] });
  };

  const handleRemoveFeature = (index) => {
    setPlanFormData({ ...planFormData, features: planFormData.features.filter((_, i) => i !== index) });
  };

  const handleUpdateFeature = (index, value) => {
    const newFeatures = [...planFormData.features];
    newFeatures[index] = value;
    setPlanFormData({ ...planFormData, features: newFeatures });
  };

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter]);

  const loadTemplates = async () => {
    try {
      const response = await axiosInstance.get('/admin/mentorship-templates');
      setTemplates(response.data);
    } catch (error) {
      console.log('Failed to load templates');
    }
  };

  const [analytics, setAnalytics] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);

  const loadAnalytics = async () => {
    try {
      const response = await axiosInstance.get('/admin/analytics');
      setAnalytics(response.data);
    } catch (error) {
      console.log('Failed to load analytics');
    }
  };

  const checkCurrentLogo = async () => {
    try {
      const response = await axiosInstance.get('/settings/logo-status');
      if (response.data.has_logo) {
        setCurrentLogo(`${BACKEND_URL}${response.data.logo_url}?t=${Date.now()}`);
      }
    } catch (error) {
      console.log('No custom logo set');
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Invalid file type. Please upload PNG, JPG, SVG, or WebP');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 5MB');
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      toast.error('Please select a logo file first');
      return;
    }
    
    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', logoFile);
    
    try {
      await axiosInstance.post('/admin/settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Logo uploaded successfully!');
      setLogoFile(null);
      setLogoPreview(null);
      checkCurrentLogo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Are you sure you want to delete the current logo?')) {
      return;
    }
    
    try {
      await axiosInstance.delete('/admin/settings/logo');
      toast.success('Logo deleted successfully');
      setCurrentLogo(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete logo');
    }
  };

  // Church Management Functions
  const loadChurchData = async () => {
    try {
      const [ministriesRes, announcementsRes, prayerRes, statsRes] = await Promise.all([
        axiosInstance.get('/ministries').catch(() => ({ data: [] })),
        axiosInstance.get('/announcements').catch(() => ({ data: [] })),
        axiosInstance.get('/prayer-requests?public_only=false').catch(() => ({ data: [] })),
        axiosInstance.get('/church/stats').catch(() => ({ data: null }))
      ]);
      setMinistries(ministriesRes.data);
      setAnnouncements(announcementsRes.data);
      setPrayerRequests(prayerRes.data);
      setChurchStats(statsRes.data);
    } catch (error) {
      console.error('Error loading church data:', error);
    }
  };

  const loadLivestreamData = async () => {
    try {
      const [streamsRes, permissionsRes, instructionsRes] = await Promise.all([
        axiosInstance.get('/livestream').catch(() => ({ data: [] })),
        axiosInstance.get('/livestream/permissions').catch(() => ({ data: [] })),
        axiosInstance.get('/livestream/setup-instructions').catch(() => ({ data: null }))
      ]);
      setLivestreams(streamsRes.data);
      setStreamPermissions(permissionsRes.data);
      setStreamInstructions(instructionsRes.data);
    } catch (error) {
      console.error('Error loading livestream data:', error);
    }
  };

  const handleCreateMinistry = async () => {
    if (!newMinistryData.name) {
      toast.error('Ministry name is required');
      return;
    }
    try {
      await axiosInstance.post('/ministries', newMinistryData);
      toast.success('Ministry created');
      setShowAddMinistry(false);
      setNewMinistryData({ name: '', description: '', category: 'general', meeting_schedule: '' });
      loadChurchData();
    } catch (error) {
      toast.error('Failed to create ministry');
    }
  };

  const handleCreateAnnouncement = async () => {
    if (!newAnnouncementData.title || !newAnnouncementData.content) {
      toast.error('Title and content are required');
      return;
    }
    try {
      await axiosInstance.post('/announcements', newAnnouncementData);
      toast.success('Announcement posted');
      setShowAddAnnouncement(false);
      setNewAnnouncementData({ title: '', content: '', priority: 'normal' });
      loadChurchData();
    } catch (error) {
      toast.error('Failed to create announcement');
    }
  };

  const handleRecordAttendance = async () => {
    try {
      await axiosInstance.post('/attendance', attendanceData);
      toast.success('Attendance recorded');
      setShowRecordAttendance(false);
      setAttendanceData({ service_date: new Date().toISOString().split('T')[0], service_type: 'sunday', attendee_ids: [], visitor_count: 0 });
      loadChurchData();
    } catch (error) {
      toast.error('Failed to record attendance');
    }
  };

  // Church Events Management Functions
  const loadChurchEvents = async () => {
    setChurchEventsLoading(true);
    try {
      const response = await axiosInstance.get('/church-events');
      setChurchEvents(response.data);
    } catch (error) {
      console.error('Error loading church events:', error);
    }
    setChurchEventsLoading(false);
  };

  const handleCreateChurchEvent = async () => {
    if (!newChurchEventData.title || !newChurchEventData.start_time) {
      toast.error('Event title and start time are required');
      return;
    }
    setSavingChurchEvent(true);
    try {
      await axiosInstance.post('/church-events', {
        ...newChurchEventData,
        start_time: new Date(newChurchEventData.start_time).toISOString(),
        end_time: newChurchEventData.end_time ? new Date(newChurchEventData.end_time).toISOString() : null
      });
      toast.success('Event created successfully!');
      setShowAddChurchEvent(false);
      setNewChurchEventData({
        title: '',
        description: '',
        start_time: '',
        end_time: '',
        location: '',
        event_type: 'general',
        thumbnail_url: '',
        featured_image_url: ''
      });
      loadChurchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
    setSavingChurchEvent(false);
  };

  const handleEditChurchEvent = (event) => {
    setEditingChurchEvent({
      ...event,
      start_time: event.start_time ? new Date(event.start_time).toISOString().slice(0, 16) : '',
      end_time: event.end_time ? new Date(event.end_time).toISOString().slice(0, 16) : ''
    });
    setShowEditChurchEvent(true);
  };

  const handleUpdateChurchEvent = async () => {
    if (!editingChurchEvent) return;
    setSavingChurchEvent(true);
    try {
      await axiosInstance.put(`/church-events/${editingChurchEvent.event_id}`, {
        title: editingChurchEvent.title,
        description: editingChurchEvent.description,
        start_time: editingChurchEvent.start_time ? new Date(editingChurchEvent.start_time).toISOString() : null,
        end_time: editingChurchEvent.end_time ? new Date(editingChurchEvent.end_time).toISOString() : null,
        location: editingChurchEvent.location,
        event_type: editingChurchEvent.event_type,
        thumbnail_url: editingChurchEvent.thumbnail_url,
        featured_image_url: editingChurchEvent.featured_image_url
      });
      toast.success('Event updated successfully!');
      setShowEditChurchEvent(false);
      setEditingChurchEvent(null);
      loadChurchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    }
    setSavingChurchEvent(false);
  };

  const handleDeleteChurchEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await axiosInstance.delete(`/church-events/${eventId}`);
      toast.success('Event deleted');
      loadChurchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  const handleCreateLivestream = async () => {
    if (!newLivestreamData.title) {
      toast.error('Stream title is required');
      return;
    }
    try {
      const response = await axiosInstance.post('/livestream', newLivestreamData);
      toast.success('Live stream created');
      setShowAddLivestream(false);
      setNewLivestreamData({ title: '', description: '', stream_type: 'service', scheduled_at: '' });
      loadLivestreamData();
      // Show the stream details with key
      setShowStreamSetup(response.data);
    } catch (error) {
      console.error('Livestream error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create live stream');
    }
  };

  const handleUpdateStreamPermission = async (userId, canStream) => {
    try {
      await axiosInstance.post('/livestream/permissions', { user_id: userId, can_stream: canStream });
      toast.success(`Streaming permission ${canStream ? 'granted' : 'revoked'}`);
      loadLivestreamData();
    } catch (error) {
      toast.error('Failed to update permission');
    }
  };

  const handleGoLive = async (streamId) => {
    try {
      await axiosInstance.post(`/livestream/${streamId}/go-live`);
      toast.success('Stream is now live!');
      loadLivestreamData();
    } catch (error) {
      toast.error('Failed to start stream');
    }
  };

  const handleEndStream = async (streamId) => {
    try {
      await axiosInstance.post(`/livestream/${streamId}/end`);
      toast.success('Stream ended');
      loadLivestreamData();
    } catch (error) {
      toast.error('Failed to end stream');
    }
  };

  // Resource Library Functions
  const loadResourcesData = async () => {
    setResourcesLoading(true);
    try {
      const [resourcesRes, foldersRes, statsRes, categoriesRes] = await Promise.all([
        axiosInstance.get('/resources'),
        axiosInstance.get('/resources/folders'),
        axiosInstance.get('/resources/stats').catch(() => ({ data: null })),
        axiosInstance.get('/resources/categories/list')
      ]);
      setResources(resourcesRes.data);
      setResourceFolders(foldersRes.data);
      setResourceStats(statsRes.data);
      setResourceCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error loading resources:', error);
    } finally {
      setResourcesLoading(false);
    }
  };

  const handleUploadResource = async () => {
    if (!selectedResourceFile || !newResourceData.title) {
      toast.error('Please select a file and enter a title');
      return;
    }
    setUploadingResource(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedResourceFile);
      formData.append('title', newResourceData.title);
      formData.append('description', newResourceData.description || '');
      formData.append('category', newResourceData.category);
      formData.append('tags', newResourceData.tags);
      formData.append('is_public', newResourceData.is_public);
      if (newResourceData.folder_id) {
        formData.append('folder_id', newResourceData.folder_id);
      }

      const response = await axiosInstance.post('/resources/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });

      toast.success('Resource uploaded successfully!');
      setShowUploadResource(false);
      setSelectedResourceFile(null);
      setNewResourceData({ title: '', description: '', category: 'general', tags: '', is_public: true, folder_id: null });
      loadResourcesData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload resource');
    } finally {
      setUploadingResource(false);
      setUploadProgress(0);
    }
  };

  const handleCreateResourceFolder = async () => {
    if (!newFolderData.name) {
      toast.error('Please enter a folder name');
      return;
    }
    try {
      await axiosInstance.post('/resources/folders', newFolderData);
      toast.success('Folder created');
      setShowCreateFolder(false);
      setNewFolderData({ name: '', description: '', is_public: true });
      loadResourcesData();
    } catch (error) {
      toast.error('Failed to create folder');
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;
    try {
      await axiosInstance.delete(`/resources/${resourceId}`);
      toast.success('Resource deleted');
      loadResourcesData();
    } catch (error) {
      toast.error('Failed to delete resource');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Are you sure you want to delete this folder? It must be empty.')) return;
    try {
      await axiosInstance.delete(`/resources/folders/${folderId}`);
      toast.success('Folder deleted');
      loadResourcesData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete folder');
    }
  };

  const getFileTypeIcon = (fileType) => {
    switch (fileType) {
      case 'video': return <FileVideo className="h-5 w-5 text-purple-500" />;
      case 'audio': return <FileAudio className="h-5 w-5 text-green-500" />;
      case 'image': return <Image className="h-5 w-5 text-blue-500" />;
      case 'document': return <FileText className="h-5 w-5 text-orange-500" />;
      case 'presentation': return <FileText className="h-5 w-5 text-red-500" />;
      case 'spreadsheet': return <FileText className="h-5 w-5 text-green-600" />;
      default: return <File className="h-5 w-5 text-gray-500" />;
    }
  };

  const filteredResources = resources.filter(resource => {
    if (resourceFilter.category && resource.category !== resourceFilter.category) return false;
    if (resourceFilter.file_type && resource.file_type !== resourceFilter.file_type) return false;
    if (resourceFilter.search) {
      const searchLower = resourceFilter.search.toLowerCase();
      if (!resource.title.toLowerCase().includes(searchLower) && 
          !(resource.description || '').toLowerCase().includes(searchLower)) return false;
    }
    return true;
  });

  // Resource Preview Function
  const handlePreviewResource = async (resource) => {
    setPreviewLoading(true);
    try {
      const res = await axiosInstance.get(`/resources/preview/${resource.resource_id}`);
      setPreviewResource(res.data);
    } catch (error) {
      toast.error('Failed to load preview');
      // Fallback to direct download
      window.open(`${BACKEND_URL}${resource.file_path}`, '_blank');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Weekly Focus & Monthly Theme Functions
  const loadWeeklyFocusData = async () => {
    try {
      const res = await axiosInstance.get('/weekly-focus/all');
      setWeeklyFocusList(res.data);
    } catch (error) {
      console.error('Error loading weekly focus:', error);
    }
  };

  const loadMonthlyThemesData = async () => {
    try {
      const res = await axiosInstance.get('/monthly-theme/all');
      setMonthlyThemesList(res.data);
    } catch (error) {
      console.error('Error loading monthly themes:', error);
    }
  };

  const handleCreateWeeklyFocus = async () => {
    if (!newWeeklyFocus.title || !newWeeklyFocus.scripture) {
      toast.error('Please fill in title and scripture');
      return;
    }
    setUploadingImage(true);
    try {
      let imageUrl = newWeeklyFocus.image_url;
      
      // Upload image if selected
      if (weeklyFocusImageFile) {
        const formData = new FormData();
        formData.append('file', weeklyFocusImageFile);
        formData.append('content_type', 'weekly_focus');
        const uploadRes = await axiosInstance.post('/church-content/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        imageUrl = uploadRes.data.url;
      }
      
      await axiosInstance.post('/weekly-focus', {
        ...newWeeklyFocus,
        image_url: imageUrl,
        prayer_points: newWeeklyFocus.prayer_points.split('\n').filter(p => p.trim()),
        action_items: newWeeklyFocus.action_items.split('\n').filter(a => a.trim())
      });
      toast.success('Weekly focus created');
      setShowAddWeeklyFocus(false);
      setNewWeeklyFocus({ title: '', scripture: '', scripture_reference: '', message: '', prayer_points: '', action_items: '', week_start: '', week_end: '', image_url: '' });
      setWeeklyFocusImageFile(null);
      loadWeeklyFocusData();
    } catch (error) {
      toast.error('Failed to create weekly focus');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleUpdateWeeklyFocus = async () => {
    if (!editingWeeklyFocus) return;
    try {
      await axiosInstance.put(`/weekly-focus/${editingWeeklyFocus.focus_id}`, {
        ...editingWeeklyFocus,
        prayer_points: typeof editingWeeklyFocus.prayer_points === 'string' 
          ? editingWeeklyFocus.prayer_points.split('\n').filter(p => p.trim())
          : editingWeeklyFocus.prayer_points,
        action_items: typeof editingWeeklyFocus.action_items === 'string'
          ? editingWeeklyFocus.action_items.split('\n').filter(a => a.trim())
          : editingWeeklyFocus.action_items
      });
      toast.success('Weekly focus updated');
      setEditingWeeklyFocus(null);
      loadWeeklyFocusData();
    } catch (error) {
      toast.error('Failed to update weekly focus');
    }
  };

  const handleDeleteWeeklyFocus = async (focusId) => {
    if (!window.confirm('Delete this weekly focus?')) return;
    try {
      await axiosInstance.delete(`/weekly-focus/${focusId}`);
      toast.success('Weekly focus deleted');
      loadWeeklyFocusData();
    } catch (error) {
      toast.error('Failed to delete weekly focus');
    }
  };

  const handleCreateMonthlyTheme = async () => {
    if (!newMonthlyTheme.title || !newMonthlyTheme.month) {
      toast.error('Please fill in title and month');
      return;
    }
    setUploadingImage(true);
    try {
      let imageUrl = newMonthlyTheme.image_url;
      
      // Upload image if selected
      if (monthlyThemeImageFile) {
        const formData = new FormData();
        formData.append('file', monthlyThemeImageFile);
        formData.append('content_type', 'monthly_theme');
        const uploadRes = await axiosInstance.post('/church-content/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        imageUrl = uploadRes.data.url;
      }
      
      await axiosInstance.post('/monthly-theme', {
        ...newMonthlyTheme,
        image_url: imageUrl,
        key_events: newMonthlyTheme.key_events.split('\n').filter(e => e.trim())
      });
      toast.success('Monthly theme created');
      setShowAddMonthlyTheme(false);
      setNewMonthlyTheme({ title: '', description: '', scripture: '', scripture_reference: '', month: '', image_url: '', key_events: '' });
      setMonthlyThemeImageFile(null);
      loadMonthlyThemesData();
    } catch (error) {
      toast.error('Failed to create monthly theme');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleUpdateMonthlyTheme = async () => {
    if (!editingMonthlyTheme) return;
    try {
      await axiosInstance.put(`/monthly-theme/${editingMonthlyTheme.theme_id}`, {
        ...editingMonthlyTheme,
        key_events: typeof editingMonthlyTheme.key_events === 'string'
          ? editingMonthlyTheme.key_events.split('\n').filter(e => e.trim())
          : editingMonthlyTheme.key_events
      });
      toast.success('Monthly theme updated');
      setEditingMonthlyTheme(null);
      loadMonthlyThemesData();
    } catch (error) {
      toast.error('Failed to update monthly theme');
    }
  };

  const handleDeleteMonthlyTheme = async (themeId) => {
    if (!window.confirm('Delete this monthly theme?')) return;
    try {
      await axiosInstance.delete(`/monthly-theme/${themeId}`);
      toast.success('Monthly theme deleted');
      loadMonthlyThemesData();
    } catch (error) {
      toast.error('Failed to delete monthly theme');
    }
  };

  // Sermon Notes Functions
  const loadSermonNotesData = async () => {
    setSermonNotesLoading(true);
    try {
      const params = new URLSearchParams();
      if (sermonNoteFilter.status) params.append('status', sermonNoteFilter.status);
      if (sermonNoteFilter.series) params.append('series', sermonNoteFilter.series);
      if (sermonNoteFilter.search) params.append('search', sermonNoteFilter.search);
      
      const [notesRes, seriesRes] = await Promise.all([
        axiosInstance.get(`/sermon-notes?${params.toString()}`),
        axiosInstance.get('/sermon-notes/series')
      ]);
      setSermonNotes(notesRes.data);
      setSermonSeries(seriesRes.data);
    } catch (error) {
      console.error('Error loading sermon notes:', error);
    } finally {
      setSermonNotesLoading(false);
    }
  };

  const handleCreateSermonNote = async () => {
    if (!newSermonNote.title || !newSermonNote.main_scripture_reference) {
      toast.error('Please fill in title and main scripture reference');
      return;
    }
    try {
      await axiosInstance.post('/sermon-notes', {
        ...newSermonNote,
        supporting_scriptures: newSermonNote.supporting_scriptures.split('\n').filter(s => s.trim()),
        tags: newSermonNote.tags.split(',').map(t => t.trim()).filter(t => t)
      });
      toast.success('Sermon note created');
      setShowCreateSermonNote(false);
      setNewSermonNote({
        title: '', main_text: '', main_scripture_reference: '', supporting_scriptures: '',
        introduction: '', conclusion: '', sub_topics: [], sermon_date: '', series: '', tags: ''
      });
      loadSermonNotesData();
    } catch (error) {
      toast.error('Failed to create sermon note');
    }
  };

  const handleUpdateSermonNote = async () => {
    if (!editingSermonNote) return;
    try {
      const updateData = {
        ...editingSermonNote,
        supporting_scriptures: typeof editingSermonNote.supporting_scriptures === 'string'
          ? editingSermonNote.supporting_scriptures.split('\n').filter(s => s.trim())
          : editingSermonNote.supporting_scriptures,
        tags: typeof editingSermonNote.tags === 'string'
          ? editingSermonNote.tags.split(',').map(t => t.trim()).filter(t => t)
          : editingSermonNote.tags
      };
      await axiosInstance.put(`/sermon-notes/${editingSermonNote.note_id}`, updateData);
      toast.success('Sermon note updated');
      setEditingSermonNote(null);
      loadSermonNotesData();
    } catch (error) {
      toast.error('Failed to update sermon note');
    }
  };

  const handleDeleteSermonNote = async (noteId) => {
    if (!window.confirm('Delete this sermon note?')) return;
    try {
      await axiosInstance.delete(`/sermon-notes/${noteId}`);
      toast.success('Sermon note deleted');
      loadSermonNotesData();
    } catch (error) {
      toast.error('Failed to delete sermon note');
    }
  };

  const handlePublishSermonNote = async (noteId) => {
    try {
      await axiosInstance.post(`/sermon-notes/${noteId}/publish`);
      toast.success('Sermon note published');
      loadSermonNotesData();
    } catch (error) {
      toast.error('Failed to publish sermon note');
    }
  };

  const handleExportToPptx = async (noteId) => {
    try {
      const res = await axiosInstance.post(`/sermon-notes/${noteId}/export-pptx`);
      // Download as JSON for now (can be converted to PPTX with a library)
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${res.data.title.replace(/[^a-z0-9]/gi, '_')}_slides.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Slide structure exported! You can use this to create your PowerPoint.');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const addSubTopic = (noteState, setNoteState) => {
    const newSubTopic = {
      subtopic_id: `temp_${Date.now()}`,
      title: '',
      points: [],
      order: noteState.sub_topics.length
    };
    setNoteState({ ...noteState, sub_topics: [...noteState.sub_topics, newSubTopic] });
  };

  const updateSubTopic = (noteState, setNoteState, index, field, value) => {
    const updated = [...noteState.sub_topics];
    updated[index] = { ...updated[index], [field]: value };
    setNoteState({ ...noteState, sub_topics: updated });
  };

  const removeSubTopic = (noteState, setNoteState, index) => {
    const updated = noteState.sub_topics.filter((_, i) => i !== index);
    setNoteState({ ...noteState, sub_topics: updated });
  };

  const addPoint = (noteState, setNoteState, subtopicIndex, category) => {
    const updated = [...noteState.sub_topics];
    const newPoint = {
      point_id: `temp_point_${Date.now()}`,
      content: '',
      category: category,
      order: updated[subtopicIndex].points.length
    };
    updated[subtopicIndex].points = [...updated[subtopicIndex].points, newPoint];
    setNoteState({ ...noteState, sub_topics: updated });
  };

  const updatePoint = (noteState, setNoteState, subtopicIndex, pointIndex, content) => {
    const updated = [...noteState.sub_topics];
    updated[subtopicIndex].points[pointIndex].content = content;
    setNoteState({ ...noteState, sub_topics: updated });
  };

  const removePoint = (noteState, setNoteState, subtopicIndex, pointIndex) => {
    const updated = [...noteState.sub_topics];
    updated[subtopicIndex].points = updated[subtopicIndex].points.filter((_, i) => i !== pointIndex);
    setNoteState({ ...noteState, sub_topics: updated });
  };

  // ==================== VISITORS MANAGEMENT FUNCTIONS ====================
  const loadVisitorsData = async () => {
    setVisitorsLoading(true);
    try {
      const params = new URLSearchParams();
      if (visitorFilter.status && visitorFilter.status !== 'all') params.append('status', visitorFilter.status);
      
      const [visitorsRes, statsRes, qrRes] = await Promise.all([
        axiosInstance.get(`/visitors?${params.toString()}`),
        axiosInstance.get('/visitors/stats/summary'),
        axiosInstance.get('/visitor-qr-code')
      ]);
      
      let filtered = visitorsRes.data;
      if (visitorFilter.search) {
        const search = visitorFilter.search.toLowerCase();
        filtered = filtered.filter(v => 
          v.first_name?.toLowerCase().includes(search) ||
          v.last_name?.toLowerCase().includes(search) ||
          v.email?.toLowerCase().includes(search)
        );
      }
      setVisitors(filtered);
      setVisitorStats(statsRes.data);
      setVisitorQRCode(qrRes.data);
    } catch (error) {
      console.error('Failed to load visitors:', error);
    } finally {
      setVisitorsLoading(false);
    }
  };

  const loadSalvationContent = async () => {
    try {
      const res = await axios.get(`${API}/salvation-content`);
      setSalvationContent(res.data);
    } catch (error) {
      console.error('Failed to load salvation content:', error);
    }
  };

  const handleAssignVisitor = async (visitorId, assigneeId) => {
    try {
      await axiosInstance.put(`/visitors/${visitorId}/assign`, {
        assigned_to: assigneeId
      });
      toast.success('Visitor assigned successfully');
      setShowAssignVisitor(false);
      loadVisitorsData();
    } catch (error) {
      toast.error('Failed to assign visitor');
    }
  };

  const handleUpdateVisitorStatus = async (visitorId, status, notes = '') => {
    try {
      await axiosInstance.put(`/visitors/${visitorId}/status`, { status, notes });
      toast.success('Status updated');
      loadVisitorsData();
      if (selectedVisitor?.visitor_id === visitorId) {
        setSelectedVisitor({ ...selectedVisitor, status });
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAddVisitorNote = async (visitorId, note) => {
    try {
      await axiosInstance.post(`/visitors/${visitorId}/note?note=${encodeURIComponent(note)}`);
      toast.success('Note added');
      loadVisitorsData();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleSaveSalvationContent = async () => {
    try {
      await axiosInstance.put('/salvation-content', salvationContent);
      toast.success('Salvation page content updated');
      setShowSalvationEditor(false);
    } catch (error) {
      toast.error('Failed to save content');
    }
  };

  const loadAdminData = async () => {
    try {
      const [statsRes, usersRes, mentorshipsRes, modulesRes] = await Promise.all([
        axiosInstance.get('/admin/stats'),
        axiosInstance.get('/admin/users'),
        axiosInstance.get('/admin/mentorships'),
        axiosInstance.get('/admin/modules').catch(() => ({ data: { settings: {}, modules: {} } }))
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setMentorships(mentorshipsRes.data);
      if (modulesRes.data) {
        setModuleSettings(modulesRes.data.settings || {});
        setModuleInfo(modulesRes.data.modules || {});
      }
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error('Failed to load admin data');
    }
  };

  const handleAssignMentorship = async (e) => {
    e.preventDefault();
    
    if (!assignMentorshipData.mentor_id || !assignMentorshipData.mentee_id) {
      toast.error('Please select both a mentor and a mentee');
      return;
    }

    try {
      const response = await axiosInstance.post('/admin/mentorships', assignMentorshipData);
      let message = `Mentorship created: ${response.data.mentor_name} → ${response.data.mentee_name}`;
      if (response.data.template_used) {
        message += ` (${response.data.goals_created} goals from "${response.data.template_used}" template)`;
      }
      toast.success(message);
      setShowAssignMentorship(false);
      setAssignMentorshipData({ mentor_id: '', mentee_id: '', template_id: '' });
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create mentorship');
    }
  };

  const handleBulkAssign = async (e) => {
    e.preventDefault();
    
    if (!bulkAssignData.mentor_id || bulkAssignData.mentee_ids.length === 0) {
      toast.error('Please select a mentor and at least one mentee');
      return;
    }

    try {
      const response = await axiosInstance.post('/admin/mentorships/bulk', bulkAssignData);
      let message = `${response.data.message} for ${response.data.mentor_name}`;
      if (response.data.template_used) {
        message += ` with "${response.data.template_used}" template`;
      }
      toast.success(message);
      
      if (response.data.skipped.length > 0) {
        toast.warning(`Skipped ${response.data.skipped.length} mentee(s): ${response.data.skipped.map(s => s.reason).join(', ')}`);
      }
      
      setShowBulkAssign(false);
      setBulkAssignData({ mentor_id: '', mentee_ids: [], template_id: '' });
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create mentorships');
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    
    // Filter out empty goals
    const filteredGoals = newTemplateData.goals.filter(g => g.trim() !== '');
    if (filteredGoals.length === 0) {
      toast.error('Please add at least one goal');
      return;
    }

    try {
      await axiosInstance.post('/admin/mentorship-templates', {
        ...newTemplateData,
        goals: filteredGoals
      });
      toast.success('Template created successfully');
      setShowCreateTemplate(false);
      setNewTemplateData({ name: '', description: '', goals: [''], duration_weeks: 12 });
      loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axiosInstance.delete(`/admin/mentorship-templates/${templateId}`);
      toast.success('Template deleted');
      loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const addGoalField = () => {
    setNewTemplateData({ ...newTemplateData, goals: [...newTemplateData.goals, ''] });
  };

  const updateGoalField = (index, value) => {
    const newGoals = [...newTemplateData.goals];
    newGoals[index] = value;
    setNewTemplateData({ ...newTemplateData, goals: newGoals });
  };

  const removeGoalField = (index) => {
    if (newTemplateData.goals.length > 1) {
      const newGoals = newTemplateData.goals.filter((_, i) => i !== index);
      setNewTemplateData({ ...newTemplateData, goals: newGoals });
    }
  };

  const toggleMenteeSelection = (menteeId) => {
    setBulkAssignData(prev => ({
      ...prev,
      mentee_ids: prev.mentee_ids.includes(menteeId)
        ? prev.mentee_ids.filter(id => id !== menteeId)
        : [...prev.mentee_ids, menteeId]
    }));
  };

  const handleEndMentorship = async (mentorshipId) => {
    if (!window.confirm('Are you sure you want to end this mentorship?')) return;
    
    try {
      await axiosInstance.delete(`/admin/mentorships/${mentorshipId}`);
      toast.success('Mentorship ended');
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end mentorship');
    }
  };

  const filterUsers = () => {
    let filtered = users;
    
    if (roleFilter !== 'all') {
      filtered = filtered.filter(u => u.role === roleFilter);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(u => 
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredUsers(filtered);
  };

  // Bulk User Selection Handlers
  const handleSelectUser = (userId) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUserIds.length === filteredUsers.length) {
      setSelectedUserIds([]);
    } else {
      // Don't select current admin user
      setSelectedUserIds(filteredUsers.filter(u => u.user_id !== user?.user_id).map(u => u.user_id));
    }
  };

  const handleBulkAction = async () => {
    if (selectedUserIds.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    setBulkActionLoading(true);
    try {
      const payload = {
        user_ids: selectedUserIds,
        action: bulkActionType
      };

      if (bulkActionType === 'change_role') {
        payload.role = bulkRoleChange;
      } else if (bulkActionType === 'send_email') {
        if (!bulkEmailData.subject || !bulkEmailData.message) {
          toast.error('Email subject and message are required');
          setBulkActionLoading(false);
          return;
        }
        payload.email_subject = bulkEmailData.subject;
        payload.email_message = bulkEmailData.message;
      }

      const res = await axiosInstance.post('/admin/users/bulk-action', payload);
      
      toast.success(`${res.data.success_count} user(s) updated successfully`);
      if (res.data.failed_count > 0) {
        toast.warning(`${res.data.failed_count} action(s) failed`);
      }
      
      setShowBulkAction(false);
      setSelectedUserIds([]);
      setBulkActionType('');
      setBulkEmailData({ subject: '', message: '' });
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/admin/users/${userId}`);
      toast.success('User deleted successfully');
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleAddMentor = async (e) => {
    e.preventDefault();
    
    if (!newMentorData.name || !newMentorData.email || !newMentorData.password) {
      toast.error('Name, email, and password are required');
      return;
    }

    if (newMentorData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await axiosInstance.post('/admin/mentors', {
        name: newMentorData.name,
        email: newMentorData.email,
        password: newMentorData.password,
        bio: newMentorData.bio,
        skills: newMentorData.skills.split(',').map(s => s.trim()).filter(Boolean),
        expertise_areas: newMentorData.expertise_areas.split(',').map(s => s.trim()).filter(Boolean)
      });
      toast.success('Mentor added successfully!');
      setShowAddMentor(false);
      setNewMentorData({
        name: '',
        email: '',
        password: '',
        bio: '',
        skills: '',
        expertise_areas: ''
      });
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add mentor');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    if (!newUserData.name || !newUserData.email || !newUserData.password) {
      toast.error('Name, email, and password are required');
      return;
    }

    if (newUserData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    // Validate at least one role is selected
    const rolesToSend = newUserData.roles?.length > 0 ? newUserData.roles : [newUserData.role];
    if (rolesToSend.length === 0) {
      toast.error('Please select at least one role');
      return;
    }

    try {
      await axiosInstance.post('/admin/users', {
        name: newUserData.name,
        email: newUserData.email,
        password: newUserData.password,
        role: rolesToSend[0], // Primary role for backwards compatibility
        roles: rolesToSend,
        bio: newUserData.bio,
        phone: newUserData.phone,
        skills: newUserData.skills ? newUserData.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
        expertise_areas: newUserData.expertise_areas ? newUserData.expertise_areas.split(',').map(s => s.trim()).filter(Boolean) : []
      });
      const roleNames = rolesToSend.map(r => r.charAt(0).toUpperCase() + r.slice(1)).join(', ');
      toast.success(`User with role(s) ${roleNames} added successfully!`);
      setShowAddUser(false);
      setNewUserData({
        name: '',
        email: '',
        password: '',
        role: 'member',
        roles: ['member'],
        bio: '',
        phone: '',
        skills: '',
        expertise_areas: ''
      });
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    // Support both single role (legacy) and multiple roles
    const userRoles = user.roles?.length > 0 ? user.roles : (user.role ? [user.role] : ['member']);
    setEditUserData({
      name: user.name || '',
      role: user.role || userRoles[0] || 'member',
      roles: userRoles,
      bio: user.bio || '',
      skills: user.skills?.join(', ') || '',
      expertise_areas: user.expertise_areas?.join(', ') || '',
      hourly_rate: user.hourly_rate || '',
      enabled_modules: user.enabled_modules || [],
      disabled_modules: user.disabled_modules || []
    });
    setShowEditUser(true);
  };

  const handleSaveModuleSettings = async () => {
    setSavingModules(true);
    try {
      await axiosInstance.put('/admin/modules', moduleSettings);
      toast.success('Module settings saved successfully');
    } catch (error) {
      toast.error('Failed to save module settings');
    } finally {
      setSavingModules(false);
    }
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    
    // Validate at least one role is selected
    if (!editUserData.roles || editUserData.roles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }
    
    try {
      const updateData = {
        name: editUserData.name,
        role: editUserData.roles[0], // Primary role for backwards compatibility
        roles: editUserData.roles,
        bio: editUserData.bio,
        skills: editUserData.skills.split(',').map(s => s.trim()).filter(Boolean),
        expertise_areas: editUserData.expertise_areas.split(',').map(s => s.trim()).filter(Boolean),
        hourly_rate: editUserData.hourly_rate ? parseFloat(editUserData.hourly_rate) : null
      };
      
      await axiosInstance.put(`/admin/users/${selectedUser.user_id}`, updateData);
      
      // Save module overrides separately
      await axiosInstance.put(`/admin/users/${selectedUser.user_id}/modules`, {
        user_id: selectedUser.user_id,
        enabled_modules: editUserData.enabled_modules,
        disabled_modules: editUserData.disabled_modules
      });
      toast.success('User updated successfully');
      setShowEditUser(false);
      loadAdminData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setResetPasswordData({ newPassword: '', confirmPassword: '' });
    setShowResetPassword(true);
  };

  const handleSubmitPasswordReset = async (e) => {
    e.preventDefault();
    
    if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (resetPasswordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await axiosInstance.post(`/admin/users/${selectedUser.user_id}/reset-password`, {
        new_password: resetPasswordData.newPassword
      });
      toast.success('Password reset successfully! User has been logged out.');
      setShowResetPassword(false);
      setResetPasswordData({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    try {
      await axiosInstance.post('/auth/password/change', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      toast.success('Password changed successfully!');
      setShowPasswordChange(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleLogout = async () => {
    try {
      await axiosInstance.post('/auth/logout');
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Admin Sidebar */}
      <aside className={`hidden md:flex flex-col bg-card border-r transition-all duration-300 ${adminSidebarCollapsed ? 'w-16' : 'w-64'} sticky top-0 h-screen z-40`}>
        {/* Sidebar Header */}
        <div className="p-4 border-b flex items-center justify-between">
          {!adminSidebarCollapsed && (
            <div className="flex items-center gap-2">
              <span className="font-heading text-lg font-bold text-primary">Admin</span>
              <Badge className="bg-accent">Panel</Badge>
            </div>
          )}
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setAdminSidebarCollapsed(!adminSidebarCollapsed)}
            className="h-8 w-8"
            data-testid="admin-sidebar-toggle"
          >
            {adminSidebarCollapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </Button>
        </div>
        
        {/* Sidebar Navigation */}
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {[
            { id: 'overview', label: 'Overview', icon: LayoutDashboard },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'visitors', label: 'Visitors', icon: HandHeart },
            { id: 'mentorships', label: 'Mentorships', icon: GraduationCap },
            { id: 'church', label: 'Church', icon: Church },
            { id: 'sermon-notes', label: 'Sermon Notes', icon: BookOpen },
            { id: 'livestream', label: 'Live Stream', icon: Radio },
            { id: 'resources', label: 'Resources', icon: FolderOpen },
            { id: 'content', label: 'Content', icon: FileText },
            { id: 'store', label: 'Store', icon: ShoppingBag },
            { id: 'specfi', label: 'SpecFi Sync', icon: ArrowLeftRight },
            { id: 'settings', label: 'Settings', icon: Settings }
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeAdminTab === tab.id;
            return (
              <Button 
                key={tab.id}
                variant={isActive ? "secondary" : "ghost"} 
                className={`w-full justify-start ${adminSidebarCollapsed ? 'px-3' : ''} ${isActive ? 'bg-primary/10 text-primary' : ''}`}
                onClick={() => setActiveAdminTab(tab.id)}
                data-testid={`admin-sidebar-${tab.id}`}
              >
                <Icon className={`h-4 w-4 ${adminSidebarCollapsed ? '' : 'mr-3'}`} />
                {!adminSidebarCollapsed && tab.label}
              </Button>
            );
          })}
        </nav>
        
        {/* Sidebar Footer */}
        <div className="p-2 border-t space-y-2">
          <Button 
            variant="ghost" 
            className={`w-full justify-start ${adminSidebarCollapsed ? 'px-3' : ''}`}
            onClick={() => navigate('/admin/reports')}
          >
            <FileText className={`h-4 w-4 ${adminSidebarCollapsed ? '' : 'mr-3'}`} />
            {!adminSidebarCollapsed && 'Reports'}
          </Button>
          <Button 
            variant="ghost" 
            className={`w-full justify-start ${adminSidebarCollapsed ? 'px-3' : ''}`}
            onClick={() => navigate('/dashboard')}
          >
            <Home className={`h-4 w-4 ${adminSidebarCollapsed ? '' : 'mr-3'}`} />
            {!adminSidebarCollapsed && 'User Dashboard'}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className={`w-full justify-start ${adminSidebarCollapsed ? 'px-3' : ''}`}>
                <Avatar className="h-6 w-6">
                  <AvatarImage src={user.picture?.startsWith('/') ? BACKEND_URL + user.picture : user.picture} />
                  <AvatarFallback className="text-xs">{user.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                {!adminSidebarCollapsed && (
                  <span className="ml-2 truncate text-sm">{user.name}</span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="w-56">
              <div className="px-2 py-2 border-b">
                <p className="font-semibold text-sm">{user.name}</p>
                <p className="text-xs text-muted-foreground">{user.email}</p>
              </div>
              <DropdownMenuItem onClick={() => setShowPasswordChange(true)}>
                <Lock className="h-4 w-4 mr-2" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuItem>
                <ThemeToggleButton />
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
      
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-screen">
      {/* Mobile Admin Navbar */}
      <nav className="glass sticky top-0 z-50 border-b md:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-4">
              <div className="font-heading text-lg md:text-2xl font-bold text-primary">TheFeedingFaith Admin</div>
              <Badge className="bg-accent hidden sm:inline-flex">Administrator</Badge>
            </div>
            {/* Desktop nav items */}
            <div className="hidden md:flex items-center gap-4">
              <div className="text-sm">
                <div className="font-semibold">{user.name}</div>
                <div className="text-muted-foreground text-xs">{user.email}</div>
              </div>
              <ThemeToggleButton />
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="admin-logout-btn">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
            {/* Mobile menu button */}
            <div className="flex items-center gap-2 md:hidden">
              <ThemeToggleButton />
              <Button variant="ghost" size="icon" onClick={() => setAdminMobileMenuOpen(!adminMobileMenuOpen)} data-testid="admin-mobile-menu-btn">
                {adminMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>
        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {adminMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t bg-background/95 backdrop-blur-sm"
            >
              <div className="px-4 py-4 space-y-2">
                <div className="pb-2 border-b mb-2">
                  <p className="font-semibold text-sm">{user.name}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
                <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/dashboard'); setAdminMobileMenuOpen(false); }}>
                  <Home className="h-4 w-4 mr-2" />
                  User Dashboard
                </Button>
                <Button variant="ghost" className="w-full justify-start text-destructive" onClick={handleLogout}>
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Admin Content */}
      <div className="flex-1 px-4 sm:px-6 lg:px-8 py-4 md:py-8">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold mb-1 md:mb-2" data-testid="admin-dashboard-heading">
              {activeAdminTab.charAt(0).toUpperCase() + activeAdminTab.slice(1)}
            </h1>
            <p className="text-muted-foreground text-sm md:text-base">
              {activeAdminTab === 'overview' ? 'Platform overview and statistics' :
               activeAdminTab === 'users' ? 'Manage platform users' :
               activeAdminTab === 'visitors' ? 'Manage visitor follow-ups' :
               activeAdminTab === 'mentorships' ? 'Manage mentorship relationships' :
               activeAdminTab === 'church' ? 'Church management settings' :
               activeAdminTab === 'sermon-notes' ? 'Create and manage sermon notes' :
               activeAdminTab === 'livestream' ? 'Manage live streaming' :
               activeAdminTab === 'resources' ? 'Manage church resources' :
               activeAdminTab === 'content' ? 'Manage courses and content' :
               activeAdminTab === 'store' ? 'Manage store products' :
               activeAdminTab === 'specfi' ? 'Two-way data sync with SpecFi' :
               'Platform settings'}
            </p>
          </div>
          <Button onClick={() => navigate('/admin/reports')} data-testid="reports-btn" className="w-full sm:w-auto md:hidden">
            <FileText className="h-4 w-4 mr-2" />Reports
          </Button>
        </div>

        {/* Mobile Tab Navigation - shown only on mobile */}
        <div className="mb-6 md:hidden border-b -mx-4 px-4">
          <nav className="flex gap-1 -mb-px overflow-x-auto pb-px scrollbar-hide" data-testid="admin-tabs-mobile">
            {[
              { id: 'overview', label: 'Overview', icon: LayoutDashboard },
              { id: 'users', label: 'Users', icon: Users },
              { id: 'visitors', label: 'Visitors', icon: HandHeart },
              { id: 'mentorships', label: 'Mentorships', icon: GraduationCap },
              { id: 'church', label: 'Church', icon: Church },
              { id: 'livestream', label: 'Live', icon: Radio },
              { id: 'resources', label: 'Resources', icon: FolderOpen },
              { id: 'content', label: 'Content', icon: FileText },
              { id: 'store', label: 'Store', icon: ShoppingBag },
              { id: 'settings', label: 'Settings', icon: Settings }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveAdminTab(tab.id)}
                className={`flex items-center gap-1 px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeAdminTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`admin-tab-${tab.id}`}
              >
                <tab.icon className="h-3 w-3" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* OVERVIEW TAB */}
        {activeAdminTab === 'overview' && (
          <>
        {/* Stats Grid - Clickable */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <Card 
            className="glass-card cursor-pointer hover:shadow-lg hover:scale-105 transition-all" 
            onClick={() => handleUsersDrillDown('all')}
            data-testid="stat-total-users"
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-heading">{stats.total_users}</div>
              <p className="text-xs text-muted-foreground mt-1">Click to drill down →</p>
            </CardContent>
          </Card>
          <Card 
            className="glass-card cursor-pointer hover:shadow-lg hover:scale-105 transition-all"
            onClick={() => handleUsersDrillDown('mentor')}
            data-testid="stat-mentors"
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Mentors</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-heading text-blue-600">{stats.total_mentors}</div>
              <p className="text-xs text-muted-foreground mt-1">Click to drill down →</p>
            </CardContent>
          </Card>
          <Card 
            className="glass-card cursor-pointer hover:shadow-lg hover:scale-105 transition-all"
            onClick={() => handleUsersDrillDown('mentee')}
            data-testid="stat-mentees"
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Mentees</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-heading text-green-600">{stats.total_mentees}</div>
              <p className="text-xs text-muted-foreground mt-1">Click to drill down →</p>
            </CardContent>
          </Card>
          <Card 
            className="glass-card cursor-pointer hover:shadow-lg hover:scale-105 transition-all"
            onClick={() => handleMentorshipsDrillDown('active')}
            data-testid="stat-active-mentorships"
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Active Mentorships</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-heading text-purple-600">{stats.active_mentorships}</div>
              <p className="text-xs text-muted-foreground mt-1">Click to drill down →</p>
            </CardContent>
          </Card>
          <Card 
            className="glass-card cursor-pointer hover:shadow-lg hover:scale-105 transition-all" 
            onClick={handleRevenueDrillDown}
            data-testid="stat-revenue"
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-heading text-amber-600">${stats.total_revenue}</div>
              <p className="text-xs text-muted-foreground mt-1">Click to drill down →</p>
            </CardContent>
          </Card>
        </div>

        {/* Analytics Dashboard */}
        {analytics && (
          <Card className="glass-card mb-8" data-testid="analytics-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Mentorship Analytics
                  </CardTitle>
                  <CardDescription>Progress tracking and milestone alerts</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={loadAnalytics}>
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Overview Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div 
                  className="bg-secondary/20 rounded-lg p-4 text-center cursor-pointer hover:bg-secondary/30 transition-colors"
                  onClick={() => handleMentorshipsDrillDown('active')}
                >
                  <div className="text-2xl font-bold text-primary">{analytics.overview.active_mentorships}</div>
                  <div className="text-xs text-muted-foreground">Active</div>
                </div>
                <div 
                  className="bg-secondary/20 rounded-lg p-4 text-center cursor-pointer hover:bg-secondary/30 transition-colors"
                  onClick={() => handleGoalsDrillDown('all')}
                >
                  <div className="text-2xl font-bold">{analytics.overview.total_goals}</div>
                  <div className="text-xs text-muted-foreground">Total Goals</div>
                </div>
                <div 
                  className="bg-secondary/20 rounded-lg p-4 text-center cursor-pointer hover:bg-secondary/30 transition-colors"
                  onClick={() => handleGoalsDrillDown('completed')}
                >
                  <div className="text-2xl font-bold text-green-600">{analytics.overview.completion_rate}%</div>
                  <div className="text-xs text-muted-foreground">Completion Rate</div>
                </div>
                <div 
                  className="bg-secondary/20 rounded-lg p-4 text-center cursor-pointer hover:bg-secondary/30 transition-colors"
                  onClick={() => handleGoalsDrillDown('in_progress')}
                >
                  <div className="text-2xl font-bold text-blue-600">{analytics.overview.avg_goal_progress}%</div>
                  <div className="text-xs text-muted-foreground">Avg Progress</div>
                </div>
                <div 
                  className="bg-secondary/20 rounded-lg p-4 text-center cursor-pointer hover:bg-secondary/30 transition-colors"
                  onClick={() => handleMentorshipsDrillDown('ended')}
                >
                  <div className="text-2xl font-bold text-gray-500">{analytics.overview.ended_mentorships}</div>
                  <div className="text-xs text-muted-foreground">Ended</div>
                </div>
              </div>

              {/* Goals by Status Chart - Clickable */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className="font-semibold mb-3">Goals by Status <span className="text-xs text-muted-foreground">(Click segments)</span></h4>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPie>
                        <Pie
                          data={[
                            { name: 'Not Started', value: analytics.goals_by_status.not_started, color: '#94a3b8', status: 'not_started' },
                            { name: 'In Progress', value: analytics.goals_by_status.in_progress, color: '#3b82f6', status: 'in_progress' },
                            { name: 'Completed', value: analytics.goals_by_status.completed, color: '#22c55e', status: 'completed' }
                          ]}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={70}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                          onClick={(data) => handleGoalsDrillDown(data.status)}
                          style={{ cursor: 'pointer' }}
                        >
                          {[
                            { color: '#94a3b8' },
                            { color: '#3b82f6' },
                            { color: '#22c55e' }
                          ].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} className="cursor-pointer hover:opacity-80" />
                          ))}
                        </Pie>
                        <Tooltip />
                      </RechartsPie>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-3">Monthly Trend</h4>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analytics.monthly_trend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="new_mentorships" fill="#8b5cf6" name="New Mentorships" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Alerts Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Overdue Goals */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2 text-red-600">
                    <Clock className="h-4 w-4" />
                    Overdue Goals ({analytics.overdue_goals.length})
                  </h4>
                  {analytics.overdue_goals.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No overdue goals</p>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {analytics.overdue_goals.map(goal => (
                        <div key={goal.goal_id} className="bg-red-50 dark:bg-red-900/20 rounded p-2 text-sm">
                          <div className="font-medium text-red-700 dark:text-red-400">{goal.title}</div>
                          <div className="text-xs text-muted-foreground">
                            {goal.mentee_name} • {goal.days_overdue} days overdue • {goal.progress}% done
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Upcoming Deadlines */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2 text-amber-600">
                    <Clock className="h-4 w-4" />
                    Due This Week ({analytics.upcoming_deadlines.length})
                  </h4>
                  {analytics.upcoming_deadlines.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No upcoming deadlines</p>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {analytics.upcoming_deadlines.map(goal => (
                        <div key={goal.goal_id} className="bg-amber-50 dark:bg-amber-900/20 rounded p-2 text-sm">
                          <div className="font-medium text-amber-700 dark:text-amber-400">{goal.title}</div>
                          <div className="text-xs text-muted-foreground">
                            {goal.mentee_name} • {goal.days_until_due === 0 ? 'Due today' : `${goal.days_until_due} days left`} • {goal.progress}% done
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Mentorship Progress Table */}
              {analytics.mentorship_progress.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-semibold mb-3">Mentorship Progress (Needs Attention First)</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Mentor → Mentee</th>
                          <th className="text-center py-2">Goals</th>
                          <th className="text-center py-2">Completed</th>
                          <th className="text-center py-2">Progress</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.mentorship_progress.slice(0, 5).map(m => (
                          <tr key={m.mentorship_id} className="border-b hover:bg-secondary/10">
                            <td className="py-2">{m.mentor_name} → {m.mentee_name}</td>
                            <td className="text-center">{m.total_goals}</td>
                            <td className="text-center">{m.completed_goals}</td>
                            <td className="text-center">
                              <div className="flex items-center justify-center gap-2">
                                <Progress value={m.avg_progress} className="w-20 h-2" />
                                <span className={m.avg_progress < 30 ? 'text-red-600' : m.avg_progress < 60 ? 'text-amber-600' : 'text-green-600'}>
                                  {m.avg_progress}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Admin Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="font-heading">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="default"
                className="w-full justify-start bg-primary hover:bg-primary/90"
                onClick={() => setShowAddMentor(true)}
                data-testid="add-mentor-btn"
              >
                <Plus className="h-4 w-4 mr-2" /> Add New Mentor
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/admin/cms')}
                data-testid="edit-content-btn"
              >
                <Edit className="h-4 w-4 mr-2" /> Edit Site Content
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => setShowPasswordChange(true)}
                data-testid="change-password-btn"
              >
                🔐 Change Password
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/dashboard')}
                data-testid="view-user-dashboard-btn"
              >
                👁️ View User Dashboard
              </Button>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="font-heading">System Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Admin Email:</span>
                <span className="font-semibold">{user.email}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Role:</span>
                <Badge className="bg-primary">{user.role}</Badge>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Status:</span>
                <Badge className="bg-green-600">Active</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
          </>
        )}

        {/* SETTINGS TAB */}
        {activeAdminTab === 'specfi' && <SpecFiSync />}

        {activeAdminTab === 'settings' && (
          <>
        {/* Site Branding / Logo Upload */}
        <Card className="glass-card mb-8" data-testid="logo-upload-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Site Branding
            </CardTitle>
            <CardDescription>Upload a custom logo for the site header</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Current Logo */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Current Logo</Label>
                <div className="border-2 border-dashed border-muted rounded-lg p-4 flex items-center justify-center min-h-[120px] bg-muted/20">
                  {currentLogo ? (
                    <div className="text-center">
                      <img 
                        src={currentLogo} 
                        alt="Current Logo" 
                        className="max-h-20 max-w-[200px] object-contain mx-auto mb-2"
                        data-testid="current-logo-preview"
                      />
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        onClick={handleDeleteLogo}
                        data-testid="delete-logo-btn"
                      >
                        <Trash2 className="h-4 w-4 mr-1" /> Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground">
                      <div className="text-4xl mb-2">🏢</div>
                      <p className="text-sm">No custom logo uploaded</p>
                      <p className="text-xs">Using default text logo</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Upload New Logo */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Upload New Logo</Label>
                <div className="border-2 border-dashed border-primary/30 rounded-lg p-4 min-h-[120px] bg-primary/5">
                  {logoPreview ? (
                    <div className="text-center">
                      <img 
                        src={logoPreview} 
                        alt="Logo Preview" 
                        className="max-h-16 max-w-[180px] object-contain mx-auto mb-3"
                        data-testid="logo-upload-preview"
                      />
                      <div className="flex gap-2 justify-center">
                        <Button 
                          onClick={handleLogoUpload} 
                          disabled={uploadingLogo}
                          size="sm"
                          data-testid="confirm-logo-upload-btn"
                        >
                          {uploadingLogo ? 'Uploading...' : 'Save Logo'}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => { setLogoFile(null); setLogoPreview(null); }}
                          data-testid="cancel-logo-upload-btn"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <label className="cursor-pointer block text-center">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                        onChange={handleLogoSelect}
                        className="hidden"
                        data-testid="logo-file-input"
                      />
                      <Upload className="h-8 w-8 mx-auto mb-2 text-primary" />
                      <p className="text-sm font-medium text-primary">Click to upload</p>
                      <p className="text-xs text-muted-foreground mt-1">PNG, JPG, SVG, WebP (max 5MB)</p>
                    </label>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* API Keys Configuration */}
        <Card className="glass-card mb-8" data-testid="api-keys-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Settings className="h-5 w-5" />
              API Keys Configuration
            </CardTitle>
            <CardDescription>Configure Stripe payments and Resend email service</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Stripe API Key */}
              <div className="p-4 rounded-lg border bg-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-purple-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Stripe Payment</h4>
                    <p className="text-xs text-muted-foreground">Accept online payments</p>
                  </div>
                  <Badge className={apiKeysStatus.stripe?.configured ? "bg-green-600 ml-auto" : "bg-yellow-600 ml-auto"}>
                    {apiKeysStatus.stripe?.configured ? "Connected" : "Not Configured"}
                  </Badge>
                </div>
                {apiKeysStatus.stripe?.key_preview && (
                  <p className="text-xs text-muted-foreground mb-3">Current key: {apiKeysStatus.stripe.key_preview}</p>
                )}
                <div className="space-y-3">
                  <Input 
                    type="password" 
                    placeholder="sk_live_xxxx or sk_test_xxxx" 
                    value={stripeKeyInput}
                    onChange={(e) => setStripeKeyInput(e.target.value)}
                    data-testid="stripe-key-input"
                  />
                  <Button 
                    onClick={handleSaveStripeKey} 
                    disabled={!stripeKeyInput || savingStripeKey}
                    className="w-full"
                    data-testid="save-stripe-key-btn"
                  >
                    {savingStripeKey ? 'Saving...' : 'Save Stripe Key'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Get your API key from <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="text-primary hover:underline">Stripe Dashboard</a>
                </p>
              </div>

              {/* Resend API Key */}
              <div className="p-4 rounded-lg border bg-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <MessageCircle className="h-5 w-5 text-blue-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Resend Email</h4>
                    <p className="text-xs text-muted-foreground">Send email notifications</p>
                  </div>
                  <Badge className={apiKeysStatus.resend?.configured ? "bg-green-600 ml-auto" : "bg-yellow-600 ml-auto"}>
                    {apiKeysStatus.resend?.configured ? "Connected" : "Not Configured"}
                  </Badge>
                </div>
                {apiKeysStatus.resend?.key_preview && (
                  <p className="text-xs text-muted-foreground mb-3">Current key: {apiKeysStatus.resend.key_preview}</p>
                )}
                <div className="space-y-3">
                  <Input 
                    type="password" 
                    placeholder="re_xxxx" 
                    value={resendKeyInput}
                    onChange={(e) => setResendKeyInput(e.target.value)}
                    data-testid="resend-key-input"
                  />
                  <Button 
                    onClick={handleSaveResendKey} 
                    disabled={!resendKeyInput || savingResendKey}
                    className="w-full"
                    data-testid="save-resend-key-btn"
                  >
                    {savingResendKey ? 'Saving...' : 'Save Resend Key'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Get your API key from <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-primary hover:underline">Resend Dashboard</a> (100 emails/day free)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Social Media Links */}
        <Card className="glass-card mb-8" data-testid="social-links-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <LinkIcon className="h-5 w-5" />
                  Social Media Links
                </CardTitle>
                <CardDescription>Configure social media links shown in the footer</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={loadSocialLinks} data-testid="refresh-social-links-btn">
                  <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                </Button>
                <Button onClick={() => setShowAddSocialLink(true)} data-testid="add-social-link-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Link
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {socialLinksLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : socialLinks.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <LinkIcon className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>No social links configured yet.</p>
                <p className="text-sm">Add Instagram, YouTube, TikTok, LinkedIn and more!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {socialLinks.map((link) => (
                  <div key={link.link_id} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        link.platform === 'instagram' ? 'bg-gradient-to-br from-purple-500 to-pink-500' :
                        link.platform === 'youtube' ? 'bg-red-500' :
                        link.platform === 'tiktok' ? 'bg-black' :
                        link.platform === 'linkedin' ? 'bg-blue-600' :
                        link.platform === 'facebook' ? 'bg-blue-500' :
                        link.platform === 'twitter' || link.platform === 'x' ? 'bg-black' :
                        'bg-gray-500'
                      } text-white`}>
                        {link.platform === 'instagram' && <span className="text-lg">📸</span>}
                        {link.platform === 'youtube' && <span className="text-lg">▶️</span>}
                        {link.platform === 'tiktok' && <span className="text-lg">🎵</span>}
                        {link.platform === 'linkedin' && <span className="text-lg">💼</span>}
                        {link.platform === 'facebook' && <span className="text-lg">👤</span>}
                        {(link.platform === 'twitter' || link.platform === 'x') && <span className="text-lg">✖</span>}
                        {!['instagram', 'youtube', 'tiktok', 'linkedin', 'facebook', 'twitter', 'x'].includes(link.platform) && <LinkIcon className="h-5 w-5" />}
                      </div>
                      <div>
                        <p className="font-medium capitalize">{link.platform}</p>
                        <a href={link.url} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-primary truncate block max-w-[300px]">
                          {link.url}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={link.is_active ? "default" : "secondary"}>
                        {link.is_active ? "Active" : "Hidden"}
                      </Badge>
                      <span className="text-xs text-muted-foreground">Order: {link.display_order}</span>
                      <Button variant="outline" size="sm" onClick={() => handleEditSocialLink(link)} data-testid={`edit-social-${link.link_id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteSocialLink(link.link_id)} data-testid={`delete-social-${link.link_id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Social Link Dialog */}
        <Dialog open={showAddSocialLink} onOpenChange={setShowAddSocialLink}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Social Media Link</DialogTitle>
              <DialogDescription>Add a new social media link to display in the footer</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="social-platform">Platform *</Label>
                <Select 
                  value={newSocialLinkData.platform} 
                  onValueChange={(v) => setNewSocialLinkData({...newSocialLinkData, platform: v, icon: v})}
                >
                  <SelectTrigger data-testid="social-platform-select">
                    <SelectValue placeholder="Select platform" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="youtube">YouTube</SelectItem>
                    <SelectItem value="tiktok">TikTok</SelectItem>
                    <SelectItem value="linkedin">LinkedIn</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="twitter">Twitter/X</SelectItem>
                    <SelectItem value="pinterest">Pinterest</SelectItem>
                    <SelectItem value="spotify">Spotify</SelectItem>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="social-url">URL *</Label>
                <Input
                  id="social-url"
                  value={newSocialLinkData.url}
                  onChange={(e) => setNewSocialLinkData({...newSocialLinkData, url: e.target.value})}
                  placeholder="https://instagram.com/yourchurch"
                  data-testid="social-url-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="social-order">Display Order</Label>
                  <Input
                    id="social-order"
                    type="number"
                    min="0"
                    value={newSocialLinkData.display_order}
                    onChange={(e) => setNewSocialLinkData({...newSocialLinkData, display_order: parseInt(e.target.value) || 0})}
                    data-testid="social-order-input"
                  />
                </div>
                <div className="flex items-end pb-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="social-active"
                      checked={newSocialLinkData.is_active}
                      onChange={(e) => setNewSocialLinkData({...newSocialLinkData, is_active: e.target.checked})}
                      className="w-4 h-4 accent-primary"
                    />
                    <Label htmlFor="social-active" className="cursor-pointer">Active (visible)</Label>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-4">
                <Button variant="outline" onClick={() => setShowAddSocialLink(false)}>Cancel</Button>
                <Button onClick={handleCreateSocialLink} disabled={savingSocialLink} data-testid="create-social-link-btn">
                  {savingSocialLink ? 'Adding...' : 'Add Link'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Social Link Dialog */}
        <Dialog open={showEditSocialLink} onOpenChange={setShowEditSocialLink}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Social Media Link</DialogTitle>
              <DialogDescription>Update social media link details</DialogDescription>
            </DialogHeader>
            {editingSocialLink && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-social-platform">Platform</Label>
                  <Select 
                    value={editingSocialLink.platform} 
                    onValueChange={(v) => setEditingSocialLink({...editingSocialLink, platform: v, icon: v})}
                  >
                    <SelectTrigger data-testid="edit-social-platform-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="instagram">Instagram</SelectItem>
                      <SelectItem value="youtube">YouTube</SelectItem>
                      <SelectItem value="tiktok">TikTok</SelectItem>
                      <SelectItem value="linkedin">LinkedIn</SelectItem>
                      <SelectItem value="facebook">Facebook</SelectItem>
                      <SelectItem value="twitter">Twitter/X</SelectItem>
                      <SelectItem value="pinterest">Pinterest</SelectItem>
                      <SelectItem value="spotify">Spotify</SelectItem>
                      <SelectItem value="website">Website</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit-social-url">URL</Label>
                  <Input
                    id="edit-social-url"
                    value={editingSocialLink.url || ''}
                    onChange={(e) => setEditingSocialLink({...editingSocialLink, url: e.target.value})}
                    data-testid="edit-social-url-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-social-order">Display Order</Label>
                    <Input
                      id="edit-social-order"
                      type="number"
                      min="0"
                      value={editingSocialLink.display_order || 0}
                      onChange={(e) => setEditingSocialLink({...editingSocialLink, display_order: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="flex items-end pb-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="edit-social-active"
                        checked={editingSocialLink.is_active}
                        onChange={(e) => setEditingSocialLink({...editingSocialLink, is_active: e.target.checked})}
                        className="w-4 h-4 accent-primary"
                      />
                      <Label htmlFor="edit-social-active" className="cursor-pointer">Active</Label>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 justify-end pt-4">
                  <Button variant="outline" onClick={() => setShowEditSocialLink(false)}>Cancel</Button>
                  <Button onClick={handleUpdateSocialLink} disabled={savingSocialLink} data-testid="update-social-link-btn">
                    {savingSocialLink ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Module Access Control */}
        <Card className="glass-card mb-8" data-testid="module-access-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Package className="h-5 w-5" />
              Module Access Control
            </CardTitle>
            <CardDescription>Enable or disable modules globally for all users. Individual users can be given overrides in their profile.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground mb-4">
                Toggle modules on/off site-wide. When disabled, the feature will be hidden for all users unless they have a specific override.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Budget Tracker */}
                <div className={`p-4 rounded-lg border-2 transition-all ${moduleSettings.budget_tracker ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-muted bg-muted/20'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-green-600" />
                      <span className="font-semibold">Budget Tracker</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={moduleSettings.budget_tracker}
                        onChange={(e) => setModuleSettings({...moduleSettings, budget_tracker: e.target.checked})}
                        className="sr-only peer"
                        data-testid="module-budget-toggle"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground">Personal finance tracking and budgeting tools</p>
                </div>

                {/* Resume Builder */}
                <div className={`p-4 rounded-lg border-2 transition-all ${moduleSettings.resume_builder ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-muted bg-muted/20'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-600" />
                      <span className="font-semibold">Resume Builder</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={moduleSettings.resume_builder}
                        onChange={(e) => setModuleSettings({...moduleSettings, resume_builder: e.target.checked})}
                        className="sr-only peer"
                        data-testid="module-resume-toggle"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground">AI-powered resume creation and optimization</p>
                </div>

                {/* Business Plan */}
                <div className={`p-4 rounded-lg border-2 transition-all ${moduleSettings.business_plan ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'border-muted bg-muted/20'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-5 w-5 text-purple-600" />
                      <span className="font-semibold">Business Plan</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={moduleSettings.business_plan}
                        onChange={(e) => setModuleSettings({...moduleSettings, business_plan: e.target.checked})}
                        className="sr-only peer"
                        data-testid="module-business-toggle"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600"></div>
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground">AI-assisted business plan creation</p>
                </div>
              </div>

              <Button 
                onClick={handleSaveModuleSettings} 
                disabled={savingModules}
                className="mt-4"
                data-testid="save-modules-btn"
              >
                {savingModules ? 'Saving...' : 'Save Module Settings'}
              </Button>
            </div>
          </CardContent>
        </Card>
          </>
        )}

        {/* CHURCH MANAGEMENT TAB */}
        {activeAdminTab === 'church' && (
          <>
            {/* Church Stats */}
            {churchStats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <Card className="glass-card">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Members</p>
                        <p className="text-2xl font-bold">{churchStats.total_members}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="glass-card">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                        <Church className="h-5 w-5 text-blue-500" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Ministries</p>
                        <p className="text-2xl font-bold">{churchStats.total_ministries}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="glass-card">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                        <TrendingUp className="h-5 w-5 text-green-500" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Monthly Attendance</p>
                        <p className="text-2xl font-bold">{churchStats.monthly_attendance}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="glass-card">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                        <HandHeart className="h-5 w-5 text-purple-500" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Prayer Requests</p>
                        <p className="text-2xl font-bold">{churchStats.active_prayer_requests}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Church Events Management */}
            <Card className="glass-card mb-8" data-testid="church-events-management-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <CalendarIcon className="h-5 w-5" />
                      Church Events
                    </CardTitle>
                    <CardDescription>Manage upcoming church events, services, and activities</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={loadChurchEvents} data-testid="refresh-events-btn">
                      <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                    </Button>
                    <Button onClick={() => setShowAddChurchEvent(true)} data-testid="add-church-event-btn">
                      <Plus className="h-4 w-4 mr-2" /> Add Event
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {churchEventsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : churchEvents.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CalendarIcon className="h-12 w-12 mx-auto mb-2 opacity-30" />
                    <p>No events scheduled. Create your first church event!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {churchEvents.map((event) => (
                      <Card key={event.event_id} className="bg-muted/30">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex gap-4 flex-1">
                              {event.thumbnail_url ? (
                                <img 
                                  src={event.thumbnail_url} 
                                  alt={event.title} 
                                  className="w-24 h-16 object-cover rounded-lg"
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                              ) : (
                                <div className="w-24 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
                                  <CalendarIcon className="h-6 w-6 text-primary/40" />
                                </div>
                              )}
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-semibold">{event.title}</h4>
                                  <Badge variant="outline">{event.event_type || 'general'}</Badge>
                                </div>
                                <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{event.description}</p>
                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <CalendarIcon className="h-3 w-3" />
                                    {new Date(event.start_time).toLocaleDateString('en-US', { 
                                      weekday: 'short', 
                                      month: 'short', 
                                      day: 'numeric',
                                      hour: 'numeric',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                  {event.location && (
                                    <span className="flex items-center gap-1">
                                      <MapPin className="h-3 w-3" />
                                      {event.location}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2 ml-4">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleEditChurchEvent(event)}
                                data-testid={`edit-event-${event.event_id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-500 hover:text-red-700 hover:bg-red-100"
                                onClick={() => handleDeleteChurchEvent(event.event_id)}
                                data-testid={`delete-event-${event.event_id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          {event.featured_image_url && (
                            <div className="mt-4">
                              <img 
                                src={event.featured_image_url} 
                                alt={`${event.title} featured`}
                                className="w-full h-32 object-cover rounded-lg"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Add Church Event Dialog */}
            <Dialog open={showAddChurchEvent} onOpenChange={setShowAddChurchEvent}>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create New Event</DialogTitle>
                  <DialogDescription>Schedule a new church event or activity</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="event-title">Event Title *</Label>
                    <Input
                      id="event-title"
                      value={newChurchEventData.title}
                      onChange={(e) => setNewChurchEventData({...newChurchEventData, title: e.target.value})}
                      placeholder="e.g., Sunday Worship Service"
                      data-testid="event-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="event-description">Description</Label>
                    <Textarea
                      id="event-description"
                      value={newChurchEventData.description}
                      onChange={(e) => setNewChurchEventData({...newChurchEventData, description: e.target.value})}
                      placeholder="Event details..."
                      rows={3}
                      data-testid="event-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="event-start">Start Date & Time *</Label>
                      <Input
                        id="event-start"
                        type="datetime-local"
                        value={newChurchEventData.start_time}
                        onChange={(e) => setNewChurchEventData({...newChurchEventData, start_time: e.target.value})}
                        data-testid="event-start-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="event-end">End Date & Time</Label>
                      <Input
                        id="event-end"
                        type="datetime-local"
                        value={newChurchEventData.end_time}
                        onChange={(e) => setNewChurchEventData({...newChurchEventData, end_time: e.target.value})}
                        data-testid="event-end-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="event-location">Location</Label>
                      <Input
                        id="event-location"
                        value={newChurchEventData.location}
                        onChange={(e) => setNewChurchEventData({...newChurchEventData, location: e.target.value})}
                        placeholder="e.g., Main Sanctuary"
                        data-testid="event-location-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="event-type">Event Type</Label>
                      <Select 
                        value={newChurchEventData.event_type} 
                        onValueChange={(v) => setNewChurchEventData({...newChurchEventData, event_type: v})}
                      >
                        <SelectTrigger data-testid="event-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="worship">Worship Service</SelectItem>
                          <SelectItem value="bible_study">Bible Study</SelectItem>
                          <SelectItem value="prayer">Prayer Meeting</SelectItem>
                          <SelectItem value="youth">Youth Event</SelectItem>
                          <SelectItem value="outreach">Outreach</SelectItem>
                          <SelectItem value="fellowship">Fellowship</SelectItem>
                          <SelectItem value="special">Special Event</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="event-thumbnail">Thumbnail URL</Label>
                    <Input
                      id="event-thumbnail"
                      value={newChurchEventData.thumbnail_url}
                      onChange={(e) => setNewChurchEventData({...newChurchEventData, thumbnail_url: e.target.value})}
                      placeholder="https://example.com/thumbnail.jpg"
                      data-testid="event-thumbnail-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Small preview image for event cards</p>
                  </div>
                  <div>
                    <Label htmlFor="event-featured">Featured Image URL</Label>
                    <Input
                      id="event-featured"
                      value={newChurchEventData.featured_image_url}
                      onChange={(e) => setNewChurchEventData({...newChurchEventData, featured_image_url: e.target.value})}
                      placeholder="https://example.com/featured.jpg"
                      data-testid="event-featured-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Large banner image for event details</p>
                  </div>
                  <div className="flex gap-2 justify-end pt-4">
                    <Button variant="outline" onClick={() => setShowAddChurchEvent(false)}>Cancel</Button>
                    <Button onClick={handleCreateChurchEvent} disabled={savingChurchEvent} data-testid="create-event-btn">
                      {savingChurchEvent ? 'Creating...' : 'Create Event'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Edit Church Event Dialog */}
            <Dialog open={showEditChurchEvent} onOpenChange={setShowEditChurchEvent}>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Edit Event</DialogTitle>
                  <DialogDescription>Update event details</DialogDescription>
                </DialogHeader>
                {editingChurchEvent && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="edit-event-title">Event Title *</Label>
                      <Input
                        id="edit-event-title"
                        value={editingChurchEvent.title || ''}
                        onChange={(e) => setEditingChurchEvent({...editingChurchEvent, title: e.target.value})}
                        data-testid="edit-event-title-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-event-description">Description</Label>
                      <Textarea
                        id="edit-event-description"
                        value={editingChurchEvent.description || ''}
                        onChange={(e) => setEditingChurchEvent({...editingChurchEvent, description: e.target.value})}
                        rows={3}
                        data-testid="edit-event-description-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="edit-event-start">Start Date & Time</Label>
                        <Input
                          id="edit-event-start"
                          type="datetime-local"
                          value={editingChurchEvent.start_time || ''}
                          onChange={(e) => setEditingChurchEvent({...editingChurchEvent, start_time: e.target.value})}
                          data-testid="edit-event-start-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-event-end">End Date & Time</Label>
                        <Input
                          id="edit-event-end"
                          type="datetime-local"
                          value={editingChurchEvent.end_time || ''}
                          onChange={(e) => setEditingChurchEvent({...editingChurchEvent, end_time: e.target.value})}
                          data-testid="edit-event-end-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="edit-event-location">Location</Label>
                        <Input
                          id="edit-event-location"
                          value={editingChurchEvent.location || ''}
                          onChange={(e) => setEditingChurchEvent({...editingChurchEvent, location: e.target.value})}
                          data-testid="edit-event-location-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-event-type">Event Type</Label>
                        <Select 
                          value={editingChurchEvent.event_type || 'general'} 
                          onValueChange={(v) => setEditingChurchEvent({...editingChurchEvent, event_type: v})}
                        >
                          <SelectTrigger data-testid="edit-event-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General</SelectItem>
                            <SelectItem value="worship">Worship Service</SelectItem>
                            <SelectItem value="bible_study">Bible Study</SelectItem>
                            <SelectItem value="prayer">Prayer Meeting</SelectItem>
                            <SelectItem value="youth">Youth Event</SelectItem>
                            <SelectItem value="outreach">Outreach</SelectItem>
                            <SelectItem value="fellowship">Fellowship</SelectItem>
                            <SelectItem value="special">Special Event</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="edit-event-thumbnail">Thumbnail URL</Label>
                      <Input
                        id="edit-event-thumbnail"
                        value={editingChurchEvent.thumbnail_url || ''}
                        onChange={(e) => setEditingChurchEvent({...editingChurchEvent, thumbnail_url: e.target.value})}
                        placeholder="https://example.com/thumbnail.jpg"
                        data-testid="edit-event-thumbnail-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-event-featured">Featured Image URL</Label>
                      <Input
                        id="edit-event-featured"
                        value={editingChurchEvent.featured_image_url || ''}
                        onChange={(e) => setEditingChurchEvent({...editingChurchEvent, featured_image_url: e.target.value})}
                        placeholder="https://example.com/featured.jpg"
                        data-testid="edit-event-featured-input"
                      />
                    </div>
                    <div className="flex gap-2 justify-end pt-4">
                      <Button variant="outline" onClick={() => setShowEditChurchEvent(false)}>Cancel</Button>
                      <Button onClick={handleUpdateChurchEvent} disabled={savingChurchEvent} data-testid="update-event-btn">
                        {savingChurchEvent ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Ministries Management */}
            <Card className="glass-card mb-8">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Church className="h-5 w-5" />
                      Ministries
                    </CardTitle>
                    <CardDescription>Manage church ministries and groups</CardDescription>
                  </div>
                  <Button onClick={() => setShowAddMinistry(true)} data-testid="add-ministry-btn">
                    <Plus className="h-4 w-4 mr-2" /> Add Ministry
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {ministries.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No ministries yet. Create your first ministry!</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {ministries.map(ministry => (
                      <Card key={ministry.ministry_id} className="border">
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-semibold">{ministry.name}</h3>
                              <p className="text-sm text-muted-foreground">{ministry.description}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <Badge variant="outline">{ministry.category}</Badge>
                                <span className="text-xs text-muted-foreground">{ministry.member_count} members</span>
                              </div>
                              {ministry.meeting_schedule && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  <Clock className="h-3 w-3 inline mr-1" />
                                  {ministry.meeting_schedule}
                                </p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Announcements */}
            <Card className="glass-card mb-8">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Bell className="h-5 w-5" />
                      Announcements
                    </CardTitle>
                    <CardDescription>Post announcements for your congregation</CardDescription>
                  </div>
                  <Button onClick={() => setShowAddAnnouncement(true)} data-testid="add-announcement-btn">
                    <Plus className="h-4 w-4 mr-2" /> New Announcement
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {announcements.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No announcements yet.</p>
                ) : (
                  <div className="space-y-4">
                    {announcements.map(ann => (
                      <div key={ann.announcement_id} className="p-4 border rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{ann.title}</h3>
                              <Badge variant={ann.priority === 'urgent' ? 'destructive' : ann.priority === 'high' ? 'default' : 'secondary'}>
                                {ann.priority}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">{ann.content}</p>
                            <p className="text-xs text-muted-foreground mt-2">
                              Posted by {ann.created_by_name} • {new Date(ann.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => axiosInstance.delete(`/announcements/${ann.announcement_id}`).then(() => { toast.success('Deleted'); loadChurchData(); })}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Attendance Tracking */}
            <Card className="glass-card mb-8">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      Attendance Tracking
                    </CardTitle>
                    <CardDescription>Record and track service attendance</CardDescription>
                  </div>
                  <Button onClick={() => setShowRecordAttendance(true)} data-testid="record-attendance-btn">
                    <Plus className="h-4 w-4 mr-2" /> Record Attendance
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Track attendance for Sunday services, Wednesday services, and special events.
                </p>
              </CardContent>
            </Card>

            {/* Prayer Requests */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <HandHeart className="h-5 w-5" />
                  Prayer Requests
                </CardTitle>
                <CardDescription>View and manage prayer requests from members</CardDescription>
              </CardHeader>
              <CardContent>
                {prayerRequests.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No prayer requests yet.</p>
                ) : (
                  <div className="space-y-4">
                    {prayerRequests.slice(0, 10).map(prayer => (
                      <div key={prayer.request_id} className="p-4 border rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold">{prayer.title}</h3>
                            <p className="text-sm text-muted-foreground mt-1">{prayer.request}</p>
                            <div className="flex items-center gap-3 mt-2">
                              <span className="text-xs text-muted-foreground">
                                {prayer.is_anonymous ? 'Anonymous' : prayer.user_name}
                              </span>
                              <Badge variant="outline">{prayer.category}</Badge>
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Heart className="h-3 w-3" /> {prayer.prayer_count} prayers
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Add Ministry Dialog */}
            <Dialog open={showAddMinistry} onOpenChange={setShowAddMinistry}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Ministry</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Ministry Name</Label>
                    <Input value={newMinistryData.name} onChange={(e) => setNewMinistryData({...newMinistryData, name: e.target.value})} placeholder="e.g., Youth Ministry" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea value={newMinistryData.description} onChange={(e) => setNewMinistryData({...newMinistryData, description: e.target.value})} placeholder="Brief description of the ministry" />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={newMinistryData.category} onValueChange={(v) => setNewMinistryData({...newMinistryData, category: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="worship">Worship</SelectItem>
                        <SelectItem value="youth">Youth</SelectItem>
                        <SelectItem value="children">Children</SelectItem>
                        <SelectItem value="outreach">Outreach</SelectItem>
                        <SelectItem value="prayer">Prayer</SelectItem>
                        <SelectItem value="media">Media</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Meeting Schedule</Label>
                    <Input value={newMinistryData.meeting_schedule} onChange={(e) => setNewMinistryData({...newMinistryData, meeting_schedule: e.target.value})} placeholder="e.g., Every Sunday at 9 AM" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddMinistry(false)}>Cancel</Button>
                  <Button onClick={handleCreateMinistry}>Create Ministry</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Add Announcement Dialog */}
            <Dialog open={showAddAnnouncement} onOpenChange={setShowAddAnnouncement}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>New Announcement</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Title</Label>
                    <Input value={newAnnouncementData.title} onChange={(e) => setNewAnnouncementData({...newAnnouncementData, title: e.target.value})} placeholder="Announcement title" />
                  </div>
                  <div>
                    <Label>Content</Label>
                    <Textarea value={newAnnouncementData.content} onChange={(e) => setNewAnnouncementData({...newAnnouncementData, content: e.target.value})} placeholder="Announcement content" rows={4} />
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select value={newAnnouncementData.priority} onValueChange={(v) => setNewAnnouncementData({...newAnnouncementData, priority: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddAnnouncement(false)}>Cancel</Button>
                  <Button onClick={handleCreateAnnouncement}>Post Announcement</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Record Attendance Dialog */}
            <Dialog open={showRecordAttendance} onOpenChange={setShowRecordAttendance}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Record Attendance</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Service Date</Label>
                    <Input type="date" value={attendanceData.service_date} onChange={(e) => setAttendanceData({...attendanceData, service_date: e.target.value})} />
                  </div>
                  <div>
                    <Label>Service Type</Label>
                    <Select value={attendanceData.service_type} onValueChange={(v) => setAttendanceData({...attendanceData, service_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sunday">Sunday Service</SelectItem>
                        <SelectItem value="wednesday">Wednesday Service</SelectItem>
                        <SelectItem value="special">Special Event</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Visitor Count</Label>
                    <Input type="number" value={attendanceData.visitor_count} onChange={(e) => setAttendanceData({...attendanceData, visitor_count: parseInt(e.target.value) || 0})} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowRecordAttendance(false)}>Cancel</Button>
                  <Button onClick={handleRecordAttendance}>Save Attendance</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Weekly Focus Management */}
            <Card className="glass-card mb-8">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <CalendarIcon className="h-5 w-5" />
                      Weekly Focus
                    </CardTitle>
                    <CardDescription>Set the weekly scripture and focus for the church</CardDescription>
                  </div>
                  <Button onClick={() => setShowAddWeeklyFocus(true)} data-testid="add-weekly-focus-btn">
                    <Plus className="h-4 w-4 mr-2" /> New Weekly Focus
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {weeklyFocusList.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">No weekly focus entries. Create one to display on the homepage.</p>
                ) : (
                  <div className="space-y-4">
                    {weeklyFocusList.map(focus => (
                      <div key={focus.focus_id} className={`p-4 border rounded-lg ${focus.is_active ? 'border-primary bg-primary/5' : ''}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-medium">{focus.title}</h4>
                              {focus.is_active && <Badge className="bg-primary">Active</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground italic">"{focus.scripture}"</p>
                            <p className="text-sm text-muted-foreground">— {focus.scripture_reference}</p>
                            {focus.week_start && (
                              <p className="text-xs text-muted-foreground mt-2">
                                Week: {focus.week_start} {focus.week_end ? `to ${focus.week_end}` : ''}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingWeeklyFocus({
                                ...focus,
                                prayer_points: Array.isArray(focus.prayer_points) ? focus.prayer_points.join('\n') : focus.prayer_points,
                                action_items: Array.isArray(focus.action_items) ? focus.action_items.join('\n') : focus.action_items
                              })}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteWeeklyFocus(focus.focus_id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Monthly Theme Management */}
            <Card className="glass-card mb-8">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <CalendarIcon className="h-5 w-5" />
                      Monthly Theme
                    </CardTitle>
                    <CardDescription>Set the monthly theme for the church</CardDescription>
                  </div>
                  <Button onClick={() => setShowAddMonthlyTheme(true)} data-testid="add-monthly-theme-btn">
                    <Plus className="h-4 w-4 mr-2" /> New Monthly Theme
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {monthlyThemesList.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">No monthly themes. Create one to display on the homepage.</p>
                ) : (
                  <div className="space-y-4">
                    {monthlyThemesList.map(theme => (
                      <div key={theme.theme_id} className={`p-4 border rounded-lg ${theme.is_active ? 'border-primary bg-primary/5' : ''}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-medium">{theme.title}</h4>
                              {theme.is_active && <Badge className="bg-primary">Active</Badge>}
                              <Badge variant="outline">{theme.month}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{theme.description}</p>
                            {theme.scripture && (
                              <p className="text-sm text-muted-foreground italic mt-2">"{theme.scripture}" — {theme.scripture_reference}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingMonthlyTheme({
                                ...theme,
                                key_events: Array.isArray(theme.key_events) ? theme.key_events.join('\n') : theme.key_events
                              })}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteMonthlyTheme(theme.theme_id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Add Weekly Focus Dialog */}
            <Dialog open={showAddWeeklyFocus} onOpenChange={setShowAddWeeklyFocus}>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Weekly Focus</DialogTitle>
                  <DialogDescription>Set the weekly scripture and message for the church</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Title *</Label>
                    <Input 
                      value={newWeeklyFocus.title} 
                      onChange={(e) => setNewWeeklyFocus({...newWeeklyFocus, title: e.target.value})}
                      placeholder="e.g., Walking in Faith"
                    />
                  </div>
                  <div>
                    <Label>Scripture *</Label>
                    <Textarea 
                      value={newWeeklyFocus.scripture} 
                      onChange={(e) => setNewWeeklyFocus({...newWeeklyFocus, scripture: e.target.value})}
                      placeholder="The scripture text"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Scripture Reference *</Label>
                    <Input 
                      value={newWeeklyFocus.scripture_reference} 
                      onChange={(e) => setNewWeeklyFocus({...newWeeklyFocus, scripture_reference: e.target.value})}
                      placeholder="e.g., John 3:16"
                    />
                  </div>
                  <div>
                    <Label>Message</Label>
                    <Textarea 
                      value={newWeeklyFocus.message} 
                      onChange={(e) => setNewWeeklyFocus({...newWeeklyFocus, message: e.target.value})}
                      placeholder="Brief message for the week"
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Week Start</Label>
                      <Input 
                        type="date"
                        value={newWeeklyFocus.week_start} 
                        onChange={(e) => setNewWeeklyFocus({...newWeeklyFocus, week_start: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Week End</Label>
                      <Input 
                        type="date"
                        value={newWeeklyFocus.week_end} 
                        onChange={(e) => setNewWeeklyFocus({...newWeeklyFocus, week_end: e.target.value})}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Prayer Points (one per line)</Label>
                    <Textarea 
                      value={newWeeklyFocus.prayer_points} 
                      onChange={(e) => setNewWeeklyFocus({...newWeeklyFocus, prayer_points: e.target.value})}
                      placeholder="Pray for..."
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Action Items (one per line)</Label>
                    <Textarea 
                      value={newWeeklyFocus.action_items} 
                      onChange={(e) => setNewWeeklyFocus({...newWeeklyFocus, action_items: e.target.value})}
                      placeholder="Read one chapter daily..."
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Banner Image</Label>
                    <div className="space-y-2">
                      <Input 
                        type="file"
                        accept="image/*"
                        onChange={(e) => setWeeklyFocusImageFile(e.target.files?.[0] || null)}
                        data-testid="weekly-focus-image-input"
                      />
                      {weeklyFocusImageFile && (
                        <div className="flex items-center gap-2 p-2 bg-muted rounded">
                          <Image className="h-4 w-4" />
                          <span className="text-sm truncate">{weeklyFocusImageFile.name}</span>
                          <Button variant="ghost" size="sm" onClick={() => setWeeklyFocusImageFile(null)}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground">Or enter URL below</p>
                      <Input 
                        value={newWeeklyFocus.image_url} 
                        onChange={(e) => setNewWeeklyFocus({...newWeeklyFocus, image_url: e.target.value})}
                        placeholder="https://..."
                        disabled={!!weeklyFocusImageFile}
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setShowAddWeeklyFocus(false); setWeeklyFocusImageFile(null); }}>Cancel</Button>
                  <Button onClick={handleCreateWeeklyFocus} disabled={uploadingImage}>
                    {uploadingImage ? 'Creating...' : 'Create Focus'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Edit Weekly Focus Dialog */}
            <Dialog open={!!editingWeeklyFocus} onOpenChange={() => setEditingWeeklyFocus(null)}>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Weekly Focus</DialogTitle>
                </DialogHeader>
                {editingWeeklyFocus && (
                  <div className="space-y-4">
                    <div>
                      <Label>Title *</Label>
                      <Input 
                        value={editingWeeklyFocus.title} 
                        onChange={(e) => setEditingWeeklyFocus({...editingWeeklyFocus, title: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Scripture *</Label>
                      <Textarea 
                        value={editingWeeklyFocus.scripture} 
                        onChange={(e) => setEditingWeeklyFocus({...editingWeeklyFocus, scripture: e.target.value})}
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label>Scripture Reference</Label>
                      <Input 
                        value={editingWeeklyFocus.scripture_reference} 
                        onChange={(e) => setEditingWeeklyFocus({...editingWeeklyFocus, scripture_reference: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Message</Label>
                      <Textarea 
                        value={editingWeeklyFocus.message} 
                        onChange={(e) => setEditingWeeklyFocus({...editingWeeklyFocus, message: e.target.value})}
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Prayer Points (one per line)</Label>
                      <Textarea 
                        value={editingWeeklyFocus.prayer_points} 
                        onChange={(e) => setEditingWeeklyFocus({...editingWeeklyFocus, prayer_points: e.target.value})}
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Action Items (one per line)</Label>
                      <Textarea 
                        value={editingWeeklyFocus.action_items} 
                        onChange={(e) => setEditingWeeklyFocus({...editingWeeklyFocus, action_items: e.target.value})}
                        rows={3}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="focus-active"
                        checked={editingWeeklyFocus.is_active}
                        onChange={(e) => setEditingWeeklyFocus({...editingWeeklyFocus, is_active: e.target.checked})}
                        className="rounded"
                      />
                      <Label htmlFor="focus-active">Set as active (displays on homepage)</Label>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditingWeeklyFocus(null)}>Cancel</Button>
                  <Button onClick={handleUpdateWeeklyFocus}>Save Changes</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Add Monthly Theme Dialog */}
            <Dialog open={showAddMonthlyTheme} onOpenChange={setShowAddMonthlyTheme}>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Monthly Theme</DialogTitle>
                  <DialogDescription>Set the monthly theme for the church</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Title *</Label>
                    <Input 
                      value={newMonthlyTheme.title} 
                      onChange={(e) => setNewMonthlyTheme({...newMonthlyTheme, title: e.target.value})}
                      placeholder="e.g., Month of Prayer"
                    />
                  </div>
                  <div>
                    <Label>Month *</Label>
                    <Input 
                      type="month"
                      value={newMonthlyTheme.month} 
                      onChange={(e) => setNewMonthlyTheme({...newMonthlyTheme, month: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Description *</Label>
                    <Textarea 
                      value={newMonthlyTheme.description} 
                      onChange={(e) => setNewMonthlyTheme({...newMonthlyTheme, description: e.target.value})}
                      placeholder="This month we focus on..."
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Scripture (optional)</Label>
                    <Textarea 
                      value={newMonthlyTheme.scripture} 
                      onChange={(e) => setNewMonthlyTheme({...newMonthlyTheme, scripture: e.target.value})}
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Scripture Reference</Label>
                    <Input 
                      value={newMonthlyTheme.scripture_reference} 
                      onChange={(e) => setNewMonthlyTheme({...newMonthlyTheme, scripture_reference: e.target.value})}
                      placeholder="e.g., Philippians 4:6"
                    />
                  </div>
                  <div>
                    <Label>Key Events (one per line)</Label>
                    <Textarea 
                      value={newMonthlyTheme.key_events} 
                      onChange={(e) => setNewMonthlyTheme({...newMonthlyTheme, key_events: e.target.value})}
                      placeholder="Prayer night on the 15th..."
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Banner Image</Label>
                    <div className="space-y-2">
                      <Input 
                        type="file"
                        accept="image/*"
                        onChange={(e) => setMonthlyThemeImageFile(e.target.files?.[0] || null)}
                        data-testid="monthly-theme-image-input"
                      />
                      {monthlyThemeImageFile && (
                        <div className="flex items-center gap-2 p-2 bg-muted rounded">
                          <Image className="h-4 w-4" />
                          <span className="text-sm truncate">{monthlyThemeImageFile.name}</span>
                          <Button variant="ghost" size="sm" onClick={() => setMonthlyThemeImageFile(null)}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground">Or enter URL below</p>
                      <Input 
                        value={newMonthlyTheme.image_url} 
                        onChange={(e) => setNewMonthlyTheme({...newMonthlyTheme, image_url: e.target.value})}
                        placeholder="https://..."
                        disabled={!!monthlyThemeImageFile}
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setShowAddMonthlyTheme(false); setMonthlyThemeImageFile(null); }}>Cancel</Button>
                  <Button onClick={handleCreateMonthlyTheme} disabled={uploadingImage}>
                    {uploadingImage ? 'Creating...' : 'Create Theme'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Edit Monthly Theme Dialog */}
            <Dialog open={!!editingMonthlyTheme} onOpenChange={() => setEditingMonthlyTheme(null)}>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Monthly Theme</DialogTitle>
                </DialogHeader>
                {editingMonthlyTheme && (
                  <div className="space-y-4">
                    <div>
                      <Label>Title *</Label>
                      <Input 
                        value={editingMonthlyTheme.title} 
                        onChange={(e) => setEditingMonthlyTheme({...editingMonthlyTheme, title: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Month</Label>
                      <Input 
                        type="month"
                        value={editingMonthlyTheme.month} 
                        onChange={(e) => setEditingMonthlyTheme({...editingMonthlyTheme, month: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea 
                        value={editingMonthlyTheme.description} 
                        onChange={(e) => setEditingMonthlyTheme({...editingMonthlyTheme, description: e.target.value})}
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Scripture</Label>
                      <Textarea 
                        value={editingMonthlyTheme.scripture || ''} 
                        onChange={(e) => setEditingMonthlyTheme({...editingMonthlyTheme, scripture: e.target.value})}
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label>Key Events (one per line)</Label>
                      <Textarea 
                        value={editingMonthlyTheme.key_events} 
                        onChange={(e) => setEditingMonthlyTheme({...editingMonthlyTheme, key_events: e.target.value})}
                        rows={3}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="theme-active"
                        checked={editingMonthlyTheme.is_active}
                        onChange={(e) => setEditingMonthlyTheme({...editingMonthlyTheme, is_active: e.target.checked})}
                        className="rounded"
                      />
                      <Label htmlFor="theme-active">Set as active (displays on homepage)</Label>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditingMonthlyTheme(null)}>Cancel</Button>
                  <Button onClick={handleUpdateMonthlyTheme}>Save Changes</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}

        {/* SERMON NOTES TAB */}
        {activeAdminTab === 'sermon-notes' && (
          <>
            {/* Sermon Notes List */}
            <Card className="glass-card mb-8">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <BookOpen className="h-5 w-5" />
                      Sermon Notes
                    </CardTitle>
                    <CardDescription>Create and manage your preaching notes</CardDescription>
                  </div>
                  <Button onClick={() => setShowCreateSermonNote(true)} data-testid="create-sermon-note-btn">
                    <Plus className="h-4 w-4 mr-2" /> New Sermon Note
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="flex flex-wrap gap-3 mb-6">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input 
                        placeholder="Search notes..." 
                        value={sermonNoteFilter.search}
                        onChange={(e) => setSermonNoteFilter({...sermonNoteFilter, search: e.target.value})}
                        onKeyDown={(e) => e.key === 'Enter' && loadSermonNotesData()}
                        className="pl-9"
                      />
                    </div>
                  </div>
                  <Select value={sermonNoteFilter.status} onValueChange={(v) => { setSermonNoteFilter({...sermonNoteFilter, status: v === 'all' ? '' : v}); }}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={sermonNoteFilter.series} onValueChange={(v) => { setSermonNoteFilter({...sermonNoteFilter, series: v === 'all' ? '' : v}); }}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="All Series" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Series</SelectItem>
                      {sermonSeries.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={loadSermonNotesData}>
                    <Search className="h-4 w-4" />
                  </Button>
                </div>

                {/* Notes Grid */}
                {sermonNotesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : sermonNotes.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No sermon notes found. Create your first note!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {sermonNotes.map(note => (
                      <Card key={note.note_id} className={`border ${note.status === 'published' ? 'border-green-200' : 'border-yellow-200'}`}>
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-lg">{note.title}</h4>
                                <Badge className={note.status === 'published' ? 'bg-green-600' : 'bg-yellow-600'}>
                                  {note.status}
                                </Badge>
                                {note.series && <Badge variant="outline">{note.series}</Badge>}
                              </div>
                              <p className="text-sm text-primary font-medium">{note.main_scripture_reference}</p>
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{note.main_text}</p>
                              <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                                <span>{note.sub_topics?.length || 0} sub-topics</span>
                                <span>•</span>
                                <span>{note.sermon_date || 'No date set'}</span>
                                <span>•</span>
                                <span>By {note.created_by_name}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setViewingSermonNote(note)}
                                title="View"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setEditingSermonNote({
                                  ...note,
                                  supporting_scriptures: Array.isArray(note.supporting_scriptures) ? note.supporting_scriptures.join('\n') : note.supporting_scriptures,
                                  tags: Array.isArray(note.tags) ? note.tags.join(', ') : note.tags
                                })}
                                title="Edit"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleExportToPptx(note.note_id)}
                                title="Export to PowerPoint"
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                              {note.status === 'draft' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-green-600"
                                  onClick={() => handlePublishSermonNote(note.note_id)}
                                  title="Publish"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteSermonNote(note.note_id)}
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Create Sermon Note Dialog */}
            <Dialog open={showCreateSermonNote} onOpenChange={setShowCreateSermonNote}>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Sermon Note</DialogTitle>
                  <DialogDescription>Create a new preaching note with sub-topics and points</DialogDescription>
                </DialogHeader>
                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Sermon Title *</Label>
                      <Input 
                        value={newSermonNote.title} 
                        onChange={(e) => setNewSermonNote({...newSermonNote, title: e.target.value})}
                        placeholder="e.g., Walking by Faith"
                      />
                    </div>
                    <div>
                      <Label>Series</Label>
                      <Input 
                        value={newSermonNote.series} 
                        onChange={(e) => setNewSermonNote({...newSermonNote, series: e.target.value})}
                        placeholder="e.g., Faith Series"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Main Scripture Reference *</Label>
                      <Input 
                        value={newSermonNote.main_scripture_reference} 
                        onChange={(e) => setNewSermonNote({...newSermonNote, main_scripture_reference: e.target.value})}
                        placeholder="e.g., Hebrews 11:1-6"
                      />
                    </div>
                    <div>
                      <Label>Sermon Date</Label>
                      <Input 
                        type="date"
                        value={newSermonNote.sermon_date} 
                        onChange={(e) => setNewSermonNote({...newSermonNote, sermon_date: e.target.value})}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Main Text *</Label>
                    <Textarea 
                      value={newSermonNote.main_text} 
                      onChange={(e) => setNewSermonNote({...newSermonNote, main_text: e.target.value})}
                      placeholder="The main scripture passage..."
                      rows={4}
                      className="font-serif"
                    />
                  </div>

                  <div>
                    <Label>Supporting Scriptures (one per line)</Label>
                    <Textarea 
                      value={newSermonNote.supporting_scriptures} 
                      onChange={(e) => setNewSermonNote({...newSermonNote, supporting_scriptures: e.target.value})}
                      placeholder="Romans 10:17&#10;James 2:17"
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label>Introduction</Label>
                    <Textarea 
                      value={newSermonNote.introduction} 
                      onChange={(e) => setNewSermonNote({...newSermonNote, introduction: e.target.value})}
                      placeholder="Introduction to the sermon..."
                      rows={3}
                    />
                  </div>

                  {/* Sub-topics Section */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <Label className="text-base font-semibold">Sub-Topics & Points</Label>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => addSubTopic(newSermonNote, setNewSermonNote)}
                      >
                        <Plus className="h-4 w-4 mr-1" /> Add Sub-Topic
                      </Button>
                    </div>
                    
                    {newSermonNote.sub_topics.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No sub-topics yet. Click "Add Sub-Topic" to start building your sermon outline.
                      </p>
                    ) : (
                      <div className="space-y-6">
                        {newSermonNote.sub_topics.map((subtopic, stIdx) => (
                          <div key={subtopic.subtopic_id || stIdx} className="border rounded-lg p-4 bg-muted/30">
                            <div className="flex items-center gap-2 mb-4">
                              <Input 
                                value={subtopic.title} 
                                onChange={(e) => updateSubTopic(newSermonNote, setNewSermonNote, stIdx, 'title', e.target.value)}
                                placeholder="Sub-topic title"
                                className="flex-1 font-semibold"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeSubTopic(newSermonNote, setNewSermonNote, stIdx)}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                            
                            {/* Points by Category */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {/* Doctrine Points */}
                              <div className="border rounded p-3 bg-blue-50 dark:bg-blue-950/30">
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-sm font-medium text-blue-700 dark:text-blue-300">Doctrine</Label>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => addPoint(newSermonNote, setNewSermonNote, stIdx, 'doctrine')}
                                  >
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                </div>
                                <div className="space-y-2">
                                  {subtopic.points.filter(p => p.category === 'doctrine').map((point, pIdx) => {
                                    const actualIdx = subtopic.points.findIndex(p2 => p2.point_id === point.point_id);
                                    return (
                                      <div key={point.point_id || pIdx} className="flex gap-1">
                                        <Textarea
                                          value={point.content}
                                          onChange={(e) => updatePoint(newSermonNote, setNewSermonNote, stIdx, actualIdx, e.target.value)}
                                          placeholder="Teaching point..."
                                          rows={2}
                                          className="text-sm"
                                        />
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => removePoint(newSermonNote, setNewSermonNote, stIdx, actualIdx)}
                                        >
                                          <X className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Illustration Points */}
                              <div className="border rounded p-3 bg-amber-50 dark:bg-amber-950/30">
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-sm font-medium text-amber-700 dark:text-amber-300">Illustration</Label>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => addPoint(newSermonNote, setNewSermonNote, stIdx, 'illustration')}
                                  >
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                </div>
                                <div className="space-y-2">
                                  {subtopic.points.filter(p => p.category === 'illustration').map((point, pIdx) => {
                                    const actualIdx = subtopic.points.findIndex(p2 => p2.point_id === point.point_id);
                                    return (
                                      <div key={point.point_id || pIdx} className="flex gap-1">
                                        <Textarea
                                          value={point.content}
                                          onChange={(e) => updatePoint(newSermonNote, setNewSermonNote, stIdx, actualIdx, e.target.value)}
                                          placeholder="Story or example..."
                                          rows={2}
                                          className="text-sm"
                                        />
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => removePoint(newSermonNote, setNewSermonNote, stIdx, actualIdx)}
                                        >
                                          <X className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Application Points */}
                              <div className="border rounded p-3 bg-green-50 dark:bg-green-950/30">
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-sm font-medium text-green-700 dark:text-green-300">Application</Label>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => addPoint(newSermonNote, setNewSermonNote, stIdx, 'application')}
                                  >
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                </div>
                                <div className="space-y-2">
                                  {subtopic.points.filter(p => p.category === 'application').map((point, pIdx) => {
                                    const actualIdx = subtopic.points.findIndex(p2 => p2.point_id === point.point_id);
                                    return (
                                      <div key={point.point_id || pIdx} className="flex gap-1">
                                        <Textarea
                                          value={point.content}
                                          onChange={(e) => updatePoint(newSermonNote, setNewSermonNote, stIdx, actualIdx, e.target.value)}
                                          placeholder="Practical application..."
                                          rows={2}
                                          className="text-sm"
                                        />
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => removePoint(newSermonNote, setNewSermonNote, stIdx, actualIdx)}
                                        >
                                          <X className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label>Conclusion</Label>
                    <Textarea 
                      value={newSermonNote.conclusion} 
                      onChange={(e) => setNewSermonNote({...newSermonNote, conclusion: e.target.value})}
                      placeholder="Concluding thoughts..."
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label>Tags (comma-separated)</Label>
                    <Input 
                      value={newSermonNote.tags} 
                      onChange={(e) => setNewSermonNote({...newSermonNote, tags: e.target.value})}
                      placeholder="faith, trust, obedience"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCreateSermonNote(false)}>Cancel</Button>
                  <Button onClick={handleCreateSermonNote}>Create Note</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* View Sermon Note Dialog */}
            <Dialog open={!!viewingSermonNote} onOpenChange={() => setViewingSermonNote(null)}>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                {viewingSermonNote && (
                  <>
                    <DialogHeader>
                      <div className="flex items-center gap-2">
                        <DialogTitle className="text-2xl">{viewingSermonNote.title}</DialogTitle>
                        <Badge className={viewingSermonNote.status === 'published' ? 'bg-green-600' : 'bg-yellow-600'}>
                          {viewingSermonNote.status}
                        </Badge>
                      </div>
                      <DialogDescription>
                        {viewingSermonNote.main_scripture_reference} • {viewingSermonNote.sermon_date || 'No date'}
                        {viewingSermonNote.series && ` • ${viewingSermonNote.series}`}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-6 py-4">
                      {/* Main Text */}
                      <div className="bg-primary/5 rounded-lg p-4">
                        <h3 className="font-semibold mb-2 text-primary">Main Text</h3>
                        <p className="font-serif text-lg italic">"{viewingSermonNote.main_text}"</p>
                        <p className="text-sm font-medium text-right mt-2">— {viewingSermonNote.main_scripture_reference}</p>
                      </div>

                      {/* Supporting Scriptures */}
                      {viewingSermonNote.supporting_scriptures?.length > 0 && (
                        <div>
                          <h3 className="font-semibold mb-2">Supporting Scriptures</h3>
                          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                            {viewingSermonNote.supporting_scriptures.map((s, i) => (
                              <li key={i}>{s}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Introduction */}
                      {viewingSermonNote.introduction && (
                        <div>
                          <h3 className="font-semibold mb-2">Introduction</h3>
                          <p className="text-muted-foreground whitespace-pre-wrap">{viewingSermonNote.introduction}</p>
                        </div>
                      )}

                      {/* Sub-topics */}
                      {viewingSermonNote.sub_topics?.map((subtopic, idx) => (
                        <div key={subtopic.subtopic_id || idx} className="border rounded-lg p-4">
                          <h3 className="font-semibold text-lg mb-4">{idx + 1}. {subtopic.title}</h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Doctrine */}
                            <div>
                              <h4 className="font-medium text-blue-600 mb-2 flex items-center gap-1">
                                <BookOpen className="h-4 w-4" /> Doctrine
                              </h4>
                              <ul className="space-y-2">
                                {subtopic.points?.filter(p => p.category === 'doctrine').map((point, pIdx) => (
                                  <li key={point.point_id || pIdx} className="text-sm bg-blue-50 dark:bg-blue-950/30 p-2 rounded">
                                    {point.content}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            {/* Illustration */}
                            <div>
                              <h4 className="font-medium text-amber-600 mb-2 flex items-center gap-1">
                                <Lightbulb className="h-4 w-4" /> Illustration
                              </h4>
                              <ul className="space-y-2">
                                {subtopic.points?.filter(p => p.category === 'illustration').map((point, pIdx) => (
                                  <li key={point.point_id || pIdx} className="text-sm bg-amber-50 dark:bg-amber-950/30 p-2 rounded">
                                    {point.content}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            {/* Application */}
                            <div>
                              <h4 className="font-medium text-green-600 mb-2 flex items-center gap-1">
                                <Target className="h-4 w-4" /> Application
                              </h4>
                              <ul className="space-y-2">
                                {subtopic.points?.filter(p => p.category === 'application').map((point, pIdx) => (
                                  <li key={point.point_id || pIdx} className="text-sm bg-green-50 dark:bg-green-950/30 p-2 rounded">
                                    {point.content}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Conclusion */}
                      {viewingSermonNote.conclusion && (
                        <div className="bg-muted/30 rounded-lg p-4">
                          <h3 className="font-semibold mb-2">Conclusion</h3>
                          <p className="text-muted-foreground whitespace-pre-wrap">{viewingSermonNote.conclusion}</p>
                        </div>
                      )}
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => handleExportToPptx(viewingSermonNote.note_id)}>
                        <FileText className="h-4 w-4 mr-2" /> Export Slides
                      </Button>
                      <Button variant="outline" onClick={() => setViewingSermonNote(null)}>Close</Button>
                    </DialogFooter>
                  </>
                )}
              </DialogContent>
            </Dialog>

            {/* Edit Sermon Note Dialog - Similar to Create but pre-populated */}
            <Dialog open={!!editingSermonNote} onOpenChange={() => setEditingSermonNote(null)}>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Sermon Note</DialogTitle>
                </DialogHeader>
                {editingSermonNote && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Sermon Title *</Label>
                        <Input 
                          value={editingSermonNote.title} 
                          onChange={(e) => setEditingSermonNote({...editingSermonNote, title: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Series</Label>
                        <Input 
                          value={editingSermonNote.series || ''} 
                          onChange={(e) => setEditingSermonNote({...editingSermonNote, series: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Main Scripture Reference</Label>
                        <Input 
                          value={editingSermonNote.main_scripture_reference} 
                          onChange={(e) => setEditingSermonNote({...editingSermonNote, main_scripture_reference: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Sermon Date</Label>
                        <Input 
                          type="date"
                          value={editingSermonNote.sermon_date || ''} 
                          onChange={(e) => setEditingSermonNote({...editingSermonNote, sermon_date: e.target.value})}
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Main Text</Label>
                      <Textarea 
                        value={editingSermonNote.main_text} 
                        onChange={(e) => setEditingSermonNote({...editingSermonNote, main_text: e.target.value})}
                        rows={4}
                        className="font-serif"
                      />
                    </div>

                    <div>
                      <Label>Supporting Scriptures (one per line)</Label>
                      <Textarea 
                        value={editingSermonNote.supporting_scriptures} 
                        onChange={(e) => setEditingSermonNote({...editingSermonNote, supporting_scriptures: e.target.value})}
                        rows={3}
                      />
                    </div>

                    <div>
                      <Label>Introduction</Label>
                      <Textarea 
                        value={editingSermonNote.introduction || ''} 
                        onChange={(e) => setEditingSermonNote({...editingSermonNote, introduction: e.target.value})}
                        rows={3}
                      />
                    </div>

                    {/* Sub-topics Section for Edit */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <Label className="text-base font-semibold">Sub-Topics & Points</Label>
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => addSubTopic(editingSermonNote, setEditingSermonNote)}
                        >
                          <Plus className="h-4 w-4 mr-1" /> Add Sub-Topic
                        </Button>
                      </div>
                      
                      <div className="space-y-6">
                        {(editingSermonNote.sub_topics || []).map((subtopic, stIdx) => (
                          <div key={subtopic.subtopic_id || stIdx} className="border rounded-lg p-4 bg-muted/30">
                            <div className="flex items-center gap-2 mb-4">
                              <Input 
                                value={subtopic.title} 
                                onChange={(e) => updateSubTopic(editingSermonNote, setEditingSermonNote, stIdx, 'title', e.target.value)}
                                placeholder="Sub-topic title"
                                className="flex-1 font-semibold"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeSubTopic(editingSermonNote, setEditingSermonNote, stIdx)}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {/* Doctrine */}
                              <div className="border rounded p-3 bg-blue-50 dark:bg-blue-950/30">
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-sm font-medium text-blue-700 dark:text-blue-300">Doctrine</Label>
                                  <Button type="button" variant="ghost" size="sm" onClick={() => addPoint(editingSermonNote, setEditingSermonNote, stIdx, 'doctrine')}>
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                </div>
                                <div className="space-y-2">
                                  {subtopic.points?.filter(p => p.category === 'doctrine').map((point, pIdx) => {
                                    const actualIdx = subtopic.points.findIndex(p2 => p2.point_id === point.point_id);
                                    return (
                                      <div key={point.point_id || pIdx} className="flex gap-1">
                                        <Textarea value={point.content} onChange={(e) => updatePoint(editingSermonNote, setEditingSermonNote, stIdx, actualIdx, e.target.value)} rows={2} className="text-sm" />
                                        <Button type="button" variant="ghost" size="sm" onClick={() => removePoint(editingSermonNote, setEditingSermonNote, stIdx, actualIdx)}><X className="h-3 w-3" /></Button>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                              {/* Illustration */}
                              <div className="border rounded p-3 bg-amber-50 dark:bg-amber-950/30">
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-sm font-medium text-amber-700 dark:text-amber-300">Illustration</Label>
                                  <Button type="button" variant="ghost" size="sm" onClick={() => addPoint(editingSermonNote, setEditingSermonNote, stIdx, 'illustration')}>
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                </div>
                                <div className="space-y-2">
                                  {subtopic.points?.filter(p => p.category === 'illustration').map((point, pIdx) => {
                                    const actualIdx = subtopic.points.findIndex(p2 => p2.point_id === point.point_id);
                                    return (
                                      <div key={point.point_id || pIdx} className="flex gap-1">
                                        <Textarea value={point.content} onChange={(e) => updatePoint(editingSermonNote, setEditingSermonNote, stIdx, actualIdx, e.target.value)} rows={2} className="text-sm" />
                                        <Button type="button" variant="ghost" size="sm" onClick={() => removePoint(editingSermonNote, setEditingSermonNote, stIdx, actualIdx)}><X className="h-3 w-3" /></Button>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                              {/* Application */}
                              <div className="border rounded p-3 bg-green-50 dark:bg-green-950/30">
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-sm font-medium text-green-700 dark:text-green-300">Application</Label>
                                  <Button type="button" variant="ghost" size="sm" onClick={() => addPoint(editingSermonNote, setEditingSermonNote, stIdx, 'application')}>
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                </div>
                                <div className="space-y-2">
                                  {subtopic.points?.filter(p => p.category === 'application').map((point, pIdx) => {
                                    const actualIdx = subtopic.points.findIndex(p2 => p2.point_id === point.point_id);
                                    return (
                                      <div key={point.point_id || pIdx} className="flex gap-1">
                                        <Textarea value={point.content} onChange={(e) => updatePoint(editingSermonNote, setEditingSermonNote, stIdx, actualIdx, e.target.value)} rows={2} className="text-sm" />
                                        <Button type="button" variant="ghost" size="sm" onClick={() => removePoint(editingSermonNote, setEditingSermonNote, stIdx, actualIdx)}><X className="h-3 w-3" /></Button>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label>Conclusion</Label>
                      <Textarea 
                        value={editingSermonNote.conclusion || ''} 
                        onChange={(e) => setEditingSermonNote({...editingSermonNote, conclusion: e.target.value})}
                        rows={3}
                      />
                    </div>

                    <div>
                      <Label>Tags (comma-separated)</Label>
                      <Input 
                        value={editingSermonNote.tags || ''} 
                        onChange={(e) => setEditingSermonNote({...editingSermonNote, tags: e.target.value})}
                      />
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditingSermonNote(null)}>Cancel</Button>
                  <Button onClick={handleUpdateSermonNote}>Save Changes</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}

        {/* LIVE STREAM TAB */}
        {activeAdminTab === 'livestream' && (
          <>
            {/* Live Streams Management */}
            <Card className="glass-card mb-8">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Radio className="h-5 w-5" />
                      Live Streams
                    </CardTitle>
                    <CardDescription>Manage live stream events for your church</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowManagePermissions(true)}>
                      <Users className="h-4 w-4 mr-2" /> Permissions
                    </Button>
                    <Button onClick={() => setShowAddLivestream(true)} data-testid="create-livestream-btn">
                      <Plus className="h-4 w-4 mr-2" /> New Stream
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {livestreams.length === 0 ? (
                  <div className="text-center py-12">
                    <Radio className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Live Streams Yet</h3>
                    <p className="text-sm text-muted-foreground mb-4">Create your first live stream event to engage with your congregation.</p>
                    <Button onClick={() => setShowAddLivestream(true)}>Create Stream</Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {livestreams.map(stream => (
                      <div key={stream.stream_id} className="p-4 border rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{stream.title}</h3>
                              <Badge variant={stream.status === 'live' ? 'destructive' : stream.status === 'scheduled' ? 'default' : 'secondary'}>
                                {stream.status === 'live' && <span className="animate-pulse mr-1">●</span>}
                                {stream.status}
                              </Badge>
                            </div>
                            {stream.description && <p className="text-sm text-muted-foreground mt-1">{stream.description}</p>}
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span>Type: {stream.stream_type}</span>
                              {stream.scheduled_at && <span>Scheduled: {new Date(stream.scheduled_at).toLocaleString()}</span>}
                              <span>By: {stream.created_by_name}</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {stream.status === 'scheduled' && (
                              <Button size="sm" onClick={() => handleGoLive(stream.stream_id)}>
                                <Play className="h-4 w-4 mr-1" /> Go Live
                              </Button>
                            )}
                            {stream.status === 'live' && (
                              <Button size="sm" variant="destructive" onClick={() => handleEndStream(stream.stream_id)}>
                                End Stream
                              </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={async () => { 
                              try {
                                const res = await axiosInstance.get(`/livestream/${stream.stream_id}`);
                                setShowStreamSetup(res.data);
                              } catch (e) {
                                toast.error('Failed to load stream details');
                              }
                            }}>
                              Setup
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Stream Setup Instructions */}
            {streamInstructions && (
              <Card className="glass-card mb-8">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Mic2 className="h-5 w-5" />
                    Streaming Setup Guide
                  </CardTitle>
                  <CardDescription>Instructions for setting up your live stream</CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue={streamInstructions.platforms[0]?.name}>
                    <TabsList className="mb-4">
                      {streamInstructions.platforms.map(platform => (
                        <TabsTrigger key={platform.name} value={platform.name}>{platform.name}</TabsTrigger>
                      ))}
                    </TabsList>
                    {streamInstructions.platforms.map(platform => (
                      <TabsContent key={platform.name} value={platform.name}>
                        <div className="space-y-4">
                          <div>
                            <h4 className="font-semibold mb-2">Setup Steps:</h4>
                            <ol className="list-decimal list-inside space-y-1 text-sm">
                              {platform.steps.map((step, i) => (
                                <li key={i}>{step}</li>
                              ))}
                            </ol>
                          </div>
                          {platform.tips && (
                            <div>
                              <h4 className="font-semibold mb-2">Tips:</h4>
                              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                                {platform.tips.map((tip, i) => (
                                  <li key={i}>{tip}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </TabsContent>
                    ))}
                  </Tabs>
                  
                  <div className="mt-6 p-4 bg-muted rounded-lg">
                    <h4 className="font-semibold mb-2">Technical Requirements</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>Upload Speed: {streamInstructions.technical_requirements.recommended_upload_speed}</div>
                      <div>Resolution: {streamInstructions.technical_requirements.video_resolution}</div>
                      <div>Video Bitrate: {streamInstructions.technical_requirements.video_bitrate}</div>
                      <div>Frame Rate: {streamInstructions.technical_requirements.frame_rate}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Streaming Permissions */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Streaming Permissions
                </CardTitle>
                <CardDescription>Control who can create and manage live streams</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  By default, only admins and pastors can create live streams. Grant permission to other users below.
                </p>
                {streamPermissions.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">No additional users have streaming permissions.</p>
                ) : (
                  <div className="space-y-2">
                    {streamPermissions.map(perm => (
                      <div key={perm.user_id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={perm.user?.picture} />
                            <AvatarFallback>{perm.user?.name?.[0]}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{perm.user?.name}</p>
                            <p className="text-xs text-muted-foreground">{perm.user?.email}</p>
                          </div>
                        </div>
                        <Switch checked={perm.can_stream} onCheckedChange={(v) => handleUpdateStreamPermission(perm.user_id, v)} />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Create Livestream Dialog */}
            <Dialog open={showAddLivestream} onOpenChange={setShowAddLivestream}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create Live Stream</DialogTitle>
                  <DialogDescription>Set up a new live stream for your congregation</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                  <div>
                    <Label>Stream Title</Label>
                    <Input value={newLivestreamData.title} onChange={(e) => setNewLivestreamData({...newLivestreamData, title: e.target.value})} placeholder="e.g., Sunday Service - March 17" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea value={newLivestreamData.description} onChange={(e) => setNewLivestreamData({...newLivestreamData, description: e.target.value})} placeholder="Brief description" />
                  </div>
                  <div>
                    <Label>Stream Type</Label>
                    <Select value={newLivestreamData.stream_type} onValueChange={(v) => setNewLivestreamData({...newLivestreamData, stream_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="service">Church Service</SelectItem>
                        <SelectItem value="event">Special Event</SelectItem>
                        <SelectItem value="bible_study">Bible Study</SelectItem>
                        <SelectItem value="prayer">Prayer Meeting</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Streaming Destination */}
                  <div className="border-t pt-4">
                    <Label className="text-base font-semibold">Streaming Platform</Label>
                    <p className="text-sm text-muted-foreground mb-3">Choose where you want to stream from</p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 'youtube', label: 'YouTube Live', icon: '📺', desc: 'Stream via YouTube' },
                        { id: 'facebook', label: 'Facebook Live', icon: '📘', desc: 'Stream via Facebook' },
                        { id: 'embed', label: 'Embed URL', icon: '🔗', desc: 'Any embed link' },
                        { id: 'custom', label: 'Custom RTMP', icon: '🖥️', desc: 'Your own server' }
                      ].map(dest => (
                        <div 
                          key={dest.id}
                          onClick={() => setNewLivestreamData({...newLivestreamData, stream_destination: dest.id})}
                          className={`p-3 border rounded-lg cursor-pointer transition-all ${newLivestreamData.stream_destination === dest.id ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground'}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{dest.icon}</span>
                            <div>
                              <p className="font-medium text-sm">{dest.label}</p>
                              <p className="text-xs text-muted-foreground">{dest.desc}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Platform-specific fields */}
                  {newLivestreamData.stream_destination === 'youtube' && (
                    <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-lg">
                      <Label>YouTube Live Video URL</Label>
                      <Input 
                        value={newLivestreamData.youtube_url} 
                        onChange={(e) => setNewLivestreamData({...newLivestreamData, youtube_url: e.target.value})} 
                        placeholder="https://youtube.com/watch?v=... or youtube.com/live/..." 
                        className="mt-1"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        1. Go to YouTube Studio → Create → Go Live<br/>
                        2. Set up your stream and copy the video URL<br/>
                        3. Paste the URL above to embed the stream
                      </p>
                    </div>
                  )}

                  {newLivestreamData.stream_destination === 'facebook' && (
                    <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                      <Label>Facebook Live Video URL</Label>
                      <Input 
                        value={newLivestreamData.facebook_url} 
                        onChange={(e) => setNewLivestreamData({...newLivestreamData, facebook_url: e.target.value})} 
                        placeholder="https://facebook.com/video/..." 
                        className="mt-1"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        1. Go to your Facebook Page → Live Video<br/>
                        2. Set up your stream and copy the video URL<br/>
                        3. Paste the URL above to embed the stream
                      </p>
                    </div>
                  )}

                  {newLivestreamData.stream_destination === 'embed' && (
                    <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg">
                      <Label>Embed URL (Vimeo, Twitch, etc.)</Label>
                      <Input 
                        value={newLivestreamData.embed_url} 
                        onChange={(e) => setNewLivestreamData({...newLivestreamData, embed_url: e.target.value})} 
                        placeholder="https://player.vimeo.com/video/... or https://player.twitch.tv/..." 
                        className="mt-1"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Paste the embed URL from any streaming platform.<br/>
                        This will be embedded directly in your live stream page.
                      </p>
                    </div>
                  )}

                  {newLivestreamData.stream_destination === 'custom' && (
                    <div className="p-4 bg-gray-500/5 border border-gray-500/20 rounded-lg">
                      <Label>Custom RTMP Server URL</Label>
                      <Input 
                        value={newLivestreamData.custom_rtmp_url} 
                        onChange={(e) => setNewLivestreamData({...newLivestreamData, custom_rtmp_url: e.target.value})} 
                        placeholder="rtmp://your-server.com/live" 
                        className="mt-1"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Enter your own RTMP server URL.<br/>
                        A unique stream key will be generated for you.
                      </p>
                    </div>
                  )}

                  <div>
                    <Label>Scheduled Date/Time (Optional)</Label>
                    <Input type="datetime-local" value={newLivestreamData.scheduled_at} onChange={(e) => setNewLivestreamData({...newLivestreamData, scheduled_at: e.target.value})} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddLivestream(false)}>Cancel</Button>
                  <Button onClick={handleCreateLivestream}>Create Stream</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Manage Permissions Dialog */}
            <Dialog open={showManagePermissions} onOpenChange={setShowManagePermissions}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Manage Streaming Permissions</DialogTitle>
                  <DialogDescription>Search for users to grant streaming access</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Search Users</Label>
                    <Input placeholder="Search by name or email..." onChange={(e) => {
                      if (e.target.value.length >= 2) {
                        axiosInstance.get(`/users/search?q=${e.target.value}`).then(res => {
                          // Show search results in a list
                        });
                      }
                    }} />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Enter a user's name or email to grant them streaming permissions.
                  </p>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowManagePermissions(false)}>Close</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Stream Setup Dialog - Shows RTMP URL and Stream Key */}
            <Dialog open={!!showStreamSetup} onOpenChange={() => setShowStreamSetup(null)}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Radio className="h-5 w-5" />
                    Stream Setup Details
                  </DialogTitle>
                  <DialogDescription>Use these credentials in your streaming software</DialogDescription>
                </DialogHeader>
                {showStreamSetup && (
                  <div className="space-y-4">
                    <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <p className="text-sm font-medium text-green-600 mb-1">Stream Created Successfully!</p>
                      <p className="text-lg font-semibold">{showStreamSetup.title}</p>
                      <p className="text-sm text-muted-foreground">Platform: {showStreamSetup.stream_destination || 'custom'}</p>
                    </div>
                    
                    {/* YouTube/Facebook/Embed - Show embed info */}
                    {(showStreamSetup.stream_destination === 'youtube' || showStreamSetup.stream_destination === 'facebook' || showStreamSetup.stream_destination === 'embed') && (
                      <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                        <h4 className="font-medium mb-2">Embed Ready!</h4>
                        <p className="text-sm text-muted-foreground mb-3">
                          Your stream will be embedded automatically when you go live. Viewers can watch directly on your church website.
                        </p>
                        {showStreamSetup.playback_url && (
                          <div>
                            <Label className="text-sm">Stream URL</Label>
                            <div className="flex gap-2 mt-1">
                              <Input value={showStreamSetup.playback_url} readOnly className="font-mono text-sm" />
                              <Button variant="outline" size="sm" onClick={() => {
                                navigator.clipboard.writeText(showStreamSetup.playback_url);
                                toast.success('URL copied!');
                              }}>Copy</Button>
                            </div>
                          </div>
                        )}
                        {showStreamSetup.stream_destination === 'youtube' && (
                          <div className="mt-3 p-3 bg-red-500/10 rounded">
                            <p className="text-sm font-medium">YouTube Live Setup:</p>
                            <ol className="text-xs mt-1 space-y-1 list-decimal list-inside text-muted-foreground">
                              <li>Go to YouTube Studio → Create → Go Live</li>
                              <li>Set up your stream settings</li>
                              <li>Start streaming from OBS/your software to YouTube</li>
                              <li>Click "Go Live" below when ready to show on website</li>
                            </ol>
                          </div>
                        )}
                        {showStreamSetup.stream_destination === 'facebook' && (
                          <div className="mt-3 p-3 bg-blue-500/10 rounded">
                            <p className="text-sm font-medium">Facebook Live Setup:</p>
                            <ol className="text-xs mt-1 space-y-1 list-decimal list-inside text-muted-foreground">
                              <li>Go to your Facebook Page → Live Video</li>
                              <li>Set up your stream and go live on Facebook</li>
                              <li>Click "Go Live" below when ready to show on website</li>
                            </ol>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Custom RTMP - Show RTMP credentials */}
                    {(showStreamSetup.stream_destination === 'custom' || !showStreamSetup.stream_destination) && showStreamSetup.rtmp_url && (
                      <>
                        <div>
                          <Label className="text-sm font-medium">RTMP Server URL</Label>
                          <div className="flex gap-2 mt-1">
                            <Input value={showStreamSetup.rtmp_url} readOnly className="font-mono text-sm" />
                            <Button variant="outline" size="sm" onClick={() => {
                              navigator.clipboard.writeText(showStreamSetup.rtmp_url);
                              toast.success('RTMP URL copied!');
                            }}>
                              Copy
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Enter this as the Server/Stream URL in your streaming software</p>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium">Stream Key (Keep Private!)</Label>
                          <div className="flex gap-2 mt-1">
                            <Input value={showStreamSetup.stream_key} readOnly className="font-mono text-sm" type="password" />
                            <Button variant="outline" size="sm" onClick={() => {
                              navigator.clipboard.writeText(showStreamSetup.stream_key);
                              toast.success('Stream key copied!');
                            }}>
                              Copy
                            </Button>
                          </div>
                          <p className="text-xs text-red-500 mt-1">Never share your stream key with anyone!</p>
                        </div>

                        <div className="p-4 bg-muted rounded-lg">
                          <h4 className="font-medium mb-2">Quick Setup (OBS Studio)</h4>
                          <ol className="text-sm space-y-1 list-decimal list-inside text-muted-foreground">
                            <li>Open OBS Studio and go to Settings → Stream</li>
                            <li>Select Custom as the Service</li>
                            <li>Paste the RTMP URL in the Server field</li>
                            <li>Paste the Stream Key in the Stream Key field</li>
                            <li>Click Start Streaming when ready</li>
                          </ol>
                        </div>
                      </>
                    )}

                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Info className="h-4 w-4" />
                      <span>Status: <Badge variant={showStreamSetup.status === 'live' ? 'destructive' : 'secondary'}>{showStreamSetup.status}</Badge></span>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button onClick={() => setShowStreamSetup(null)}>Done</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}

        {/* RESOURCES TAB */}
        {activeAdminTab === 'resources' && (
          <>
            {/* Resource Stats */}
            {resourceStats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <Card className="glass-card">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <FolderOpen className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Resources</p>
                        <p className="text-2xl font-bold">{resourceStats.total_resources}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="glass-card">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                        <Folder className="h-5 w-5 text-blue-500" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Folders</p>
                        <p className="text-2xl font-bold">{resourceStats.total_folders}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="glass-card">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                        <Download className="h-5 w-5 text-green-500" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Storage Used</p>
                        <p className="text-2xl font-bold">{resourceStats.total_storage}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="glass-card">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                        <FileVideo className="h-5 w-5 text-purple-500" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Videos</p>
                        <p className="text-2xl font-bold">{resourceStats.by_type?.video || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Folders Section */}
            <Card className="glass-card mb-8">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Folder className="h-5 w-5" />
                      Resource Folders
                    </CardTitle>
                    <CardDescription>Organize resources into folders</CardDescription>
                  </div>
                  <Button onClick={() => setShowCreateFolder(true)} data-testid="create-folder-btn">
                    <Plus className="h-4 w-4 mr-2" /> New Folder
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {resourceFolders.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">No folders yet. Create folders to organize your resources.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {resourceFolders.map(folder => (
                      <div key={folder.folder_id} className="p-4 border rounded-lg hover:border-primary/50 transition-colors group">
                        <div className="flex flex-col items-center text-center">
                          <Folder className="h-12 w-12 text-yellow-500 mb-2" />
                          <h4 className="font-medium text-sm truncate w-full">{folder.name}</h4>
                          <p className="text-xs text-muted-foreground">{folder.description || 'No description'}</p>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => handleDeleteFolder(folder.folder_id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Resources List */}
            <Card className="glass-card mb-8">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <FolderOpen className="h-5 w-5" />
                      Resource Library
                    </CardTitle>
                    <CardDescription>Upload and manage files for your church team</CardDescription>
                  </div>
                  <Button onClick={() => setShowUploadResource(true)} data-testid="upload-resource-btn">
                    <Upload className="h-4 w-4 mr-2" /> Upload Resource
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="flex flex-wrap gap-3 mb-6">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input 
                        placeholder="Search resources..." 
                        value={resourceFilter.search}
                        onChange={(e) => setResourceFilter({...resourceFilter, search: e.target.value})}
                        className="pl-9"
                      />
                    </div>
                  </div>
                  <Select value={resourceFilter.category} onValueChange={(v) => setResourceFilter({...resourceFilter, category: v === 'all' ? '' : v})}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {resourceCategories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.icon} {cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={resourceFilter.file_type} onValueChange={(v) => setResourceFilter({...resourceFilter, file_type: v === 'all' ? '' : v})}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="video">Videos</SelectItem>
                      <SelectItem value="audio">Audio</SelectItem>
                      <SelectItem value="document">Documents</SelectItem>
                      <SelectItem value="presentation">Presentations</SelectItem>
                      <SelectItem value="image">Images</SelectItem>
                      <SelectItem value="spreadsheet">Spreadsheets</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Resources Grid */}
                {resourcesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : filteredResources.length === 0 ? (
                  <div className="text-center py-12">
                    <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No resources found. Upload your first resource!</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredResources.map(resource => (
                      <Card key={resource.resource_id} className="border hover:shadow-md transition-shadow">
                        <CardContent className="pt-4">
                          <div className="flex items-start gap-3">
                            <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                              {getFileTypeIcon(resource.file_type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium truncate">{resource.title}</h4>
                              <p className="text-sm text-muted-foreground truncate">{resource.description || resource.original_filename}</p>
                              <div className="flex items-center gap-2 mt-2 flex-wrap">
                                <Badge variant="outline" className="text-xs">{resource.category}</Badge>
                                <Badge variant="secondary" className="text-xs">{resource.file_type}</Badge>
                                <span className="text-xs text-muted-foreground">{resource.file_size_formatted}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-4 pt-3 border-t">
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Download className="h-3 w-3" />
                              {resource.download_count} downloads
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePreviewResource(resource)}
                                title="Preview"
                                data-testid={`preview-resource-${resource.resource_id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${resource.file_path}`, '_blank')}
                                title="Download"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteResource(resource.resource_id)}
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Upload Resource Dialog */}
            <Dialog open={showUploadResource} onOpenChange={setShowUploadResource}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Upload Resource</DialogTitle>
                  <DialogDescription>Upload videos, PowerPoints, documents, and other files for your team</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>File *</Label>
                    <div className="mt-1">
                      <Input
                        type="file"
                        onChange={(e) => setSelectedResourceFile(e.target.files?.[0] || null)}
                        accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.mp4,.mov,.avi,.mp3,.wav,.png,.jpg,.jpeg,.gif,.webp"
                        data-testid="resource-file-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Max file size: 100MB</p>
                    </div>
                    {selectedResourceFile && (
                      <div className="mt-2 p-2 bg-muted rounded flex items-center gap-2">
                        {getFileTypeIcon(selectedResourceFile.name.split('.').pop())}
                        <span className="text-sm truncate">{selectedResourceFile.name}</span>
                        <span className="text-xs text-muted-foreground">({(selectedResourceFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label>Title *</Label>
                    <Input 
                      value={newResourceData.title} 
                      onChange={(e) => setNewResourceData({...newResourceData, title: e.target.value})} 
                      placeholder="e.g., Sunday Service Slides - March 2026"
                      data-testid="resource-title-input"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea 
                      value={newResourceData.description} 
                      onChange={(e) => setNewResourceData({...newResourceData, description: e.target.value})} 
                      placeholder="Brief description of this resource"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={newResourceData.category} onValueChange={(v) => setNewResourceData({...newResourceData, category: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {resourceCategories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.icon} {cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tags (comma-separated)</Label>
                    <Input 
                      value={newResourceData.tags} 
                      onChange={(e) => setNewResourceData({...newResourceData, tags: e.target.value})} 
                      placeholder="e.g., worship, slides, 2026"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="resource-public"
                      checked={newResourceData.is_public}
                      onChange={(e) => setNewResourceData({...newResourceData, is_public: e.target.checked})}
                      className="rounded"
                    />
                    <Label htmlFor="resource-public" className="text-sm font-normal">Make publicly visible to all members</Label>
                  </div>

                  {/* Upload Progress */}
                  {uploadingResource && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>Uploading...</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <Progress value={uploadProgress} />
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowUploadResource(false)} disabled={uploadingResource}>Cancel</Button>
                  <Button onClick={handleUploadResource} disabled={uploadingResource || !selectedResourceFile}>
                    {uploadingResource ? 'Uploading...' : 'Upload Resource'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Create Folder Dialog */}
            <Dialog open={showCreateFolder} onOpenChange={setShowCreateFolder}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Folder</DialogTitle>
                  <DialogDescription>Organize your resources into folders</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Folder Name *</Label>
                    <Input 
                      value={newFolderData.name} 
                      onChange={(e) => setNewFolderData({...newFolderData, name: e.target.value})} 
                      placeholder="e.g., 2026 Sermons"
                      data-testid="folder-name-input"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input 
                      value={newFolderData.description} 
                      onChange={(e) => setNewFolderData({...newFolderData, description: e.target.value})} 
                      placeholder="Brief description"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="folder-public"
                      checked={newFolderData.is_public}
                      onChange={(e) => setNewFolderData({...newFolderData, is_public: e.target.checked})}
                      className="rounded"
                    />
                    <Label htmlFor="folder-public" className="text-sm font-normal">Visible to all members</Label>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCreateFolder(false)}>Cancel</Button>
                  <Button onClick={handleCreateResourceFolder}>Create Folder</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Resource Preview Dialog */}
            <Dialog open={!!previewResource} onOpenChange={() => setPreviewResource(null)}>
              <DialogContent className="max-w-4xl max-h-[90vh]">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {previewResource && getFileTypeIcon(previewResource.file_type)}
                    {previewResource?.title}
                  </DialogTitle>
                  <DialogDescription>
                    {previewResource?.original_filename} • {previewResource?.file_size_formatted}
                  </DialogDescription>
                </DialogHeader>
                <div className="flex-1 overflow-auto min-h-[400px]">
                  {previewLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : previewResource?.preview_type === 'image' ? (
                    <img 
                      src={`${BACKEND_URL}${previewResource.preview_url}`} 
                      alt={previewResource.title}
                      className="max-w-full max-h-[60vh] mx-auto object-contain"
                    />
                  ) : previewResource?.preview_type === 'video' ? (
                    <video 
                      src={`${BACKEND_URL}${previewResource.preview_url}`} 
                      controls 
                      className="w-full max-h-[60vh]"
                    />
                  ) : previewResource?.preview_type === 'audio' ? (
                    <div className="flex flex-col items-center justify-center h-full p-8">
                      <FileAudio className="h-24 w-24 text-green-500 mb-4" />
                      <audio 
                        src={`${BACKEND_URL}${previewResource.preview_url}`} 
                        controls 
                        className="w-full max-w-md"
                      />
                    </div>
                  ) : previewResource?.preview_type === 'pdf' ? (
                    <iframe 
                      src={`${BACKEND_URL}${previewResource.preview_url}`}
                      className="w-full h-[60vh]"
                      title={previewResource.title}
                    />
                  ) : previewResource?.preview_type === 'google_docs' ? (
                    <iframe 
                      src={previewResource.preview_url}
                      className="w-full h-[60vh]"
                      title={previewResource.title}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full p-8">
                      <File className="h-24 w-24 text-muted-foreground mb-4" />
                      <p className="text-muted-foreground mb-4">Preview not available for this file type</p>
                      <Button onClick={() => window.open(`${BACKEND_URL}${previewResource?.direct_url || previewResource?.preview_url}`, '_blank')}>
                        <Download className="h-4 w-4 mr-2" />
                        Download File
                      </Button>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setPreviewResource(null)}>Close</Button>
                  <Button onClick={() => window.open(`${BACKEND_URL}${previewResource?.direct_url || previewResource?.preview_url}`, '_blank')}>
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}

        {/* CONTENT TAB */}
        {activeAdminTab === 'content' && (
          <>
        {/* Homepage Content Management */}
        <Card className="glass-card mb-8" data-testid="homepage-content-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Home className="h-5 w-5" />
              Homepage Content
            </CardTitle>
            <CardDescription>Manage hero image and content displayed on the homepage</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Hero Image Section */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Hero Image
                </h4>
                <p className="text-sm text-muted-foreground mb-4">
                  This image appears in the hero section of the homepage. Recommended size: 1200x800px.
                </p>
                
                {/* Current/Preview Image */}
                {heroImagePreview && (
                  <div className="mb-4">
                    <p className="text-xs text-muted-foreground mb-2">Current Image:</p>
                    <img 
                      src={heroImagePreview} 
                      alt="Hero Preview" 
                      className="max-w-md rounded-lg shadow-md border"
                      data-testid="hero-image-preview"
                    />
                  </div>
                )}
                
                {/* Image URL Input */}
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="hero-image-url">Image URL</Label>
                    <Input
                      id="hero-image-url"
                      placeholder="https://example.com/image.jpg"
                      value={heroImageUrl}
                      onChange={(e) => {
                        setHeroImageUrl(e.target.value);
                        setHeroImagePreview(e.target.value);
                        setHeroImageFile(null);
                      }}
                      data-testid="hero-image-url-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Paste an image URL from Unsplash, Pexels, or your own hosting</p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">— or —</span>
                  </div>
                  
                  <div>
                    <Label htmlFor="hero-image-upload">Upload Image</Label>
                    <Input
                      id="hero-image-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleHeroImageSelect}
                      className="cursor-pointer"
                      data-testid="hero-image-upload-input"
                    />
                    {heroImageFile && (
                      <p className="text-xs text-green-600 mt-1">File selected: {heroImageFile.name}</p>
                    )}
                  </div>
                  
                  <Button 
                    onClick={handleSaveHeroImage} 
                    disabled={savingHeroImage || (!heroImageFile && !heroImageUrl)}
                    className="w-full"
                    data-testid="save-hero-image-btn"
                  >
                    {savingHeroImage ? 'Saving...' : 'Save Hero Image'}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* About Us Content Management */}
        <Card className="glass-card mb-8" data-testid="about-us-content-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Info className="h-5 w-5" />
              About Us Page Content
            </CardTitle>
            <CardDescription>Manage content displayed on the About Us page</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Button 
                onClick={() => navigate('/admin/cms')} 
                className="w-full"
                data-testid="edit-about-content-btn"
              >
                <Edit className="h-4 w-4 mr-2" />
                Open Full CMS Editor
              </Button>
              <p className="text-sm text-muted-foreground text-center">
                Use the CMS Editor to update mission, vision, leadership info, and contact details on the About Us page.
              </p>
              <div className="border rounded-lg p-4 bg-muted/30">
                <h4 className="font-semibold text-sm mb-2">Quick Guide:</h4>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• <strong>Page:</strong> about</li>
                  <li>• <strong>Sections:</strong> hero, mission, vision, values, leadership, contact</li>
                  <li>• <strong>Keys:</strong> title, text, image, leader1_name, leader1_role, etc.</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pastor's Corner Management */}
        <Card className="glass-card mb-8" data-testid="pastor-corner-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Pastor's Corner
                </CardTitle>
                <CardDescription>Manage spiritual messages and posts from the pastor</CardDescription>
              </div>
              <Button onClick={() => setShowAddPastorPost(true)} data-testid="add-pastor-post-btn">
                <Plus className="h-4 w-4 mr-2" /> New Post
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {pastorPosts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>No posts yet. Create your first Pastor's Corner post!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pastorPosts.map((post) => (
                  <Card key={post.post_id} className="bg-muted/30">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-semibold">{post.title}</h4>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{post.content}</p>
                          <p className="text-xs text-muted-foreground mt-2">
                            {new Date(post.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        {post.image && (
                          <img src={post.image.startsWith('/') ? BACKEND_URL + post.image : post.image} alt="" className="w-16 h-16 object-cover rounded ml-4" />
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500 hover:text-red-700 ml-2"
                          onClick={() => handleDeletePastorPost(post.post_id)}
                          data-testid={`delete-pastor-post-${post.post_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Pastor Post Dialog */}
        <Dialog open={showAddPastorPost} onOpenChange={setShowAddPastorPost}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Pastor's Corner Post</DialogTitle>
              <DialogDescription>Share a spiritual message or reflection with the congregation</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddPastorPost} className="space-y-4">
              <div>
                <Label htmlFor="pastor-post-title">Title</Label>
                <Input
                  id="pastor-post-title"
                  value={newPastorPost.title}
                  onChange={(e) => setNewPastorPost({...newPastorPost, title: e.target.value})}
                  placeholder="Enter post title..."
                  data-testid="pastor-post-title-input"
                />
              </div>
              <div>
                <Label htmlFor="pastor-post-content">Content</Label>
                <Textarea
                  id="pastor-post-content"
                  value={newPastorPost.content}
                  onChange={(e) => setNewPastorPost({...newPastorPost, content: e.target.value})}
                  placeholder="Write your message..."
                  rows={6}
                  data-testid="pastor-post-content-input"
                />
              </div>
              <div>
                <Label htmlFor="pastor-post-image">Image (Optional)</Label>
                <Input
                  id="pastor-post-image"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPastorPostImage(e.target.files[0])}
                  data-testid="pastor-post-image-input"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowAddPastorPost(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={savingPastorPost} data-testid="save-pastor-post-btn">
                  {savingPastorPost ? 'Saving...' : 'Publish Post'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Advanced Courses Management */}
        <Card className="glass-card mb-8" data-testid="advanced-courses-management-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Advanced Courses (Bible Study)
                </CardTitle>
                <CardDescription>Manage advanced study courses, pricing, and visibility</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={loadAdvancedCourses} data-testid="refresh-advanced-courses-btn">
                  <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                </Button>
                <Button variant="outline" onClick={() => setShowAIGenerate(true)} className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-purple-300 hover:border-purple-400" data-testid="ai-generate-course-btn">
                  <Lightbulb className="h-4 w-4 mr-2 text-purple-500" /> AI Generate
                </Button>
                <Button onClick={() => setShowCreateAdvancedCourse(true)} data-testid="create-advanced-course-btn">
                  <Plus className="h-4 w-4 mr-2" /> Create Course
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {advancedCoursesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : advancedCourses.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <BookOpen className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>No advanced courses found.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {advancedCourses.map((course) => (
                  <Card key={course.course_id} className="bg-muted/30">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex gap-4 flex-1">
                          {course.thumbnail_url && (
                            <img 
                              src={course.thumbnail_url?.startsWith('/') ? BACKEND_URL + course.thumbnail_url : course.thumbnail_url} 
                              alt={course.title} 
                              className="w-20 h-20 object-cover rounded-lg"
                            />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold">{course.title}</h4>
                              <Badge className={course.status === 'active' ? 'bg-green-500' : course.status === 'draft' ? 'bg-yellow-500' : 'bg-gray-500'}>
                                {course.status || 'active'}
                              </Badge>
                              {course.ai_generated && (
                                <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">
                                  <Lightbulb className="h-3 w-3 mr-1" /> AI Generated
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{course.description}</p>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <BookOpen className="h-3 w-3" />
                                {course.lesson_count || course.lessons?.length || 0} lessons
                              </span>
                              <span className="flex items-center gap-1">
                                <DollarSign className="h-3 w-3" />
                                ${course.price?.toFixed(2) || '0.00'}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {course.enrollment_count || 0} enrolled
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {course.category || 'Uncategorized'}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          {course.status === 'draft' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-green-600 border-green-300 hover:bg-green-50"
                              onClick={() => handlePublishAdvancedCourse(course.course_id)}
                              data-testid={`publish-course-${course.course_id}`}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" /> Publish
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-blue-600 border-blue-300 hover:bg-blue-50"
                            onClick={() => handleOpenAdminEnroll(course)}
                            data-testid={`enroll-users-${course.course_id}`}
                          >
                            <Users className="h-4 w-4 mr-1" /> Enroll
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleEditAdvancedCourse(course)}
                            data-testid={`edit-course-${course.course_id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-100"
                            onClick={() => handleDeleteAdvancedCourse(course.course_id)}
                            data-testid={`delete-course-${course.course_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Advanced Course Dialog */}
        <Dialog open={showEditAdvancedCourse} onOpenChange={setShowEditAdvancedCourse}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Course</DialogTitle>
              <DialogDescription>Update course details and pricing</DialogDescription>
            </DialogHeader>
            {editingAdvancedCourse && (
              <form onSubmit={handleSaveAdvancedCourse} className="space-y-4">
                <div>
                  <Label htmlFor="course-title">Course Title</Label>
                  <Input
                    id="course-title"
                    value={editingAdvancedCourse.title || ''}
                    onChange={(e) => setEditingAdvancedCourse({...editingAdvancedCourse, title: e.target.value})}
                    placeholder="Course title..."
                    data-testid="course-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="course-description">Description</Label>
                  <Textarea
                    id="course-description"
                    value={editingAdvancedCourse.description || ''}
                    onChange={(e) => setEditingAdvancedCourse({...editingAdvancedCourse, description: e.target.value})}
                    placeholder="Course description..."
                    rows={3}
                    data-testid="course-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="course-price">Price ($)</Label>
                    <Input
                      id="course-price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingAdvancedCourse.price || ''}
                      onChange={(e) => setEditingAdvancedCourse({...editingAdvancedCourse, price: e.target.value})}
                      placeholder="49.99"
                      data-testid="course-price-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Set to 0 for free courses</p>
                  </div>
                  <div>
                    <Label htmlFor="course-status">Status</Label>
                    <Select 
                      value={editingAdvancedCourse.status || 'active'} 
                      onValueChange={(v) => setEditingAdvancedCourse({...editingAdvancedCourse, status: v})}
                    >
                      <SelectTrigger data-testid="course-status-select">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active (Visible)</SelectItem>
                        <SelectItem value="draft">Draft (Hidden)</SelectItem>
                        <SelectItem value="archived">Archived</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="course-category">Category</Label>
                  <Input
                    id="course-category"
                    value={editingAdvancedCourse.category || ''}
                    onChange={(e) => setEditingAdvancedCourse({...editingAdvancedCourse, category: e.target.value})}
                    placeholder="Bible Study, Church History..."
                    data-testid="course-category-input"
                  />
                </div>
                <div>
                  <Label htmlFor="course-thumbnail">Thumbnail URL</Label>
                  <Input
                    id="course-thumbnail"
                    value={editingAdvancedCourse.thumbnail_url || ''}
                    onChange={(e) => setEditingAdvancedCourse({...editingAdvancedCourse, thumbnail_url: e.target.value})}
                    placeholder="https://example.com/image.jpg"
                    data-testid="course-thumbnail-input"
                  />
                </div>
                <div className="flex gap-2 justify-end pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowEditAdvancedCourse(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={savingAdvancedCourse} data-testid="save-course-btn">
                    {savingAdvancedCourse ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Advanced Course Dialog */}
        <Dialog open={showCreateAdvancedCourse} onOpenChange={setShowCreateAdvancedCourse}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Course</DialogTitle>
              <DialogDescription>Build a new advanced Bible study course with lessons</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Course Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="new-course-title">Course Title *</Label>
                  <Input
                    id="new-course-title"
                    value={newCourseData.title}
                    onChange={(e) => setNewCourseData({...newCourseData, title: e.target.value})}
                    placeholder="e.g., Understanding the Prophets"
                    data-testid="new-course-title-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="new-course-desc">Description *</Label>
                  <Textarea
                    id="new-course-desc"
                    value={newCourseData.description}
                    onChange={(e) => setNewCourseData({...newCourseData, description: e.target.value})}
                    placeholder="Describe what students will learn..."
                    rows={3}
                    data-testid="new-course-desc-input"
                  />
                </div>
                <div>
                  <Label htmlFor="new-course-category">Category</Label>
                  <Select 
                    value={newCourseData.category} 
                    onValueChange={(v) => setNewCourseData({...newCourseData, category: v})}
                  >
                    <SelectTrigger data-testid="new-course-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bible Study">Bible Study</SelectItem>
                      <SelectItem value="Church History">Church History</SelectItem>
                      <SelectItem value="Theology">Theology</SelectItem>
                      <SelectItem value="Discipleship">Discipleship</SelectItem>
                      <SelectItem value="Leadership">Leadership</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="new-course-price">Price ($)</Label>
                  <Input
                    id="new-course-price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={newCourseData.price}
                    onChange={(e) => setNewCourseData({...newCourseData, price: e.target.value})}
                    placeholder="0.00"
                    data-testid="new-course-price-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Set to 0 for free courses</p>
                </div>
                <div className="col-span-2">
                  <Label htmlFor="new-course-thumb">Thumbnail URL (optional)</Label>
                  <Input
                    id="new-course-thumb"
                    value={newCourseData.thumbnail_url}
                    onChange={(e) => setNewCourseData({...newCourseData, thumbnail_url: e.target.value})}
                    placeholder="https://example.com/course-image.jpg"
                    data-testid="new-course-thumb-input"
                  />
                </div>
              </div>

              {/* Lessons Section */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-semibold">Lessons ({newCourseData.lessons.length})</h4>
                    <p className="text-sm text-muted-foreground">Add lessons to your course</p>
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowAddLesson(true)}
                    data-testid="add-lesson-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add Lesson
                  </Button>
                </div>
                
                {newCourseData.lessons.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground bg-muted/20 rounded-lg">
                    <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    <p>No lessons yet. Click "Add Lesson" to get started.</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {newCourseData.lessons.map((lesson, idx) => (
                      <div key={lesson.lesson_id} className="flex items-center justify-between bg-muted/30 rounded-lg p-3">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-sm font-medium">
                            {lesson.lesson_number}
                          </span>
                          <div>
                            <p className="font-medium text-sm">{lesson.title}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>{lesson.slides?.length || 0} slides</span>
                              <span>•</span>
                              <span>{lesson.duration_minutes} min</span>
                              {lesson.is_free_preview && (
                                <>
                                  <span>•</span>
                                  <Badge variant="secondary" className="text-xs">Free Preview</Badge>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button 
                          type="button"
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleRemoveLessonFromNewCourse(idx)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowCreateAdvancedCourse(false);
                    setNewCourseData({
                      title: '',
                      description: '',
                      category: 'Bible Study',
                      price: '0',
                      thumbnail_url: '',
                      lessons: []
                    });
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateAdvancedCourse} 
                  disabled={creatingCourse || !newCourseData.title || newCourseData.lessons.length === 0}
                  data-testid="create-course-submit-btn"
                >
                  {creatingCourse ? 'Creating...' : 'Create Course'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Lesson Dialog */}
        <Dialog open={showAddLesson} onOpenChange={setShowAddLesson}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Lesson</DialogTitle>
              <DialogDescription>Create a lesson with content that will be converted to slides</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="lesson-title">Lesson Title *</Label>
                <Input
                  id="lesson-title"
                  value={newLessonData.title}
                  onChange={(e) => setNewLessonData({...newLessonData, title: e.target.value})}
                  placeholder="e.g., Introduction to Isaiah"
                  data-testid="lesson-title-input"
                />
              </div>
              <div>
                <Label htmlFor="lesson-desc">Short Description</Label>
                <Input
                  id="lesson-desc"
                  value={newLessonData.description}
                  onChange={(e) => setNewLessonData({...newLessonData, description: e.target.value})}
                  placeholder="Brief overview of the lesson"
                  data-testid="lesson-desc-input"
                />
              </div>
              <div>
                <Label htmlFor="lesson-content">Lesson Content *</Label>
                <Textarea
                  id="lesson-content"
                  value={newLessonData.content}
                  onChange={(e) => setNewLessonData({...newLessonData, content: e.target.value})}
                  placeholder="Enter the full lesson content here. Each paragraph (separated by blank lines) will become a slide. Include scripture references, key points, and explanations."
                  rows={8}
                  data-testid="lesson-content-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Tip: Separate paragraphs with blank lines to create multiple slides
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lesson-duration">Duration (minutes)</Label>
                  <Input
                    id="lesson-duration"
                    type="number"
                    min="1"
                    value={newLessonData.duration_minutes}
                    onChange={(e) => setNewLessonData({...newLessonData, duration_minutes: e.target.value})}
                    data-testid="lesson-duration-input"
                  />
                </div>
                <div className="flex items-end pb-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="lesson-free-preview"
                      checked={newLessonData.is_free_preview}
                      onChange={(e) => setNewLessonData({...newLessonData, is_free_preview: e.target.checked})}
                      className="w-4 h-4 accent-primary"
                      data-testid="lesson-free-preview-checkbox"
                    />
                    <Label htmlFor="lesson-free-preview" className="cursor-pointer">Free preview lesson</Label>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddLesson(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleAddLessonToNewCourse}
                  disabled={!newLessonData.title || !newLessonData.content}
                  data-testid="add-lesson-submit-btn"
                >
                  Add Lesson
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* AI Generate Course Dialog */}
        <Dialog open={showAIGenerate} onOpenChange={setShowAIGenerate}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-purple-500" />
                AI Course Generator
              </DialogTitle>
              <DialogDescription>
                Let AI create a complete Bible study course with lessons, slides, and quiz questions
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="ai-topic">Course Topic *</Label>
                <Input
                  id="ai-topic"
                  value={aiGenerateForm.topic}
                  onChange={(e) => setAiGenerateForm({...aiGenerateForm, topic: e.target.value})}
                  placeholder="e.g., The Parables of Jesus, Book of Romans, Spiritual Gifts"
                  data-testid="ai-topic-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Be specific for better results</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ai-lessons">Number of Lessons</Label>
                  <Select 
                    value={aiGenerateForm.num_lessons.toString()} 
                    onValueChange={(v) => setAiGenerateForm({...aiGenerateForm, num_lessons: parseInt(v)})}
                  >
                    <SelectTrigger data-testid="ai-lessons-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 lessons</SelectItem>
                      <SelectItem value="5">5 lessons</SelectItem>
                      <SelectItem value="7">7 lessons</SelectItem>
                      <SelectItem value="10">10 lessons</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="ai-category">Category</Label>
                  <Select 
                    value={aiGenerateForm.category} 
                    onValueChange={(v) => setAiGenerateForm({...aiGenerateForm, category: v})}
                  >
                    <SelectTrigger data-testid="ai-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bible Study">Bible Study</SelectItem>
                      <SelectItem value="Church History">Church History</SelectItem>
                      <SelectItem value="Theology">Theology</SelectItem>
                      <SelectItem value="Discipleship">Discipleship</SelectItem>
                      <SelectItem value="Leadership">Leadership</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="ai-difficulty">Difficulty Level</Label>
                <Select 
                  value={aiGenerateForm.difficulty} 
                  onValueChange={(v) => setAiGenerateForm({...aiGenerateForm, difficulty: v})}
                >
                  <SelectTrigger data-testid="ai-difficulty-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner - New believers, foundational concepts</SelectItem>
                    <SelectItem value="intermediate">Intermediate - Growing Christians, deeper study</SelectItem>
                    <SelectItem value="advanced">Advanced - Mature believers, theological depth</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2 bg-muted/30 p-3 rounded-lg">
                <input
                  type="checkbox"
                  id="ai-quiz"
                  checked={aiGenerateForm.include_quiz}
                  onChange={(e) => setAiGenerateForm({...aiGenerateForm, include_quiz: e.target.checked})}
                  className="w-4 h-4 accent-primary"
                  data-testid="ai-quiz-checkbox"
                />
                <Label htmlFor="ai-quiz" className="cursor-pointer">
                  Generate quiz questions for assessment
                </Label>
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg text-sm">
                <p className="font-medium text-purple-800 dark:text-purple-300 mb-1">What AI will generate:</p>
                <ul className="text-purple-700 dark:text-purple-400 space-y-1">
                  <li>• Course title and description</li>
                  <li>• {aiGenerateForm.num_lessons} lessons with 3-5 slides each</li>
                  <li>• Scripture references and key points</li>
                  {aiGenerateForm.include_quiz && <li>• Quiz questions for each lesson</li>}
                  <li>• TTS narration ready (uses ElevenLabs)</li>
                </ul>
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowAIGenerate(false)}
                  disabled={aiGenerating}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleAIGenerateCourse}
                  disabled={aiGenerating || !aiGenerateForm.topic.trim()}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                  data-testid="ai-generate-submit-btn"
                >
                  {aiGenerating ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Generating...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Lightbulb className="h-4 w-4" />
                      Generate Course
                    </div>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Admin Enroll Users Dialog */}
        <Dialog open={showAdminEnroll} onOpenChange={setShowAdminEnroll}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="admin-enroll-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-primary" />
                Enroll Users in Course
              </DialogTitle>
              <DialogDescription>
                {enrollingCourse && `Manually enroll users in "${enrollingCourse.title}" without requiring payment`}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Search Users */}
              <div className="space-y-2">
                <Label>Search Users by Email or Name</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter email or name..."
                    value={enrollUserSearch}
                    onChange={(e) => setEnrollUserSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearchUsersForEnroll()}
                    className="flex-1"
                    data-testid="enroll-user-search-input"
                  />
                  <Button onClick={handleSearchUsersForEnroll} data-testid="enroll-search-btn">
                    <Search className="h-4 w-4 mr-1" /> Search
                  </Button>
                </div>
              </div>

              {/* Search Results */}
              {enrollUserResults.length > 0 && (
                <div className="space-y-2">
                  <Label>Search Results</Label>
                  <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                    {enrollUserResults.map(user => (
                      <div key={user.user_id} className="flex items-center justify-between p-3 hover:bg-muted/50">
                        <div>
                          <p className="font-medium">{user.name || 'No Name'}</p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleAdminEnrollUser(user.user_id, user.name || user.email)}
                          disabled={enrollingUser}
                          data-testid={`enroll-user-${user.user_id}`}
                        >
                          {enrollingUser ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4 mr-1" />}
                          Enroll
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Current Enrollments */}
              <div className="space-y-2 pt-4 border-t">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Current Enrollments ({courseEnrollments.length})
                </Label>
                {loadingEnrollments ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : courseEnrollments.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No users enrolled yet</p>
                ) : (
                  <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                    {courseEnrollments.map(enrollment => (
                      <div key={enrollment.enrollment_id} className="flex items-center justify-between p-3">
                        <div>
                          <p className="font-medium">{enrollment.user?.name || enrollment.user?.email || 'Unknown User'}</p>
                          <p className="text-xs text-muted-foreground">
                            {enrollment.enrollment_type === 'admin_grant' ? (
                              <span className="text-blue-600">Admin Granted</span>
                            ) : (
                              <span className="text-green-600">Paid</span>
                            )}
                            {enrollment.enrolled_at && ` • ${new Date(enrollment.enrolled_at).toLocaleDateString()}`}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleRemoveEnrollment(enrollment.enrollment_id)}
                          data-testid={`remove-enrollment-${enrollment.enrollment_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdminEnroll(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
          </>
        )}

        {/* MENTORSHIP MANAGEMENT SECTIONS - Goals, Tasks, Plans */}
        {activeAdminTab === 'mentorships' && (
          <>
        {/* Goals Management Section */}
        <Card className="glass-card mb-8" data-testid="goals-management-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Goals Management
                </CardTitle>
                <CardDescription>View, edit, and delete mentorship goals</CardDescription>
              </div>
              <div className="flex gap-2">
                <Select value={goalsFilter} onValueChange={(v) => { setGoalsFilter(v); loadAdminGoals(v); }}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Goals</SelectItem>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => loadAdminGoals(goalsFilter)} data-testid="refresh-goals-btn">
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {adminGoals.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Target className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>No goals found. Click Refresh to load goals.</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {adminGoals.map((goal) => (
                  <Card key={goal.goal_id} className="bg-muted/30">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold">{goal.title}</h4>
                            <Badge className={
                              goal.status === 'completed' ? 'bg-green-500' : 
                              goal.status === 'in_progress' ? 'bg-blue-500' : 
                              'bg-gray-500'
                            }>
                              {goal.status?.replace('_', ' ') || 'pending'}
                            </Badge>
                          </div>
                          {goal.description && <p className="text-sm text-muted-foreground">{goal.description}</p>}
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span>Mentee: {goal.mentee_name || 'N/A'}</span>
                            <span>Progress: {goal.progress || 0}%</span>
                            {goal.target_date && <span>Due: {new Date(goal.target_date).toLocaleDateString()}</span>}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setEditingGoal(goal)}
                            data-testid={`edit-goal-${goal.goal_id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteGoal(goal.goal_id)}
                            data-testid={`delete-goal-${goal.goal_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Goal Dialog */}
        <Dialog open={!!editingGoal} onOpenChange={(open) => !open && setEditingGoal(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Goal</DialogTitle>
            </DialogHeader>
            {editingGoal && (
              <div className="space-y-4">
                <div>
                  <Label>Title</Label>
                  <Input 
                    value={editingGoal.title || ''} 
                    onChange={(e) => setEditingGoal({...editingGoal, title: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea 
                    value={editingGoal.description || ''} 
                    onChange={(e) => setEditingGoal({...editingGoal, description: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Status</Label>
                    <Select value={editingGoal.status || 'not_started'} onValueChange={(v) => setEditingGoal({...editingGoal, status: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not_started">Not Started</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Progress (%)</Label>
                    <Input 
                      type="number" 
                      min="0" 
                      max="100"
                      value={editingGoal.progress || 0} 
                      onChange={(e) => setEditingGoal({...editingGoal, progress: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setEditingGoal(null)}>Cancel</Button>
                  <Button onClick={() => handleUpdateGoal(editingGoal.goal_id, editingGoal)}>Save Changes</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Tasks Management Section */}
        <Card className="glass-card mb-8" data-testid="tasks-management-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  Tasks Management
                </CardTitle>
                <CardDescription>View, edit, and delete user tasks</CardDescription>
              </div>
              <div className="flex gap-2">
                <Select value={tasksFilter} onValueChange={(v) => { setTasksFilter(v); loadAdminTasks(v); }}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tasks</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => loadAdminTasks(tasksFilter)} data-testid="refresh-tasks-btn">
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {adminTasks.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>No tasks found. Click Refresh to load tasks.</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {adminTasks.map((task) => (
                  <Card key={task.task_id} className="bg-muted/30">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold">{task.title}</h4>
                            <Badge className={
                              task.status === 'completed' ? 'bg-green-500' : 
                              task.status === 'in_progress' ? 'bg-blue-500' : 
                              task.priority === 'high' ? 'bg-red-500' :
                              'bg-gray-500'
                            }>
                              {task.status || 'pending'}
                            </Badge>
                            {task.priority && (
                              <Badge variant="outline" className={
                                task.priority === 'high' ? 'border-red-500 text-red-500' :
                                task.priority === 'medium' ? 'border-yellow-500 text-yellow-500' :
                                'border-gray-500 text-gray-500'
                              }>
                                {task.priority}
                              </Badge>
                            )}
                          </div>
                          {task.description && <p className="text-sm text-muted-foreground line-clamp-2">{task.description}</p>}
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span>Assigned to: {task.user_name || 'N/A'}</span>
                            <span>Created by: {task.creator_name || 'System'}</span>
                            {task.due_date && <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setEditingTask(task)}
                            data-testid={`edit-task-${task.task_id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteTask(task.task_id)}
                            data-testid={`delete-task-${task.task_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Task Dialog */}
        <Dialog open={!!editingTask} onOpenChange={(open) => !open && setEditingTask(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Task</DialogTitle>
            </DialogHeader>
            {editingTask && (
              <div className="space-y-4">
                <div>
                  <Label>Title</Label>
                  <Input 
                    value={editingTask.title || ''} 
                    onChange={(e) => setEditingTask({...editingTask, title: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea 
                    value={editingTask.description || ''} 
                    onChange={(e) => setEditingTask({...editingTask, description: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Status</Label>
                    <Select value={editingTask.status || 'pending'} onValueChange={(v) => setEditingTask({...editingTask, status: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select value={editingTask.priority || 'medium'} onValueChange={(v) => setEditingTask({...editingTask, priority: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setEditingTask(null)}>Cancel</Button>
                  <Button onClick={() => handleUpdateTask(editingTask.task_id, editingTask)}>Save Changes</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Drill-Down Data Modal */}
        <Dialog open={showDrillDown} onOpenChange={setShowDrillDown}>
          <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                {drillDownData.title}
              </DialogTitle>
              <DialogDescription>
                Showing {drillDownData.data?.length || 0} items
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto max-h-[60vh]">
              {drillDownData.type === 'users' && (
                <div className="space-y-2">
                  {drillDownData.data?.map((user, idx) => (
                    <div key={user.user_id || idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={user.picture} />
                          <AvatarFallback>{user.name?.[0] || 'U'}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">{user.name}</div>
                          <div className="text-sm text-muted-foreground">{user.email}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={user.role === 'admin' ? 'bg-purple-500' : user.role === 'mentor' ? 'bg-blue-500' : 'bg-green-500'}>
                          {user.role}
                        </Badge>
                        <div className="text-xs text-muted-foreground mt-1">
                          Joined {new Date(user.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {drillDownData.type === 'goals' && (
                <div className="space-y-2">
                  {drillDownData.data?.map((goal, idx) => (
                    <div key={goal.goal_id || idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50">
                      <div className="flex-1">
                        <div className="font-medium">{goal.title}</div>
                        <div className="text-sm text-muted-foreground">{goal.mentee_name || 'Unknown mentee'}</div>
                        {goal.target_date && (
                          <div className="text-xs text-muted-foreground">
                            Due: {new Date(goal.target_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-lg">{goal.progress || 0}%</div>
                        <Badge className={
                          goal.status === 'completed' ? 'bg-green-500' : 
                          goal.status === 'in_progress' ? 'bg-blue-500' : 
                          'bg-gray-500'
                        }>
                          {goal.status?.replace('_', ' ') || 'pending'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {(!drillDownData.data || drillDownData.data.length === 0) && (
                    <p className="text-center text-muted-foreground py-8">No goals found</p>
                  )}
                </div>
              )}
              
              {drillDownData.type === 'mentorships' && (
                <div className="space-y-2">
                  {drillDownData.data?.map((m, idx) => (
                    <div key={m.mentorship_id || idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50">
                      <div className="flex-1">
                        <div className="font-medium">{m.mentor_name || 'Mentor'} → {m.mentee_name || 'Mentee'}</div>
                        <div className="text-sm text-muted-foreground">
                          Started: {m.created_at ? new Date(m.created_at).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                      <Badge className={m.status === 'active' ? 'bg-green-500' : 'bg-gray-500'}>
                        {m.status}
                      </Badge>
                    </div>
                  ))}
                  {(!drillDownData.data || drillDownData.data.length === 0) && (
                    <p className="text-center text-muted-foreground py-8">No mentorships found</p>
                  )}
                </div>
              )}
              
              {drillDownData.type === 'transactions' && (
                <div className="space-y-2">
                  {drillDownData.data?.map((t, idx) => (
                    <div key={t.transaction_id || idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50">
                      <div className="flex-1">
                        <div className="font-medium">{t.description || 'Payment'}</div>
                        <div className="text-sm text-muted-foreground">{t.user_email || t.user_name || 'Unknown'}</div>
                        <div className="text-xs text-muted-foreground">
                          {t.created_at ? new Date(t.created_at).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-lg text-green-600">${(t.amount / 100).toFixed(2)}</div>
                        <Badge className={t.status === 'succeeded' ? 'bg-green-500' : 'bg-yellow-500'}>
                          {t.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {(!drillDownData.data || drillDownData.data.length === 0) && (
                    <p className="text-center text-muted-foreground py-8">No transactions found</p>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Mentorship Plans Management */}
        <Card className="glass-card mb-8" data-testid="mentorship-plans-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Mentorship Plans
                </CardTitle>
                <CardDescription>Configure subscription plans for mentorship program</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(mentorshipPlansAdmin).map(([key, plan]) => (
                <Card key={key} className={`bg-secondary/10 relative ${key === 'elite' ? 'ring-2 ring-primary' : ''} ${key === 'lifetime' ? 'ring-2 ring-amber-500' : ''}`}>
                  {key === 'elite' && <Badge className="absolute -top-2 -right-2 bg-primary">Popular</Badge>}
                  {key === 'lifetime' && <Badge className="absolute -top-2 -right-2 bg-amber-500">Best Value</Badge>}
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">{plan.name}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div>
                      {plan.original_price && (
                        <span className="text-sm text-muted-foreground line-through mr-2">${plan.original_price}</span>
                      )}
                      <span className="text-2xl font-bold">${plan.price}</span>
                      {plan.interval && <span className="text-xs text-muted-foreground">/{plan.interval}</span>}
                      {!plan.interval && <span className="text-xs text-muted-foreground block">one-time</span>}
                    </div>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {(plan.features || []).slice(0, 3).map((f, i) => (
                        <li key={i} className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3 text-green-500" />
                          {f}
                        </li>
                      ))}
                      {(plan.features || []).length > 3 && (
                        <li className="text-muted-foreground">+{plan.features.length - 3} more</li>
                      )}
                    </ul>
                    <Button size="sm" variant="outline" className="w-full mt-2" onClick={() => handleEditPlan(key)} data-testid={`edit-plan-${key}`}>
                      <Edit className="h-3 w-3 mr-1" /> Edit
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Edit Plan Dialog */}
        <Dialog open={showEditPricing} onOpenChange={setShowEditPricing}>
          <DialogContent className="max-w-lg" data-testid="edit-plan-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Edit {planFormData.name} Plan</DialogTitle>
              <DialogDescription>Update pricing and features for this plan</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="plan-name">Plan Name</Label>
                  <Input
                    id="plan-name"
                    value={planFormData.name}
                    onChange={(e) => setPlanFormData({ ...planFormData, name: e.target.value })}
                    data-testid="plan-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="plan-price">Price ($)</Label>
                  <Input
                    id="plan-price"
                    type="number"
                    step="0.01"
                    value={planFormData.price}
                    onChange={(e) => setPlanFormData({ ...planFormData, price: parseFloat(e.target.value) || 0 })}
                    data-testid="plan-price-input"
                  />
                </div>
              </div>
              {editingPlan === 'lifetime' && (
                <div>
                  <Label htmlFor="original-price">Original Price (for strikethrough)</Label>
                  <Input
                    id="original-price"
                    type="number"
                    step="0.01"
                    value={planFormData.original_price || ''}
                    onChange={(e) => setPlanFormData({ ...planFormData, original_price: parseFloat(e.target.value) || null })}
                    placeholder="e.g., 500"
                  />
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Features</Label>
                  <Button type="button" size="sm" variant="outline" onClick={handleAddFeature}>
                    <Plus className="h-3 w-3 mr-1" /> Add Feature
                  </Button>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {planFormData.features.map((feature, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={feature}
                        onChange={(e) => handleUpdateFeature(index, e.target.value)}
                        placeholder="Feature description"
                      />
                      <Button type="button" size="icon" variant="ghost" className="text-red-500" onClick={() => handleRemoveFeature(index)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setShowEditPricing(false); setEditingPlan(null); }}>
                  Cancel
                </Button>
                <Button onClick={handleSavePlan} disabled={savingPricing} data-testid="save-plan-btn">
                  {savingPricing ? 'Saving...' : 'Save Plan'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
          </>
        )}

        {/* Sermon Content Management - part of CONTENT tab */}
        {activeAdminTab === 'content' && (
          <>
        <Card className="glass-card mb-8" data-testid="sermon-management-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Video className="h-5 w-5" />
                  Sermon Management
                </CardTitle>
                <CardDescription>Add and manage church sermons with video/audio uploads</CardDescription>
              </div>
              <Button onClick={() => setShowAddSermon(true)} data-testid="add-sermon-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Sermon
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {sermons.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No sermons yet. Click "Add Sermon" to add your first sermon.</p>
            ) : (
              <div className="space-y-3">
                {sermons.slice(0, 10).map((sermon) => (
                  <div key={sermon.sermon_id} className="p-4 rounded-lg bg-secondary/20 hover:bg-secondary/30 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                          <Video className="h-6 w-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold">{sermon.title}</div>
                          <div className="text-sm text-muted-foreground">
                            {sermon.preacher} • {new Date(sermon.date).toLocaleDateString()}
                            {sermon.series && ` • ${sermon.series}`}
                          </div>
                          {sermon.scripture_reference && (
                            <div className="text-xs text-muted-foreground">{sermon.scripture_reference}</div>
                          )}
                          <div className="flex gap-1 mt-1">
                            {sermon.video_file && <Badge className="bg-blue-600 text-xs">Video</Badge>}
                            {sermon.audio_file && <Badge className="bg-green-600 text-xs">Audio</Badge>}
                            {sermon.youtube_url && <Badge className="bg-red-600 text-xs">YouTube</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {sermon.youtube_url && (
                          <a href={sermon.youtube_url} target="_blank" rel="noreferrer">
                            <Button variant="outline" size="sm">
                              <LinkIcon className="h-4 w-4 mr-1" /> Watch
                            </Button>
                          </a>
                        )}
                        {sermon.video_file && (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}${sermon.video_file}`} target="_blank" rel="noreferrer">
                            <Button variant="outline" size="sm">
                              <Video className="h-4 w-4 mr-1" /> Play
                            </Button>
                          </a>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEditSermon(sermon)}
                          data-testid={`edit-sermon-${sermon.sermon_id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDeleteSermon(sermon.sermon_id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {/* Upload Section */}
                    <div className="mt-3 pt-3 border-t border-secondary/30 flex flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`video-${sermon.sermon_id}`} className="cursor-pointer">
                          <Button variant="outline" size="sm" asChild disabled={uploadingSermonMedia === sermon.sermon_id}>
                            <span>
                              <Upload className="h-3 w-3 mr-1" />
                              {uploadingSermonMedia === sermon.sermon_id ? 'Uploading...' : 'Upload Video'}
                            </span>
                          </Button>
                        </Label>
                        <input
                          type="file"
                          id={`video-${sermon.sermon_id}`}
                          className="hidden"
                          accept="video/*"
                          onChange={(e) => handleSermonFileUpload(sermon.sermon_id, e.target.files[0], 'video')}
                          data-testid={`upload-video-${sermon.sermon_id}`}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`audio-${sermon.sermon_id}`} className="cursor-pointer">
                          <Button variant="outline" size="sm" asChild disabled={uploadingSermonMedia === sermon.sermon_id}>
                            <span>
                              <Music className="h-3 w-3 mr-1" />
                              {uploadingSermonMedia === sermon.sermon_id ? 'Uploading...' : 'Upload Audio'}
                            </span>
                          </Button>
                        </Label>
                        <input
                          type="file"
                          id={`audio-${sermon.sermon_id}`}
                          className="hidden"
                          accept="audio/*"
                          onChange={(e) => handleSermonFileUpload(sermon.sermon_id, e.target.files[0], 'audio')}
                          data-testid={`upload-audio-${sermon.sermon_id}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Sermon Dialog */}
        <Dialog open={showAddSermon} onOpenChange={setShowAddSermon}>
          <DialogContent className="max-w-lg" data-testid="add-sermon-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Add New Sermon</DialogTitle>
              <DialogDescription>Upload a new sermon for your congregation</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddSermon} className="space-y-4">
              <div>
                <Label htmlFor="sermon-title">Title *</Label>
                <Input
                  id="sermon-title"
                  value={newSermonData.title}
                  onChange={(e) => setNewSermonData({ ...newSermonData, title: e.target.value })}
                  placeholder="Sermon title"
                  required
                  data-testid="sermon-title-input"
                />
              </div>
              <div>
                <Label htmlFor="sermon-preacher">Preacher *</Label>
                <Input
                  id="sermon-preacher"
                  value={newSermonData.preacher}
                  onChange={(e) => setNewSermonData({ ...newSermonData, preacher: e.target.value })}
                  placeholder="Pastor name"
                  required
                  data-testid="sermon-preacher-input"
                />
              </div>
              <div>
                <Label htmlFor="sermon-description">Description</Label>
                <Textarea
                  id="sermon-description"
                  value={newSermonData.description}
                  onChange={(e) => setNewSermonData({ ...newSermonData, description: e.target.value })}
                  placeholder="Brief summary of the sermon..."
                  rows={2}
                  data-testid="sermon-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="sermon-youtube">YouTube URL</Label>
                  <Input
                    id="sermon-youtube"
                    value={newSermonData.youtube_url}
                    onChange={(e) => setNewSermonData({ ...newSermonData, youtube_url: e.target.value })}
                    placeholder="https://youtube.com/..."
                    data-testid="sermon-youtube-input"
                  />
                </div>
                <div>
                  <Label htmlFor="sermon-scripture">Scripture Reference</Label>
                  <Input
                    id="sermon-scripture"
                    value={newSermonData.scripture_reference}
                    onChange={(e) => setNewSermonData({ ...newSermonData, scripture_reference: e.target.value })}
                    placeholder="John 3:16"
                    data-testid="sermon-scripture-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="sermon-series">Series (optional)</Label>
                <Input
                  id="sermon-series"
                  value={newSermonData.series}
                  onChange={(e) => setNewSermonData({ ...newSermonData, series: e.target.value })}
                  placeholder="e.g., Faith Foundations"
                  data-testid="sermon-series-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddSermon(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-sermon-btn">
                  Add Sermon
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Sermon Dialog */}
        <Dialog open={showEditSermon} onOpenChange={setShowEditSermon}>
          <DialogContent className="max-w-lg" data-testid="edit-sermon-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Edit Sermon</DialogTitle>
              <DialogDescription>Update sermon details</DialogDescription>
            </DialogHeader>
            {editSermonData && (
              <form onSubmit={handleUpdateSermon} className="space-y-4">
                <div>
                  <Label htmlFor="edit-sermon-title">Title *</Label>
                  <Input
                    id="edit-sermon-title"
                    value={editSermonData.title}
                    onChange={(e) => setEditSermonData({ ...editSermonData, title: e.target.value })}
                    placeholder="Sermon title"
                    required
                    data-testid="edit-sermon-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-sermon-preacher">Preacher *</Label>
                  <Input
                    id="edit-sermon-preacher"
                    value={editSermonData.preacher}
                    onChange={(e) => setEditSermonData({ ...editSermonData, preacher: e.target.value })}
                    placeholder="Pastor name"
                    required
                    data-testid="edit-sermon-preacher-input"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-sermon-description">Description</Label>
                  <Textarea
                    id="edit-sermon-description"
                    value={editSermonData.description}
                    onChange={(e) => setEditSermonData({ ...editSermonData, description: e.target.value })}
                    placeholder="Brief summary of the sermon..."
                    rows={2}
                    data-testid="edit-sermon-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-sermon-youtube">YouTube URL</Label>
                    <Input
                      id="edit-sermon-youtube"
                      value={editSermonData.youtube_url}
                      onChange={(e) => setEditSermonData({ ...editSermonData, youtube_url: e.target.value })}
                      placeholder="https://youtube.com/..."
                      data-testid="edit-sermon-youtube-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-sermon-scripture">Scripture Reference</Label>
                    <Input
                      id="edit-sermon-scripture"
                      value={editSermonData.scripture_reference}
                      onChange={(e) => setEditSermonData({ ...editSermonData, scripture_reference: e.target.value })}
                      placeholder="John 3:16"
                      data-testid="edit-sermon-scripture-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="edit-sermon-series">Series (optional)</Label>
                  <Input
                    id="edit-sermon-series"
                    value={editSermonData.series}
                    onChange={(e) => setEditSermonData({ ...editSermonData, series: e.target.value })}
                    placeholder="e.g., Faith Foundations"
                    data-testid="edit-sermon-series-input"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowEditSermon(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="update-sermon-btn">
                    Update Sermon
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
          </>
        )}

        {/* STORE TAB */}
        {activeAdminTab === 'store' && (
          <>
        {/* Worship Songs Management */}
        <Card className="glass-card mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Music className="h-5 w-5" />
                  Worship Songs
                </CardTitle>
                <CardDescription>Manage worship music for your congregation</CardDescription>
              </div>
              <Button onClick={() => setShowAddSong(true)} data-testid="add-song-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Song
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {worshipSongs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Music className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No worship songs uploaded yet</p>
                <Button variant="outline" className="mt-4" onClick={() => setShowAddSong(true)}>
                  Upload Your First Song
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {worshipSongs.map((song) => (
                  <div key={song.song_id} className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded bg-primary/10 flex items-center justify-center">
                        {song.cover_image ? (
                          <img src={`${BACKEND_URL}${song.cover_image}`} alt="" className="w-full h-full object-cover rounded" />
                        ) : (
                          <Music className="h-6 w-6 text-primary" />
                        )}
                      </div>
                      <div>
                        <div className="font-semibold">{song.title}</div>
                        <div className="text-sm text-muted-foreground">{song.artist}</div>
                        {song.album && <div className="text-xs text-muted-foreground">{song.album}</div>}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {song.genre && <Badge variant="secondary">{song.genre}</Badge>}
                      <div className="text-sm text-muted-foreground flex items-center gap-1">
                        <Play className="h-4 w-4" />
                        {song.play_count || 0} plays
                      </div>
                      <Badge variant="outline">${song.download_price || '2.99'}</Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditSong(song)}
                        data-testid={`edit-song-${song.song_id}`}
                      >
                        <Edit className="h-4 w-4 text-blue-500" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteSong(song.song_id)}
                        data-testid={`delete-song-${song.song_id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Song Dialog */}
        <Dialog open={showAddSong} onOpenChange={setShowAddSong}>
          <DialogContent className="max-w-lg" data-testid="add-song-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Upload Worship Song</DialogTitle>
              <DialogDescription>Add a new song for your congregation to enjoy</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddSong} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="song-title">Title *</Label>
                  <Input
                    id="song-title"
                    value={newSongData.title}
                    onChange={(e) => setNewSongData({ ...newSongData, title: e.target.value })}
                    placeholder="Song title"
                    required
                    data-testid="song-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="song-artist">Artist *</Label>
                  <Input
                    id="song-artist"
                    value={newSongData.artist}
                    onChange={(e) => setNewSongData({ ...newSongData, artist: e.target.value })}
                    placeholder="Artist name"
                    required
                    data-testid="song-artist-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="song-album">Album</Label>
                  <Input
                    id="song-album"
                    value={newSongData.album}
                    onChange={(e) => setNewSongData({ ...newSongData, album: e.target.value })}
                    placeholder="Album name"
                    data-testid="song-album-input"
                  />
                </div>
                <div>
                  <Label htmlFor="song-genre">Genre</Label>
                  <Select value={newSongData.genre} onValueChange={(v) => setNewSongData({ ...newSongData, genre: v })}>
                    <SelectTrigger data-testid="song-genre-select">
                      <SelectValue placeholder="Select genre" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Worship">Worship</SelectItem>
                      <SelectItem value="Praise">Praise</SelectItem>
                      <SelectItem value="Gospel">Gospel</SelectItem>
                      <SelectItem value="Hymn">Hymn</SelectItem>
                      <SelectItem value="Contemporary">Contemporary</SelectItem>
                      <SelectItem value="Traditional">Traditional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="song-audio">Audio File * (MP3, WAV)</Label>
                <Input
                  id="song-audio"
                  type="file"
                  accept="audio/*"
                  onChange={(e) => setSongAudioFile(e.target.files[0])}
                  required
                  data-testid="song-audio-input"
                />
              </div>
              <div>
                <Label htmlFor="song-cover">Cover Image (Optional)</Label>
                <Input
                  id="song-cover"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setSongCoverFile(e.target.files[0])}
                  data-testid="song-cover-input"
                />
              </div>
              <div>
                <Label htmlFor="song-lyrics">Lyrics (Optional)</Label>
                <Textarea
                  id="song-lyrics"
                  value={newSongData.lyrics}
                  onChange={(e) => setNewSongData({ ...newSongData, lyrics: e.target.value })}
                  placeholder="Enter song lyrics..."
                  rows={4}
                  data-testid="song-lyrics-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAddSong(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={uploadingSong} data-testid="upload-song-submit">
                  {uploadingSong ? 'Uploading...' : 'Upload Song'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Song Dialog */}
        <Dialog open={showEditSong} onOpenChange={setShowEditSong}>
          <DialogContent className="max-w-lg" data-testid="edit-song-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Edit Worship Song</DialogTitle>
              <DialogDescription>Update song information</DialogDescription>
            </DialogHeader>
            {editingSong && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-song-title">Title</Label>
                    <Input
                      id="edit-song-title"
                      value={editingSong.title || ''}
                      onChange={(e) => setEditingSong({ ...editingSong, title: e.target.value })}
                      placeholder="Song title"
                      data-testid="edit-song-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-song-artist">Artist</Label>
                    <Input
                      id="edit-song-artist"
                      value={editingSong.artist || ''}
                      onChange={(e) => setEditingSong({ ...editingSong, artist: e.target.value })}
                      placeholder="Artist name"
                      data-testid="edit-song-artist-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-song-album">Album</Label>
                    <Input
                      id="edit-song-album"
                      value={editingSong.album || ''}
                      onChange={(e) => setEditingSong({ ...editingSong, album: e.target.value })}
                      placeholder="Album name"
                      data-testid="edit-song-album-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-song-genre">Genre</Label>
                    <Select value={editingSong.genre || ''} onValueChange={(v) => setEditingSong({ ...editingSong, genre: v })}>
                      <SelectTrigger data-testid="edit-song-genre-select">
                        <SelectValue placeholder="Select genre" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Worship">Worship</SelectItem>
                        <SelectItem value="Praise">Praise</SelectItem>
                        <SelectItem value="Gospel">Gospel</SelectItem>
                        <SelectItem value="Hymn">Hymn</SelectItem>
                        <SelectItem value="Contemporary">Contemporary</SelectItem>
                        <SelectItem value="Traditional">Traditional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-song-price">Download Price ($)</Label>
                    <Input
                      id="edit-song-price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingSong.download_price ?? 2.99}
                      onChange={(e) => setEditingSong({ ...editingSong, download_price: parseFloat(e.target.value) || 0 })}
                      placeholder="2.99"
                      data-testid="edit-song-price-input"
                    />
                  </div>
                  <div className="flex items-end">
                    <p className="text-sm text-muted-foreground">Set to 0 for free songs</p>
                  </div>
                </div>
                <div>
                  <Label htmlFor="edit-song-lyrics">Lyrics</Label>
                  <Textarea
                    id="edit-song-lyrics"
                    value={editingSong.lyrics || ''}
                    onChange={(e) => setEditingSong({ ...editingSong, lyrics: e.target.value })}
                    placeholder="Enter song lyrics..."
                    rows={4}
                    data-testid="edit-song-lyrics-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowEditSong(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleUpdateSong} data-testid="update-song-submit">
                    Update Song
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Store Product Management */}
        <Card className="glass-card mb-8" data-testid="store-management-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <ShoppingBag className="h-5 w-5" />
                  Store Products
                </CardTitle>
                <CardDescription>Manage products in your church store</CardDescription>
              </div>
              <Button onClick={() => setShowAddProduct(true)} data-testid="add-product-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {storeProducts.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No products yet. Add your first product!</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {storeProducts.map(product => (
                  <Card key={product.product_id} className="border">
                    {product.image ? (
                      <img src={product.image} alt={product.name} className="w-full h-32 object-cover rounded-t-lg" />
                    ) : (
                      <div className="w-full h-32 bg-muted flex items-center justify-center rounded-t-lg">
                        <ShoppingBag className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                    <CardContent className="p-4">
                      <h4 className="font-semibold line-clamp-1">{product.name}</h4>
                      <p className="text-sm text-muted-foreground line-clamp-2">{product.description}</p>
                      <div className="flex items-center justify-between mt-3">
                        <span className="font-bold text-primary">${product.price?.toFixed(2)}</span>
                        <Badge variant="outline">{product.category}</Badge>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => { setEditingProduct(product); setShowEditProduct(true); }}
                          data-testid={`edit-product-${product.product_id}`}
                        >
                          <Edit className="h-3 w-3 mr-1" />Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive" 
                          onClick={() => handleDeleteProduct(product.product_id)}
                          data-testid={`delete-product-${product.product_id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Product Dialog */}
        <Dialog open={showAddProduct} onOpenChange={setShowAddProduct}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Product</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddProduct} className="space-y-4">
              <div>
                <Label htmlFor="product-name">Product Name *</Label>
                <Input
                  id="product-name"
                  value={newProductData.name}
                  onChange={(e) => setNewProductData({ ...newProductData, name: e.target.value })}
                  placeholder="Enter product name"
                  required
                  data-testid="product-name-input"
                />
              </div>
              <div>
                <Label htmlFor="product-description">Description</Label>
                <Textarea
                  id="product-description"
                  value={newProductData.description}
                  onChange={(e) => setNewProductData({ ...newProductData, description: e.target.value })}
                  placeholder="Enter product description"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="product-price">Price ($) *</Label>
                  <Input
                    id="product-price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={newProductData.price}
                    onChange={(e) => setNewProductData({ ...newProductData, price: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="product-price-input"
                  />
                </div>
                <div>
                  <Label htmlFor="product-category">Category</Label>
                  <Select value={newProductData.category} onValueChange={(v) => setNewProductData({ ...newProductData, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="books">Books</SelectItem>
                      <SelectItem value="devotionals">Devotionals</SelectItem>
                      <SelectItem value="apparel">Apparel</SelectItem>
                      <SelectItem value="accessories">Accessories</SelectItem>
                      <SelectItem value="music">Music</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="product-image">Product Image</Label>
                <Input
                  id="product-image"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setProductImageFile(e.target.files[0])}
                  data-testid="product-image-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Or paste image URL:</p>
                <Input
                  placeholder="https://..."
                  value={newProductData.image}
                  onChange={(e) => { setNewProductData({ ...newProductData, image: e.target.value }); setProductImageFile(null); }}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowAddProduct(false)}>Cancel</Button>
                <Button type="submit" disabled={savingProduct} data-testid="save-product-btn">
                  {savingProduct ? 'Saving...' : 'Add Product'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Product Dialog */}
        <Dialog open={showEditProduct} onOpenChange={setShowEditProduct}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Product</DialogTitle>
            </DialogHeader>
            {editingProduct && (
              <form onSubmit={handleEditProduct} className="space-y-4">
                <div>
                  <Label>Product Name *</Label>
                  <Input
                    value={editingProduct.name}
                    onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={editingProduct.description || ''}
                    onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Price ($) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingProduct.price}
                      onChange={(e) => setEditingProduct({ ...editingProduct, price: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={editingProduct.category} onValueChange={(v) => setEditingProduct({ ...editingProduct, category: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="books">Books</SelectItem>
                        <SelectItem value="devotionals">Devotionals</SelectItem>
                        <SelectItem value="apparel">Apparel</SelectItem>
                        <SelectItem value="accessories">Accessories</SelectItem>
                        <SelectItem value="music">Music</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Product Image</Label>
                  {editingProduct.image && (
                    <img src={editingProduct.image} alt="Current" className="w-20 h-20 object-cover rounded mb-2" />
                  )}
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setProductImageFile(e.target.files[0])}
                  />
                  <Input
                    placeholder="Or paste image URL"
                    value={editingProduct.image || ''}
                    onChange={(e) => { setEditingProduct({ ...editingProduct, image: e.target.value }); setProductImageFile(null); }}
                    className="mt-2"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowEditProduct(false)}>Cancel</Button>
                  <Button type="submit" disabled={savingProduct}>
                    {savingProduct ? 'Saving...' : 'Update Product'}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
          </>
        )}

        {/* USERS TAB */}
        {activeAdminTab === 'users' && (
          <>
        {/* User Management */}
        <Card className="glass-card mb-8" id="user-management">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading">User Management</CardTitle>
                <CardDescription>View, edit, and manage all users</CardDescription>
              </div>
              <Button onClick={() => setShowAddUser(true)} data-testid="add-user-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Search and Filter */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex-1">
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-users-input"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full md:w-[180px]" data-testid="role-filter-select">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="pastor">Pastor</SelectItem>
                  <SelectItem value="church_worker">Church Worker</SelectItem>
                  <SelectItem value="course_creator">Course Creator</SelectItem>
                  <SelectItem value="mentor">Mentor</SelectItem>
                  <SelectItem value="mentee">Mentee</SelectItem>
                  <SelectItem value="member">Member</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Bulk Action Bar */}
            {selectedUserIds.length > 0 && (
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge className="bg-primary">{selectedUserIds.length} selected</Badge>
                  <span className="text-sm text-muted-foreground">Select an action to perform on selected users</span>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setBulkActionType('change_role'); setShowBulkAction(true); }}>
                    Change Role
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setBulkActionType('send_email'); setShowBulkAction(true); }}>
                    Send Email
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setBulkActionType('reset_onboarding'); setShowBulkAction(true); }}>
                    Reset Onboarding
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setBulkActionType('deactivate'); setShowBulkAction(true); }}>
                    Deactivate
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => { setBulkActionType('delete'); setShowBulkAction(true); }}>
                    Delete
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedUserIds([])}>
                    Clear
                  </Button>
                </div>
              </div>
            )}

            {/* Users Table */}
            <div className="overflow-x-auto">
              {filteredUsers.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No users found</p>
              ) : (
                <div className="space-y-3">
                  {/* Select All Header */}
                  <div className="flex items-center gap-3 p-2 bg-secondary/10 rounded-lg">
                    <input
                      type="checkbox"
                      checked={selectedUserIds.length === filteredUsers.filter(u => u.user_id !== user?.user_id).length && filteredUsers.length > 0}
                      onChange={handleSelectAll}
                      className="w-5 h-5 accent-primary rounded"
                      data-testid="select-all-checkbox"
                    />
                    <span className="text-sm font-medium">Select All ({filteredUsers.length} users)</span>
                  </div>
                  {filteredUsers.map((u) => (
                    <div
                      key={u.user_id}
                      className={`flex items-center justify-between p-4 rounded-lg transition-colors ${selectedUserIds.includes(u.user_id) ? 'bg-primary/10 border border-primary/30' : 'bg-secondary/20 hover:bg-secondary/30'}`}
                      data-testid="user-row"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        {u.user_id !== user?.user_id && (
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(u.user_id)}
                            onChange={() => handleSelectUser(u.user_id)}
                            className="w-5 h-5 accent-primary rounded"
                            data-testid={`select-user-${u.user_id}`}
                          />
                        )}
                        <Avatar>
                          <AvatarImage src={u.picture} />
                          <AvatarFallback>{u.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="font-semibold">{u.name}</div>
                          <div className="text-sm text-muted-foreground">{u.email}</div>
                        </div>
                        <Badge className={
                          u.role === 'admin' ? 'bg-red-600' :
                          u.role === 'pastor' ? 'bg-purple-600' :
                          u.role === 'church_worker' ? 'bg-blue-600' :
                          u.role === 'course_creator' ? 'bg-teal-600' :
                          u.role === 'mentor' ? 'bg-primary' : 
                          u.role === 'mentee' ? 'bg-accent' : 'bg-secondary'
                        }>
                          {u.role === 'church_worker' ? 'Church Worker' : u.role === 'course_creator' ? 'Course Creator' : u.role}
                        </Badge>
                        {/* Show additional roles if user has multiple */}
                        {u.roles && u.roles.length > 1 && u.roles.slice(1).map((role, idx) => (
                          <Badge key={idx} className={
                            role === 'admin' ? 'bg-red-600' :
                            role === 'pastor' ? 'bg-purple-600' :
                            role === 'support' ? 'bg-orange-600' :
                            role === 'church_worker' ? 'bg-blue-600' :
                            role === 'mentor' ? 'bg-primary' : 
                            role === 'mentee' ? 'bg-accent' : 'bg-secondary'
                          }>
                            {role === 'church_worker' ? 'Church Worker' : role}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditUser(u)}
                          data-testid={`edit-user-${u.user_id}-btn`}
                        >
                          Edit
                        </Button>
                        {u.user_id !== user.user_id && (
                          <>
                            {/* Impersonate button - Admin only, not for other admins */}
                            {user.role === 'admin' && u.role !== 'admin' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-amber-500 text-amber-600 hover:bg-amber-50"
                                onClick={async () => {
                                  try {
                                    await axiosInstance.post('/impersonate/start', { target_user_id: u.user_id });
                                    toast.success(`Now viewing as ${u.name}`);
                                    window.location.href = '/dashboard';
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || 'Failed to impersonate user');
                                  }
                                }}
                                data-testid={`impersonate-${u.user_id}-btn`}
                              >
                                <Eye className="h-3 w-3 mr-1" />
                                View As
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-accent text-accent hover:bg-accent hover:text-white"
                              onClick={() => handleResetPassword(u)}
                              data-testid={`reset-password-${u.user_id}-btn`}
                            >
                              Reset Password
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteUser(u.user_id)}
                              data-testid={`delete-user-${u.user_id}-btn`}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
          </>
        )}

        {/* VISITORS TAB */}
        {activeAdminTab === 'visitors' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="p-4">
                <div className="text-2xl font-bold text-primary">{visitorStats?.total || 0}</div>
                <div className="text-sm text-muted-foreground">Total Visitors</div>
              </Card>
              <Card className="p-4">
                <div className="text-2xl font-bold text-green-600">{visitorStats?.this_week || 0}</div>
                <div className="text-sm text-muted-foreground">This Week</div>
              </Card>
              <Card className="p-4">
                <div className="text-2xl font-bold text-amber-600">{visitorStats?.accepted_christ || 0}</div>
                <div className="text-sm text-muted-foreground">Accepted Christ</div>
              </Card>
              <Card className="p-4">
                <div className="text-2xl font-bold text-blue-600">{visitorStats?.by_status?.new || 0}</div>
                <div className="text-sm text-muted-foreground">New (Unassigned)</div>
              </Card>
            </div>

            {/* Actions Bar */}
            <div className="flex flex-wrap gap-3 mb-6">
              <Button onClick={() => { loadSalvationContent(); setShowSalvationEditor(true); }} variant="outline">
                <Edit className="h-4 w-4 mr-2" />
                Edit Salvation Page
              </Button>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <BookOpen className="h-4 w-4 mr-2" />
                    View QR Code
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>New Visitor QR Code</DialogTitle>
                    <DialogDescription>Scan to open the visitor information form</DialogDescription>
                  </DialogHeader>
                  {visitorQRCode && (
                    <div className="flex flex-col items-center gap-4 p-4">
                      <img src={visitorQRCode.qr_code} alt="Visitor Form QR Code" className="w-64 h-64" />
                      <p className="text-sm text-muted-foreground text-center break-all">{visitorQRCode.url}</p>
                      <Button onClick={() => {
                        const link = document.createElement('a');
                        link.href = visitorQRCode.qr_code;
                        link.download = 'visitor-form-qrcode.png';
                        link.click();
                      }}>
                        <Download className="h-4 w-4 mr-2" />
                        Download QR Code
                      </Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
              <Button onClick={() => window.open('/welcome-to-gods-family', '_blank')} variant="outline">
                <Eye className="h-4 w-4 mr-2" />
                Preview Salvation Page
              </Button>
              <Button onClick={() => window.open('/new-visitor', '_blank')} variant="outline">
                <Eye className="h-4 w-4 mr-2" />
                Preview Visitor Form
              </Button>
            </div>

            {/* Visitors List */}
            <Card className="glass-card">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <CardTitle>Visitor Follow-ups</CardTitle>
                    <CardDescription>Manage and track visitor connections</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search visitors..."
                      value={visitorFilter.search}
                      onChange={(e) => setVisitorFilter({...visitorFilter, search: e.target.value})}
                      className="w-48"
                    />
                    <Select value={visitorFilter.status} onValueChange={(v) => setVisitorFilter({...visitorFilter, status: v})}>
                      <SelectTrigger className="w-36">
                        <SelectValue placeholder="All Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="assigned">Assigned</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="follow_up">Follow Up</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={loadVisitorsData}>
                      <Search className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {visitorsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : visitors.length === 0 ? (
                  <div className="text-center py-12">
                    <HandHeart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No visitors found</p>
                    <p className="text-sm text-muted-foreground mt-2">Share the QR code or visitor form link to start collecting visitor information</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {visitors.map(visitor => (
                      <Card key={visitor.visitor_id} className="p-4 hover:bg-muted/50 transition">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <Avatar className="h-12 w-12">
                              <AvatarFallback className="bg-primary/10 text-primary">
                                {visitor.first_name?.[0]}{visitor.last_name?.[0]}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="font-semibold">{visitor.first_name} {visitor.last_name}</div>
                              <div className="text-sm text-muted-foreground">{visitor.email}</div>
                              {visitor.phone && <div className="text-sm text-muted-foreground">{visitor.phone}</div>}
                              <div className="flex flex-wrap gap-2 mt-2">
                                <Badge variant={
                                  visitor.status === 'completed' ? 'default' :
                                  visitor.status === 'new' ? 'destructive' :
                                  visitor.status === 'contacted' ? 'secondary' :
                                  'outline'
                                }>
                                  {visitor.status}
                                </Badge>
                                {visitor.accepted_christ && <Badge className="bg-amber-100 text-amber-800">Accepted Christ</Badge>}
                                {visitor.wants_baptism && <Badge className="bg-blue-100 text-blue-800">Wants Baptism</Badge>}
                                {visitor.wants_membership && <Badge className="bg-green-100 text-green-800">Wants Membership</Badge>}
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col md:items-end gap-2">
                            <div className="text-sm text-muted-foreground">
                              {new Date(visitor.created_at).toLocaleDateString()}
                            </div>
                            {visitor.assignee_info && (
                              <div className="text-sm">
                                <span className="text-muted-foreground">Assigned to:</span> {visitor.assignee_info.name}
                              </div>
                            )}
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => { setSelectedVisitor(visitor); setShowVisitorDetails(true); }}>
                                <Eye className="h-4 w-4 mr-1" /> View
                              </Button>
                              {!visitor.assigned_to && (
                                <Button size="sm" onClick={() => { setSelectedVisitor(visitor); setShowAssignVisitor(true); }}>
                                  <UserCog className="h-4 w-4 mr-1" /> Assign
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Visitor Details Dialog */}
            <Dialog open={showVisitorDetails} onOpenChange={setShowVisitorDetails}>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Visitor Details</DialogTitle>
                </DialogHeader>
                {selectedVisitor && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-4">
                      <Avatar className="h-16 w-16">
                        <AvatarFallback className="bg-primary/10 text-primary text-xl">
                          {selectedVisitor.first_name?.[0]}{selectedVisitor.last_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="text-xl font-semibold">{selectedVisitor.first_name} {selectedVisitor.last_name}</h3>
                        <p className="text-muted-foreground">{selectedVisitor.email}</p>
                        {selectedVisitor.phone && <p className="text-muted-foreground">{selectedVisitor.phone}</p>}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Status</Label>
                        <Select 
                          value={selectedVisitor.status} 
                          onValueChange={(v) => handleUpdateVisitorStatus(selectedVisitor.visitor_id, v)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="assigned">Assigned</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="follow_up">Follow Up</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Submitted</Label>
                        <p className="font-medium">{new Date(selectedVisitor.created_at).toLocaleString()}</p>
                      </div>
                    </div>

                    {selectedVisitor.address && (
                      <div>
                        <Label className="text-muted-foreground">Address</Label>
                        <p className="font-medium">
                          {selectedVisitor.address}, {selectedVisitor.city}, {selectedVisitor.state} {selectedVisitor.zip_code}
                        </p>
                      </div>
                    )}

                    {selectedVisitor.how_found_us && (
                      <div>
                        <Label className="text-muted-foreground">How They Found Us</Label>
                        <p className="font-medium">{selectedVisitor.how_found_us}</p>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                      {selectedVisitor.accepted_christ && <Badge className="bg-amber-100 text-amber-800">Accepted Christ</Badge>}
                      {selectedVisitor.wants_baptism && <Badge className="bg-blue-100 text-blue-800">Wants Baptism</Badge>}
                      {selectedVisitor.wants_membership && <Badge className="bg-green-100 text-green-800">Wants Membership</Badge>}
                    </div>

                    {selectedVisitor.interested_in?.length > 0 && (
                      <div>
                        <Label className="text-muted-foreground">Interested In</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {selectedVisitor.interested_in.map((interest, idx) => (
                            <Badge key={idx} variant="outline">{interest}</Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedVisitor.prayer_request && (
                      <div>
                        <Label className="text-muted-foreground">Prayer Request / Notes</Label>
                        <p className="bg-muted p-3 rounded-lg mt-1">{selectedVisitor.prayer_request}</p>
                      </div>
                    )}

                    {selectedVisitor.follow_up_notes?.length > 0 && (
                      <div>
                        <Label className="text-muted-foreground">Follow-up Notes</Label>
                        <div className="space-y-2 mt-2">
                          {selectedVisitor.follow_up_notes.map((note, idx) => (
                            <div key={idx} className="bg-muted/50 p-3 rounded-lg text-sm">
                              <p>{note.note}</p>
                              <p className="text-muted-foreground text-xs mt-1">
                                {note.by_name || 'Staff'} • {new Date(note.at).toLocaleString()}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Add Note */}
                    <div>
                      <Label>Add Note</Label>
                      <div className="flex gap-2 mt-1">
                        <Input 
                          id="visitor-note-input"
                          placeholder="Enter follow-up note..."
                        />
                        <Button onClick={() => {
                          const input = document.getElementById('visitor-note-input');
                          if (input?.value) {
                            handleAddVisitorNote(selectedVisitor.visitor_id, input.value);
                            input.value = '';
                          }
                        }}>Add</Button>
                      </div>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Assign Visitor Dialog */}
            <Dialog open={showAssignVisitor} onOpenChange={setShowAssignVisitor}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Assign Visitor for Follow-up</DialogTitle>
                  <DialogDescription>
                    Select a pastor or ministry leader to follow up with {selectedVisitor?.first_name} {selectedVisitor?.last_name}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <Label>Assign To</Label>
                  <Select onValueChange={(v) => handleAssignVisitor(selectedVisitor?.visitor_id, v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select team member" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.filter(u => ['admin', 'pastor', 'church_worker'].includes(u.role)).map(u => (
                        <SelectItem key={u.user_id} value={u.user_id}>
                          {u.name} ({u.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </DialogContent>
            </Dialog>

            {/* Salvation Page Editor Dialog */}
            <Dialog open={showSalvationEditor} onOpenChange={setShowSalvationEditor}>
              <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Salvation Page Content</DialogTitle>
                  <DialogDescription>Update the "Welcome to God's Family" page content</DialogDescription>
                </DialogHeader>
                {salvationContent && (
                  <div className="space-y-6 py-4">
                    <div>
                      <Label>Page Title</Label>
                      <Input
                        value={salvationContent.page_title || ''}
                        onChange={(e) => setSalvationContent({...salvationContent, page_title: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Introduction Text</Label>
                      <Textarea
                        value={salvationContent.intro_text || ''}
                        onChange={(e) => setSalvationContent({...salvationContent, intro_text: e.target.value})}
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Salvation Prayer</Label>
                      <Textarea
                        value={salvationContent.salvation_prayer || ''}
                        onChange={(e) => setSalvationContent({...salvationContent, salvation_prayer: e.target.value})}
                        rows={10}
                      />
                    </div>
                    <div>
                      <Label>Things to Know About God's Kingdom (one per line)</Label>
                      <Textarea
                        value={(salvationContent.things_to_know || []).join('\n')}
                        onChange={(e) => setSalvationContent({...salvationContent, things_to_know: e.target.value.split('\n').filter(l => l.trim())})}
                        rows={6}
                        placeholder="You are now a child of God (John 1:12)&#10;Your sins are forgiven (1 John 1:9)"
                      />
                    </div>
                    <div>
                      <Label>What We Believe</Label>
                      <p className="text-sm text-muted-foreground mb-2">Each belief has a title and content that expands when clicked</p>
                      <div className="space-y-3">
                        {(salvationContent.beliefs || []).map((belief, idx) => (
                          <Card key={idx} className="p-4">
                            <div className="flex gap-2 items-start">
                              <span className="text-muted-foreground font-medium">{idx + 1}.</span>
                              <div className="flex-1 space-y-2">
                                <Input
                                  placeholder="Title (e.g., The Bible)"
                                  value={belief.title}
                                  onChange={(e) => {
                                    const updated = [...salvationContent.beliefs];
                                    updated[idx] = {...updated[idx], title: e.target.value};
                                    setSalvationContent({...salvationContent, beliefs: updated});
                                  }}
                                />
                                <Textarea
                                  placeholder="Content description..."
                                  value={belief.content}
                                  onChange={(e) => {
                                    const updated = [...salvationContent.beliefs];
                                    updated[idx] = {...updated[idx], content: e.target.value};
                                    setSalvationContent({...salvationContent, beliefs: updated});
                                  }}
                                  rows={2}
                                />
                              </div>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  const updated = salvationContent.beliefs.filter((_, i) => i !== idx);
                                  setSalvationContent({...salvationContent, beliefs: updated});
                                }}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </Card>
                        ))}
                        <Button 
                          variant="outline" 
                          onClick={() => {
                            const newBelief = { title: '', content: '', order: salvationContent.beliefs.length + 1 };
                            setSalvationContent({...salvationContent, beliefs: [...(salvationContent.beliefs || []), newBelief]});
                          }}
                        >
                          <Plus className="h-4 w-4 mr-2" /> Add Belief
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label>Footer Message</Label>
                      <Textarea
                        value={salvationContent.footer_message || ''}
                        onChange={(e) => setSalvationContent({...salvationContent, footer_message: e.target.value})}
                        rows={2}
                      />
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowSalvationEditor(false)}>Cancel</Button>
                  <Button onClick={handleSaveSalvationContent}>Save Changes</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}

        {/* Mentorships Assignment Management - Part of Mentorships Tab */}
        {activeAdminTab === 'mentorships' && (
          <>
        {/* Mentorships Management */}
        <Card className="glass-card mb-8" id="mentorships-section">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading">Mentorship Management</CardTitle>
                <CardDescription>Assign mentors to mentees and manage relationships</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowTemplates(true)} data-testid="manage-templates-btn">
                  <FileText className="h-4 w-4 mr-2" /> Templates
                </Button>
                <Button variant="outline" onClick={() => setShowBulkAssign(true)} data-testid="bulk-assign-btn">
                  <Users className="h-4 w-4 mr-2" /> Bulk Assign
                </Button>
                <Button onClick={() => setShowAssignMentorship(true)} data-testid="assign-mentorship-btn">
                  <Plus className="h-4 w-4 mr-2" /> Assign
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {mentorships.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">No mentorships yet</p>
                <Button variant="outline" onClick={() => setShowAssignMentorship(true)}>
                  Create First Mentorship
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {mentorships.map((mentorship) => (
                  <div
                    key={mentorship.mentorship_id}
                    className="flex items-center justify-between p-4 rounded-lg bg-secondary/20 hover:bg-secondary/30 transition-colors"
                    data-testid="admin-mentorship-item"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={mentorship.mentor?.picture} />
                          <AvatarFallback>{mentorship.mentor?.name?.charAt(0) || 'M'}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-semibold text-sm">{mentorship.mentor?.name || 'Unknown Mentor'}</div>
                          <div className="text-xs text-muted-foreground">Mentor</div>
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      <div className="flex items-center gap-2">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={mentorship.mentee?.picture} />
                          <AvatarFallback>{mentorship.mentee?.name?.charAt(0) || 'M'}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-semibold text-sm">{mentorship.mentee?.name || 'Unknown Mentee'}</div>
                          <div className="text-xs text-muted-foreground">Mentee</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <Badge className={mentorship.status === 'active' ? 'bg-green-600' : 'bg-gray-500'}>
                          {mentorship.status}
                        </Badge>
                        <div className="text-xs text-muted-foreground mt-1">
                          {new Date(mentorship.started_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/admin/mentorship-audit/${mentorship.mentorship_id}`)}
                          data-testid={`audit-mentorship-${mentorship.mentorship_id}-btn`}
                        >
                          Audit
                        </Button>
                        {mentorship.status === 'active' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive border-destructive hover:bg-destructive hover:text-white"
                            onClick={() => handleEndMentorship(mentorship.mentorship_id)}
                            data-testid={`end-mentorship-${mentorship.mentorship_id}-btn`}
                          >
                            End
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
          </>
        )}
      </div>

      {/* Templates Dialog */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="templates-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="font-heading">Mentorship Templates</DialogTitle>
                <DialogDescription>Predefined goal sets for new mentorships</DialogDescription>
              </div>
              <Button onClick={() => { setShowTemplates(false); setShowCreateTemplate(true); }} data-testid="create-template-btn">
                <Plus className="h-4 w-4 mr-2" /> New Template
              </Button>
            </div>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {templates.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No templates yet. Create your first template!</p>
            ) : (
              templates.map(template => (
                <Card key={template.template_id} className="bg-secondary/20">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{template.name}</CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{template.duration_weeks} weeks</Badge>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteTemplate(template.template_id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <CardDescription>{template.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm font-medium mb-2">Goals ({template.goals?.length || 0}):</div>
                    <ul className="space-y-1">
                      {template.goals?.map((goal, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Target className="h-3 w-3 text-primary" />
                          {goal}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Template Dialog */}
      <Dialog open={showCreateTemplate} onOpenChange={setShowCreateTemplate}>
        <DialogContent className="max-w-2xl" data-testid="create-template-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Mentorship Template</DialogTitle>
            <DialogDescription>Define a reusable set of goals for mentorships</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTemplate} className="space-y-4">
            <div>
              <Label>Template Name *</Label>
              <Input
                value={newTemplateData.name}
                onChange={(e) => setNewTemplateData({ ...newTemplateData, name: e.target.value })}
                placeholder="e.g., Career Development"
                required
                data-testid="template-name-input"
              />
            </div>
            <div>
              <Label>Description *</Label>
              <Input
                value={newTemplateData.description}
                onChange={(e) => setNewTemplateData({ ...newTemplateData, description: e.target.value })}
                placeholder="Brief description of this mentorship track"
                required
                data-testid="template-description-input"
              />
            </div>
            <div>
              <Label>Duration (weeks)</Label>
              <Input
                type="number"
                min="1"
                max="52"
                value={newTemplateData.duration_weeks}
                onChange={(e) => setNewTemplateData({ ...newTemplateData, duration_weeks: parseInt(e.target.value) || 12 })}
                data-testid="template-duration-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Goals *</Label>
                <Button type="button" size="sm" variant="outline" onClick={addGoalField}>
                  <Plus className="h-3 w-3 mr-1" /> Add Goal
                </Button>
              </div>
              <div className="space-y-2">
                {newTemplateData.goals.map((goal, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      value={goal}
                      onChange={(e) => updateGoalField(idx, e.target.value)}
                      placeholder={`Goal ${idx + 1}`}
                      data-testid={`goal-input-${idx}`}
                    />
                    {newTemplateData.goals.length > 1 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeGoalField(idx)}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreateTemplate(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-template-btn">Create Template</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={showBulkAssign} onOpenChange={setShowBulkAssign}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="bulk-assign-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Bulk Assign Mentorships</DialogTitle>
            <DialogDescription>Assign one mentor to multiple mentees at once</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBulkAssign} className="space-y-4">
            <div>
              <Label>Select Mentor *</Label>
              <Select 
                value={bulkAssignData.mentor_id} 
                onValueChange={(v) => setBulkAssignData({ ...bulkAssignData, mentor_id: v })}
              >
                <SelectTrigger data-testid="bulk-select-mentor">
                  <SelectValue placeholder="Choose a mentor" />
                </SelectTrigger>
                <SelectContent>
                  {mentors.map(mentor => (
                    <SelectItem key={mentor.user_id} value={mentor.user_id}>
                      {mentor.name} ({mentor.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Apply Template (Optional)</Label>
              <Select 
                value={bulkAssignData.template_id} 
                onValueChange={(v) => setBulkAssignData({ ...bulkAssignData, template_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="bulk-select-template">
                  <SelectValue placeholder="No template (start fresh)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No template (start fresh)</SelectItem>
                  {templates.map(template => (
                    <SelectItem key={template.template_id} value={template.template_id}>
                      {template.name} ({template.goals?.length} goals, {template.duration_weeks} weeks)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Select Mentees * ({bulkAssignData.mentee_ids.length} selected)</Label>
              <div className="border rounded-lg p-3 max-h-60 overflow-y-auto mt-2 space-y-2">
                {mentees.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No mentees available</p>
                ) : (
                  mentees.map(mentee => (
                    <div
                      key={mentee.user_id}
                      className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                        bulkAssignData.mentee_ids.includes(mentee.user_id) 
                          ? 'bg-primary/20 border border-primary' 
                          : 'hover:bg-secondary/50'
                      }`}
                      onClick={() => toggleMenteeSelection(mentee.user_id)}
                      data-testid={`select-mentee-${mentee.user_id}`}
                    >
                      <Checkbox 
                        checked={bulkAssignData.mentee_ids.includes(mentee.user_id)} 
                        onCheckedChange={() => toggleMenteeSelection(mentee.user_id)}
                      />
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>{mentee.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{mentee.name}</div>
                        <div className="text-xs text-muted-foreground">{mentee.email}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowBulkAssign(false)}>Cancel</Button>
              <Button type="submit" disabled={bulkAssignData.mentee_ids.length === 0} data-testid="submit-bulk-assign-btn">
                Create {bulkAssignData.mentee_ids.length} Mentorship(s)
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Mentorship Dialog */}
      <Dialog open={showAssignMentorship} onOpenChange={setShowAssignMentorship}>
        <DialogContent data-testid="assign-mentorship-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Assign Mentor to Mentee</DialogTitle>
            <DialogDescription>Create a new mentorship relationship</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAssignMentorship} className="space-y-4">
            <div>
              <Label>Select Mentor *</Label>
              <Select 
                value={assignMentorshipData.mentor_id} 
                onValueChange={(v) => setAssignMentorshipData({ ...assignMentorshipData, mentor_id: v })}
              >
                <SelectTrigger data-testid="select-mentor-trigger">
                  <SelectValue placeholder="Choose a mentor" />
                </SelectTrigger>
                <SelectContent>
                  {mentors.length === 0 ? (
                    <SelectItem value="none" disabled>No mentors available</SelectItem>
                  ) : (
                    mentors.map(mentor => (
                      <SelectItem key={mentor.user_id} value={mentor.user_id}>
                        {mentor.name} ({mentor.email})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Select Mentee *</Label>
              <Select 
                value={assignMentorshipData.mentee_id} 
                onValueChange={(v) => setAssignMentorshipData({ ...assignMentorshipData, mentee_id: v })}
              >
                <SelectTrigger data-testid="select-mentee-trigger">
                  <SelectValue placeholder="Choose a mentee" />
                </SelectTrigger>
                <SelectContent>
                  {mentees.length === 0 ? (
                    <SelectItem value="none" disabled>No mentees available</SelectItem>
                  ) : (
                    mentees.map(mentee => (
                      <SelectItem key={mentee.user_id} value={mentee.user_id}>
                        {mentee.name} ({mentee.email})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Apply Template (Optional)</Label>
              <Select 
                value={assignMentorshipData.template_id} 
                onValueChange={(v) => setAssignMentorshipData({ ...assignMentorshipData, template_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="select-template-trigger">
                  <SelectValue placeholder="No template (start fresh)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No template (start fresh)</SelectItem>
                  {templates.map(template => (
                    <SelectItem key={template.template_id} value={template.template_id}>
                      {template.name} ({template.goals?.length} goals)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Templates auto-create goals for the mentorship</p>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAssignMentorship(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-assign-mentorship-btn">
                Create Mentorship
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Mentor Dialog */}
      <Dialog open={showAddMentor} onOpenChange={setShowAddMentor}>
        <DialogContent className="max-w-2xl" data-testid="add-mentor-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add New Mentor</DialogTitle>
            <DialogDescription>Create a new mentor account with pricing</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddMentor} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mentor-name">Full Name *</Label>
                <Input
                  id="mentor-name"
                  value={newMentorData.name}
                  onChange={(e) => setNewMentorData({ ...newMentorData, name: e.target.value })}
                  placeholder="John Smith"
                  required
                  data-testid="mentor-name-input"
                />
              </div>
              <div>
                <Label htmlFor="mentor-email">Email *</Label>
                <Input
                  id="mentor-email"
                  type="email"
                  value={newMentorData.email}
                  onChange={(e) => setNewMentorData({ ...newMentorData, email: e.target.value })}
                  placeholder="mentor@example.com"
                  required
                  data-testid="mentor-email-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mentor-password">Password *</Label>
                <Input
                  id="mentor-password"
                  type="password"
                  value={newMentorData.password}
                  onChange={(e) => setNewMentorData({ ...newMentorData, password: e.target.value })}
                  placeholder="Min 6 characters"
                  required
                  minLength={6}
                  data-testid="mentor-password-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="mentor-bio">Bio</Label>
              <Textarea
                id="mentor-bio"
                value={newMentorData.bio}
                onChange={(e) => setNewMentorData({ ...newMentorData, bio: e.target.value })}
                placeholder="Brief description about the mentor..."
                rows={3}
                data-testid="mentor-bio-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mentor-skills">Skills (comma-separated)</Label>
                <Input
                  id="mentor-skills"
                  value={newMentorData.skills}
                  onChange={(e) => setNewMentorData({ ...newMentorData, skills: e.target.value })}
                  placeholder="Leadership, Communication, Strategy"
                  data-testid="mentor-skills-input"
                />
              </div>
              <div>
                <Label htmlFor="mentor-expertise">Expertise Areas (comma-separated)</Label>
                <Input
                  id="mentor-expertise"
                  value={newMentorData.expertise_areas}
                  onChange={(e) => setNewMentorData({ ...newMentorData, expertise_areas: e.target.value })}
                  placeholder="Career Coaching, Business Development"
                  data-testid="mentor-expertise-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddMentor(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-add-mentor-btn">
                Add Mentor
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
        <DialogContent className="max-w-2xl" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit User</DialogTitle>
            <DialogDescription>Update user information and assign multiple roles</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveUser} className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editUserData.name}
                onChange={(e) => setEditUserData({ ...editUserData, name: e.target.value })}
                data-testid="edit-user-name-input"
              />
            </div>
            <div>
              <Label className="mb-3 block">Roles (select one or more)</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="edit-user-roles-checkboxes">
                {[
                  { value: 'admin', label: 'Admin', color: 'bg-red-600' },
                  { value: 'pastor', label: 'Pastor', color: 'bg-purple-600' },
                  { value: 'support', label: 'Support', color: 'bg-orange-600' },
                  { value: 'church_worker', label: 'Church Worker', color: 'bg-blue-600' },
                  { value: 'mentor', label: 'Mentor', color: 'bg-primary' },
                  { value: 'mentee', label: 'Mentee', color: 'bg-accent' },
                  { value: 'member', label: 'Member', color: 'bg-secondary' }
                ].map((role) => (
                  <label 
                    key={role.value} 
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                      editUserData.roles?.includes(role.value) 
                        ? 'border-primary bg-primary/10' 
                        : 'border-muted hover:border-primary/50'
                    }`}
                    data-testid={`edit-role-checkbox-${role.value}`}
                  >
                    <input
                      type="checkbox"
                      checked={editUserData.roles?.includes(role.value) || false}
                      onChange={(e) => {
                        const currentRoles = editUserData.roles || [];
                        let newRoles;
                        if (e.target.checked) {
                          newRoles = [...currentRoles, role.value];
                        } else {
                          newRoles = currentRoles.filter(r => r !== role.value);
                        }
                        setEditUserData({ 
                          ...editUserData, 
                          roles: newRoles,
                          role: newRoles[0] || 'member' // Update primary role
                        });
                      }}
                      className="w-4 h-4 accent-primary rounded"
                    />
                    <Badge className={role.color}>{role.label}</Badge>
                  </label>
                ))}
              </div>
              {editUserData.roles?.length === 0 && (
                <p className="text-sm text-destructive mt-2">Please select at least one role</p>
              )}
            </div>
            <div>
              <Label htmlFor="edit-bio">Bio</Label>
              <Textarea
                id="edit-bio"
                value={editUserData.bio}
                onChange={(e) => setEditUserData({ ...editUserData, bio: e.target.value })}
                rows={3}
                data-testid="edit-user-bio-input"
              />
            </div>
            <div>
              <Label htmlFor="edit-skills">Skills (comma-separated)</Label>
              <Input
                id="edit-skills"
                value={editUserData.skills}
                onChange={(e) => setEditUserData({ ...editUserData, skills: e.target.value })}
                data-testid="edit-user-skills-input"
              />
            </div>
            {editUserData.roles?.includes('mentor') && (
              <>
                <div>
                  <Label htmlFor="edit-expertise">Expertise Areas (comma-separated)</Label>
                  <Input
                    id="edit-expertise"
                    value={editUserData.expertise_areas}
                    onChange={(e) => setEditUserData({ ...editUserData, expertise_areas: e.target.value })}
                    data-testid="edit-user-expertise-input"
                  />
                </div>
              </>
            )}
            
            {/* Module Access Overrides */}
            <div className="border-t pt-4 mt-4">
              <Label className="mb-3 block font-semibold">Module Access Overrides</Label>
              <p className="text-xs text-muted-foreground mb-3">Override global module settings for this specific user</p>
              <div className="space-y-3">
                {[
                  { key: 'budget_tracker', name: 'Budget Tracker', icon: DollarSign, color: 'green' },
                  { key: 'resume_builder', name: 'Resume Builder', icon: FileText, color: 'blue' },
                  { key: 'business_plan', name: 'Business Plan', icon: Briefcase, color: 'purple' }
                ].map((module) => {
                  const isEnabled = editUserData.enabled_modules?.includes(module.key);
                  const isDisabled = editUserData.disabled_modules?.includes(module.key);
                  const status = isEnabled ? 'enabled' : isDisabled ? 'disabled' : 'default';
                  const Icon = module.icon;
                  
                  return (
                    <div key={module.key} className="flex items-center justify-between p-3 rounded-lg border bg-muted/20">
                      <div className="flex items-center gap-2">
                        <Icon className={`h-4 w-4 text-${module.color}-600`} />
                        <span className="text-sm font-medium">{module.name}</span>
                      </div>
                      <Select 
                        value={status}
                        onValueChange={(value) => {
                          let newEnabled = (editUserData.enabled_modules || []).filter(m => m !== module.key);
                          let newDisabled = (editUserData.disabled_modules || []).filter(m => m !== module.key);
                          
                          if (value === 'enabled') {
                            newEnabled.push(module.key);
                          } else if (value === 'disabled') {
                            newDisabled.push(module.key);
                          }
                          
                          setEditUserData({
                            ...editUserData,
                            enabled_modules: newEnabled,
                            disabled_modules: newDisabled
                          });
                        }}
                      >
                        <SelectTrigger className="w-[140px]" data-testid={`module-override-${module.key}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Use Global</SelectItem>
                          <SelectItem value="enabled">Force Enable</SelectItem>
                          <SelectItem value="disabled">Force Disable</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90" data-testid="save-user-btn">
                Save Changes
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowEditUser(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add New User</DialogTitle>
            <DialogDescription>Create a new user account with assigned roles and permissions</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="new-user-name">Full Name *</Label>
                <Input
                  id="new-user-name"
                  value={newUserData.name}
                  onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
                  placeholder="John Doe"
                  required
                  data-testid="new-user-name-input"
                />
              </div>
              <div>
                <Label htmlFor="new-user-email">Email *</Label>
                <Input
                  id="new-user-email"
                  type="email"
                  value={newUserData.email}
                  onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                  placeholder="user@example.com"
                  required
                  data-testid="new-user-email-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="new-user-password">Password *</Label>
              <Input
                id="new-user-password"
                type="password"
                value={newUserData.password}
                onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                placeholder="Minimum 6 characters"
                required
                data-testid="new-user-password-input"
              />
            </div>
            <div>
              <Label className="mb-3 block">Roles * (select one or more)</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="new-user-roles-checkboxes">
                {[
                  { value: 'admin', label: 'Admin', color: 'bg-red-600' },
                  { value: 'pastor', label: 'Pastor', color: 'bg-purple-600' },
                  { value: 'support', label: 'Support', color: 'bg-orange-600' },
                  { value: 'church_worker', label: 'Church Worker', color: 'bg-blue-600' },
                  { value: 'mentor', label: 'Mentor', color: 'bg-primary' },
                  { value: 'mentee', label: 'Mentee', color: 'bg-accent' },
                  { value: 'member', label: 'Member', color: 'bg-secondary' }
                ].map((role) => (
                  <label 
                    key={role.value} 
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                      newUserData.roles?.includes(role.value) 
                        ? 'border-primary bg-primary/10' 
                        : 'border-muted hover:border-primary/50'
                    }`}
                    data-testid={`new-role-checkbox-${role.value}`}
                  >
                    <input
                      type="checkbox"
                      checked={newUserData.roles?.includes(role.value) || false}
                      onChange={(e) => {
                        const currentRoles = newUserData.roles || [];
                        let newRoles;
                        if (e.target.checked) {
                          newRoles = [...currentRoles, role.value];
                        } else {
                          newRoles = currentRoles.filter(r => r !== role.value);
                        }
                        setNewUserData({ 
                          ...newUserData, 
                          roles: newRoles,
                          role: newRoles[0] || 'member'
                        });
                      }}
                      className="w-4 h-4 accent-primary rounded"
                    />
                    <Badge className={role.color}>{role.label}</Badge>
                  </label>
                ))}
              </div>
              {(!newUserData.roles || newUserData.roles.length === 0) && (
                <p className="text-sm text-destructive mt-2">Please select at least one role</p>
              )}
            </div>
            <div>
              <Label htmlFor="new-user-phone">Phone (Optional)</Label>
              <Input
                id="new-user-phone"
                value={newUserData.phone}
                onChange={(e) => setNewUserData({ ...newUserData, phone: e.target.value })}
                placeholder="555-123-4567"
                data-testid="new-user-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="new-user-bio">Bio (Optional)</Label>
              <Textarea
                id="new-user-bio"
                value={newUserData.bio}
                onChange={(e) => setNewUserData({ ...newUserData, bio: e.target.value })}
                rows={2}
                placeholder="Brief description about the user..."
                data-testid="new-user-bio-input"
              />
            </div>
            {(newUserData.roles?.includes('mentor') || newUserData.roles?.includes('pastor')) && (
              <>
                <div>
                  <Label htmlFor="new-user-skills">Skills (comma-separated)</Label>
                  <Input
                    id="new-user-skills"
                    value={newUserData.skills}
                    onChange={(e) => setNewUserData({ ...newUserData, skills: e.target.value })}
                    placeholder="Leadership, Counseling, Teaching"
                    data-testid="new-user-skills-input"
                  />
                </div>
                <div>
                  <Label htmlFor="new-user-expertise">Expertise Areas (comma-separated)</Label>
                  <Input
                    id="new-user-expertise"
                    value={newUserData.expertise_areas}
                    onChange={(e) => setNewUserData({ ...newUserData, expertise_areas: e.target.value })}
                    placeholder="Career Development, Spiritual Growth"
                    data-testid="new-user-expertise-input"
                  />
                </div>
              </>
            )}
            
            {/* Role Permissions Info */}
            {newUserData.roles?.length > 0 && (
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-semibold text-sm mb-2">Combined Role Permissions:</h4>
                <div className="text-xs text-muted-foreground">
                  <ul className="list-disc list-inside space-y-1">
                    {newUserData.roles.includes('admin') && <li><strong>Admin:</strong> Full system access and user management</li>}
                    {newUserData.roles.includes('pastor') && <li><strong>Pastor:</strong> Manage sermons, counseling, all content</li>}
                    {newUserData.roles.includes('support') && <li><strong>Support:</strong> Handle support tickets and knowledge base</li>}
                    {newUserData.roles.includes('church_worker') && <li><strong>Church Worker:</strong> Manage store, events, check-ins</li>}
                    {newUserData.roles.includes('mentor') && <li><strong>Mentor:</strong> Mentorship management, assign goals/tasks</li>}
                    {newUserData.roles.includes('mentee') && <li><strong>Mentee:</strong> Request mentorship, AI tools access</li>}
                    {newUserData.roles.includes('member') && <li><strong>Member:</strong> Basic church features, sermons, prayers</li>}
                  </ul>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddUser(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="create-user-btn" disabled={!newUserData.roles || newUserData.roles.length === 0}>
                <Plus className="h-4 w-4 mr-2" />
                Create User
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPassword} onOpenChange={setShowResetPassword}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reset User Password</DialogTitle>
            <DialogDescription>
              Set a new password for {selectedUser?.name} ({selectedUser?.email})
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitPasswordReset} className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              ⚠️ This will immediately log out the user from all devices. They will need to login with the new password.
            </div>
            <div>
              <Label htmlFor="reset-new-password">New Password</Label>
              <Input
                id="reset-new-password"
                type="password"
                placeholder="Enter new password"
                value={resetPasswordData.newPassword}
                onChange={(e) => setResetPasswordData({ ...resetPasswordData, newPassword: e.target.value })}
                required
                minLength={6}
                data-testid="reset-new-password-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Minimum 6 characters</p>
            </div>
            <div>
              <Label htmlFor="reset-confirm-password">Confirm New Password</Label>
              <Input
                id="reset-confirm-password"
                type="password"
                placeholder="Confirm new password"
                value={resetPasswordData.confirmPassword}
                onChange={(e) => setResetPasswordData({ ...resetPasswordData, confirmPassword: e.target.value })}
                required
                minLength={6}
                data-testid="reset-confirm-password-input"
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1 bg-accent hover:bg-accent/90" data-testid="submit-reset-password-btn">
                Reset Password
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowResetPassword(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordChange} onOpenChange={setShowPasswordChange}>
        <DialogContent data-testid="password-change-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Change Password</DialogTitle>
            <DialogDescription>Update your admin account password</DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <Label htmlFor="currentPassword">Current Password</Label>
              <Input
                id="currentPassword"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                required
                data-testid="current-password-input"
              />
            </div>
            <div>
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                required
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                required
                data-testid="confirm-password-input"
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90" data-testid="submit-password-change-btn">
                Change Password
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowPasswordChange(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Confirmation Dialog */}
      <Dialog open={showBulkAction} onOpenChange={setShowBulkAction}>
        <DialogContent data-testid="bulk-action-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {bulkActionType === 'delete' ? '⚠️ Delete Users' :
               bulkActionType === 'deactivate' ? 'Deactivate Users' :
               bulkActionType === 'change_role' ? 'Change User Role' :
               bulkActionType === 'send_email' ? 'Send Email to Users' :
               bulkActionType === 'reset_onboarding' ? 'Reset Onboarding' :
               'Bulk Action'}
            </DialogTitle>
            <DialogDescription>
              This action will affect {selectedUserIds.length} selected user(s).
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {bulkActionType === 'delete' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
                <p className="font-semibold">Warning: This action cannot be undone!</p>
                <p className="text-sm mt-1">All user data will be permanently deleted including their profile, messages, and activity history.</p>
              </div>
            )}
            
            {bulkActionType === 'deactivate' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
                <p className="text-sm">Selected users will be deactivated and will no longer be able to log in.</p>
              </div>
            )}
            
            {bulkActionType === 'change_role' && (
              <div className="space-y-3">
                <Label>Select New Role</Label>
                <Select value={bulkRoleChange} onValueChange={setBulkRoleChange}>
                  <SelectTrigger data-testid="bulk-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="pastor">Pastor</SelectItem>
                    <SelectItem value="church_worker">Church Worker</SelectItem>
                    <SelectItem value="course_creator">Course Creator</SelectItem>
                    <SelectItem value="mentor">Mentor</SelectItem>
                    <SelectItem value="mentee">Mentee</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {bulkActionType === 'send_email' && (
              <div className="space-y-3">
                <div>
                  <Label htmlFor="bulk-email-subject">Email Subject</Label>
                  <Input
                    id="bulk-email-subject"
                    placeholder="Enter email subject..."
                    value={bulkEmailData.subject}
                    onChange={(e) => setBulkEmailData({ ...bulkEmailData, subject: e.target.value })}
                    data-testid="bulk-email-subject"
                  />
                </div>
                <div>
                  <Label htmlFor="bulk-email-message">Email Message</Label>
                  <Textarea
                    id="bulk-email-message"
                    placeholder="Enter email message..."
                    rows={5}
                    value={bulkEmailData.message}
                    onChange={(e) => setBulkEmailData({ ...bulkEmailData, message: e.target.value })}
                    data-testid="bulk-email-message"
                  />
                </div>
              </div>
            )}
            
            {bulkActionType === 'reset_onboarding' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-blue-800">
                <p className="text-sm">Selected users will need to complete their role-based onboarding again before accessing the platform.</p>
              </div>
            )}
          </div>
          
          <div className="flex gap-2 mt-4">
            <Button
              onClick={handleBulkAction}
              className={bulkActionType === 'delete' ? 'flex-1 bg-red-600 hover:bg-red-700' : 'flex-1 bg-primary hover:bg-primary/90'}
              disabled={bulkActionLoading}
              data-testid="confirm-bulk-action-btn"
            >
              {bulkActionLoading ? 'Processing...' : `Confirm ${bulkActionType === 'delete' ? 'Delete' : 'Action'}`}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => {
                setShowBulkAction(false);
                setBulkActionType('');
              }}
              data-testid="cancel-bulk-action-btn"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}

function Dashboard({ user: initialUser }) {
  const [user, setUser] = useState(initialUser);
  const [mentorships, setMentorships] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [requests, setRequests] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [goals, setGoals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [drillDownType, setDrillDownType] = useState(null);
  const [notifications, setNotifications] = useState({ total: 0 });
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationDetails, setNotificationDetails] = useState([]);
  const [cartItemCount, setCartItemCount] = useState(0);
  const [membership, setMembership] = useState({ tier: 'free', modules: ['dashboard', 'courses', 'calendar', 'community', 'sermons', 'worship'] });
  // Announcements state
  const [dashboardAnnouncements, setDashboardAnnouncements] = useState([]);
  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Impersonation state
  const [impersonation, setImpersonation] = useState({ is_impersonating: false });
  // Tour state
  const [runTour, setRunTour] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const navigate = useNavigate();

  // Initialize tour for new users
  useEffect(() => {
    if (user && !hasTourCompleted('dashboard')) {
      setTimeout(() => setRunTour(true), 2000);
    }
  }, [user]);

  // Start tour manually
  const startTour = () => {
    resetTour('dashboard');
    setTourStep(0);
    setRunTour(true);
  };
  
  const handleNextStep = () => {
    if (tourStep < dashboardTourSteps.length - 1) {
      setTourStep(tourStep + 1);
    }
  };
  
  const handlePrevStep = () => {
    if (tourStep > 0) {
      setTourStep(tourStep - 1);
    }
  };
  
  const handleSkipTour = () => {
    setRunTour(false);
    markTourCompleted('dashboard');
  };
  
  const handleFinishTour = () => {
    setRunTour(false);
    markTourCompleted('dashboard');
    toast.success('Tour completed! You can restart it anytime from the help menu.');
  };

  useEffect(() => {
    if (!user) {
      axiosInstance.get('/auth/me').then((res) => setUser(res.data));
    }
    loadDashboardData();
    loadNotifications();
    loadCartCount();
    loadMembership();
    loadDashboardAnnouncements();
    checkImpersonationStatus();
    loadModuleOverrides();
  }, [user]);

  const checkImpersonationStatus = async () => {
    try {
      const res = await axiosInstance.get('/impersonate/status');
      setImpersonation(res.data);
    } catch (e) {
      // Not impersonating
    }
  };

  const endImpersonation = async () => {
    try {
      await axiosInstance.post('/impersonate/end');
      toast.success('Impersonation ended');
      // Refresh the page to go back to admin session
      window.location.reload();
    } catch (e) {
      toast.error('Failed to end impersonation');
    }
  };

  const loadMembership = async () => {
    try {
      const res = await axiosInstance.get('/membership/my');
      setMembership(res.data);
    } catch (e) {
      // Keep default free tier
    }
  };

  // Admin per-user module overrides (force enable/disable)
  const [moduleOverrides, setModuleOverrides] = useState({ enabled: [], disabled: [] });

  const loadModuleOverrides = async () => {
    try {
      const res = await axiosInstance.get('/users/modules');
      setModuleOverrides(res.data.overrides || { enabled: [], disabled: [] });
    } catch (e) {
      // keep defaults
    }
  };

  // Helper to check module access (admin overrides > membership)
  const MODULE_KEY_ALIASES = { budget: 'budget_tracker', resume: 'resume_builder' };
  const hasModuleAccess = (module) => {
    // Admins and pastors always have full access
    if (['admin', 'pastor'].includes(user?.role)) return true;
    const key = MODULE_KEY_ALIASES[module] || module;
    if (moduleOverrides.enabled.includes(key)) return true;
    if (moduleOverrides.disabled.includes(key)) return false;
    return membership?.modules?.includes(module);
  };

  const loadCartCount = () => {
    const savedCart = localStorage.getItem('churchStoreCart');
    if (savedCart) {
      const cart = JSON.parse(savedCart);
      setCartItemCount(cart.reduce((sum, item) => sum + item.quantity, 0));
    }
  };

  // Listen for cart changes
  useEffect(() => {
    const handleStorageChange = () => loadCartCount();
    window.addEventListener('storage', handleStorageChange);
    const interval = setInterval(loadCartCount, 2000); // Poll for changes
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const loadNotifications = async () => {
    try {
      const res = await axiosInstance.get('/notifications/counts');
      setNotifications(res.data);
    } catch (error) {
      console.error('Failed to load notifications');
    }
  };

  const loadNotificationDetails = async () => {
    try {
      const res = await axiosInstance.get('/notifications/details');
      setNotificationDetails(res.data);
    } catch (error) {
      console.error('Failed to load notification details');
    }
  };

  const loadDashboardAnnouncements = async () => {
    try {
      const res = await axiosInstance.get('/announcements');
      setDashboardAnnouncements(res.data);
    } catch (error) {
      console.error('Failed to load announcements');
    }
  };

  const handleNotificationClick = () => {
    if (!showNotifications) {
      loadNotificationDetails();
    }
    setShowNotifications(!showNotifications);
  };

  const loadDashboardData = async () => {
    try {
      const [mentorshipsRes, sessionsRes, goalsRes, requestsRes, tasksRes] = await Promise.all([
        axiosInstance.get('/mentorships'),
        axiosInstance.get('/sessions'),
        axiosInstance.get('/goals'),
        axiosInstance.get('/mentorship/requests'),
        axiosInstance.get('/tasks')
      ]);
      setMentorships(mentorshipsRes.data);
      setSessions(sessionsRes.data);
      setGoals(goalsRes.data);
      setRequests(requestsRes.data);
      setTasks(tasksRes.data);

      if (user?.role === 'mentee') {
        const mentorsRes = await axiosInstance.get('/mentors');
        setMentors(mentorsRes.data);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await axiosInstance.post('/auth/logout');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Prepare chart data
  const tasksChartData = [
    { name: 'To Do', value: tasks.filter(t => t.status === 'todo').length, color: '#94a3b8' },
    { name: 'In Progress', value: tasks.filter(t => t.status === 'in_progress').length, color: '#D97706' },
    { name: 'Done', value: tasks.filter(t => t.status === 'done').length, color: '#0F392B' }
  ];

  const goalsProgressData = goals.map(g => ({
    name: g.title.length > 15 ? g.title.substring(0, 15) + '...' : g.title,
    progress: g.progress
  }));

  const activeMentorships = mentorships.filter(m => m.status === 'active');
  
  const mentorshipActivityData = activeMentorships.map(m => {
    const mentorshipTasks = tasks.filter(t => t.mentorship_id === m.mentorship_id);
    const mentorshipGoals = goals.filter(g => g.mentorship_id === m.mentorship_id);
    const mentorshipSessions = sessions.filter(s => s.mentorship_id === m.mentorship_id);
    
    return {
      name: `Mentorship ${m.mentorship_id.slice(-4)}`,
      tasks: mentorshipTasks.length,
      goals: mentorshipGoals.length,
      sessions: mentorshipSessions.length
    };
  });

  const COLORS = ['#0F392B', '#D97706', '#94a3b8', '#16a34a', '#dc2626'];

  const handleAcceptRequest = async (requestId) => {
    try {
      await axiosInstance.put(`/mentorship/request/${requestId}/accept`);
      toast.success('Request accepted!');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to accept request');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await axiosInstance.put(`/mentorship/request/${requestId}/reject`);
      toast.success('Request rejected');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-tour="welcome">
      {/* Product Tour Overlay */}
      <AnimatePresence>
        {runTour && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[10000]"
              onClick={handleSkipTour}
            />
            {/* Tour Tooltip */}
            <TourTooltip
              step={dashboardTourSteps[tourStep]}
              currentStep={tourStep}
              totalSteps={dashboardTourSteps.length}
              onNext={handleNextStep}
              onPrev={handlePrevStep}
              onSkip={handleSkipTour}
              onFinish={handleFinishTour}
            />
          </>
        )}
      </AnimatePresence>
      
      {/* Impersonation Banner */}
      {impersonation.is_impersonating && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-amber-950 py-2 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              <span className="font-medium">
                Viewing as: {impersonation.target_user_name} ({impersonation.target_user_email})
              </span>
              <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                Impersonation Mode
              </Badge>
            </div>
            <Button 
              size="sm" 
              variant="outline" 
              className="bg-white hover:bg-amber-50 border-amber-300"
              onClick={endImpersonation}
            >
              <EyeOff className="h-4 w-4 mr-2" />
              End Impersonation
            </Button>
          </div>
        </div>
      )}
      
      {/* Main Layout with Sidebar */}
      <div className={`flex min-h-screen ${impersonation.is_impersonating ? 'pt-12' : ''}`}>
        {/* Collapsible Sidebar */}
        <aside className={`hidden md:flex flex-col bg-card border-r transition-all duration-300 ${sidebarCollapsed ? 'w-16' : 'w-64'} sticky top-0 h-screen`}>
          {/* Sidebar Header */}
          <div className="p-4 border-b flex items-center justify-between">
            {!sidebarCollapsed && (
              <span className="font-heading text-lg font-bold text-primary">TheFeedingFaith</span>
            )}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="h-8 w-8"
              data-testid="sidebar-toggle"
            >
              {sidebarCollapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            </Button>
          </div>
          
          {/* Sidebar Navigation */}
          <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
            {[
              { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
              { path: '/courses', label: 'Courses', icon: GraduationCap },
              { path: '/calendar', label: 'Calendar', icon: CalendarIcon },
              { path: '/community', label: 'Community', icon: Users },
              { path: '/sermons', label: 'Sermons', icon: BookOpen },
              { path: '/worship', label: 'Worship', icon: Music },
              { path: '/giving', label: 'Give', icon: Heart },
              { path: '/store', label: 'Store', icon: ShoppingBag },
              { path: '/budget', label: 'Budget', icon: PiggyBank, roles: ['admin', 'pastor', 'mentor', 'mentee'] },
            ].map(item => {
              // Check role-based visibility (module force-enable overrides role restriction)
              if (item.roles && !item.roles.includes(user?.role)) {
                if (!(item.path === '/budget' && hasModuleAccess('budget'))) return null;
              }
              const Icon = item.icon;
              const isActive = window.location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}>
                  <Button 
                    variant={isActive ? "secondary" : "ghost"} 
                    className={`w-full justify-start ${sidebarCollapsed ? 'px-3' : ''}`}
                    data-testid={`sidebar-${item.label.toLowerCase()}`}
                  >
                    <Icon className={`h-4 w-4 ${sidebarCollapsed ? '' : 'mr-3'}`} />
                    {!sidebarCollapsed && item.label}
                  </Button>
                </Link>
              );
            })}
            
            {/* Mentee-specific: Find Mentors */}
            {user.role === 'mentee' && (
              <Link to="/mentors">
                <Button variant="ghost" className={`w-full justify-start ${sidebarCollapsed ? 'px-3' : ''}`}>
                  <UserCog className={`h-4 w-4 ${sidebarCollapsed ? '' : 'mr-3'}`} />
                  {!sidebarCollapsed && 'Find Mentors'}
                </Button>
              </Link>
            )}
            
            {/* Admin/Pastor: Admin Panel */}
            {(user.role === 'admin' || user.role === 'pastor') && (
              <Link to="/admin">
                <Button variant="ghost" className={`w-full justify-start text-purple-600 dark:text-purple-400 ${sidebarCollapsed ? 'px-3' : ''}`}>
                  <Settings className={`h-4 w-4 ${sidebarCollapsed ? '' : 'mr-3'}`} />
                  {!sidebarCollapsed && 'Admin Panel'}
                </Button>
              </Link>
            )}
          </nav>
          
          {/* Sidebar Footer - User Info */}
          <div className="p-2 border-t">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className={`w-full justify-start ${sidebarCollapsed ? 'px-3' : ''}`}>
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={user.picture?.startsWith('/') ? BACKEND_URL + user.picture : user.picture} />
                    <AvatarFallback className="text-xs">{user.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  {!sidebarCollapsed && (
                    <span className="ml-2 truncate text-sm">{user.name}</span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="start" className="w-56">
                <div className="px-2 py-2 border-b">
                  <p className="font-semibold text-sm">{user.name}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="h-4 w-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/support')}>
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Support
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </aside>
        
        {/* Main Content Area */}
        <div className="flex-1 flex flex-col">
      {/* Navbar (for mobile + header on desktop) */}
      <nav className="glass sticky top-0 z-50 md:hidden" data-tour="navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="font-heading text-2xl font-bold text-primary">TheFeedingFaith</div>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/dashboard">
                <Button variant="ghost" data-testid="nav-dashboard-btn">Dashboard</Button>
              </Link>
              <Link to="/courses">
                <Button variant="ghost" data-testid="nav-courses-btn">Courses</Button>
              </Link>
              <Link to="/study">
                <Button variant="ghost" data-testid="nav-study-btn">
                  <BookOpen className="h-4 w-4 mr-1" />
                  Bible Study
                </Button>
              </Link>
              <Link to="/calendar">
                <Button variant="ghost" data-testid="nav-calendar-btn">Calendar</Button>
              </Link>
              {user.role === 'mentee' && (
                <Link to="/mentors">
                  <Button variant="ghost" data-testid="nav-find-mentors-btn">Find Mentors</Button>
                </Link>
              )}
              <Link to="/store">
                <Button variant="ghost" data-testid="nav-store-btn">
                  <ShoppingBag className="h-4 w-4 mr-1" />
                  Store
                </Button>
              </Link>
              {(user.role === 'admin' || user.role === 'pastor') && (
                <Link to="/admin">
                  <Button variant="ghost" className="text-purple-600 dark:text-purple-400" data-testid="nav-admin-btn">
                    <Settings className="h-4 w-4 mr-1" />
                    Admin
                  </Button>
                </Link>
              )}
              <div className="flex items-center gap-3">
                {/* Shopping Cart Icon */}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="relative"
                  onClick={() => navigate('/store')}
                  data-testid="nav-cart-btn"
                >
                  <ShoppingBag className="h-5 w-5" />
                  {cartItemCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-accent text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                      {cartItemCount > 9 ? '9+' : cartItemCount}
                    </span>
                  )}
                </Button>
                <ThemeToggleButton />
                
                {/* User Dropdown Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="user-menu-btn" data-tour="user-menu">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.picture?.startsWith('/') ? BACKEND_URL + user.picture : user.picture} />
                        <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <ChevronRight className="h-4 w-4 rotate-90" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-2 border-b">
                      <p className="font-semibold text-sm">{user.name}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                      {membership?.has_mentorship && (
                        <Badge className="mt-1 text-xs" variant="secondary">{membership.mentorship_plan_name}</Badge>
                      )}
                    </div>
                    <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                      <User className="h-4 w-4 mr-2" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/support')} data-testid="menu-support">
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Support
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/my-purchases')} data-testid="menu-purchases">
                      <Package className="h-4 w-4 mr-2" />
                      My Purchases
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={startTour} data-testid="menu-tour">
                      <Lightbulb className="h-4 w-4 mr-2" />
                      Take a Tour
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/knowledge-base')} data-testid="menu-help">
                      <HelpCircle className="h-4 w-4 mr-2" />
                      Help Center
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/subscribe')} data-testid="menu-upgrade" className="text-primary">
                      <Award className="h-4 w-4 mr-2" />
                      {membership?.has_mentorship ? 'Manage Plan' : 'Upgrade Membership'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X /> : <Menu />}
            </Button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass border-b"
          >
            <div className="px-4 py-4 space-y-2">
              <div className="flex items-center justify-between pb-2 border-b mb-2">
                <span className="text-sm text-muted-foreground">Theme</span>
                <ThemeToggleButton />
              </div>
              <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}>Dashboard</Button>
              <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/courses'); setMobileMenuOpen(false); }}>Courses</Button>
              <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/study'); setMobileMenuOpen(false); }}>
                <BookOpen className="h-4 w-4 mr-2" />
                Bible Study
              </Button>
              <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/calendar'); setMobileMenuOpen(false); }}>Calendar</Button>
              {user.role === 'mentee' && (
                <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/mentors'); setMobileMenuOpen(false); }}>Find Mentors</Button>
              )}
              <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/support'); setMobileMenuOpen(false); }}>Support</Button>
              <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/store'); setMobileMenuOpen(false); }}>
                <ShoppingBag className="h-4 w-4 mr-2" />
                Store {cartItemCount > 0 && `(${cartItemCount})`}
              </Button>
              <Button variant="ghost" className="w-full justify-start text-primary" onClick={() => { startTour(); setMobileMenuOpen(false); }}>
                <Lightbulb className="h-4 w-4 mr-2" />
                Take a Tour
              </Button>
              <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/my-purchases'); setMobileMenuOpen(false); }}>My Purchases</Button>
              <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/knowledge-base'); setMobileMenuOpen(false); }}>Knowledge Base</Button>
              <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }}>Profile</Button>
              {(user.role === 'admin' || user.role === 'pastor') && (
                <Button variant="ghost" className="w-full justify-start text-purple-600 dark:text-purple-400" onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}>
                  <Settings className="h-4 w-4 mr-2" />
                  Admin Dashboard
                </Button>
              )}
              <Button variant="ghost" className="w-full justify-start text-destructive" onClick={handleLogout}>Logout</Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dashboard Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-4xl font-bold mb-2" data-testid="dashboard-heading">Welcome back, {user.name}!</h1>
              <p className="text-muted-foreground">Here's what's happening with your mentorship journey.</p>
            </div>
            <div className="flex items-center gap-3">
              {/* Notification Bell */}
              <div className="relative">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleNotificationClick}
                  className="relative"
                  data-testid="notification-bell-btn"
                >
                  <Bell className="h-5 w-5" />
                  {notifications.total > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                      {notifications.total > 99 ? '99+' : notifications.total}
                    </span>
                  )}
                </Button>
                
                {/* Notification Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 top-12 w-80 bg-white rounded-lg shadow-xl border z-50 max-h-96 overflow-hidden" data-testid="notification-dropdown">
                    <div className="p-3 border-b bg-gray-50">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold">Notifications</h3>
                        <button onClick={() => setShowNotifications(false)} className="text-gray-400 hover:text-gray-600">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="flex gap-2 mt-2 text-xs">
                        {notifications.courses > 0 && (
                          <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                            {notifications.courses} Courses
                          </Badge>
                        )}
                        {notifications.tasks > 0 && (
                          <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                            {notifications.tasks} Tasks
                          </Badge>
                        )}
                        {notifications.goals > 0 && (
                          <Badge variant="secondary" className="bg-green-100 text-green-700">
                            {notifications.goals} Goals
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="overflow-y-auto max-h-72">
                      {notificationDetails.length === 0 ? (
                        <div className="p-4 text-center text-muted-foreground">
                          <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                          <p>You're all caught up!</p>
                        </div>
                      ) : (
                        notificationDetails.map((notif, idx) => (
                          <div 
                            key={idx} 
                            className="p-3 border-b hover:bg-gray-50 cursor-pointer"
                            onClick={() => {
                              setShowNotifications(false);
                              if (notif.type === 'course') navigate('/my-courses');
                              else if (notif.type === 'task') navigate('/dashboard');
                              else if (notif.type === 'goal') navigate('/dashboard');
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                notif.type === 'course' ? 'bg-blue-100 text-blue-600' :
                                notif.type === 'task' ? 'bg-orange-100 text-orange-600' :
                                'bg-green-100 text-green-600'
                              }`}>
                                {notif.type === 'course' && <BookOpen className="h-4 w-4" />}
                                {notif.type === 'task' && <CheckCircle className="h-4 w-4" />}
                                {notif.type === 'goal' && <Target className="h-4 w-4" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{notif.title}</p>
                                <p className="text-xs text-muted-foreground">
                                  {notif.type.charAt(0).toUpperCase() + notif.type.slice(1)} • 
                                  {notif.status === 'assigned' ? ' New' : ` ${notif.status?.replace('_', ' ')}`}
                                </p>
                                {notif.due_date && (
                                  <p className="text-xs text-orange-600">
                                    Due: {new Date(notif.due_date).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    <div className="p-2 border-t bg-gray-50">
                      <Button variant="ghost" size="sm" className="w-full" onClick={() => { setShowNotifications(false); navigate('/my-courses'); }}>
                        View All Assignments
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              
              <Button
                variant={showAnalytics ? 'default' : 'outline'}
                onClick={() => setShowAnalytics(!showAnalytics)}
                data-testid="toggle-analytics-btn"
              >
                {showAnalytics ? <><Activity className="h-4 w-4 mr-2" /> Hide Analytics</> : <><BarChart3 className="h-4 w-4 mr-2" /> Show Analytics</>}
              </Button>
            </div>
          </div>
        </div>

        {/* Announcements Banner */}
        {dashboardAnnouncements.length > 0 && (
          <div className="mb-8" data-testid="announcements-banner">
            <Card className="glass-card border-l-4 border-l-primary bg-gradient-to-r from-primary/5 to-transparent">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 rounded-full p-2">
                    <Bell className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-lg">Church Announcements</h3>
                      {dashboardAnnouncements.length > 1 && (
                        <Badge variant="secondary">{dashboardAnnouncements.length} announcements</Badge>
                      )}
                    </div>
                    <div className="space-y-3">
                      {dashboardAnnouncements.slice(0, 3).map((announcement) => (
                        <div 
                          key={announcement.announcement_id} 
                          className={`p-3 rounded-lg ${
                            announcement.priority === 'urgent' ? 'bg-red-50 border border-red-200' :
                            announcement.priority === 'high' ? 'bg-amber-50 border border-amber-200' :
                            'bg-white/50'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium">{announcement.title}</h4>
                            {announcement.priority === 'urgent' && (
                              <Badge className="bg-red-500 text-white text-xs">Urgent</Badge>
                            )}
                            {announcement.priority === 'high' && (
                              <Badge className="bg-amber-500 text-white text-xs">Important</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{announcement.content}</p>
                          <p className="text-xs text-muted-foreground mt-2">
                            Posted by {announcement.created_by_name} • {new Date(announcement.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Stats Grid - Role-based */}
        {user?.role !== 'member' ? (
          /* Mentorship Stats for mentors/mentees */
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-tour="mentorship-stats">
            <Card 
              className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105" 
              onClick={() => setDrillDownType('mentorships')}
              data-testid="stat-mentorships"
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">
                  Active Mentorships
                  <Users className="h-4 w-4" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold font-heading text-primary">{mentorships.filter(m => m.status === 'active').length}</div>
                <p className="text-xs text-muted-foreground mt-1">Click to view details</p>
              </CardContent>
            </Card>
            <Card 
              className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105" 
              onClick={() => setDrillDownType('sessions')}
              data-testid="stat-sessions"
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">
                  Upcoming Sessions
                  <CalendarIcon className="h-4 w-4" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold font-heading text-accent">{sessions.filter(s => s.status === 'scheduled').length}</div>
                <p className="text-xs text-muted-foreground mt-1">Click to view details</p>
              </CardContent>
            </Card>
            <Card 
              className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105" 
              onClick={() => setDrillDownType('goals')}
              data-testid="stat-goals"
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">
                  Active Goals
                  <Target className="h-4 w-4" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold font-heading text-primary">{goals.filter(g => g.status === 'in_progress').length}</div>
                <p className="text-xs text-muted-foreground mt-1">Click to view details</p>
              </CardContent>
            </Card>
            <Card 
              className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105" 
              onClick={() => setDrillDownType('tasks')}
              data-testid="stat-requests"
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">
                  Pending Requests
                  <MessageCircle className="h-4 w-4" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold font-heading text-accent">{requests.filter(r => r.status === 'pending').length}</div>
                <p className="text-xs text-muted-foreground mt-1">Click to review</p>
              </CardContent>
            </Card>
          </div>
        ) : (
          /* Member Stats - Church focused */
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card 
              className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105" 
              onClick={() => navigate('/giving')}
              data-testid="stat-giving"
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">
                  Give to Church
                  <Heart className="h-4 w-4" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xl font-bold font-heading text-primary">Support Ministry</div>
                <p className="text-xs text-muted-foreground mt-1">Click to give</p>
              </CardContent>
            </Card>
            <Card 
              className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105" 
              onClick={() => navigate('/sermons')}
              data-testid="stat-devotions"
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">
                  Daily Devotions
                  <FileText className="h-4 w-4" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xl font-bold font-heading text-accent">Watch Sermons</div>
                <p className="text-xs text-muted-foreground mt-1">Click to view</p>
              </CardContent>
            </Card>
            <Card 
              className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105" 
              onClick={() => navigate('/community')}
              data-testid="stat-community"
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">
                  Community
                  <Users className="h-4 w-4" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xl font-bold font-heading text-blue-500">Connect</div>
                <p className="text-xs text-muted-foreground mt-1">Click to join</p>
              </CardContent>
            </Card>
            <Card 
              className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105" 
              onClick={() => navigate('/prayer-requests')}
              data-testid="stat-prayer"
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">
                  Prayer Requests
                  <Heart className="h-4 w-4" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xl font-bold font-heading text-purple-500">Share & Pray</div>
                <p className="text-xs text-muted-foreground mt-1">Click to pray</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quick Links Section - Role-based */}
        <div className="mb-8" data-tour="quick-actions">
          <h2 className="font-heading text-xl font-semibold mb-4">Quick Actions</h2>
          
          {/* Membership Banner for non-elite users */}
          {!hasModuleAccess('budget') && user?.role === 'member' && (
            <Card className="glass-card mb-4 bg-gradient-to-r from-primary/5 to-accent/5 border-primary/20">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Award className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">Upgrade to Elite</p>
                    <p className="text-xs text-muted-foreground">Get access to Budget, Business Plan & more</p>
                  </div>
                </div>
                <Button size="sm" onClick={() => navigate('/subscribe')} data-testid="upgrade-membership-btn">
                  Upgrade Now
                </Button>
              </CardContent>
            </Card>
          )}
          
          {user?.role !== 'member' ? (
            <>
            {/* Mentorship Quick Actions */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-primary/10 to-primary/5"
                onClick={() => navigate('/checkin')}
                data-testid="quick-checkin-btn"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary/20 flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-primary" />
                  </div>
                  <div className="font-semibold">Church Check-In</div>
                  <p className="text-xs text-muted-foreground mt-1">Mark attendance</p>
                </CardContent>
              </Card>
              {hasModuleAccess('ai_tools') && (
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-accent/10 to-accent/5"
                onClick={() => navigate('/ai-tools')}
                data-testid="quick-ai-tools-btn"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent/20 flex items-center justify-center">
                    <FileText className="h-6 w-6 text-accent" />
                  </div>
                  <div className="font-semibold">AI Tools</div>
                  <p className="text-xs text-muted-foreground mt-1">Resume & Business</p>
                </CardContent>
              </Card>
              )}
              {hasModuleAccess('leadership_quiz') && (
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-blue-500/10 to-blue-500/5"
                onClick={() => navigate('/leadership-assessment')}
                data-testid="quick-leadership-btn"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-blue-500" />
                  </div>
                  <div className="font-semibold">Leadership Quiz</div>
                  <p className="text-xs text-muted-foreground mt-1">Assess your style</p>
                </CardContent>
              </Card>
              )}
              {hasModuleAccess('prayer_requests') && (
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-purple-500/10 to-purple-500/5"
                onClick={() => navigate('/prayer-requests')}
                data-testid="quick-prayer-btn"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Heart className="h-6 w-6 text-purple-500" />
                  </div>
                  <div className="font-semibold">Prayer Requests</div>
                  <p className="text-xs text-muted-foreground mt-1">Share & pray</p>
                </CardContent>
              </Card>
              )}
              {hasModuleAccess('counseling') && (
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-orange-500/10 to-orange-500/5"
                onClick={() => navigate('/counseling')}
                data-testid="quick-counseling-btn"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <MessageCircle className="h-6 w-6 text-orange-500" />
                  </div>
                  <div className="font-semibold">{user?.role === 'pastor' || user?.role === 'admin' ? 'Counseling Requests' : 'Connect with Pastor'}</div>
                  <p className="text-xs text-muted-foreground mt-1">{user?.role === 'pastor' || user?.role === 'admin' ? 'Manage requests' : 'Request counseling'}</p>
                </CardContent>
              </Card>
              )}
              {/* Weekly Focus Management - Pastor/Admin only */}
              {['pastor', 'admin'].includes(user?.role) && (
                <Card 
                  className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5"
                  onClick={() => navigate('/weekly-focus-manage')}
                  data-testid="quick-weekly-focus-btn"
                >
                  <CardContent className="pt-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Target className="h-6 w-6 text-emerald-500" />
                    </div>
                    <div className="font-semibold">Weekly Focus</div>
                    <p className="text-xs text-muted-foreground mt-1">Update focus message</p>
                  </CardContent>
                </Card>
              )}
            </div>
            {/* Second Row - Gamification & Exports */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-yellow-500/10 to-yellow-500/5"
                onClick={() => navigate('/badges')}
                data-testid="quick-badges-btn"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <Award className="h-6 w-6 text-yellow-500" />
                  </div>
                  <div className="font-semibold">My Badges</div>
                  <p className="text-xs text-muted-foreground mt-1">View achievements</p>
                </CardContent>
              </Card>
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-indigo-500/10 to-indigo-500/5"
                onClick={() => navigate('/leaderboard')}
                data-testid="quick-leaderboard-btn"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-indigo-500/20 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-indigo-500" />
                  </div>
                  <div className="font-semibold">Leaderboard</div>
                  <p className="text-xs text-muted-foreground mt-1">Top achievers</p>
                </CardContent>
              </Card>
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-teal-500/10 to-teal-500/5"
                onClick={() => navigate('/my-exports')}
                data-testid="quick-exports-btn"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-teal-500/20 flex items-center justify-center">
                    <Download className="h-6 w-6 text-teal-500" />
                  </div>
                  <div className="font-semibold">My Exports</div>
                  <p className="text-xs text-muted-foreground mt-1">Download reports</p>
                </CardContent>
              </Card>
              {hasModuleAccess('ministry_groups') && (
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-pink-500/10 to-pink-500/5"
                onClick={() => navigate('/ministry-groups')}
                data-testid="quick-groups-btn"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-pink-500/20 flex items-center justify-center">
                    <Users className="h-6 w-6 text-pink-500" />
                  </div>
                  <div className="font-semibold">Ministry Groups</div>
                  <p className="text-xs text-muted-foreground mt-1">Join a group</p>
                </CardContent>
              </Card>
              )}
              {hasModuleAccess('budget') && (
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-green-500/10 to-green-500/5"
                onClick={() => navigate('/budget')}
                data-testid="quick-budget-btn"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-500/20 flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-green-500" />
                  </div>
                  <div className="font-semibold">Budget Tracker</div>
                  <p className="text-xs text-muted-foreground mt-1">Track finances</p>
                </CardContent>
              </Card>
              )}
              {hasModuleAccess('business_plan') && (
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-violet-500/10 to-violet-500/5"
                onClick={() => navigate('/ai-tools')}
                data-testid="quick-business-plan-btn"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-violet-500/20 flex items-center justify-center">
                    <Briefcase className="h-6 w-6 text-violet-500" />
                  </div>
                  <div className="font-semibold">Business Plan</div>
                  <p className="text-xs text-muted-foreground mt-1">AI Generator</p>
                </CardContent>
              </Card>
              )}
              {['admin', 'pastor', 'mentor'].includes(user?.role) && (
                <Card 
                  className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-cyan-500/10 to-cyan-500/5"
                  onClick={() => navigate('/course-assignments')}
                  data-testid="quick-course-assignments-btn"
                >
                  <CardContent className="pt-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-cyan-500/20 flex items-center justify-center">
                      <BookOpen className="h-6 w-6 text-cyan-500" />
                    </div>
                    <div className="font-semibold">Course Assign</div>
                    <p className="text-xs text-muted-foreground mt-1">Assign courses</p>
                  </CardContent>
                </Card>
              )}
            </div>
            </>
          ) : (
            <>
            {/* Member Quick Actions - No mentorship items */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-green-500/10 to-green-500/5"
                onClick={() => navigate('/giving')}
                data-testid="quick-give-btn"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-500/20 flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-green-500" />
                  </div>
                  <div className="font-semibold">Give</div>
                  <p className="text-xs text-muted-foreground mt-1">Support ministry</p>
                </CardContent>
              </Card>
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-primary/10 to-primary/5"
                onClick={() => navigate('/sermons')}
                data-testid="quick-devotions-btn"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary/20 flex items-center justify-center">
                    <FileText className="h-6 w-6 text-primary" />
                  </div>
                  <div className="font-semibold">Daily Devotions</div>
                  <p className="text-xs text-muted-foreground mt-1">Watch sermons</p>
                </CardContent>
              </Card>
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-blue-500/10 to-blue-500/5"
                onClick={() => navigate('/community')}
                data-testid="quick-community-btn"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Users className="h-6 w-6 text-blue-500" />
                  </div>
                  <div className="font-semibold">Community</div>
                  <p className="text-xs text-muted-foreground mt-1">Connect with others</p>
                </CardContent>
              </Card>
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-accent/10 to-accent/5"
                onClick={() => navigate('/counseling')}
                data-testid="quick-pastor-btn"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent/20 flex items-center justify-center">
                    <MessageCircle className="h-6 w-6 text-accent" />
                  </div>
                  <div className="font-semibold">Connect with Pastor</div>
                  <p className="text-xs text-muted-foreground mt-1">Request counseling</p>
                </CardContent>
              </Card>
            </div>
            {/* Second Row for Members - Gamification */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-yellow-500/10 to-yellow-500/5"
                onClick={() => navigate('/badges')}
                data-testid="member-badges-btn"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <Award className="h-6 w-6 text-yellow-500" />
                  </div>
                  <div className="font-semibold">My Badges</div>
                  <p className="text-xs text-muted-foreground mt-1">View achievements</p>
                </CardContent>
              </Card>
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-indigo-500/10 to-indigo-500/5"
                onClick={() => navigate('/leaderboard')}
                data-testid="member-leaderboard-btn"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-indigo-500/20 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-indigo-500" />
                  </div>
                  <div className="font-semibold">Leaderboard</div>
                  <p className="text-xs text-muted-foreground mt-1">Top achievers</p>
                </CardContent>
              </Card>
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-teal-500/10 to-teal-500/5"
                onClick={() => navigate('/my-exports')}
                data-testid="member-exports-btn"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-teal-500/20 flex items-center justify-center">
                    <Download className="h-6 w-6 text-teal-500" />
                  </div>
                  <div className="font-semibold">My Exports</div>
                  <p className="text-xs text-muted-foreground mt-1">Download reports</p>
                </CardContent>
              </Card>
              <Card 
                className="glass-card cursor-pointer hover:shadow-lg transition-all hover:scale-105 bg-gradient-to-br from-pink-500/10 to-pink-500/5"
                onClick={() => navigate('/ministry-groups')}
                data-testid="member-groups-btn"
              >
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-pink-500/20 flex items-center justify-center">
                    <Users className="h-6 w-6 text-pink-500" />
                  </div>
                  <div className="font-semibold">Ministry Groups</div>
                  <p className="text-xs text-muted-foreground mt-1">Join a group</p>
                </CardContent>
              </Card>
            </div>
            </>
          )}
        </div>

        {/* Analytics Dashboard */}
        {showAnalytics && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-8"
          >
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="font-heading text-2xl flex items-center gap-2">
                  <TrendingUp className="h-6 w-6" />
                  Analytics Dashboard
                </CardTitle>
                <CardDescription>Visual insights into your mentorship activity</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  {/* Tasks Breakdown */}
                  <Card className="p-4">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <PieChart className="h-4 w-4" />
                      Tasks Breakdown
                    </h3>
                    {tasks.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">No tasks yet</p>
                    ) : (
                      <ResponsiveContainer width="100%" height={250}>
                        <RechartsPie>
                          <Pie
                            data={tasksChartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, value }) => `${name}: ${value}`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {tasksChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </RechartsPie>
                      </ResponsiveContainer>
                    )}
                  </Card>

                  {/* Goals Progress */}
                  <Card className="p-4">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      Goals Progress
                    </h3>
                    {goals.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">No goals yet</p>
                    ) : (
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={goalsProgressData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="progress" fill="#0F392B" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </Card>
                </div>

                {/* Mentorship Activity */}
                {mentorshipActivityData.length > 0 && (
                  <Card className="p-4">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      Activity by Mentorship
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={mentorshipActivityData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="tasks" fill="#0F392B" name="Tasks" />
                        <Bar dataKey="goals" fill="#D97706" name="Goals" />
                        <Bar dataKey="sessions" fill="#94a3b8" name="Sessions" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Filter Indicator */}
        {selectedFilter !== 'all' && (
          <div className="mb-4 flex items-center gap-2">
            <Badge className="bg-primary">Filtered: {selectedFilter}</Badge>
            <Button variant="ghost" size="sm" onClick={() => setSelectedFilter('all')}>
              Clear Filter
            </Button>
          </div>
        )}

        <Tabs defaultValue={selectedFilter === 'all' ? 'mentorships' : selectedFilter} value={selectedFilter === 'all' ? undefined : selectedFilter} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="mentorships" onClick={() => setSelectedFilter('mentorships')} data-testid="tab-mentorships">Mentorships</TabsTrigger>
            <TabsTrigger value="sessions" onClick={() => setSelectedFilter('sessions')} data-testid="tab-sessions">Sessions</TabsTrigger>
            <TabsTrigger value="goals" onClick={() => setSelectedFilter('goals')} data-testid="tab-goals">Goals</TabsTrigger>
            <TabsTrigger value="requests" onClick={() => setSelectedFilter('requests')} data-testid="tab-requests">Requests</TabsTrigger>
          </TabsList>

          <TabsContent value="mentorships">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {mentorships.length === 0 ? (
                <Card className="glass-card md:col-span-2">
                  <CardContent className="py-12 text-center">
                    <p className="text-muted-foreground">No mentorships yet.</p>
                    {user.role === 'mentee' && (
                      <Button className="mt-4 bg-accent hover:bg-accent/90" onClick={() => navigate('/mentors')} data-testid="find-mentors-btn">
                        Find Mentors
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                mentorships.map((mentorship) => (
                  <Card key={mentorship.mentorship_id} className="glass-card hover:shadow-lg transition-shadow" data-testid="mentorship-card">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="font-heading">Mentorship</CardTitle>
                          <CardDescription>Started {new Date(mentorship.started_at).toLocaleDateString()}</CardDescription>
                        </div>
                        <Badge className="bg-primary">{mentorship.status}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Button className="w-full" onClick={() => navigate(`/mentorship/${mentorship.mentorship_id}`)} data-testid="view-mentorship-btn">
                        View Details
                      </Button>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="sessions">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="font-heading">Upcoming Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                {sessions.filter(s => s.status === 'scheduled').length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No upcoming sessions</p>
                ) : (
                  <div className="space-y-4">
                    {sessions
                      .filter(s => s.status === 'scheduled')
                      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
                      .map((session) => (
                        <div key={session.session_id} className="flex items-center justify-between p-4 rounded-lg bg-secondary/20 hover:bg-secondary/40 transition-colors" data-testid="session-item">
                          <div>
                            <div className="font-semibold">{session.title}</div>
                            <div className="text-sm text-muted-foreground">
                              {new Date(session.scheduled_at).toLocaleString()} • {session.duration_minutes} min
                            </div>
                          </div>
                          <Calendar className="h-5 w-5 text-muted-foreground" />
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="goals">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {goals.length === 0 ? (
                <Card className="glass-card md:col-span-2">
                  <CardContent className="py-12 text-center">
                    <p className="text-muted-foreground">No goals yet.</p>
                    {user.role === 'mentee' && (
                      <Button className="mt-4 bg-accent hover:bg-accent/90" onClick={() => navigate('/mentors')} data-testid="find-mentors-btn">
                        Find Mentors
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <>
                  {goals.map((goal) => (
                    <Card key={goal.goal_id} className="glass-card hover:shadow-lg transition-shadow cursor-pointer" data-testid="goal-card">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <CardTitle className="font-heading text-lg">{goal.title}</CardTitle>
                          <Badge variant={goal.status === 'completed' ? 'default' : 'secondary'}>{goal.status}</Badge>
                        </div>
                        {goal.description && <CardDescription>{goal.description}</CardDescription>}
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Progress</span>
                            <span className="font-semibold">{goal.progress}%</span>
                          </div>
                          <Progress value={goal.progress} className="h-2" />
                          {goal.target_date && (
                            <div className="text-sm text-muted-foreground">
                              Target: {new Date(goal.target_date).toLocaleDateString()}
                            </div>
                          )}
                          <div className="flex gap-1 mt-3">
                            {[0, 25, 50, 75, 100].map((val) => (
                              <div
                                key={val}
                                className={`flex-1 h-2 rounded ${
                                  goal.progress >= val ? 'bg-primary' : 'bg-gray-200'
                                }`}
                              ></div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {/* Goals Summary Card */}
                  <Card className="glass-card md:col-span-2 bg-gradient-to-br from-primary/5 to-accent/5">
                    <CardHeader>
                      <CardTitle className="font-heading flex items-center gap-2">
                        <Target className="h-5 w-5" />
                        Goals Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-4 gap-4">
                        <div className="text-center">
                          <div className="text-3xl font-bold text-primary">{goals.length}</div>
                          <div className="text-sm text-muted-foreground">Total Goals</div>
                        </div>
                        <div className="text-center">
                          <div className="text-3xl font-bold text-accent">{goals.filter(g => g.status === 'in_progress').length}</div>
                          <div className="text-sm text-muted-foreground">In Progress</div>
                        </div>
                        <div className="text-center">
                          <div className="text-3xl font-bold text-green-600">{goals.filter(g => g.status === 'completed').length}</div>
                          <div className="text-sm text-muted-foreground">Completed</div>
                        </div>
                        <div className="text-center">
                          <div className="text-3xl font-bold text-primary">
                            {goals.length > 0 ? Math.round(goals.reduce((acc, g) => acc + g.progress, 0) / goals.length) : 0}%
                          </div>
                          <div className="text-sm text-muted-foreground">Avg Progress</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="requests">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {requests.length === 0 ? (
                <Card className="glass-card md:col-span-2">
                  <CardContent className="py-12 text-center">
                    <p className="text-muted-foreground">No requests</p>
                  </CardContent>
                </Card>
              ) : (
                requests.map((request) => (
                  <Card key={request.request_id} className="glass-card" data-testid="request-card">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <CardTitle className="font-heading text-lg">
                          {user.role === 'mentor' ? 'Mentorship Request' : 'Your Request'}
                        </CardTitle>
                        <Badge
                          variant={request.status === 'pending' ? 'secondary' : request.status === 'accepted' ? 'default' : 'destructive'}
                        >
                          {request.status}
                        </Badge>
                      </div>
                      <CardDescription>{request.message}</CardDescription>
                    </CardHeader>
                    {user.role === 'mentor' && request.status === 'pending' && (
                      <CardContent>
                        <div className="flex gap-2">
                          <Button className="flex-1 bg-primary hover:bg-primary/90" onClick={() => handleAcceptRequest(request.request_id)} data-testid="accept-request-btn">
                            Accept
                          </Button>
                          <Button variant="outline" className="flex-1" onClick={() => handleRejectRequest(request.request_id)} data-testid="reject-request-btn">
                            Reject
                          </Button>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Drill-down Dialog */}
      {drillDownType && (
        <DashboardDrillDown type={drillDownType} onClose={() => setDrillDownType(null)} />
      )}

      {/* Floating Help Button */}
      <div className="fixed bottom-6 right-6 z-40" data-tour="help-button">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              className="h-14 w-14 rounded-full shadow-lg bg-primary hover:bg-primary/90"
              data-testid="floating-help-btn"
            >
              <HelpCircle className="h-6 w-6" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 mb-2">
            <DropdownMenuItem onClick={startTour}>
              <Lightbulb className="h-4 w-4 mr-2" />
              Take a Tour
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/knowledge-base')}>
              <BookOpen className="h-4 w-4 mr-2" />
              Knowledge Base
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/support')}>
              <MessageCircle className="h-4 w-4 mr-2" />
              Contact Support
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
        </div>
      </div>
    </div>
  );
}

// ==================== LMS COMPONENTS ====================

function CourseCatalog() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [user, setUser] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [coursesRes, userRes] = await Promise.all([
        axiosInstance.get('/courses'),
        axiosInstance.get('/auth/me').catch(() => null)
      ]);
      setCourses(coursesRes.data);
      if (userRes) {
        setUser(userRes.data);
        const enrollRes = await axiosInstance.get('/enrollments');
        setEnrollments(enrollRes.data);
      }
    } catch (error) {
      console.error('Error loading courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEnrollmentStatus = (courseId) => {
    return enrollments.find(e => e.course_id === courseId);
  };

  const filteredCourses = courses.filter(course => {
    if (filter === 'all') return true;
    if (filter === 'enrolled') return getEnrollmentStatus(course.course_id);
    if (filter === 'completed') {
      const enrollment = getEnrollmentStatus(course.course_id);
      return enrollment && enrollment.status === 'completed';
    }
    return course.category === filter;
  });

  const categories = [...new Set(courses.map(c => c.category))];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl font-bold mb-2" data-testid="courses-heading">Learning Center</h1>
            <p className="text-muted-foreground">Explore courses and expand your knowledge</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
              ← Dashboard
            </Button>
            {(user?.role === 'admin' || user?.role === 'mentor') && (
              <Button onClick={() => navigate('/courses/create')} data-testid="create-course-btn">
                <Plus className="h-4 w-4 mr-2" /> Create Course
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <Button 
            variant={filter === 'all' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setFilter('all')}
          >
            All Courses
          </Button>
          {user && (
            <>
              <Button 
                variant={filter === 'enrolled' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setFilter('enrolled')}
              >
                My Courses
              </Button>
              <Button 
                variant={filter === 'completed' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setFilter('completed')}
              >
                Completed
              </Button>
            </>
          )}
          {categories.map(cat => (
            <Button 
              key={cat}
              variant={filter === cat ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setFilter(cat)}
            >
              {cat}
            </Button>
          ))}
        </div>

        {/* Course Grid */}
        {filteredCourses.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <p className="text-muted-foreground">No courses found</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map(course => {
              const enrollment = getEnrollmentStatus(course.course_id);
              return (
                <Card 
                  key={course.course_id} 
                  className="glass-card hover:shadow-lg transition-all cursor-pointer overflow-hidden"
                  onClick={() => navigate(`/courses/${course.course_id}`)}
                  data-testid="course-card"
                >
                  <div className="h-40 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                    {course.thumbnail_url ? (
                      <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-6xl">📚</div>
                    )}
                  </div>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline">{course.category}</Badge>
                      <Badge className={
                        course.difficulty === 'beginner' ? 'bg-green-500' :
                        course.difficulty === 'intermediate' ? 'bg-yellow-500' : 'bg-red-500'
                      }>
                        {course.difficulty}
                      </Badge>
                    </div>
                    <CardTitle className="font-heading line-clamp-2">{course.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{course.description}</p>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>{course.total_lessons} lessons</span>
                      <span>{course.total_duration_minutes} min</span>
                    </div>
                    {enrollment && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span>Progress</span>
                          <span>{enrollment.progress_percent}%</span>
                        </div>
                        <Progress value={enrollment.progress_percent} className="h-2" />
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function CourseDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const courseId = location.pathname.split('/').pop();
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('lessons');
  const [discussions, setDiscussions] = useState([]);
  const [newDiscussion, setNewDiscussion] = useState('');

  useEffect(() => {
    loadCourse();
  }, [courseId]);

  const loadCourse = async () => {
    try {
      const [courseRes, userRes] = await Promise.all([
        axiosInstance.get(`/courses/${courseId}`),
        axiosInstance.get('/auth/me').catch(() => null)
      ]);
      setCourse(courseRes.data);
      if (userRes) setUser(userRes.data);
      
      // Load discussions
      const discRes = await axiosInstance.get(`/discussions?course_id=${courseId}`);
      setDiscussions(discRes.data);
    } catch (error) {
      console.error('Error loading course:', error);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    setEnrolling(true);
    try {
      await axiosInstance.post('/enrollments', { course_id: courseId });
      toast.success('Enrolled successfully!');
      loadCourse();
    } catch (error) {
      toast.error('Failed to enroll');
    } finally {
      setEnrolling(false);
    }
  };

  const handlePostDiscussion = async (e) => {
    e.preventDefault();
    if (!newDiscussion.trim()) return;
    
    try {
      await axiosInstance.post('/discussions', {
        course_id: courseId,
        content: newDiscussion
      });
      setNewDiscussion('');
      loadCourse();
      toast.success('Posted!');
    } catch (error) {
      toast.error('Failed to post');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 text-center">
          <p className="text-muted-foreground mb-4">Course not found</p>
          <Button onClick={() => navigate('/courses')}>Back to Courses</Button>
        </Card>
      </div>
    );
  }

  const isEnrolled = course.enrollment;
  const isCompleted = isEnrolled && isEnrolled.status === 'completed';

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" onClick={() => navigate('/courses')}>
            ← Back to Courses
          </Button>
          {user && (user.role === 'admin' || course.created_by === user.user_id) && (
            <Button variant="outline" onClick={() => navigate(`/courses/${courseId}/edit`)} data-testid="edit-course-btn">
              <Settings className="h-4 w-4 mr-2" /> Edit Course
            </Button>
          )}
        </div>

        {/* Course Header */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          <div className="lg:col-span-2">
            <div className="flex gap-2 mb-4">
              <Badge variant="outline">{course.category}</Badge>
              <Badge className={
                course.difficulty === 'beginner' ? 'bg-green-500' :
                course.difficulty === 'intermediate' ? 'bg-yellow-500' : 'bg-red-500'
              }>
                {course.difficulty}
              </Badge>
            </div>
            <h1 className="font-heading text-4xl font-bold mb-4" data-testid="course-title">{course.title}</h1>
            <p className="text-muted-foreground mb-6">{course.description}</p>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><FileText className="h-4 w-4" /> {course.total_lessons} lessons</span>
              <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {course.total_duration_minutes} min</span>
              <span className="flex items-center gap-1"><Users className="h-4 w-4" /> {course.enrolled_count} enrolled</span>
            </div>
          </div>
          
          <Card className="glass-card">
            <CardContent className="p-6">
              {isEnrolled ? (
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span>Your Progress</span>
                      <span className="font-semibold">{isEnrolled.progress_percent}%</span>
                    </div>
                    <Progress value={isEnrolled.progress_percent} className="h-3" />
                  </div>
                  {isCompleted ? (
                    <Button className="w-full" onClick={() => navigate(`/certificates/${courseId}`)} data-testid="view-certificate-btn">
                      <CheckCircle className="h-4 w-4 mr-2" /> View Certificate
                    </Button>
                  ) : (
                    <Button className="w-full" onClick={() => {
                      const firstIncomplete = course.lessons?.find(l => {
                        const progress = isEnrolled.completed_lessons_ids || [];
                        return !progress.includes(l.lesson_id);
                      });
                      if (firstIncomplete) {
                        navigate(`/lessons/${firstIncomplete.lesson_id}`);
                      } else if (course.lessons?.length > 0) {
                        navigate(`/lessons/${course.lessons[0].lesson_id}`);
                      }
                    }} data-testid="continue-learning-btn">
                      Continue Learning
                    </Button>
                  )}
                </div>
              ) : (
                <Button className="w-full" onClick={handleEnroll} disabled={enrolling} data-testid="enroll-btn">
                  {enrolling ? 'Enrolling...' : 'Enroll Now - Free'}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="lessons">Lessons</TabsTrigger>
            <TabsTrigger value="discussions">Discussions ({discussions.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="lessons">
            <div className="space-y-3">
              {course.lessons?.map((lesson, index) => (
                <Card 
                  key={lesson.lesson_id} 
                  className={`glass-card hover:shadow-md transition-all cursor-pointer ${!isEnrolled ? 'opacity-75' : ''}`}
                  onClick={() => isEnrolled && navigate(`/lessons/${lesson.lesson_id}`)}
                  data-testid="lesson-item"
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-semibold">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold">{lesson.title}</div>
                      <div className="text-sm text-muted-foreground flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          {lesson.content_type === 'video' && <Video className="h-3 w-3" />}
                          {lesson.content_type === 'document' && <FileText className="h-3 w-3" />}
                          {lesson.content_type === 'quiz' && <Target className="h-3 w-3" />}
                          {lesson.content_type}
                        </span>
                        <span>{lesson.duration_minutes} min</span>
                      </div>
                    </div>
                    {!isEnrolled && <Badge variant="outline">Enroll to access</Badge>}
                  </CardContent>
                </Card>
              ))}
              {(!course.lessons || course.lessons.length === 0) && (
                <Card className="glass-card p-8 text-center">
                  <p className="text-muted-foreground">No lessons yet</p>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="discussions">
            {user && (
              <Card className="glass-card mb-6">
                <CardContent className="p-4">
                  <form onSubmit={handlePostDiscussion} className="flex gap-2">
                    <Input
                      placeholder="Start a discussion..."
                      value={newDiscussion}
                      onChange={(e) => setNewDiscussion(e.target.value)}
                      data-testid="discussion-input"
                    />
                    <Button type="submit">Post</Button>
                  </form>
                </CardContent>
              </Card>
            )}
            <div className="space-y-4">
              {discussions.map(disc => (
                <Card key={disc.discussion_id} className="glass-card">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback>{disc.user_name?.[0] || 'U'}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold">{disc.user_name}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(disc.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm">{disc.content}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                          <button className="hover:text-primary">❤️ {disc.likes}</button>
                          <span>{disc.replies_count} replies</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {discussions.length === 0 && (
                <Card className="glass-card p-8 text-center">
                  <p className="text-muted-foreground">No discussions yet. Be the first to start one!</p>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ==================== LESSON COLLABORATION (Notes & Discussions) ====================

function LessonCollaboration({ lessonId, courseId }) {
  const [activeTab, setActiveTab] = useState('notes');
  const [notes, setNotes] = useState([]);
  const [discussions, setDiscussions] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [newDiscussion, setNewDiscussion] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [lessonId]);

  const loadData = async () => {
    try {
      const [notesRes, discussionsRes] = await Promise.all([
        axiosInstance.get(`/notes?lesson_id=${lessonId}`),
        axiosInstance.get(`/discussions?lesson_id=${lessonId}`)
      ]);
      setNotes(notesRes.data);
      setDiscussions(discussionsRes.data);
    } catch (error) {
      console.error('Error loading collaboration data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await axiosInstance.post('/notes', {
        content: newNote,
        lesson_id: lessonId,
        course_id: courseId,
        is_private: true
      });
      setNewNote('');
      loadData();
      toast.success('Note saved');
    } catch (error) {
      toast.error('Failed to save note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axiosInstance.delete(`/notes/${noteId}`);
      loadData();
      toast.success('Note deleted');
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleAddDiscussion = async () => {
    if (!newDiscussion.trim()) return;
    try {
      await axiosInstance.post('/discussions', {
        content: newDiscussion,
        lesson_id: lessonId,
        course_id: courseId
      });
      setNewDiscussion('');
      loadData();
      toast.success('Posted to discussion');
    } catch (error) {
      toast.error('Failed to post');
    }
  };

  const handleReply = async (parentId) => {
    if (!replyContent.trim()) return;
    try {
      await axiosInstance.post('/discussions', {
        content: replyContent,
        lesson_id: lessonId,
        course_id: courseId,
        parent_id: parentId
      });
      setReplyTo(null);
      setReplyContent('');
      loadData();
      toast.success('Reply posted');
    } catch (error) {
      toast.error('Failed to reply');
    }
  };

  const handleLike = async (discussionId) => {
    try {
      await axiosInstance.post(`/discussions/${discussionId}/like`);
      loadData();
    } catch (error) {
      toast.error('Failed to like');
    }
  };

  return (
    <div className="mt-8 border-t pt-8">
      <div className="flex gap-2 mb-4">
        <Button 
          variant={activeTab === 'notes' ? 'default' : 'outline'}
          onClick={() => setActiveTab('notes')}
          size="sm"
        >
          <FileText className="h-4 w-4 mr-2" /> My Notes
        </Button>
        <Button 
          variant={activeTab === 'discussions' ? 'default' : 'outline'}
          onClick={() => setActiveTab('discussions')}
          size="sm"
        >
          <MessageCircle className="h-4 w-4 mr-2" /> Discussion ({discussions.length})
        </Button>
      </div>

      {activeTab === 'notes' && (
        <div className="space-y-4">
          <div className="flex gap-2">
            <Textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a personal note about this lesson..."
              className="flex-1"
              rows={3}
            />
            <Button onClick={handleAddNote} className="self-end">
              <Plus className="h-4 w-4 mr-1" /> Save
            </Button>
          </div>
          
          {notes.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No notes yet. Add your first note above.
            </p>
          ) : (
            <div className="space-y-3">
              {notes.map(note => (
                <Card key={note.note_id} className="glass-card">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start">
                      <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteNote(note.note_id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(note.created_at).toLocaleDateString()}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'discussions' && (
        <div className="space-y-4">
          <div className="flex gap-2">
            <Textarea
              value={newDiscussion}
              onChange={(e) => setNewDiscussion(e.target.value)}
              placeholder="Start a discussion or ask a question..."
              className="flex-1"
              rows={3}
            />
            <Button onClick={handleAddDiscussion} className="self-end">
              <MessageCircle className="h-4 w-4 mr-1" /> Post
            </Button>
          </div>
          
          {discussions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No discussions yet. Be the first to start one!
            </p>
          ) : (
            <div className="space-y-4">
              {discussions.map(disc => (
                <Card key={disc.discussion_id} className="glass-card">
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={disc.user_picture} />
                        <AvatarFallback>{disc.user_name?.[0]}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{disc.user_name}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(disc.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm mt-1 whitespace-pre-wrap">{disc.content}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <Button variant="ghost" size="sm" onClick={() => handleLike(disc.discussion_id)}>
                            <Heart className={`h-4 w-4 mr-1 ${disc.likes?.length > 0 ? 'fill-red-500 text-red-500' : ''}`} />
                            {disc.likes?.length || 0}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setReplyTo(disc.discussion_id)}>
                            <MessageCircle className="h-4 w-4 mr-1" /> Reply
                          </Button>
                        </div>

                        {/* Reply Input */}
                        {replyTo === disc.discussion_id && (
                          <div className="mt-3 flex gap-2">
                            <Input
                              value={replyContent}
                              onChange={(e) => setReplyContent(e.target.value)}
                              placeholder="Write a reply..."
                              className="flex-1"
                            />
                            <Button size="sm" onClick={() => handleReply(disc.discussion_id)}>Reply</Button>
                            <Button size="sm" variant="ghost" onClick={() => setReplyTo(null)}>Cancel</Button>
                          </div>
                        )}

                        {/* Replies */}
                        {disc.replies?.length > 0 && (
                          <div className="mt-3 pl-4 border-l-2 space-y-3">
                            {disc.replies.map(reply => (
                              <div key={reply.discussion_id} className="flex items-start gap-2">
                                <Avatar className="h-6 w-6">
                                  <AvatarImage src={reply.user_picture} />
                                  <AvatarFallback>{reply.user_name?.[0]}</AvatarFallback>
                                </Avatar>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-xs">{reply.user_name}</span>
                                    <span className="text-xs text-muted-foreground">
                                      {new Date(reply.created_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                  <p className="text-sm">{reply.content}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== SHARE DIALOG COMPONENT ====================

function ShareDialog({ open, onOpenChange, resourceType, resourceId, resourceName }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [shareLink, setShareLink] = useState('');
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadCollaborators();
    }
  }, [open, resourceId]);

  const loadCollaborators = async () => {
    try {
      const res = await axiosInstance.get(`/collaborators/${resourceType}/${resourceId}`);
      setCollaborators(res.data);
    } catch (error) {
      console.error('Error loading collaborators:', error);
    }
  };

  const searchUsers = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await axiosInstance.get(`/users/search?q=${encodeURIComponent(query)}`);
      setSearchResults(res.data);
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => searchUsers(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleShare = async (userId) => {
    setLoading(true);
    try {
      await axiosInstance.post('/share', {
        resource_type: resourceType,
        resource_id: resourceId,
        share_with_user_id: userId,
        can_edit: canEdit
      });
      toast.success('Shared successfully');
      setSearchQuery('');
      setSearchResults([]);
      loadCollaborators();
    } catch (error) {
      toast.error('Failed to share');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLink = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.post('/share', {
        resource_type: resourceType,
        resource_id: resourceId,
        is_public_link: true,
        can_edit: canEdit
      });
      const link = `${window.location.origin}/shared/${res.data.share_link}`;
      setShareLink(link);
      toast.success('Link generated');
    } catch (error) {
      toast.error('Failed to generate link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success('Link copied to clipboard');
  };

  const handleRemoveCollaborator = async (shareId) => {
    try {
      await axiosInstance.delete(`/share/${shareId}`);
      toast.success('Collaborator removed');
      loadCollaborators();
    } catch (error) {
      toast.error('Failed to remove collaborator');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Share {resourceName || resourceType}</DialogTitle>
          <DialogDescription>
            Invite others to view or collaborate
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search Users */}
          <div>
            <Label>Share with people</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or email..."
              />
            </div>
            
            {searchResults.length > 0 && (
              <div className="mt-2 border rounded-lg max-h-40 overflow-y-auto">
                {searchResults.map(user => (
                  <div 
                    key={user.user_id}
                    className="flex items-center justify-between p-2 hover:bg-muted cursor-pointer"
                    onClick={() => handleShare(user.user_id)}
                  >
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.picture} />
                        <AvatarFallback>{user.name?.[0]}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{user.name}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                    <Plus className="h-4 w-4" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Permission Toggle */}
          <div className="flex items-center justify-between">
            <Label>Can edit</Label>
            <Switch checked={canEdit} onCheckedChange={setCanEdit} />
          </div>

          {/* Current Collaborators */}
          {collaborators.length > 0 && (
            <div>
              <Label>Collaborators</Label>
              <div className="mt-2 space-y-2">
                {collaborators.map(collab => (
                  <div key={collab.user_id} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={collab.picture} />
                        <AvatarFallback>{collab.name?.[0]}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{collab.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {collab.can_edit ? 'Can edit' : 'View only'}
                        </p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleRemoveCollaborator(collab.share_id)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Generate Link */}
          <div className="border-t pt-4">
            <Label>Or share via link</Label>
            <div className="flex gap-2 mt-2">
              {shareLink ? (
                <>
                  <Input value={shareLink} readOnly className="flex-1" />
                  <Button onClick={handleCopyLink}>Copy</Button>
                </>
              ) : (
                <Button onClick={handleGenerateLink} disabled={loading} className="w-full">
                  <LinkIcon className="h-4 w-4 mr-2" /> Generate Shareable Link
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== SHARED WITH ME PAGE ====================

function SharedWithMe() {
  const navigate = useNavigate();
  const [sharedItems, setSharedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSharedItems();
  }, []);

  const loadSharedItems = async () => {
    try {
      const res = await axiosInstance.get('/shared-with-me');
      setSharedItems(res.data);
    } catch (error) {
      toast.error('Failed to load shared items');
    } finally {
      setLoading(false);
    }
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'budget': return <DollarSign className="h-5 w-5" />;
      case 'note': return <FileText className="h-5 w-5" />;
      case 'goal': return <Target className="h-5 w-5" />;
      case 'task': return <CheckCircle className="h-5 w-5" />;
      default: return <FileText className="h-5 w-5" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div onClick={() => navigate('/')} className="cursor-pointer">
              <SiteLogo className="h-10 max-w-[200px]" />
            </div>
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>← Back to Dashboard</Button>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-4 max-w-4xl mx-auto">
        <h1 className="font-heading text-4xl font-bold mb-2">Shared with Me</h1>
        <p className="text-muted-foreground mb-8">Items that others have shared with you</p>

        {sharedItems.length === 0 ? (
          <Card className="glass-card">
            <CardContent className="pt-6 text-center">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No shared items yet</p>
              <p className="text-sm text-muted-foreground">When others share budgets, notes, or goals with you, they'll appear here.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {sharedItems.map(item => (
              <Card key={item.share_id} className="glass-card hover:shadow-lg transition-shadow cursor-pointer">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        {getResourceIcon(item.resource_type)}
                      </div>
                      <div>
                        <p className="font-medium capitalize">{item.resource_type}</p>
                        <p className="text-sm text-muted-foreground">
                          Shared by {item.owner_name} • {item.can_edit ? 'Can edit' : 'View only'}
                        </p>
                      </div>
                    </div>
                    <Badge variant={item.can_edit ? 'default' : 'secondary'}>
                      {item.can_edit ? 'Editor' : 'Viewer'}
                    </Badge>
                  </div>
                  {item.resource && (
                    <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                      {item.resource_type === 'budget' && (
                        <p className="text-sm">{item.resource.description} - ${item.resource.amount}</p>
                      )}
                      {item.resource_type === 'note' && (
                        <p className="text-sm line-clamp-2">{item.resource.content}</p>
                      )}
                      {item.resource_type === 'goal' && (
                        <p className="text-sm">{item.resource.title}</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== SHARED VIEW (Public Link) ====================

function SharedView() {
  const { shareLink } = useParams();
  const [sharedData, setSharedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSharedData();
  }, [shareLink]);

  const loadSharedData = async () => {
    try {
      const res = await axiosInstance.get(`/share/link/${shareLink}`);
      setSharedData(res.data);
    } catch (err) {
      setError('This share link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="glass-card max-w-md">
          <CardContent className="pt-6 text-center">
            <X className="h-12 w-12 mx-auto text-red-500 mb-4" />
            <p className="text-lg font-medium">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { share, resource } = sharedData;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <Badge className="mb-4">Shared {share.resource_type}</Badge>
          <h1 className="font-heading text-3xl font-bold mb-2">
            {share.owner_name} shared this {share.resource_type} with you
          </h1>
          <p className="text-muted-foreground">
            {share.can_edit ? 'You can view and edit this content' : 'View only access'}
          </p>
        </div>

        <Card className="glass-card">
          <CardContent className="pt-6">
            {share.resource_type === 'budget' && resource && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{resource.description}</span>
                  <span className={`text-xl font-bold ${resource.category === 'income' ? 'text-green-600' : resource.category === 'expense' ? 'text-red-600' : 'text-blue-600'}`}>
                    ${resource.amount?.toLocaleString()}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Badge variant="outline">{resource.category}</Badge>
                  <Badge variant="outline">{resource.frequency}</Badge>
                </div>
              </div>
            )}
            {share.resource_type === 'note' && resource && (
              <div>
                <p className="whitespace-pre-wrap">{resource.content}</p>
                <p className="text-sm text-muted-foreground mt-4">
                  Created: {new Date(resource.created_at).toLocaleDateString()}
                </p>
              </div>
            )}
            {share.resource_type === 'goal' && resource && (
              <div className="space-y-4">
                <h3 className="text-xl font-semibold">{resource.title}</h3>
                <p>{resource.description}</p>
                <Progress value={resource.progress || 0} />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function LessonViewer() {
  const navigate = useNavigate();
  const location = useLocation();
  const lessonId = location.pathname.split('/').pop();
  
  const [lesson, setLesson] = useState(null);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  // Helper to convert YouTube URLs to embed format
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    // Handle various YouTube URL formats: watch, embed, youtu.be, with playlists
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&?\s]+)/);
    if (match) {
      // Preserve playlist if present
      const playlistMatch = url.match(/[?&]list=([^&\s]+)/);
      const playlist = playlistMatch ? `?list=${playlistMatch[1]}` : '';
      return `https://www.youtube.com/embed/${match[1]}${playlist}`;
    }
    return url; // Return original if not a YouTube URL
  };

  useEffect(() => {
    loadLesson();
  }, [lessonId]);

  const loadLesson = async () => {
    try {
      const lessonRes = await axiosInstance.get(`/lessons/${lessonId}`);
      setLesson(lessonRes.data);
      
      const courseRes = await axiosInstance.get(`/courses/${lessonRes.data.course_id}`);
      setCourse(courseRes.data);
    } catch (error) {
      console.error('Error loading lesson:', error);
      toast.error('Failed to load lesson');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = async () => {
    setCompleting(true);
    try {
      await axiosInstance.post(`/lessons/${lessonId}/complete`);
      toast.success('Lesson completed!');
      loadLesson();
      
      // Navigate to next lesson if available
      if (course?.lessons) {
        const currentIndex = course.lessons.findIndex(l => l.lesson_id === lessonId);
        if (currentIndex < course.lessons.length - 1) {
          const nextLesson = course.lessons[currentIndex + 1];
          navigate(`/lessons/${nextLesson.lesson_id}`);
        }
      }
    } catch (error) {
      toast.error('Failed to complete lesson');
    } finally {
      setCompleting(false);
    }
  };

  const handleQuizSubmit = async () => {
    try {
      const result = await axiosInstance.post('/quizzes/submit', {
        lesson_id: lessonId,
        answers: quizAnswers
      });
      setQuizResult(result.data);
      if (result.data.passed) {
        toast.success(`Quiz passed! Score: ${result.data.score}%`);
      } else {
        toast.error(`Quiz failed. Score: ${result.data.score}%. Try again!`);
      }
    } catch (error) {
      toast.error('Failed to submit quiz');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 text-center">
          <p className="text-muted-foreground mb-4">Lesson not found</p>
          <Button onClick={() => navigate('/courses')}>Back to Courses</Button>
        </Card>
      </div>
    );
  }

  const renderContent = () => {
    if (lesson.content_type === 'video') {
      const embedUrl = getYouTubeEmbedUrl(lesson.content);
      return (
        <div className="aspect-video bg-black rounded-lg overflow-hidden">
          <iframe
            src={embedUrl}
            className="w-full h-full"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            title={lesson.title}
          />
        </div>
      );
    }

    if (lesson.content_type === 'document') {
      const handleReadAloud = async () => {
        if (!lesson.content) return;
        setTtsLoading(true);
        try {
          const cleanText = lesson.content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
          const res = await axios.post(`${API}/tts/generate`, { text: cleanText.slice(0, 5000) });
          if (res.data.audio_url && audioRef.current) {
            audioRef.current.src = res.data.audio_url;
            audioRef.current.play();
            setIsPlaying(true);
          }
        } catch (error) {
          if (error.response?.status === 503) {
            toast.error('Text-to-speech not configured. Add ElevenLabs API key to enable.');
          } else {
            toast.error('Failed to generate audio');
          }
        } finally {
          setTtsLoading(false);
        }
      };

      return (
        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex justify-end mb-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleReadAloud}
                disabled={ttsLoading || isPlaying}
              >
                {ttsLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2" />
                ) : isPlaying ? (
                  <Volume2 className="h-4 w-4 mr-2 animate-pulse" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                {ttsLoading ? 'Loading...' : isPlaying ? 'Playing...' : 'Read Aloud'}
              </Button>
              <audio ref={audioRef} onEnded={() => setIsPlaying(false)} className="hidden" />
            </div>
            <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: lesson.content.replace(/\n/g, '<br/>') }} />
          </CardContent>
        </Card>
      );
    }

    if (lesson.content_type === 'quiz') {
      let quizData;
      try {
        quizData = JSON.parse(lesson.content);
      } catch {
        return <p className="text-destructive">Invalid quiz format</p>;
      }

      if (quizResult) {
        return (
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className={quizResult.passed ? 'text-green-600' : 'text-red-600'}>
                {quizResult.passed ? '🎉 Quiz Passed!' : '❌ Quiz Failed'}
              </CardTitle>
              <CardDescription>
                Score: {quizResult.score}% ({quizResult.correct}/{quizResult.total} correct)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!quizResult.passed && (
                <Button onClick={() => { setQuizResult(null); setQuizAnswers({}); }}>
                  Try Again
                </Button>
              )}
            </CardContent>
          </Card>
        );
      }

      return (
        <div className="space-y-6">
          {quizData.questions?.map((q, index) => (
            <Card key={q.id} className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg">Q{index + 1}: {q.question}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {q.options?.map((option, optIndex) => (
                    <label
                      key={optIndex}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        quizAnswers[q.id] === option ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name={q.id}
                        value={option}
                        checked={quizAnswers[q.id] === option}
                        onChange={() => setQuizAnswers({ ...quizAnswers, [q.id]: option })}
                        className="hidden"
                      />
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        quizAnswers[q.id] === option ? 'border-primary bg-primary' : 'border-muted-foreground'
                      }`}>
                        {quizAnswers[q.id] === option && <div className="w-2 h-2 rounded-full bg-white" />}
                      </div>
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
          <Button 
            onClick={handleQuizSubmit} 
            className="w-full"
            disabled={Object.keys(quizAnswers).length < (quizData.questions?.length || 0)}
          >
            Submit Quiz
          </Button>
        </div>
      );
    }

    return <p>Unknown content type</p>;
  };

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Navigation */}
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" onClick={() => navigate(`/courses/${lesson.course_id}`)}>
            ← Back to Course
          </Button>
          {course && (
            <span className="text-sm text-muted-foreground">
              Lesson {course.lessons?.findIndex(l => l.lesson_id === lessonId) + 1} of {course.lessons?.length}
            </span>
          )}
        </div>

        {/* Lesson Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline">
              {lesson.content_type === 'video' && <Video className="h-3 w-3 mr-1" />}
              {lesson.content_type === 'document' && <FileText className="h-3 w-3 mr-1" />}
              {lesson.content_type === 'quiz' && <Target className="h-3 w-3 mr-1" />}
              {lesson.content_type}
            </Badge>
            <span className="text-sm text-muted-foreground">{lesson.duration_minutes} min</span>
          </div>
          <h1 className="font-heading text-3xl font-bold" data-testid="lesson-title">{lesson.title}</h1>
          {lesson.description && (
            <p className="text-muted-foreground mt-2">{lesson.description}</p>
          )}
        </div>

        {/* Content */}
        <div className="mb-8">
          {renderContent()}
        </div>

        {/* Complete Button */}
        {lesson.content_type !== 'quiz' && (
          <div className="flex justify-end">
            {lesson.progress?.completed ? (
              <Badge className="bg-green-500 text-white px-4 py-2">
                <CheckCircle className="h-4 w-4 mr-2" /> Completed
              </Badge>
            ) : (
              <Button onClick={handleMarkComplete} disabled={completing} data-testid="complete-lesson-btn">
                {completing ? 'Completing...' : 'Mark as Complete'}
              </Button>
            )}
          </div>
        )}

        {/* Notes & Discussion Section */}
        <LessonCollaboration lessonId={lessonId} courseId={lesson.course_id} />
      </div>
    </div>
  );
}

function CourseCreate() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    difficulty: 'beginner',
    is_public: true
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axiosInstance.post('/courses', formData);
      toast.success('Course created! Now add some lessons.');
      navigate(`/courses/${response.data.course_id}/edit`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create course');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/courses')} className="mb-4">
          ← Back to Courses
        </Button>
        
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="font-heading text-2xl">Create New Course</CardTitle>
            <CardDescription>Fill in the details to create a new course</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Course Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Introduction to Leadership"
                  required
                  data-testid="course-title-input"
                />
              </div>
              
              <div>
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What will students learn in this course?"
                  rows={4}
                  required
                  data-testid="course-description-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category *</Label>
                  <Input
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="Leadership, Technology, etc."
                    required
                    data-testid="course-category-input"
                  />
                </div>
                
                <div>
                  <Label>Difficulty</Label>
                  <Select value={formData.difficulty} onValueChange={(v) => setFormData({ ...formData, difficulty: v })}>
                    <SelectTrigger data-testid="course-difficulty-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_public"
                  checked={formData.is_public}
                  onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="is_public">Make this course public (visible to all users)</Label>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => navigate('/courses')}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} data-testid="create-course-submit">
                  {loading ? 'Creating...' : 'Create Course'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function CourseEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const courseId = location.pathname.split('/')[2]; // /courses/:id/edit
  
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [showAddLesson, setShowAddLesson] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [savingCourse, setSavingCourse] = useState(false);
  
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    category: '',
    difficulty: 'beginner',
    is_public: true,
    status: 'draft'
  });
  
  const [lessonForm, setLessonForm] = useState({
    title: '',
    description: '',
    content_type: 'document',
    content: '',
    duration_minutes: 10,
    order: 1
  });
  
  const [quizQuestions, setQuizQuestions] = useState([{
    id: 'q1',
    question: '',
    options: ['', '', '', ''],
    correct_answer: ''
  }]);

  useEffect(() => {
    loadCourseData();
  }, [courseId]);

  const loadCourseData = async () => {
    try {
      const [courseRes, analyticsRes] = await Promise.all([
        axiosInstance.get(`/courses/${courseId}`),
        axiosInstance.get(`/courses/${courseId}/analytics`).catch(() => ({ data: null }))
      ]);
      
      setCourse(courseRes.data);
      setLessons(courseRes.data.lessons || []);
      setAnalytics(analyticsRes.data);
      setCourseForm({
        title: courseRes.data.title,
        description: courseRes.data.description,
        category: courseRes.data.category,
        difficulty: courseRes.data.difficulty,
        is_public: courseRes.data.is_public,
        status: courseRes.data.status || 'draft'
      });
      
      if (courseRes.data.thumbnail_url) {
        setThumbnailPreview(`${BACKEND_URL}${courseRes.data.thumbnail_url}?t=${Date.now()}`);
      }
    } catch (error) {
      console.error('Error loading course:', error);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCourse = async () => {
    setSavingCourse(true);
    try {
      await axiosInstance.put(`/courses/${courseId}`, courseForm);
      toast.success('Course updated!');
      loadCourseData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update course');
    } finally {
      setSavingCourse(false);
    }
  };

  const handleThumbnailSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
        toast.error('Please upload PNG, JPG, or WebP');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Max file size is 5MB');
        return;
      }
      setThumbnailFile(file);
      setThumbnailPreview(URL.createObjectURL(file));
    }
  };

  const handleUploadThumbnail = async () => {
    if (!thumbnailFile) return;
    
    setUploadingThumbnail(true);
    const formData = new FormData();
    formData.append('file', thumbnailFile);
    
    try {
      await axiosInstance.post(`/courses/${courseId}/thumbnail`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Thumbnail uploaded!');
      setThumbnailFile(null);
      loadCourseData();
    } catch (error) {
      toast.error('Failed to upload thumbnail');
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleAddLesson = async (e) => {
    e.preventDefault();
    
    let content = lessonForm.content;
    if (lessonForm.content_type === 'quiz') {
      content = JSON.stringify({
        questions: quizQuestions,
        passing_score: 70
      });
    }
    
    try {
      await axiosInstance.post('/lessons', {
        ...lessonForm,
        content,
        course_id: courseId,
        order: lessons.length + 1
      });
      toast.success('Lesson added!');
      setShowAddLesson(false);
      setLessonForm({
        title: '',
        description: '',
        content_type: 'document',
        content: '',
        duration_minutes: 10,
        order: lessons.length + 2
      });
      setQuizQuestions([{ id: 'q1', question: '', options: ['', '', '', ''], correct_answer: '' }]);
      loadCourseData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add lesson');
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm('Delete this lesson?')) return;
    
    try {
      await axiosInstance.delete(`/lessons/${lessonId}`);
      toast.success('Lesson deleted');
      loadCourseData();
    } catch (error) {
      toast.error('Failed to delete lesson');
    }
  };

  const addQuizQuestion = () => {
    setQuizQuestions([...quizQuestions, {
      id: `q${quizQuestions.length + 1}`,
      question: '',
      options: ['', '', '', ''],
      correct_answer: ''
    }]);
  };

  const updateQuizQuestion = (index, field, value) => {
    const updated = [...quizQuestions];
    if (field === 'options') {
      updated[index].options = value;
    } else {
      updated[index][field] = value;
    }
    setQuizQuestions(updated);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 text-center">
          <p className="text-muted-foreground mb-4">Course not found</p>
          <Button onClick={() => navigate('/courses')}>Back to Courses</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/courses')}>
              ← Back
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold">Edit Course</h1>
              <p className="text-sm text-muted-foreground">{course.title}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Badge className={course.status === 'published' ? 'bg-green-500' : 'bg-yellow-500'}>
              {course.status || 'draft'}
            </Badge>
            <Button variant="outline" onClick={() => navigate(`/courses/${courseId}`)}>
              Preview
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="details">Course Details</TabsTrigger>
            <TabsTrigger value="lessons">Lessons ({lessons.length})</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
          </TabsList>

          {/* Course Details Tab */}
          <TabsContent value="details">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle>Basic Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={courseForm.title}
                        onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                        data-testid="edit-course-title"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={courseForm.description}
                        onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                        rows={4}
                        data-testid="edit-course-description"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Category</Label>
                        <Input
                          value={courseForm.category}
                          onChange={(e) => setCourseForm({ ...courseForm, category: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Difficulty</Label>
                        <Select value={courseForm.difficulty} onValueChange={(v) => setCourseForm({ ...courseForm, difficulty: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="beginner">Beginner</SelectItem>
                            <SelectItem value="intermediate">Intermediate</SelectItem>
                            <SelectItem value="advanced">Advanced</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Status</Label>
                        <Select value={courseForm.status} onValueChange={(v) => setCourseForm({ ...courseForm, status: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="published">Published</SelectItem>
                            <SelectItem value="archived">Archived</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2 pt-6">
                        <input
                          type="checkbox"
                          id="edit_is_public"
                          checked={courseForm.is_public}
                          onChange={(e) => setCourseForm({ ...courseForm, is_public: e.target.checked })}
                        />
                        <Label htmlFor="edit_is_public">Public Course</Label>
                      </div>
                    </div>
                    <Button onClick={handleSaveCourse} disabled={savingCourse} className="w-full" data-testid="save-course-btn">
                      {savingCourse ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Thumbnail Upload */}
              <div>
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle>Course Thumbnail</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="aspect-video bg-muted rounded-lg overflow-hidden flex items-center justify-center">
                        {thumbnailPreview ? (
                          <img src={thumbnailPreview} alt="Thumbnail" className="w-full h-full object-cover" />
                        ) : (
                          <div className="text-center text-muted-foreground">
                            <div className="text-4xl mb-2">🖼️</div>
                            <p className="text-sm">No thumbnail</p>
                          </div>
                        )}
                      </div>
                      <div>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          onChange={handleThumbnailSelect}
                          className="hidden"
                          id="thumbnail-upload"
                          data-testid="thumbnail-input"
                        />
                        <label htmlFor="thumbnail-upload">
                          <Button variant="outline" className="w-full" asChild>
                            <span><Upload className="h-4 w-4 mr-2" /> Choose Image</span>
                          </Button>
                        </label>
                      </div>
                      {thumbnailFile && (
                        <Button onClick={handleUploadThumbnail} disabled={uploadingThumbnail} className="w-full" data-testid="upload-thumbnail-btn">
                          {uploadingThumbnail ? 'Uploading...' : 'Upload Thumbnail'}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Lessons Tab */}
          <TabsContent value="lessons">
            <Card className="glass-card mb-6">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Course Lessons</CardTitle>
                <Button onClick={() => setShowAddLesson(true)} data-testid="add-lesson-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Lesson
                </Button>
              </CardHeader>
              <CardContent>
                {lessons.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <div className="text-5xl mb-4">📚</div>
                    <p>No lessons yet. Add your first lesson!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {lessons.map((lesson, index) => (
                      <Card key={lesson.lesson_id} className="border">
                        <CardContent className="p-4 flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-bold">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold">{lesson.title}</div>
                            <div className="text-sm text-muted-foreground flex items-center gap-3">
                              <Badge variant="outline">
                                {lesson.content_type === 'video' && <Video className="h-3 w-3 mr-1" />}
                                {lesson.content_type === 'document' && <FileText className="h-3 w-3 mr-1" />}
                                {lesson.content_type === 'quiz' && <Target className="h-3 w-3 mr-1" />}
                                {lesson.content_type}
                              </Badge>
                              <span>{lesson.duration_minutes} min</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteLesson(lesson.lesson_id)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Add Lesson Dialog */}
            <Dialog open={showAddLesson} onOpenChange={setShowAddLesson}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-lesson-dialog">
                <DialogHeader>
                  <DialogTitle>Add New Lesson</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddLesson} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Lesson Title *</Label>
                      <Input
                        value={lessonForm.title}
                        onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
                        placeholder="Introduction to..."
                        required
                        data-testid="lesson-title-input"
                      />
                    </div>
                    <div>
                      <Label>Content Type *</Label>
                      <Select value={lessonForm.content_type} onValueChange={(v) => setLessonForm({ ...lessonForm, content_type: v })}>
                        <SelectTrigger data-testid="lesson-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="document">📄 Document</SelectItem>
                          <SelectItem value="video">🎥 Video</SelectItem>
                          <SelectItem value="quiz">📝 Quiz</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Description</Label>
                    <Input
                      value={lessonForm.description}
                      onChange={(e) => setLessonForm({ ...lessonForm, description: e.target.value })}
                      placeholder="Brief description of this lesson"
                    />
                  </div>
                  
                  <div>
                    <Label>Duration (minutes)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={lessonForm.duration_minutes}
                      onChange={(e) => setLessonForm({ ...lessonForm, duration_minutes: parseInt(e.target.value) })}
                    />
                  </div>

                  {/* Content based on type */}
                  {lessonForm.content_type === 'video' && (
                    <div>
                      <Label>Video URL (YouTube/Vimeo embed URL) *</Label>
                      <Input
                        value={lessonForm.content}
                        onChange={(e) => setLessonForm({ ...lessonForm, content: e.target.value })}
                        placeholder="https://www.youtube.com/embed/..."
                        required
                        data-testid="video-url-input"
                      />
                    </div>
                  )}

                  {lessonForm.content_type === 'document' && (
                    <div>
                      <Label>Document Content *</Label>
                      <Textarea
                        value={lessonForm.content}
                        onChange={(e) => setLessonForm({ ...lessonForm, content: e.target.value })}
                        placeholder="Write your lesson content here..."
                        rows={8}
                        required
                        data-testid="document-content-input"
                      />
                    </div>
                  )}

                  {lessonForm.content_type === 'quiz' && (
                    <div className="space-y-4">
                      <Label>Quiz Questions</Label>
                      {quizQuestions.map((q, qIndex) => (
                        <Card key={q.id} className="p-4 border">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <Label>Question {qIndex + 1}</Label>
                              {quizQuestions.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setQuizQuestions(quizQuestions.filter((_, i) => i !== qIndex))}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </div>
                            <Input
                              value={q.question}
                              onChange={(e) => updateQuizQuestion(qIndex, 'question', e.target.value)}
                              placeholder="Enter your question"
                              required
                            />
                            <div className="grid grid-cols-2 gap-2">
                              {q.options.map((opt, optIndex) => (
                                <Input
                                  key={optIndex}
                                  value={opt}
                                  onChange={(e) => {
                                    const newOpts = [...q.options];
                                    newOpts[optIndex] = e.target.value;
                                    updateQuizQuestion(qIndex, 'options', newOpts);
                                  }}
                                  placeholder={`Option ${optIndex + 1}`}
                                  required
                                />
                              ))}
                            </div>
                            <div>
                              <Label>Correct Answer</Label>
                              <Select
                                value={q.correct_answer}
                                onValueChange={(v) => updateQuizQuestion(qIndex, 'correct_answer', v)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select correct answer" />
                                </SelectTrigger>
                                <SelectContent>
                                  {q.options.filter(o => o).map((opt, i) => (
                                    <SelectItem key={i} value={opt}>{opt}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </Card>
                      ))}
                      <Button type="button" variant="outline" onClick={addQuizQuestion} className="w-full">
                        <Plus className="h-4 w-4 mr-2" /> Add Question
                      </Button>
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowAddLesson(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="submit-lesson-btn">
                      Add Lesson
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            {analytics ? (
              <div className="space-y-6">
                {/* Overview Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="glass-card">
                    <CardContent className="p-4 text-center">
                      <div className="text-3xl font-bold text-primary">{analytics.overview.total_enrolled}</div>
                      <div className="text-sm text-muted-foreground">Total Enrolled</div>
                    </CardContent>
                  </Card>
                  <Card className="glass-card">
                    <CardContent className="p-4 text-center">
                      <div className="text-3xl font-bold text-green-600">{analytics.overview.completed}</div>
                      <div className="text-sm text-muted-foreground">Completed</div>
                    </CardContent>
                  </Card>
                  <Card className="glass-card">
                    <CardContent className="p-4 text-center">
                      <div className="text-3xl font-bold text-blue-600">{analytics.overview.completion_rate}%</div>
                      <div className="text-sm text-muted-foreground">Completion Rate</div>
                    </CardContent>
                  </Card>
                  <Card className="glass-card">
                    <CardContent className="p-4 text-center">
                      <div className="text-3xl font-bold text-amber-600">{analytics.overview.average_progress}%</div>
                      <div className="text-sm text-muted-foreground">Avg Progress</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Lesson Stats */}
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle>Lesson Completion Rates</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics.lesson_stats.length > 0 ? (
                      <div className="space-y-4">
                        {analytics.lesson_stats.map((lesson, index) => (
                          <div key={lesson.lesson_id} className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span>{index + 1}. {lesson.title}</span>
                              <span>{lesson.completions} completions ({lesson.completion_rate}%)</span>
                            </div>
                            <Progress value={lesson.completion_rate} className="h-2" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-4">No lesson data yet</p>
                    )}
                  </CardContent>
                </Card>

                {/* Quiz Stats */}
                {analytics.quiz_stats.length > 0 && (
                  <Card className="glass-card">
                    <CardHeader>
                      <CardTitle>Quiz Performance</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2">Quiz</th>
                              <th className="text-center py-2">Attempts</th>
                              <th className="text-center py-2">Avg Score</th>
                              <th className="text-center py-2">Pass Rate</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analytics.quiz_stats.map(quiz => (
                              <tr key={quiz.lesson_id} className="border-b">
                                <td className="py-2">{quiz.title}</td>
                                <td className="text-center">{quiz.total_attempts}</td>
                                <td className="text-center">{quiz.average_score}%</td>
                                <td className="text-center">
                                  <Badge className={quiz.pass_rate >= 70 ? 'bg-green-500' : 'bg-red-500'}>
                                    {quiz.pass_rate}%
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Recent Enrollments */}
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle>Recent Enrollments</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics.recent_enrollments.length > 0 ? (
                      <div className="space-y-3">
                        {analytics.recent_enrollments.map(enrollment => (
                          <div key={enrollment.enrollment_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback>{enrollment.user?.name?.[0] || 'U'}</AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="font-medium">{enrollment.user?.name || 'Unknown'}</div>
                                <div className="text-xs text-muted-foreground">{enrollment.user?.email}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-medium">{enrollment.progress_percent}%</div>
                              <div className="text-xs text-muted-foreground">
                                {new Date(enrollment.enrolled_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-4">No enrollments yet</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="glass-card p-8 text-center">
                <p className="text-muted-foreground">No analytics data available yet</p>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function CertificateView() {
  const navigate = useNavigate();
  const location = useLocation();
  const courseId = location.pathname.split('/').pop();
  
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadCertificate();
  }, [courseId]);

  const loadCertificate = async () => {
    try {
      const response = await axiosInstance.get(`/certificates/${courseId}`);
      setCertificate(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Certificate not available');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await axiosInstance.get(`/certificates/${courseId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate_${certificate.verification_code}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Certificate downloaded!');
    } catch (error) {
      toast.error('Failed to download certificate');
    } finally {
      setDownloading(false);
    }
  };

  const getShareUrl = () => {
    return `${window.location.origin}/certificate/verify/${certificate?.verification_code}`;
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(getShareUrl());
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareTwitter = () => {
    const text = `I just completed "${certificate.course_title}" at TheFeedingFaith Church! 🎓`;
    const url = getShareUrl();
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const handleShareFacebook = () => {
    const url = getShareUrl();
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
  };

  const handleShareLinkedIn = () => {
    const url = getShareUrl();
    const title = `Completed ${certificate.course_title}`;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
  };

  const handleShareWhatsApp = () => {
    const text = `I just completed "${certificate.course_title}" at TheFeedingFaith Church! 🎓 Check it out: ${getShareUrl()}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 text-center">
          <p className="text-muted-foreground mb-4">Certificate not available. Complete the course first.</p>
          <Button onClick={() => navigate('/courses')}>Back to Courses</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-between items-center mb-4">
          <Button variant="ghost" onClick={() => navigate('/courses')}>
            ← Back to Courses
          </Button>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => setShowShareModal(true)}
              className="gap-2"
              data-testid="share-certificate-btn"
            >
              <ArrowLeftRight className="h-4 w-4" />
              Share
            </Button>
            <Button 
              onClick={handleDownload} 
              disabled={downloading}
              className="gap-2"
              data-testid="download-certificate-btn"
            >
              <Download className="h-4 w-4" />
              {downloading ? 'Downloading...' : 'Download PDF'}
            </Button>
          </div>
        </div>
        
        <Card className="glass-card overflow-hidden">
          <div className="bg-gradient-to-br from-primary to-accent p-8 text-white text-center">
            <div className="text-6xl mb-4">🎓</div>
            <h1 className="font-heading text-3xl font-bold mb-2">Certificate of Completion</h1>
            <p className="opacity-90">TheFeedingFaith Mentorship</p>
          </div>
          
          <CardContent className="p-8 text-center">
            <p className="text-lg text-muted-foreground mb-2">This certifies that</p>
            <h2 className="font-heading text-3xl font-bold text-primary mb-4">{certificate.user_name}</h2>
            <p className="text-lg text-muted-foreground mb-2">has successfully completed</p>
            <h3 className="font-heading text-2xl font-semibold mb-6">{certificate.course_title}</h3>
            
            <div className="flex justify-center gap-8 text-sm text-muted-foreground border-t pt-6">
              <div>
                <div className="font-semibold">Date Issued</div>
                <div>{new Date(certificate.issued_at).toLocaleDateString()}</div>
              </div>
              <div>
                <div className="font-semibold">Verification Code</div>
                <div className="font-mono">{certificate.verification_code}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Share Modal */}
        <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Share Your Achievement 🎉</DialogTitle>
              <DialogDescription>
                Let your friends and network know about your accomplishment!
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Share Link */}
              <div className="space-y-2">
                <Label>Shareable Link</Label>
                <div className="flex gap-2">
                  <Input 
                    value={getShareUrl()} 
                    readOnly 
                    className="flex-1 text-sm"
                  />
                  <Button 
                    variant="outline" 
                    onClick={handleCopyLink}
                    className="shrink-0"
                    data-testid="copy-link-btn"
                  >
                    {copied ? '✓ Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>

              {/* Social Share Buttons */}
              <div className="space-y-2">
                <Label>Share on Social Media</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    variant="outline" 
                    onClick={handleShareTwitter}
                    className="gap-2 justify-start"
                    data-testid="share-twitter-btn"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    Twitter/X
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleShareFacebook}
                    className="gap-2 justify-start"
                    data-testid="share-facebook-btn"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    Facebook
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleShareLinkedIn}
                    className="gap-2 justify-start"
                    data-testid="share-linkedin-btn"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    LinkedIn
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleShareWhatsApp}
                    className="gap-2 justify-start"
                    data-testid="share-whatsapp-btn"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

// ==================== CERTIFICATE VERIFICATION (PUBLIC) ====================

function CertificateVerify() {
  const { verificationCode } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCertificate();
  }, [verificationCode]);

  const loadCertificate = async () => {
    try {
      const response = await axiosInstance.get(`/certificates/share/${verificationCode}`);
      setCertificate(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || 'Certificate not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !certificate) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-background flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md">
          <div className="text-5xl mb-4">❌</div>
          <h2 className="text-xl font-bold mb-2">Certificate Not Found</h2>
          <p className="text-muted-foreground mb-4">{error || 'This certificate does not exist or the verification code is invalid.'}</p>
          <Button onClick={() => window.location.href = '/'}>Go to Homepage</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-background py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Verified Badge */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full">
            <CheckCircle className="h-5 w-5" />
            <span className="font-medium">Verified Certificate</span>
          </div>
        </div>

        <Card className="glass-card overflow-hidden">
          <div className="bg-gradient-to-br from-primary to-accent p-8 text-white text-center">
            <div className="text-6xl mb-4">🎓</div>
            <h1 className="font-heading text-3xl font-bold mb-2">Certificate of Completion</h1>
            <p className="opacity-90">TheFeedingFaith Church Mentorship</p>
          </div>
          
          <CardContent className="p-8 text-center">
            <p className="text-lg text-muted-foreground mb-2">This certifies that</p>
            <h2 className="font-heading text-3xl font-bold text-primary mb-4">{certificate.user_name}</h2>
            <p className="text-lg text-muted-foreground mb-2">has successfully completed</p>
            <h3 className="font-heading text-2xl font-semibold mb-2">{certificate.course_title}</h3>
            {certificate.course_description && (
              <p className="text-sm text-muted-foreground mb-6 max-w-lg mx-auto">{certificate.course_description}</p>
            )}
            
            <div className="flex justify-center gap-8 text-sm text-muted-foreground border-t pt-6">
              <div>
                <div className="font-semibold">Date Issued</div>
                <div>{new Date(certificate.issued_at).toLocaleDateString()}</div>
              </div>
              <div>
                <div className="font-semibold">Verification Code</div>
                <div className="font-mono bg-muted px-2 py-1 rounded">{certificate.verification_code}</div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t">
              <p className="text-xs text-muted-foreground">
                This certificate was issued by TheFeedingFaith Church. 
                The verification code above confirms its authenticity.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Call to Action */}
        <div className="text-center mt-8">
          <p className="text-muted-foreground mb-4">Want to earn your own certificate?</p>
          <Button onClick={() => window.location.href = '/'} className="gap-2">
            <ArrowRight className="h-4 w-4" />
            Join TheFeedingFaith
          </Button>
        </div>
      </div>
    </div>
  );
}

// ==================== CALENDAR PAGE ====================

function CalendarPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [showEditEvent, setShowEditEvent] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    event_type: 'meeting',
    start_time: '',
    end_time: '',
    location: '',
    is_virtual: false,
    meeting_link: '',
    invitees: [],
    is_recurring: false,
    recurrence_type: 'weekly',
    recurrence_count: 4,
    thumbnail: ''
  });
  const [eventThumbnailFile, setEventThumbnailFile] = useState(null);
  const [uploadingEventThumbnail, setUploadingEventThumbnail] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedDate]);

  const loadData = async () => {
    try {
      const [eventsRes, userRes, usersRes] = await Promise.all([
        axiosInstance.get(`/events?month=${selectedDate.getMonth() + 1}&year=${selectedDate.getFullYear()}`),
        axiosInstance.get('/auth/me'),
        axiosInstance.get('/admin/users').catch(() => ({ data: [] }))
      ]);
      setEvents(eventsRes.data);
      setUser(userRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error loading calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    
    try {
      let thumbnailUrl = eventForm.thumbnail;
      
      // If thumbnail file is selected, upload it first
      if (eventThumbnailFile) {
        setUploadingEventThumbnail(true);
        const formData = new FormData();
        formData.append('file', eventThumbnailFile);
        try {
          const uploadRes = await axiosInstance.post('/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          thumbnailUrl = `${BACKEND_URL}${uploadRes.data.url}`;
        } catch (uploadError) {
          console.error('Failed to upload thumbnail:', uploadError);
        }
        setUploadingEventThumbnail(false);
      }
      
      const payload = {
        ...eventForm,
        thumbnail: thumbnailUrl,
        start_time: new Date(eventForm.start_time).toISOString(),
        end_time: eventForm.end_time ? new Date(eventForm.end_time).toISOString() : null,
        send_invites: eventForm.invitees.length > 0
      };
      
      const response = await axiosInstance.post('/events', payload);
      
      // Show appropriate message based on email status
      let message = '';
      if (eventForm.is_recurring && response.data.total_created) {
        message = `Created ${response.data.total_created} recurring events!`;
      } else {
        message = 'Event created!';
      }
      
      // Add info about invites
      if (response.data.invites_created > 0) {
        if (response.data.email_status === 'sent') {
          message += ` Email invites sent to ${response.data.invites_created} user(s).`;
        } else if (response.data.email_status === 'not_configured') {
          message += ` ${response.data.invites_created} user(s) invited (email notifications not configured).`;
          toast.info('Tip: Configure email settings to send automatic invite notifications.');
        }
      }
      
      toast.success(message);
      
      setShowCreateEvent(false);
      setEventForm({
        title: '', description: '', event_type: 'meeting',
        start_time: '', end_time: '', location: '',
        is_virtual: false, meeting_link: '', invitees: [],
        is_recurring: false, recurrence_type: 'weekly', recurrence_count: 4,
        thumbnail: ''
      });
      setEventThumbnailFile(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    
    try {
      await axiosInstance.delete(`/events/${eventId}`);
      toast.success('Event deleted');
      setSelectedEvent(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent({
      ...event,
      start_time: event.start_time ? new Date(event.start_time).toISOString().slice(0, 16) : '',
      end_time: event.end_time ? new Date(event.end_time).toISOString().slice(0, 16) : ''
    });
    setSelectedEvent(null);
    setShowEditEvent(true);
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    if (!editingEvent) return;
    
    try {
      const updateData = {
        title: editingEvent.title,
        description: editingEvent.description,
        event_type: editingEvent.event_type,
        start_time: editingEvent.start_time,
        end_time: editingEvent.end_time || null,
        location: editingEvent.location,
        is_virtual: editingEvent.is_virtual,
        meeting_link: editingEvent.meeting_link,
        thumbnail: editingEvent.thumbnail,
        status: editingEvent.status
      };
      
      await axiosInstance.put(`/events/${editingEvent.event_id}`, updateData);
      toast.success('Event updated successfully!');
      setShowEditEvent(false);
      setEditingEvent(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    }
  };

  const handleRespondToInvite = async (eventId, response) => {
    try {
      await axiosInstance.post(`/events/${eventId}/respond?response=${response}`);
      toast.success(`Invitation ${response}`);
      loadData();
    } catch (error) {
      toast.error('Failed to respond');
    }
  };

  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.start_time);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const eventTypeColors = {
    meeting: 'bg-blue-500',
    session: 'bg-green-500',
    deadline: 'bg-red-500',
    reminder: 'bg-yellow-500'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl font-bold mb-2" data-testid="calendar-heading">Calendar</h1>
            <p className="text-muted-foreground">Schedule and manage your events</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              ← Dashboard
            </Button>
            <Button onClick={() => setShowCreateEvent(true)} data-testid="create-event-btn">
              <Plus className="h-4 w-4 mr-2" /> New Event
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Custom Calendar */}
          <Card className="glass-card lg:col-span-2">
            <CardContent className="p-6">
              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-6">
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1))}
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <h2 className="font-heading text-xl font-semibold">
                  {selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h2>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1))}
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
              
              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                    {day}
                  </div>
                ))}
              </div>
              
              <div className="grid grid-cols-7 gap-1">
                {(() => {
                  const year = selectedDate.getFullYear();
                  const month = selectedDate.getMonth();
                  const firstDay = new Date(year, month, 1).getDay();
                  const daysInMonth = new Date(year, month + 1, 0).getDate();
                  const days = [];
                  
                  // Empty cells for days before month starts
                  for (let i = 0; i < firstDay; i++) {
                    days.push(<div key={`empty-${i}`} className="h-12"></div>);
                  }
                  
                  // Days of the month
                  for (let day = 1; day <= daysInMonth; day++) {
                    const date = new Date(year, month, day);
                    const isSelected = date.toDateString() === selectedDate.toDateString();
                    const isToday = date.toDateString() === new Date().toDateString();
                    const dayEvents = events.filter(e => new Date(e.start_time).toDateString() === date.toDateString());
                    
                    days.push(
                      <button
                        key={day}
                        onClick={() => setSelectedDate(date)}
                        className={`h-12 rounded-lg relative flex flex-col items-center justify-center transition-all hover:bg-muted ${
                          isSelected ? 'bg-primary text-primary-foreground hover:bg-primary' : ''
                        } ${isToday && !isSelected ? 'border-2 border-primary' : ''}`}
                      >
                        <span className="text-sm font-medium">{day}</span>
                        {dayEvents.length > 0 && (
                          <div className="flex gap-0.5 mt-0.5">
                            {dayEvents.slice(0, 3).map((e, i) => (
                              <div key={i} className={`w-1.5 h-1.5 rounded-full ${eventTypeColors[e.event_type]}`}></div>
                            ))}
                          </div>
                        )}
                      </button>
                    );
                  }
                  
                  return days;
                })()}
              </div>
              
              {/* Legend */}
              <div className="flex gap-4 mt-6 pt-4 border-t flex-wrap">
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span>Meeting</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span>Session</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span>Deadline</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <span>Reminder</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Selected Date Events */}
          <div className="space-y-4">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg">
                  {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {getEventsForDate(selectedDate).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No events scheduled</p>
                ) : (
                  <div className="space-y-3">
                    {getEventsForDate(selectedDate).map(event => (
                      <div
                        key={event.event_id}
                        className="p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-all"
                        onClick={() => setSelectedEvent(event)}
                        data-testid="event-item"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`w-2 h-2 rounded-full ${eventTypeColors[event.event_type]}`}></div>
                          <span className="font-medium text-sm">{event.title}</span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(event.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          {event.end_time && ` - ${new Date(event.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                        </div>
                        {event.invite_status && event.invite_status !== 'accepted' && (
                          <Badge variant="outline" className="mt-2 text-xs">
                            {event.invite_status === 'pending' ? 'Pending Response' : event.invite_status}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Upcoming Events */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg">Upcoming Events</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {events.filter(e => new Date(e.start_time) >= new Date()).slice(0, 5).map(event => (
                    <div key={event.event_id} className="flex items-center gap-3 p-2 rounded hover:bg-muted/50 cursor-pointer" onClick={() => setSelectedEvent(event)}>
                      <div className={`w-2 h-2 rounded-full ${eventTypeColors[event.event_type]}`}></div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{event.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(event.start_time).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Create Event Dialog */}
        <Dialog open={showCreateEvent} onOpenChange={setShowCreateEvent}>
          <DialogContent className="max-w-lg" data-testid="create-event-dialog">
            <DialogHeader>
              <DialogTitle>Create New Event</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateEvent} className="space-y-4">
              <div>
                <Label>Event Title *</Label>
                <Input
                  value={eventForm.title}
                  onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                  placeholder="Team Meeting"
                  required
                  data-testid="event-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Event Type</Label>
                  <Select value={eventForm.event_type} onValueChange={(v) => setEventForm({ ...eventForm, event_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="meeting">Meeting</SelectItem>
                      <SelectItem value="session">Session</SelectItem>
                      <SelectItem value="deadline">Deadline</SelectItem>
                      <SelectItem value="reminder">Reminder</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="is_virtual"
                    checked={eventForm.is_virtual}
                    onChange={(e) => setEventForm({ ...eventForm, is_virtual: e.target.checked })}
                  />
                  <Label htmlFor="is_virtual">Virtual Event</Label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Time *</Label>
                  <Input
                    type="datetime-local"
                    value={eventForm.start_time}
                    onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })}
                    required
                    data-testid="event-start-input"
                  />
                </div>
                <div>
                  <Label>End Time</Label>
                  <Input
                    type="datetime-local"
                    value={eventForm.end_time}
                    onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })}
                  />
                </div>
              </div>
              {eventForm.is_virtual ? (
                <div>
                  <Label>Meeting Link</Label>
                  <Input
                    value={eventForm.meeting_link}
                    onChange={(e) => setEventForm({ ...eventForm, meeting_link: e.target.value })}
                    placeholder="https://zoom.us/j/..."
                  />
                </div>
              ) : (
                <div>
                  <Label>Location</Label>
                  <Input
                    value={eventForm.location}
                    onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                    placeholder="Conference Room A"
                  />
                </div>
              )}
              <div>
                <Label>Description</Label>
                <Textarea
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  placeholder="Event details..."
                  rows={3}
                />
              </div>
              {users.length > 0 && (
                <div>
                  <Label>Invite Users (Email invites will be sent)</Label>
                  <Select
                    value=""
                    onValueChange={(v) => {
                      if (v && !eventForm.invitees.includes(v)) {
                        setEventForm({ ...eventForm, invitees: [...eventForm.invitees, v] });
                      }
                    }}
                  >
                    <SelectTrigger><SelectValue placeholder="Select users to invite" /></SelectTrigger>
                    <SelectContent>
                      {users.filter(u => u.user_id !== user?.user_id).map(u => (
                        <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {eventForm.invitees.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {eventForm.invitees.map(id => {
                        const invitee = users.find(u => u.user_id === id);
                        return (
                          <Badge key={id} variant="secondary" className="cursor-pointer" onClick={() => setEventForm({ ...eventForm, invitees: eventForm.invitees.filter(i => i !== id) })}>
                            {invitee?.name || id} ×
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              
              {/* Recurring Event Options */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="is_recurring"
                    checked={eventForm.is_recurring}
                    onChange={(e) => setEventForm({ ...eventForm, is_recurring: e.target.checked })}
                  />
                  <Label htmlFor="is_recurring" className="font-medium">Make this a recurring event</Label>
                </div>
                
                {eventForm.is_recurring && (
                  <div className="grid grid-cols-2 gap-4 pl-6">
                    <div>
                      <Label>Repeat</Label>
                      <Select value={eventForm.recurrence_type} onValueChange={(v) => setEventForm({ ...eventForm, recurrence_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Occurrences</Label>
                      <Input
                        type="number"
                        min="2"
                        max="52"
                        value={eventForm.recurrence_count}
                        onChange={(e) => setEventForm({ ...eventForm, recurrence_count: parseInt(e.target.value) })}
                      />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Event Thumbnail */}
              <div className="border rounded-lg p-3 space-y-3">
                <Label className="font-medium">Event Thumbnail (Optional)</Label>
                <p className="text-xs text-muted-foreground">Add an image to make your event stand out</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Image URL</Label>
                    <Input
                      placeholder="https://example.com/image.jpg"
                      value={eventForm.thumbnail}
                      onChange={(e) => {
                        setEventForm({ ...eventForm, thumbnail: e.target.value });
                        setEventThumbnailFile(null);
                      }}
                      data-testid="event-thumbnail-url"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Or Upload</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          setEventThumbnailFile(e.target.files[0]);
                          setEventForm({ ...eventForm, thumbnail: '' });
                        }
                      }}
                      data-testid="event-thumbnail-upload"
                    />
                  </div>
                </div>
                {(eventForm.thumbnail || eventThumbnailFile) && (
                  <div className="flex items-center gap-2">
                    <img 
                      src={eventThumbnailFile ? URL.createObjectURL(eventThumbnailFile) : eventForm.thumbnail} 
                      alt="Preview" 
                      className="h-16 w-24 object-cover rounded"
                    />
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setEventForm({ ...eventForm, thumbnail: '' });
                        setEventThumbnailFile(null);
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateEvent(false)}>Cancel</Button>
                <Button type="submit" disabled={uploadingEventThumbnail} data-testid="submit-event-btn">
                  {uploadingEventThumbnail ? 'Uploading...' : eventForm.is_recurring ? `Create ${eventForm.recurrence_count} Events` : 'Create Event'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Event Details Dialog */}
        <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{selectedEvent?.title}</DialogTitle>
            </DialogHeader>
            {selectedEvent && (
              <div className="space-y-4">
                {/* Event Thumbnail */}
                {selectedEvent.thumbnail && (
                  <div className="rounded-lg overflow-hidden">
                    <img 
                      src={selectedEvent.thumbnail} 
                      alt={selectedEvent.title}
                      className="w-full h-48 object-cover"
                    />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Badge className={eventTypeColors[selectedEvent.event_type]}>{selectedEvent.event_type}</Badge>
                  <Badge variant="outline">{selectedEvent.status}</Badge>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {new Date(selectedEvent.start_time).toLocaleString()}
                      {selectedEvent.end_time && ` - ${new Date(selectedEvent.end_time).toLocaleTimeString()}`}
                    </span>
                  </div>
                  {selectedEvent.location && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedEvent.location}</span>
                    </div>
                  )}
                  {selectedEvent.meeting_link && (
                    <div className="flex items-center gap-2">
                      <LinkIcon className="h-4 w-4 text-muted-foreground" />
                      <a href={selectedEvent.meeting_link} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                        Join Meeting
                      </a>
                    </div>
                  )}
                </div>
                {selectedEvent.description && (
                  <p className="text-sm text-muted-foreground">{selectedEvent.description}</p>
                )}
                {selectedEvent.invite_status === 'pending' && (
                  <div className="flex gap-2">
                    <Button onClick={() => handleRespondToInvite(selectedEvent.event_id, 'accepted')} className="flex-1">
                      Accept
                    </Button>
                    <Button variant="outline" onClick={() => handleRespondToInvite(selectedEvent.event_id, 'declined')} className="flex-1">
                      Decline
                    </Button>
                  </div>
                )}
                {(selectedEvent.created_by === user?.user_id || ['admin', 'pastor'].includes(user?.role)) && (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => handleEditEvent(selectedEvent)} className="flex-1" data-testid="edit-event-btn">
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Event
                    </Button>
                    <Button variant="destructive" onClick={() => handleDeleteEvent(selectedEvent.event_id)} className="flex-1" data-testid="delete-event-btn">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Event Dialog */}
        <Dialog open={showEditEvent} onOpenChange={setShowEditEvent}>
          <DialogContent className="max-w-lg" data-testid="edit-event-dialog">
            <DialogHeader>
              <DialogTitle>Edit Event</DialogTitle>
              <DialogDescription>Update event details</DialogDescription>
            </DialogHeader>
            {editingEvent && (
              <form onSubmit={handleUpdateEvent} className="space-y-4">
                <div>
                  <Label>Event Title *</Label>
                  <Input
                    value={editingEvent.title || ''}
                    onChange={(e) => setEditingEvent({ ...editingEvent, title: e.target.value })}
                    placeholder="Event title"
                    required
                    data-testid="edit-event-title-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Event Type</Label>
                    <Select 
                      value={editingEvent.event_type || 'meeting'} 
                      onValueChange={(v) => setEditingEvent({ ...editingEvent, event_type: v })}
                    >
                      <SelectTrigger data-testid="edit-event-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="meeting">Meeting</SelectItem>
                        <SelectItem value="session">Session</SelectItem>
                        <SelectItem value="deadline">Deadline</SelectItem>
                        <SelectItem value="reminder">Reminder</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select 
                      value={editingEvent.status || 'scheduled'} 
                      onValueChange={(v) => setEditingEvent({ ...editingEvent, status: v })}
                    >
                      <SelectTrigger data-testid="edit-event-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="scheduled">Scheduled</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start Time *</Label>
                    <Input
                      type="datetime-local"
                      value={editingEvent.start_time || ''}
                      onChange={(e) => setEditingEvent({ ...editingEvent, start_time: e.target.value })}
                      required
                      data-testid="edit-event-start-input"
                    />
                  </div>
                  <div>
                    <Label>End Time</Label>
                    <Input
                      type="datetime-local"
                      value={editingEvent.end_time || ''}
                      onChange={(e) => setEditingEvent({ ...editingEvent, end_time: e.target.value })}
                      data-testid="edit-event-end-input"
                    />
                  </div>
                </div>
                <div>
                  <Label>Location</Label>
                  <Input
                    value={editingEvent.location || ''}
                    onChange={(e) => setEditingEvent({ ...editingEvent, location: e.target.value })}
                    placeholder="Event location"
                    data-testid="edit-event-location-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="edit-is-virtual"
                    checked={editingEvent.is_virtual || false}
                    onChange={(e) => setEditingEvent({ ...editingEvent, is_virtual: e.target.checked })}
                  />
                  <Label htmlFor="edit-is-virtual">Virtual Event</Label>
                </div>
                {editingEvent.is_virtual && (
                  <div>
                    <Label>Meeting Link</Label>
                    <Input
                      value={editingEvent.meeting_link || ''}
                      onChange={(e) => setEditingEvent({ ...editingEvent, meeting_link: e.target.value })}
                      placeholder="https://meet.example.com/..."
                      data-testid="edit-event-meeting-link-input"
                    />
                  </div>
                )}
                <div>
                  <Label>Thumbnail URL</Label>
                  <Input
                    value={editingEvent.thumbnail || ''}
                    onChange={(e) => setEditingEvent({ ...editingEvent, thumbnail: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                    data-testid="edit-event-thumbnail-input"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={editingEvent.description || ''}
                    onChange={(e) => setEditingEvent({ ...editingEvent, description: e.target.value })}
                    placeholder="Event description..."
                    rows={3}
                    data-testid="edit-event-description-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowEditEvent(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="update-event-submit">
                    Update Event
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

// ==================== CMS EDITOR PAGE ====================

function CMSEditor() {
  const navigate = useNavigate();
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [newBlock, setNewBlock] = useState({ page: 'home', section: '', key: '', value: '', content_type: 'text' });
  const [showAddBlock, setShowAddBlock] = useState(false);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const response = await axiosInstance.get('/cms/content');
      setContent(response.data);
    } catch (error) {
      console.error('Error loading CMS content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      await axiosInstance.post('/cms/seed-defaults');
      toast.success('Default content seeded!');
      loadContent();
    } catch (error) {
      toast.error('Failed to seed defaults');
    }
  };

  const handleSaveBlock = async () => {
    if (!editingBlock) return;
    
    setSaving(true);
    try {
      await axiosInstance.put(
        `/cms/content/${editingBlock.page}/${editingBlock.section}/${editingBlock.key}`,
        { value: editValue }
      );
      toast.success('Content saved!');
      setEditingBlock(null);
      loadContent();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleAddBlock = async (e) => {
    e.preventDefault();
    
    try {
      await axiosInstance.post('/cms/content', newBlock);
      toast.success('Content block added!');
      setShowAddBlock(false);
      setNewBlock({ page: 'home', section: '', key: '', value: '', content_type: 'text' });
      loadContent();
    } catch (error) {
      toast.error('Failed to add block');
    }
  };

  const handleDeleteBlock = async (page, section, key) => {
    if (!window.confirm('Delete this content block?')) return;
    
    try {
      await axiosInstance.delete(`/cms/content/${page}/${section}/${key}`);
      toast.success('Content deleted');
      loadContent();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const pages = Object.keys(content);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl font-bold mb-2" data-testid="cms-heading">Content Editor</h1>
            <p className="text-muted-foreground">Edit website content without code</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
              ← Admin Dashboard
            </Button>
            <Button variant="outline" onClick={handleSeedDefaults}>
              Seed Defaults
            </Button>
            <Button onClick={() => setShowAddBlock(true)} data-testid="add-content-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Content
            </Button>
          </div>
        </div>

        {pages.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <div className="text-5xl mb-4">📝</div>
            <p className="text-muted-foreground mb-4">No content blocks yet. Click "Seed Defaults" to get started.</p>
            <Button onClick={handleSeedDefaults}>Seed Default Content</Button>
          </Card>
        ) : (
          <div className="space-y-8">
            {pages.map(page => (
              <Card key={page} className="glass-card">
                <CardHeader>
                  <CardTitle className="font-heading capitalize">{page} Page</CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.entries(content[page]).map(([section, blocks]) => (
                    <div key={section} className="mb-6">
                      <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-3">{section}</h3>
                      <div className="space-y-3">
                        {Object.entries(blocks).map(([key, value]) => (
                          <div key={key} className="flex items-start gap-4 p-3 rounded-lg border bg-muted/20">
                            <div className="flex-1">
                              <div className="text-xs text-muted-foreground mb-1">{key}</div>
                              {editingBlock?.page === page && editingBlock?.section === section && editingBlock?.key === key ? (
                                <div className="space-y-2">
                                  <Textarea
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    rows={3}
                                    className="text-sm"
                                  />
                                  <div className="flex gap-2">
                                    <Button size="sm" onClick={handleSaveBlock} disabled={saving}>
                                      {saving ? 'Saving...' : 'Save'}
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => setEditingBlock(null)}>
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <p className="text-sm">{value}</p>
                              )}
                            </div>
                            {!editingBlock && (
                              <div className="flex gap-1">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => {
                                    setEditingBlock({ page, section, key });
                                    setEditValue(value);
                                  }}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => handleDeleteBlock(page, section, key)}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add Content Block Dialog */}
        <Dialog open={showAddBlock} onOpenChange={setShowAddBlock}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Content Block</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddBlock} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Page *</Label>
                  <Select value={newBlock.page} onValueChange={(v) => setNewBlock({ ...newBlock, page: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="home">Home</SelectItem>
                      <SelectItem value="about">About</SelectItem>
                      <SelectItem value="features">Features</SelectItem>
                      <SelectItem value="global">Global</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Content Type</Label>
                  <Select value={newBlock.content_type} onValueChange={(v) => setNewBlock({ ...newBlock, content_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">Text</SelectItem>
                      <SelectItem value="html">HTML</SelectItem>
                      <SelectItem value="image">Image URL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Section *</Label>
                  <Input
                    value={newBlock.section}
                    onChange={(e) => setNewBlock({ ...newBlock, section: e.target.value })}
                    placeholder="hero, features, footer..."
                    required
                  />
                </div>
                <div>
                  <Label>Key *</Label>
                  <Input
                    value={newBlock.key}
                    onChange={(e) => setNewBlock({ ...newBlock, key: e.target.value })}
                    placeholder="title, subtitle, cta..."
                    required
                  />
                </div>
              </div>
              <div>
                <Label>Value *</Label>
                <Textarea
                  value={newBlock.value}
                  onChange={(e) => setNewBlock({ ...newBlock, value: e.target.value })}
                  placeholder="Enter content..."
                  rows={4}
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowAddBlock(false)}>Cancel</Button>
                <Button type="submit">Add Content</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

// ==================== DASHBOARD DRILL-DOWN DIALOG ====================

function DashboardDrillDown({ type, onClose }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [type]);

  const loadData = async () => {
    try {
      const response = await axiosInstance.get(`/dashboard/stats/detail/${type}`);
      setData(response.data.data);
    } catch (error) {
      console.error('Error loading drill-down data:', error);
    } finally {
      setLoading(false);
    }
  };

  const titles = {
    mentorships: 'Mentorships',
    goals: 'Goals',
    tasks: 'Tasks',
    sessions: 'Sessions',
    courses: 'Course Enrollments',
    events: 'Events'
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{titles[type] || type}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : data.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No data available</p>
        ) : (
          <div className="space-y-3">
            {data.map((item, index) => (
              <Card key={index} className="border">
                <CardContent className="p-4">
                  {type === 'mentorships' && (
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">Mentor: {item.mentor?.name || 'Unknown'}</div>
                        <div className="text-sm text-muted-foreground">Mentee: {item.mentee?.name || 'Unknown'}</div>
                      </div>
                      <Badge>{item.status}</Badge>
                    </div>
                  )}
                  {type === 'goals' && (
                    <div>
                      <div className="font-medium">{item.title}</div>
                      <div className="text-sm text-muted-foreground">{item.description}</div>
                      <Badge className="mt-2">{item.status}</Badge>
                    </div>
                  )}
                  {type === 'tasks' && (
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{item.title}</div>
                        <div className="text-sm text-muted-foreground">{item.description}</div>
                      </div>
                      <Badge className={
                        item.status === 'Done' ? 'bg-green-500' :
                        item.status === 'In Progress' ? 'bg-yellow-500' : 'bg-gray-500'
                      }>{item.status}</Badge>
                    </div>
                  )}
                  {type === 'sessions' && (
                    <div>
                      <div className="font-medium">{item.title}</div>
                      <div className="text-sm text-muted-foreground">
                        {new Date(item.scheduled_at).toLocaleString()}
                      </div>
                      <Badge className="mt-2">{item.status}</Badge>
                    </div>
                  )}
                  {type === 'courses' && (
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{item.course?.title || 'Unknown Course'}</div>
                        <div className="text-sm text-muted-foreground">
                          Enrolled: {new Date(item.enrolled_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-primary">{item.progress_percent}%</div>
                        <Badge>{item.status}</Badge>
                      </div>
                    </div>
                  )}
                  {type === 'events' && (
                    <div>
                      <div className="font-medium">{item.title}</div>
                      <div className="text-sm text-muted-foreground">
                        {new Date(item.start_time).toLocaleString()}
                      </div>
                      <Badge className="mt-2">{item.event_type}</Badge>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ==================== SALVATION PAGE ====================
function SalvationPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedBelief, setExpandedBelief] = useState(null);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const res = await axios.get(`${API}/salvation-content`);
      setContent(res.data);
    } catch (error) {
      console.error('Failed to load salvation content:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gradient-to-b from-amber-50 to-white'}`}>
      {/* Header */}
      <header className="py-4 px-6 border-b bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <button onClick={() => navigate('/')} className="text-2xl font-bold text-primary hover:opacity-80 transition">
            TheFeedingFaith
          </button>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate('/new-visitor')}>
              Connect With Us
            </Button>
            <Button onClick={() => navigate('/login')}>Sign In</Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
            <Heart className="h-5 w-5 fill-current" />
            <span className="font-medium">A New Beginning</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
            {content?.page_title || "Welcome to God's Family"}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {content?.intro_text || "We are so glad you've taken this step of faith!"}
          </p>
        </motion.div>

        {/* Salvation Prayer Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="mb-8 border-primary/20 shadow-lg">
            <CardHeader className="bg-primary/5 border-b">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <BookOpen className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl">The Salvation Prayer</CardTitle>
                  <CardDescription>Pray this prayer from your heart</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p className="whitespace-pre-line text-foreground/90 leading-relaxed italic">
                  {content?.salvation_prayer}
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Things to Know Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="mb-8 shadow-lg">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Lightbulb className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <CardTitle className="text-xl">Things to Know About God's Kingdom</CardTitle>
                  <CardDescription>Your new identity in Christ</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid gap-3">
                {content?.things_to_know?.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-900/20">
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* What We Believe Section - Collapsible */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="mb-8 shadow-lg">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Church className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <CardTitle className="text-xl">What We Believe</CardTitle>
                  <CardDescription>Our core beliefs and doctrines</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-2">
                {content?.beliefs?.sort((a, b) => a.order - b.order).map((belief, idx) => (
                  <div key={idx} className="border rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedBelief(expandedBelief === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition"
                    >
                      <div className="flex items-center gap-3">
                        <span className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
                          {idx + 1}
                        </span>
                        <span className="font-medium">{belief.title}</span>
                      </div>
                      <ChevronRight className={`h-5 w-5 text-muted-foreground transition-transform ${expandedBelief === idx ? 'rotate-90' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {expandedBelief === idx && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 pb-4 pt-0 ml-11 text-muted-foreground">
                            {belief.content}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Footer Message & CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-center"
        >
          <Card className="bg-primary text-primary-foreground shadow-lg">
            <CardContent className="p-8">
              <HandHeart className="h-12 w-12 mx-auto mb-4 opacity-90" />
              <p className="text-lg mb-6 opacity-90">
                {content?.footer_message || "We would love to connect with you and help you grow in your new faith!"}
              </p>
              <Button 
                size="lg" 
                variant="secondary"
                onClick={() => navigate('/new-visitor')}
                className="text-lg px-8"
              >
                Connect With Us <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </main>

      {/* Simple Footer */}
      <footer className="py-8 px-6 border-t mt-12">
        <div className="max-w-4xl mx-auto text-center text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} TheFeedingFaith. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

// ==================== NEW VISITOR FORM PAGE ====================
function NewVisitorPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    how_found_us: '',
    prayer_request: '',
    interested_in: [],
    accepted_christ: false,
    wants_baptism: false,
    wants_membership: false,
    notes: ''
  });

  const interestOptions = [
    'Attending Sunday Service',
    'Small Groups / Bible Study',
    'Children\'s Ministry',
    'Youth Ministry',
    'Music / Worship Team',
    'Volunteer Opportunities',
    'Counseling / Support',
    'Marriage Ministry',
    'Men\'s / Women\'s Ministry'
  ];

  const howFoundOptions = [
    'Friend or Family',
    'Social Media',
    'Website / Online Search',
    'Church Event',
    'Community Outreach',
    'Driving By',
    'Other'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/visitors`, formData);
      setSubmitted(true);
      toast.success('Information submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleInterest = (interest) => {
    setFormData(prev => ({
      ...prev,
      interested_in: prev.interested_in.includes(interest)
        ? prev.interested_in.filter(i => i !== interest)
        : [...prev.interested_in, interest]
    }));
  };

  if (submitted) {
    return (
      <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gradient-to-b from-amber-50 to-white'}`}>
        <header className="py-4 px-6 border-b bg-background/80 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto">
            <button onClick={() => navigate('/')} className="text-2xl font-bold text-primary hover:opacity-80 transition">
              TheFeedingFaith
            </button>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-6 py-16 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="h-20 w-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="h-10 w-10 text-green-600" />
          </motion.div>
          <h1 className="text-3xl font-bold mb-4">Thank You!</h1>
          <p className="text-lg text-muted-foreground mb-8">
            We have received your information. Someone from our team will be in touch with you soon. 
            We're excited to connect with you!
          </p>
          <div className="flex gap-4 justify-center">
            <Button onClick={() => navigate('/welcome-to-gods-family')} variant="outline">
              <BookOpen className="mr-2 h-4 w-4" /> Read About Our Faith
            </Button>
            <Button onClick={() => navigate('/')}>
              <Home className="mr-2 h-4 w-4" /> Go to Homepage
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gradient-to-b from-amber-50 to-white'}`}>
      {/* Header */}
      <header className="py-4 px-6 border-b bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <button onClick={() => navigate('/')} className="text-2xl font-bold text-primary hover:opacity-80 transition">
            TheFeedingFaith
          </button>
          <Button variant="outline" onClick={() => navigate('/welcome-to-gods-family')}>
            Learn About Our Faith
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
            <Users className="h-5 w-5" />
            <span className="font-medium">Welcome</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">We'd Love to Connect With You</h1>
          <p className="text-muted-foreground">
            Please share your information so we can follow up and help you on your journey.
          </p>
        </motion.div>

        <Card className="shadow-lg">
          <CardContent className="p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                      required
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                      required
                      placeholder="Doe"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  Address (Optional)
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="address">Street Address</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      placeholder="123 Main Street"
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        value={formData.state}
                        onChange={(e) => setFormData({...formData, state: e.target.value})}
                        placeholder="TX"
                      />
                    </div>
                    <div>
                      <Label htmlFor="zip_code">Zip Code</Label>
                      <Input
                        id="zip_code"
                        value={formData.zip_code}
                        onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                        placeholder="12345"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* How did you find us */}
              <div>
                <Label htmlFor="how_found_us">How did you hear about us?</Label>
                <Select value={formData.how_found_us} onValueChange={(v) => setFormData({...formData, how_found_us: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    {howFoundOptions.map(opt => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Interests */}
              <div>
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Heart className="h-5 w-5 text-primary" />
                  What are you interested in?
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {interestOptions.map(interest => (
                    <div 
                      key={interest}
                      onClick={() => toggleInterest(interest)}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${
                        formData.interested_in.includes(interest) 
                          ? 'border-primary bg-primary/5' 
                          : 'hover:bg-muted/50'
                      }`}
                    >
                      <div className={`h-5 w-5 rounded border flex items-center justify-center ${
                        formData.interested_in.includes(interest) 
                          ? 'bg-primary border-primary text-primary-foreground' 
                          : 'border-muted-foreground/30'
                      }`}>
                        {formData.interested_in.includes(interest) && <Check className="h-3.5 w-3.5" />}
                      </div>
                      <span className="text-sm">{interest}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Spiritual Decisions */}
              <div>
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Church className="h-5 w-5 text-primary" />
                  Spiritual Decisions
                </h3>
                <div className="space-y-3">
                  <div 
                    onClick={() => setFormData({...formData, accepted_christ: !formData.accepted_christ})}
                    className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition ${
                      formData.accepted_christ ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                    }`}
                  >
                    <div className={`h-5 w-5 rounded border flex items-center justify-center ${
                      formData.accepted_christ 
                        ? 'bg-primary border-primary text-primary-foreground' 
                        : 'border-muted-foreground/30'
                    }`}>
                      {formData.accepted_christ && <Check className="h-3.5 w-3.5" />}
                    </div>
                    <div>
                      <div className="font-medium">I accepted Jesus Christ as my Savior</div>
                      <div className="text-sm text-muted-foreground">I prayed the salvation prayer today</div>
                    </div>
                  </div>
                  <div 
                    onClick={() => setFormData({...formData, wants_baptism: !formData.wants_baptism})}
                    className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition ${
                      formData.wants_baptism ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                    }`}
                  >
                    <div className={`h-5 w-5 rounded border flex items-center justify-center ${
                      formData.wants_baptism 
                        ? 'bg-primary border-primary text-primary-foreground' 
                        : 'border-muted-foreground/30'
                    }`}>
                      {formData.wants_baptism && <Check className="h-3.5 w-3.5" />}
                    </div>
                    <div>
                      <div className="font-medium">I want to be baptized</div>
                      <div className="text-sm text-muted-foreground">I'd like to learn more about water baptism</div>
                    </div>
                  </div>
                  <div 
                    onClick={() => setFormData({...formData, wants_membership: !formData.wants_membership})}
                    className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition ${
                      formData.wants_membership ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                    }`}
                  >
                    <div className={`h-5 w-5 rounded border flex items-center justify-center ${
                      formData.wants_membership 
                        ? 'bg-primary border-primary text-primary-foreground' 
                        : 'border-muted-foreground/30'
                    }`}>
                      {formData.wants_membership && <Check className="h-3.5 w-3.5" />}
                    </div>
                    <div>
                      <div className="font-medium">I want to become a member</div>
                      <div className="text-sm text-muted-foreground">I'd like to join the church family</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Prayer Request */}
              <div>
                <Label htmlFor="prayer_request">Prayer Request or Questions (Optional)</Label>
                <Textarea
                  id="prayer_request"
                  value={formData.prayer_request}
                  onChange={(e) => setFormData({...formData, prayer_request: e.target.value})}
                  placeholder="Share any prayer requests or questions you have..."
                  rows={4}
                />
              </div>

              {/* Submit */}
              <Button type="submit" size="lg" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>Submit Information</>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t mt-12">
        <div className="max-w-3xl mx-auto text-center text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} TheFeedingFaith. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

// ==================== ADVANCED COURSES PAGES ====================
function AdvancedCoursesPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      const res = await axios.get(`${API}/advanced-courses`);
      setCourses(res.data);
    } catch (error) {
      console.error('Failed to load courses:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gradient-to-b from-amber-50 to-white'}`}>
      <header className="py-4 px-6 border-b bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <button onClick={() => navigate('/')} className="text-2xl font-bold text-primary hover:opacity-80 transition">
            TheFeedingFaith
          </button>
          <Button onClick={() => navigate('/login')}>Sign In</Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
            <GraduationCap className="h-5 w-5" />
            <span className="font-medium">Bible Study Courses</span>
          </div>
          <h1 className="text-4xl font-bold mb-4">Deepen Your Faith Through Study</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Comprehensive courses with video lessons, interactive slideshows, and assessments. 
            Earn certificates upon completion.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course, idx) => (
            <motion.div
              key={course.course_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
            >
              <Card className="h-full hover:shadow-lg transition overflow-hidden group">
                <div className="h-40 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                  <BookOpen className="h-16 w-16 text-primary/40 group-hover:scale-110 transition" />
                </div>
                <CardContent className="p-6">
                  <Badge variant="outline" className="mb-3">{course.category}</Badge>
                  <h3 className="font-bold text-lg mb-2 line-clamp-2">{course.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-3">{course.description}</p>
                  <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                    <span className="flex items-center gap-1">
                      <BookOpen className="h-4 w-4" /> {course.lesson_count} lessons
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" /> {course.enrollment_count} enrolled
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-primary">${course.price}</span>
                    <Button onClick={() => navigate(`/study/${course.course_id}`)}>
                      View Course <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
}

function AdvancedCourseDetail() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    loadCourse();
    checkAuth();
  }, [courseId]);

  const checkAuth = async () => {
    try {
      const res = await axiosInstance.get('/auth/me');
      setUser(res.data);
      loadProgress();
    } catch {
      setUser(null);
    }
  };

  const loadCourse = async () => {
    try {
      const res = await axios.get(`${API}/advanced-courses/${courseId}`);
      setCourse(res.data);
    } catch (error) {
      console.error('Failed to load course:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProgress = async () => {
    try {
      const res = await axiosInstance.get(`/advanced-courses/${courseId}/progress`);
      setProgress(res.data);
    } catch {
      setProgress(null);
    }
  };

  const handleEnroll = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setEnrolling(true);
    try {
      await axiosInstance.post(`/advanced-courses/${courseId}/enroll`);
      toast.success('Enrolled successfully!');
      loadProgress();
    } catch (error) {
      toast.error('Failed to enroll');
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Course not found</p>
      </div>
    );
  }

  const isEnrolled = progress?.enrolled;

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gradient-to-b from-amber-50 to-white'}`}>
      <header className="py-4 px-6 border-b bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <button onClick={() => navigate('/study')} className="text-2xl font-bold text-primary hover:opacity-80 transition">
            TheFeedingFaith
          </button>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate('/study')}>
              <ArrowRight className="mr-2 h-4 w-4 rotate-180" /> All Courses
            </Button>
            {user ? (
              <Button variant="outline" onClick={() => navigate('/dashboard')}>Dashboard</Button>
            ) : (
              <Button onClick={() => navigate('/login')}>Sign In</Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Course Header */}
        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          <div className="lg:col-span-2">
            <Badge variant="outline" className="mb-4">{course.category}</Badge>
            <h1 className="text-3xl font-bold mb-4">{course.title}</h1>
            <p className="text-muted-foreground mb-6">{course.description}</p>
            
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2 bg-muted px-3 py-2 rounded-lg">
                <BookOpen className="h-4 w-4 text-primary" />
                <span>{course.lessons?.length || 0} Lessons</span>
              </div>
              <div className="flex items-center gap-2 bg-muted px-3 py-2 rounded-lg">
                <Users className="h-4 w-4 text-primary" />
                <span>{course.enrollment_count} Enrolled</span>
              </div>
              <div className="flex items-center gap-2 bg-muted px-3 py-2 rounded-lg">
                <Award className="h-4 w-4 text-primary" />
                <span>Certificate on Completion</span>
              </div>
            </div>
          </div>

          <Card className="p-6">
            <div className="text-center mb-6">
              <div className="text-4xl font-bold text-primary mb-2">${course.price}</div>
              <p className="text-sm text-muted-foreground">One-time payment</p>
            </div>

            {isEnrolled ? (
              <div className="space-y-4">
                <div className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 p-3 rounded-lg text-center">
                  <CheckCircle className="h-5 w-5 inline mr-2" /> Enrolled
                </div>
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-2">Progress</p>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all" 
                      style={{ width: `${progress?.progress_percentage || 0}%` }}
                    />
                  </div>
                  <p className="text-sm mt-1">{progress?.completed_lessons || 0} / {progress?.total_lessons || 0} lessons</p>
                </div>
                {progress?.assessment_score && (
                  <div className="text-center bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                    <p className="text-sm text-muted-foreground">Assessment Score</p>
                    <p className="text-2xl font-bold text-amber-600">{progress.assessment_score}%</p>
                  </div>
                )}
                {progress?.certificate_id && (
                  <Button className="w-full" onClick={() => navigate(`/certificates/${progress.certificate_id}`)}>
                    <Award className="mr-2 h-4 w-4" /> View Certificate
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <Button className="w-full" size="lg" onClick={handleEnroll} disabled={enrolling}>
                  {enrolling ? 'Enrolling...' : 'Enroll Now'}
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  First 2 lessons available as free preview
                </p>
              </div>
            )}
          </Card>
        </div>

        {/* Lessons List */}
        <Card>
          <CardHeader>
            <CardTitle>Course Curriculum</CardTitle>
            <CardDescription>{course.lessons?.length || 0} lessons</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {course.lessons?.map((lesson, idx) => {
                const isCompleted = progress?.completed_lesson_ids?.includes(lesson.lesson_id);
                const isLocked = !lesson.is_free_preview && !isEnrolled;
                
                return (
                  <div
                    key={lesson.lesson_id}
                    className={`flex items-center gap-4 p-4 rounded-lg border transition ${
                      isLocked ? 'opacity-60' : 'hover:bg-muted/50 cursor-pointer'
                    }`}
                    onClick={() => !isLocked && navigate(`/study/${courseId}/lesson/${lesson.lesson_id}`)}
                  >
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                      isCompleted ? 'bg-green-100 text-green-600' : 
                      lesson.is_free_preview ? 'bg-primary/10 text-primary' : 
                      'bg-muted text-muted-foreground'
                    }`}>
                      {isCompleted ? <CheckCircle className="h-5 w-5" /> : 
                       isLocked ? <Lock className="h-5 w-5" /> : 
                       <span className="font-semibold">{lesson.lesson_number}</span>}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{lesson.title}</h4>
                        {lesson.is_free_preview && !isEnrolled && (
                          <Badge variant="secondary" className="text-xs">Free Preview</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-1">{lesson.description}</p>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {lesson.duration_minutes} min
                    </div>
                    {!isLocked && <ChevronRight className="h-5 w-5 text-muted-foreground" />}
                  </div>
                );
              })}
            </div>

            {/* Assessment Button */}
            {isEnrolled && progress?.progress_percentage === 100 && !progress?.certificate_id && (
              <div className="mt-6 p-4 bg-primary/5 rounded-lg text-center">
                <h4 className="font-semibold mb-2">Ready for Assessment?</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Complete a 60-question assessment to earn your certificate
                </p>
                <Button onClick={() => navigate(`/study/${courseId}/assessment`)}>
                  Start Assessment <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function AdvancedLessonPlayer() {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [autoPlay, setAutoPlay] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    loadLesson();
  }, [courseId, lessonId]);

  // Auto-play audio when slide changes (if auto-play is enabled)
  useEffect(() => {
    if (autoPlay && lesson?.slides?.[currentSlide] && !ttsLoading && !isPlaying) {
      playSlideAudio();
    }
  }, [currentSlide, autoPlay, lesson]);

  const loadLesson = async () => {
    try {
      // Try with auth first, fall back to public for free preview
      let res;
      try {
        res = await axiosInstance.get(`/advanced-courses/${courseId}/lessons/${lessonId}`);
      } catch (authError) {
        // Try public API for free preview lessons
        res = await axios.get(`${API}/advanced-courses/${courseId}/lessons/${lessonId}`);
      }
      setLesson(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Enrollment required to access this lesson');
        navigate(`/study/${courseId}`);
      } else {
        toast.error('Failed to load lesson');
        navigate(`/study/${courseId}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const generateTTS = async (text) => {
    setTtsLoading(true);
    try {
      // TTS requires authentication for rate limiting
      const res = await axios.post(`${API}/tts/generate`, { text });
      setAudioUrl(res.data.audio_url);
      return res.data.audio_url;
    } catch (error) {
      if (error.response?.status === 503) {
        toast.error('Text-to-speech not configured. Add ElevenLabs API key to enable.');
      } else {
        toast.error('Failed to generate audio');
      }
      return null;
    } finally {
      setTtsLoading(false);
    }
  };

  const playSlideAudio = async () => {
    if (!lesson?.slides?.[currentSlide]) return;
    
    const slide = lesson.slides[currentSlide];
    const text = `${slide.title}. ${slide.content}`;
    
    const url = await generateTTS(text);
    if (url && audioRef.current) {
      audioRef.current.src = url;
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    // Auto-advance to next slide when audio ends (if auto-play is on)
    if (autoPlay && currentSlide < (lesson?.slides?.length || 0) - 1) {
      setCurrentSlide(currentSlide + 1);
      // Note: The useEffect will trigger playSlideAudio for the new slide
    }
  };

  const nextSlide = () => {
    if (currentSlide < (lesson?.slides?.length || 0) - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Lesson not found</p>
      </div>
    );
  }

  const slide = lesson.slides?.[currentSlide];

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Header */}
      <header className="py-3 px-6 border-b bg-background/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/study/${courseId}`)}>
              <ChevronLeft className="h-4 w-4 mr-1" /> Back to Course
            </Button>
            <span className="text-sm text-muted-foreground">
              Lesson {lesson.lesson_number}: {lesson.title}
            </span>
          </div>
          <div className="text-sm text-muted-foreground">
            Slide {currentSlide + 1} of {lesson.slides?.length || 0}
          </div>
        </div>
      </header>

      {/* Slideshow */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        <Card className="overflow-hidden">
          {/* Slide Content */}
          <div className="min-h-[400px] p-8 bg-gradient-to-br from-primary/5 to-background">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="h-full"
            >
              {/* Slide Image (if available) */}
              {slide?.image_url && (
                <div className="mb-6 rounded-lg overflow-hidden shadow-lg max-w-md mx-auto">
                  <img 
                    src={slide.image_url} 
                    alt={slide.title}
                    className="w-full h-48 object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
              <h2 className="text-3xl font-bold mb-6">{slide?.title}</h2>
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p className="whitespace-pre-line">{slide?.content}</p>
              </div>
              {slide?.bullet_points?.length > 0 && (
                <ul className="mt-6 space-y-2">
                  {slide.bullet_points.map((point, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              )}
              {slide?.scripture_reference && (
                <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border-l-4 border-amber-500">
                  <p className="font-medium text-amber-800 dark:text-amber-200">{slide.scripture_reference}</p>
                </div>
              )}
            </motion.div>
          </div>

          {/* Controls */}
          <div className="p-4 border-t bg-muted/30">
            <div className="flex items-center justify-between">
              <Button variant="outline" onClick={prevSlide} disabled={currentSlide === 0}>
                <ChevronLeft className="h-4 w-4 mr-1" /> Previous
              </Button>
              
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    onClick={playSlideAudio}
                    disabled={ttsLoading || isPlaying}
                  >
                    {ttsLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2" />
                    ) : isPlaying ? (
                      <Volume2 className="h-4 w-4 mr-2 animate-pulse" />
                    ) : (
                      <Play className="h-4 w-4 mr-2" />
                    )}
                    {ttsLoading ? 'Loading...' : isPlaying ? 'Playing...' : 'Listen'}
                  </Button>
                  
                  {/* Auto-Play Toggle */}
                  <Button 
                    variant={autoPlay ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setAutoPlay(!autoPlay);
                      if (!autoPlay && !isPlaying && !ttsLoading) {
                        // If turning on auto-play, start playing current slide
                        playSlideAudio();
                      }
                    }}
                    className={autoPlay ? "bg-green-600 hover:bg-green-700" : ""}
                    data-testid="auto-play-toggle"
                  >
                    {autoPlay ? (
                      <>
                        <Repeat className="h-4 w-4 mr-1" /> Auto
                      </>
                    ) : (
                      <>
                        <Repeat className="h-4 w-4 mr-1" /> Auto
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Progress indicator */}
                <div className="flex gap-1">
                  {lesson.slides?.map((_, i) => (
                    <div
                      key={i}
                      className={`h-2 w-2 rounded-full transition ${
                        i === currentSlide ? 'bg-primary' : 
                        i < currentSlide ? 'bg-primary/40' : 'bg-muted'
                      }`}
                      onClick={() => setCurrentSlide(i)}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </div>
              </div>

              <Button 
                onClick={nextSlide} 
                disabled={currentSlide === (lesson.slides?.length || 0) - 1}
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </Card>

        {/* Hidden Audio Element */}
        <audio ref={audioRef} onEnded={handleAudioEnded} />

        {/* Lesson Complete Button */}
        {currentSlide === (lesson.slides?.length || 0) - 1 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 text-center"
          >
            <Button size="lg" onClick={() => navigate(`/study/${courseId}`)}>
              <CheckCircle className="mr-2 h-5 w-5" /> Complete Lesson
            </Button>
          </motion.div>
        )}
      </main>
    </div>
  );
}

function AdvancedAssessment() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(90 * 60); // 90 minutes in seconds

  useEffect(() => {
    loadAssessment();
  }, [courseId]);

  useEffect(() => {
    if (assessment && !results && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(t => {
          if (t <= 1) {
            clearInterval(timer);
            handleSubmit();
            return 0;
          }
          return t - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [assessment, results]);

  const loadAssessment = async () => {
    try {
      const res = await axiosInstance.get(`/advanced-courses/${courseId}/assessment`);
      setAssessment(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load assessment');
      navigate(`/study/${courseId}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await axiosInstance.post(`/advanced-courses/${courseId}/assessment/submit`, {
        answers
      });
      setResults(res.data);
      if (res.data.passed) {
        toast.success('Congratulations! You passed the assessment!');
      } else {
        toast.error(`Score: ${res.data.score}%. You need 70% to pass.`);
      }
    } catch (error) {
      toast.error('Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (results) {
    return (
      <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gradient-to-b from-amber-50 to-white'}`}>
        <main className="max-w-2xl mx-auto px-6 py-16">
          <Card className="text-center p-8">
            <div className={`h-20 w-20 rounded-full mx-auto mb-6 flex items-center justify-center ${
              results.passed ? 'bg-green-100' : 'bg-red-100'
            }`}>
              {results.passed ? (
                <Award className="h-10 w-10 text-green-600" />
              ) : (
                <X className="h-10 w-10 text-red-600" />
              )}
            </div>
            <h1 className="text-3xl font-bold mb-4">
              {results.passed ? 'Congratulations!' : 'Keep Trying!'}
            </h1>
            <div className="text-6xl font-bold mb-4" style={{ color: results.passed ? '#16a34a' : '#dc2626' }}>
              {results.score}%
            </div>
            <p className="text-muted-foreground mb-6">
              You got {results.correct_count} out of {results.total_questions} questions correct.
              {!results.passed && ` You need 70% to pass.`}
            </p>
            
            {results.certificate && (
              <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <Award className="h-8 w-8 text-amber-600 mx-auto mb-2" />
                <p className="font-medium">Certificate Earned!</p>
                <p className="text-sm text-muted-foreground">ID: {results.certificate.certificate_id}</p>
                {results.certificate.qr_code && (
                  <img src={results.certificate.qr_code} alt="Certificate QR" className="w-32 h-32 mx-auto mt-4" />
                )}
              </div>
            )}

            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => navigate(`/study/${courseId}`)}>
                Back to Course
              </Button>
              {results.certificate && (
                <Button onClick={() => navigate(`/certificates/${results.certificate.certificate_id}`)}>
                  View Certificate
                </Button>
              )}
            </div>
          </Card>
        </main>
      </div>
    );
  }

  const question = assessment?.questions?.[currentQuestion];

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'}`}>
      <header className="py-3 px-6 border-b bg-background/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="font-semibold">{assessment?.course_title} - Assessment</h1>
            <p className="text-sm text-muted-foreground">
              Question {currentQuestion + 1} of {assessment?.total_questions}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className={`px-4 py-2 rounded-lg font-mono font-bold ${
              timeRemaining < 300 ? 'bg-red-100 text-red-600' : 'bg-muted'
            }`}>
              {formatTime(timeRemaining)}
            </div>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Assessment'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all"
              style={{ width: `${(Object.keys(answers).length / assessment?.total_questions) * 100}%` }}
            />
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            {Object.keys(answers).length} of {assessment?.total_questions} answered
          </p>
        </div>

        {/* Question Card */}
        <Card className="p-6">
          <div className="mb-4 flex items-center gap-2">
            <Badge variant="outline">{question?.question_type?.replace('_', ' ')}</Badge>
            {question?.book_reference && (
              <Badge variant="secondary">{question.book_reference}</Badge>
            )}
          </div>
          
          <h3 className="text-xl font-semibold mb-6">{question?.question_text}</h3>

          <div className="space-y-3">
            {question?.question_type === 'fill_blank' ? (
              <Input
                placeholder="Type your answer..."
                value={answers[question?.question_id] || ''}
                onChange={(e) => handleAnswer(question?.question_id, e.target.value)}
              />
            ) : (
              question?.options?.map((option) => (
                <div
                  key={option.id}
                  onClick={() => handleAnswer(question?.question_id, option.id)}
                  className={`p-4 rounded-lg border cursor-pointer transition ${
                    answers[question?.question_id] === option.id
                      ? 'border-primary bg-primary/5'
                      : 'hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                      answers[question?.question_id] === option.id
                        ? 'border-primary bg-primary'
                        : 'border-muted-foreground'
                    }`}>
                      {answers[question?.question_id] === option.id && (
                        <Check className="h-3 w-3 text-white" />
                      )}
                    </div>
                    <span>{option.text}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
            disabled={currentQuestion === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          
          <div className="flex gap-2 flex-wrap justify-center max-w-md">
            {assessment?.questions?.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentQuestion(i)}
                className={`h-8 w-8 rounded text-sm font-medium transition ${
                  i === currentQuestion ? 'bg-primary text-white' :
                  answers[assessment.questions[i].question_id] ? 'bg-green-100 text-green-700' :
                  'bg-muted hover:bg-muted/80'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <Button
            onClick={() => setCurrentQuestion(Math.min(assessment?.total_questions - 1, currentQuestion + 1))}
            disabled={currentQuestion === assessment?.total_questions - 1}
          >
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </main>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/welcome-to-gods-family" element={<SalvationPage />} />
      <Route path="/new-visitor" element={<NewVisitorPage />} />
      <Route path="/study" element={<AdvancedCoursesPage />} />
      <Route path="/study/:courseId" element={<AdvancedCourseDetail />} />
      <Route path="/study/:courseId/lesson/:lessonId" element={<AdvancedLessonPlayer />} />
      <Route path="/study/:courseId/assessment" element={<ProtectedRoute><AdvancedAssessment /></ProtectedRoute>} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/admin/mentorship-audit/:mentorshipId" element={<ProtectedRoute><MentorshipAuditPage /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/role-onboarding" element={<ProtectedRoute skipOnboardingCheck><RoleOnboarding /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/mentors" element={<ProtectedRoute><MentorBrowse /></ProtectedRoute>} />
      <Route path="/mentorship/:id" element={<ProtectedRoute><MentorshipDetail /></ProtectedRoute>} />
      <Route path="/budget" element={<ProtectedRoute><PersonalBudgetV2 /></ProtectedRoute>} />
      <Route path="/budget/:id" element={<ProtectedRoute><BudgetPage /></ProtectedRoute>} />
      <Route path="/shared" element={<ProtectedRoute><SharedWithMe /></ProtectedRoute>} />
      <Route path="/shared/:shareLink" element={<SharedView />} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
      <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/donation-success" element={<ProtectedRoute><DonationSuccess /></ProtectedRoute>} />
      {/* LMS Routes */}
      <Route path="/courses" element={<CourseCatalog />} />
      <Route path="/courses/create" element={<ProtectedRoute><CourseCreate /></ProtectedRoute>} />
      <Route path="/courses/:id/edit" element={<ProtectedRoute><CourseEditor /></ProtectedRoute>} />
      <Route path="/courses/:id" element={<CourseDetail />} />
      <Route path="/lessons/:id" element={<ProtectedRoute><LessonViewer /></ProtectedRoute>} />
      <Route path="/certificates/:id" element={<ProtectedRoute><CertificateView /></ProtectedRoute>} />
      <Route path="/certificate/verify/:verificationCode" element={<CertificateVerify />} />
      {/* Calendar & CMS Routes */}
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/admin/cms" element={<ProtectedRoute><CMSEditor /></ProtectedRoute>} />
      {/* Church CRM Routes */}
      <Route path="/about" element={<AboutPage />} />
      <Route path="/worship" element={<WorshipSongsPage />} />
      <Route path="/sermons" element={<SermonsPage />} />
      <Route path="/store" element={<StorePage />} />
      <Route path="/store/success" element={<StoreSuccessPage />} />
      <Route path="/my-purchases" element={<ProtectedRoute><MyPurchasesPage /></ProtectedRoute>} />
      <Route path="/giving" element={<GivingPage />} />
      <Route path="/community" element={<CommunityPage />} />
      <Route path="/prayer-requests" element={<PrayerRequestsPage />} />
      <Route path="/pastor-corner" element={<PastorCornerPage />} />
      <Route path="/weekly-focus-manage" element={<ProtectedRoute><WeeklyFocusManagement /></ProtectedRoute>} />
      <Route path="/counseling" element={<ProtectedRoute><CounselingPage /></ProtectedRoute>} />
      <Route path="/members" element={<ProtectedRoute><MembersPage /></ProtectedRoute>} />
      <Route path="/checkin" element={<ProtectedRoute><CheckInPage /></ProtectedRoute>} />
      <Route path="/leadership-assessment" element={<ProtectedRoute><LeadershipAssessmentPage /></ProtectedRoute>} />
      <Route path="/ai-tools" element={<ProtectedRoute><AIToolsPage /></ProtectedRoute>} />
      
      {/* New Routes for Ministry Groups, Teams, Support, Knowledge Base */}
      <Route path="/ministry-groups" element={<ProtectedRoute><MinistryGroupsPage /></ProtectedRoute>} />
      <Route path="/teams" element={<ProtectedRoute><TeamsPage /></ProtectedRoute>} />
      <Route path="/course-assignments" element={<ProtectedRoute><CourseAssignmentsPage /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><SupportTicketsPage /></ProtectedRoute>} />
      <Route path="/support/new" element={<ProtectedRoute><CreateTicketPage /></ProtectedRoute>} />
      <Route path="/support/:ticketId" element={<ProtectedRoute><TicketDetailPage /></ProtectedRoute>} />
      <Route path="/my-exports" element={<ProtectedRoute><MyExportsPage /></ProtectedRoute>} />
      <Route path="/badges" element={<ProtectedRoute><BadgesPage /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
      <Route path="/subscribe" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/subscription-success" element={<ProtectedRoute><SubscriptionSuccessPage /></ProtectedRoute>} />
      <Route path="/membership-success" element={<ProtectedRoute><MembershipSuccessPage /></ProtectedRoute>} />
      <Route path="/mentorship-success" element={<ProtectedRoute><MembershipSuccessPage /></ProtectedRoute>} />
      <Route path="/mentorship-pricing" element={<Navigate to="/subscribe" replace />} />
      <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
      <Route path="/knowledge-base/new" element={<ProtectedRoute><CreateArticlePage /></ProtectedRoute>} />
      <Route path="/knowledge-base/:articleId" element={<KnowledgeArticlePage />} />
    </Routes>
  );
}

// ==================== CHECK-IN PAGE ====================

function CheckInPage() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState('sunday_service');

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const res = await axiosInstance.get('/checkin/history');
      setHistory(res.data);
    } catch (e) {
      console.log('Failed to load check-in history');
    }
    setLoading(false);
  };

  const handleCheckIn = async () => {
    setCheckingIn(true);
    try {
      await axiosInstance.post('/checkin', {
        event_type: selectedEvent
      });
      toast.success('Checked in! God bless you!');
      loadHistory();
    } catch (e) {
      toast.error('Check-in failed');
    }
    setCheckingIn(false);
  };

  const eventTypes = [
    { value: 'sunday_service', label: 'Sunday Service', icon: '⛪' },
    { value: 'bible_study', label: 'Bible Study', icon: '📖' },
    { value: 'small_group', label: 'Small Group', icon: '👥' },
    { value: 'prayer_meeting', label: 'Prayer Meeting', icon: '🙏' },
    { value: 'youth_service', label: 'Youth Service', icon: '🎉' },
    { value: 'special_event', label: 'Special Event', icon: '✨' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <nav className="glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div onClick={() => navigate('/')} className="cursor-pointer">
              <SiteLogo className="h-10 max-w-[200px]" />
            </div>
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>← Dashboard</Button>
          </div>
        </div>
      </nav>
      <div className="pt-24 pb-12 px-4 max-w-2xl mx-auto">
        <h1 className="font-heading text-4xl font-bold mb-2 text-center">Church Check-In</h1>
        <p className="text-muted-foreground text-center mb-8">Mark your attendance at church events</p>

        {/* Check-in Card */}
        <Card className="glass-card mb-8">
          <CardHeader>
            <CardTitle>Check In Now</CardTitle>
            <CardDescription>Select the event you're attending</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
              {eventTypes.map(event => (
                <div
                  key={event.value}
                  onClick={() => setSelectedEvent(event.value)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all text-center ${
                    selectedEvent === event.value 
                      ? 'border-primary bg-primary/10' 
                      : 'border-transparent bg-secondary/20 hover:bg-secondary/40'
                  }`}
                >
                  <div className="text-3xl mb-2">{event.icon}</div>
                  <div className="text-sm font-medium">{event.label}</div>
                </div>
              ))}
            </div>
            <Button 
              className="w-full" 
              size="lg" 
              onClick={handleCheckIn}
              disabled={checkingIn}
            >
              {checkingIn ? 'Checking In...' : '✓ Check In'}
            </Button>
          </CardContent>
        </Card>

        {/* History */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle>Your Attendance History</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p>Loading...</p>
            ) : history.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No check-ins yet. Check in to your first event!</p>
            ) : (
              <div className="space-y-3">
                {history.slice(0, 10).map(checkin => (
                  <div key={checkin.checkin_id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/20">
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">
                        {eventTypes.find(e => e.value === checkin.event_type)?.icon || '⛪'}
                      </div>
                      <div>
                        <div className="font-medium">{checkin.event_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(checkin.checkin_time).toLocaleDateString()} at {new Date(checkin.checkin_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </div>
                      </div>
                    </div>
                    <Badge variant="outline">✓</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ==================== LEADERSHIP ASSESSMENT PAGE ====================

function LeadershipAssessmentPage() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadQuiz();
  }, []);

  const loadQuiz = async () => {
    try {
      const res = await axiosInstance.get('/leadership/quiz');
      setQuestions(res.data);
    } catch (e) {
      toast.error('Failed to load quiz');
    }
    setLoading(false);
  };

  const handleAnswer = (optionId) => {
    setAnswers({ ...answers, [questions[currentQuestion].id]: optionId });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length < questions.length) {
      toast.error('Please answer all questions');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axiosInstance.post('/leadership/quiz/submit', { answers });
      setResult(res.data);
      toast.success('Assessment complete!');
    } catch (e) {
      toast.error('Failed to submit assessment');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-background">
      <nav className="glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div onClick={() => navigate('/')} className="cursor-pointer">
              <SiteLogo className="h-10 max-w-[200px]" />
            </div>
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>← Dashboard</Button>
          </div>
        </div>
      </nav>
      <div className="pt-24 pb-12 px-4 max-w-3xl mx-auto">
        <h1 className="font-heading text-4xl font-bold mb-2 text-center">Leadership Assessment</h1>
        <p className="text-muted-foreground text-center mb-8">Discover your leadership style and growth areas</p>

        {result ? (
          /* Results View */
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-center text-2xl">Your Leadership Profile</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Top Traits */}
              <div className="mb-8">
                <h3 className="font-semibold mb-4">Your Top Leadership Traits</h3>
                <div className="flex flex-wrap gap-2">
                  {result.top_traits.map(([trait, score], idx) => (
                    <Badge key={trait} className={idx === 0 ? 'bg-primary text-lg px-4 py-2' : ''}>
                      {trait.charAt(0).toUpperCase() + trait.slice(1)} ({score} pts)
                    </Badge>
                  ))}
                </div>
              </div>

              {/* AI Analysis */}
              {result.ai_analysis && (
                <div className="bg-secondary/20 rounded-lg p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <span>🤖</span> AI-Powered Analysis
                  </h3>
                  <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                    {result.ai_analysis}
                  </div>
                </div>
              )}

              <div className="flex justify-center mt-8">
                <Button onClick={() => { setResult(null); setAnswers({}); setCurrentQuestion(0); }}>
                  Take Assessment Again
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Quiz View */
          <Card className="glass-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Question {currentQuestion + 1} of {questions.length}</CardTitle>
                <span className="text-sm text-muted-foreground">
                  {Object.keys(answers).length}/{questions.length} answered
                </span>
              </div>
              <Progress value={(Object.keys(answers).length / questions.length) * 100} className="mt-2" />
            </CardHeader>
            <CardContent>
              {questions.length > 0 && (
                <>
                  <h3 className="text-xl font-medium mb-6">{questions[currentQuestion].question}</h3>
                  <div className="space-y-3">
                    {questions[currentQuestion].options.map((option) => (
                      <div
                        key={option.id}
                        onClick={() => handleAnswer(option.id)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          answers[questions[currentQuestion].id] === option.id
                            ? 'border-primary bg-primary/10'
                            : 'border-transparent bg-secondary/20 hover:bg-secondary/40'
                        }`}
                      >
                        {option.text}
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-between mt-8">
                    <Button variant="outline" onClick={handlePrev} disabled={currentQuestion === 0}>
                      ← Previous
                    </Button>
                    {currentQuestion === questions.length - 1 ? (
                      <Button onClick={handleSubmit} disabled={submitting}>
                        {submitting ? 'Analyzing...' : 'Submit Assessment'}
                      </Button>
                    ) : (
                      <Button onClick={handleNext}>
                        Next →
                      </Button>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ==================== AI TOOLS PAGE ====================

function AIToolsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('resume');
  const [resumeText, setResumeText] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeResult, setResumeResult] = useState(null);
  const [businessIdea, setBusinessIdea] = useState('');
  const [businessStage, setBusinessStage] = useState('ideation');
  const [businessResult, setBusinessResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [moduleAccess, setModuleAccess] = useState({ resume_builder: true, business_plan: true });

  useEffect(() => {
    // Fetch module access for current user
    axiosInstance.get('/users/modules')
      .then(res => {
        setModuleAccess(res.data.modules || {});
        // Set default tab based on access
        if (!res.data.modules?.resume_builder && res.data.modules?.business_plan) {
          setActiveTab('business');
        }
      })
      .catch(() => {});
  }, []);

  const handleResumeReview = async () => {
    if (!resumeText && !resumeFile) {
      toast.error('Please enter resume text or upload a file');
      return;
    }
    setLoading(true);
    try {
      let response;
      if (resumeFile) {
        const formData = new FormData();
        formData.append('file', resumeFile);
        response = await axiosInstance.post('/ai/resume-review', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        response = await axiosInstance.post('/ai/resume-review', null, {
          params: { resume_text: resumeText }
        });
      }
      setResumeResult(response.data.feedback);
      toast.success('Resume analyzed!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to analyze resume');
    }
    setLoading(false);
  };

  const handleBusinessPlan = async () => {
    if (!businessIdea.trim()) {
      toast.error('Please describe your business idea');
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.post('/ai/business-plan', null, {
        params: { idea: businessIdea, stage: businessStage }
      });
      setBusinessResult(response.data.advice);
      toast.success('Business advice generated!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate advice');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div onClick={() => navigate('/')} className="cursor-pointer">
              <SiteLogo className="h-10 max-w-[200px]" />
            </div>
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>← Dashboard</Button>
          </div>
        </div>
      </nav>
      <div className="pt-24 pb-12 px-4 max-w-4xl mx-auto">
        <h1 className="font-heading text-4xl font-bold mb-2 text-center">AI-Powered Tools</h1>
        <p className="text-muted-foreground text-center mb-8">Get AI assistance for your career and business goals</p>

        {/* Tabs - Only show if module is enabled */}
        <div className="flex gap-2 mb-8 justify-center flex-wrap">
          {moduleAccess.resume_builder && (
            <Button 
              variant={activeTab === 'resume' ? 'default' : 'outline'}
              onClick={() => setActiveTab('resume')}
            >
              📄 Resume Review
            </Button>
          )}
          {moduleAccess.business_plan && (
            <Button 
              variant={activeTab === 'business' ? 'default' : 'outline'}
              onClick={() => setActiveTab('business')}
            >
              💼 Business Plan
            </Button>
          )}
        </div>

        {/* No access message */}
        {!moduleAccess.resume_builder && !moduleAccess.business_plan && (
          <Card className="glass-card text-center py-12">
            <CardContent>
              <Lock className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-xl font-semibold mb-2">AI Tools Unavailable</h3>
              <p className="text-muted-foreground">These tools are currently not available for your account. Contact an administrator for access.</p>
            </CardContent>
          </Card>
        )}

        {/* Resume Review Tab */}
        {activeTab === 'resume' && moduleAccess.resume_builder && (
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>AI Resume Review</CardTitle>
              <CardDescription>Get professional feedback on your resume to improve your job search</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Paste your resume text</Label>
                <textarea
                  className="w-full p-3 rounded-lg bg-secondary/20 resize-none min-h-[200px]"
                  placeholder="Paste your resume content here..."
                  value={resumeText}
                  onChange={(e) => setResumeText(e.target.value)}
                  data-testid="resume-text-input"
                />
              </div>
              <div className="text-center text-muted-foreground">— OR —</div>
              <div>
                <Label>Upload resume file (PDF, DOC, DOCX, or TXT)</Label>
                <Input
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => setResumeFile(e.target.files[0])}
                  data-testid="resume-file-input"
                />
                {resumeFile && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Selected: {resumeFile.name} ({(resumeFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>
              <Button className="w-full" onClick={handleResumeReview} disabled={loading} data-testid="analyze-resume-btn">
                {loading ? 'Analyzing Resume...' : '🔍 Analyze My Resume'}
              </Button>

              {resumeResult && (
                <div className="mt-6 bg-secondary/20 rounded-lg p-6">
                  <h3 className="font-semibold mb-4">AI Feedback</h3>
                  <div className="whitespace-pre-wrap">{resumeResult}</div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Business Plan Tab */}
        {activeTab === 'business' && moduleAccess.business_plan && (
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>AI Business Plan Assistant</CardTitle>
              <CardDescription>Get strategic advice for your business idea at any stage</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Your Business Idea</Label>
                <textarea
                  className="w-full p-3 rounded-lg bg-secondary/20 resize-none min-h-[120px]"
                  placeholder="Describe your business idea in detail..."
                  value={businessIdea}
                  onChange={(e) => setBusinessIdea(e.target.value)}
                />
              </div>
              <div>
                <Label>Stage of Development</Label>
                <Select value={businessStage} onValueChange={setBusinessStage}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ideation">Ideation - I have an idea</SelectItem>
                    <SelectItem value="planning">Planning - Ready to plan</SelectItem>
                    <SelectItem value="execution">Execution - Ready to launch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full" onClick={handleBusinessPlan} disabled={loading}>
                {loading ? 'Generating Advice...' : 'Get AI Business Advice'}
              </Button>

              {businessResult && (
                <div className="mt-6 bg-secondary/20 rounded-lg p-6">
                  <h3 className="font-semibold mb-4">Strategic Advice</h3>
                  <div className="whitespace-pre-wrap">{businessResult}</div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ==================== WORSHIP SONGS PAGE ====================

function WorshipSongsPage() {
  const navigate = useNavigate();
  const [songs, setSongs] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [currentSong, setCurrentSong] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [showCreatePlaylist, setShowCreatePlaylist] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [activeTab, setActiveTab] = useState('songs');
  const [purchasedSongs, setPurchasedSongs] = useState([]);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    loadSongs();
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const res = await axiosInstance.get('/auth/me');
      setUser(res.data);
      loadPlaylists();
      loadPurchasedSongs();
    } catch (e) {
      setUser(null);
    }
  };

  const loadSongs = async () => {
    try {
      const res = await axiosInstance.get('/songs');
      setSongs(res.data);
    } catch (e) {
      console.error('Error loading songs:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadPlaylists = async () => {
    try {
      const res = await axiosInstance.get('/playlists');
      setPlaylists(res.data);
    } catch (e) {
      console.error('Error loading playlists:', e);
    }
  };

  const loadPurchasedSongs = async () => {
    try {
      const res = await axiosInstance.get('/songs/purchased');
      setPurchasedSongs(res.data.map(s => s.song_id));
    } catch (e) {
      console.error('Error loading purchased songs:', e);
    }
  };

  const playSong = async (song) => {
    if (currentSong?.song_id === song.song_id) {
      if (isPlaying) {
        audioRef.current?.pause();
        setIsPlaying(false);
      } else {
        try {
          await audioRef.current?.play();
          setIsPlaying(true);
        } catch (err) {
          console.log('Play error:', err);
        }
      }
    } else {
      setCurrentSong(song);
      setIsPlaying(true);
      // Reset current time for new song
      setCurrentTime(0);
      // Increment play count
      try {
        await axiosInstance.post(`/songs/${song.song_id}/play`);
      } catch (e) {
        console.error('Error incrementing play count:', e);
      }
    }
  };

  const PREVIEW_LIMIT = 60; // 60 seconds preview for free
  const [showPreviewMessage, setShowPreviewMessage] = useState(false);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      setDuration(audioRef.current.duration || 0);
      
      // Check if preview limit reached for non-purchased songs
      if (currentSong && !purchasedSongs.includes(currentSong.song_id)) {
        if (audioRef.current.currentTime >= PREVIEW_LIMIT) {
          audioRef.current.pause();
          setIsPlaying(false);
          setShowPreviewMessage(true);
        }
      }
    }
  };

  // Handle audio load and play
  const handleAudioCanPlay = () => {
    if (audioRef.current && isPlaying) {
      audioRef.current.play().catch(err => {
        console.log('Autoplay prevented:', err);
        setIsPlaying(false);
      });
    }
  };

  const handleAudioError = (e) => {
    console.error('Audio error:', e);
    toast.error('Failed to load audio. Please try again.');
    setIsPlaying(false);
  };

  const handleSeek = (e) => {
    const newTime = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = volume || 1;
        setIsMuted(false);
      } else {
        audioRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };

  const playNext = () => {
    if (!currentSong || songs.length === 0) return;
    const currentIndex = songs.findIndex(s => s.song_id === currentSong.song_id);
    let nextIndex;
    if (shuffle) {
      nextIndex = Math.floor(Math.random() * songs.length);
    } else {
      nextIndex = (currentIndex + 1) % songs.length;
    }
    playSong(songs[nextIndex]);
  };

  const playPrevious = () => {
    if (!currentSong || songs.length === 0) return;
    const currentIndex = songs.findIndex(s => s.song_id === currentSong.song_id);
    const prevIndex = currentIndex === 0 ? songs.length - 1 : currentIndex - 1;
    playSong(songs[prevIndex]);
  };

  const handleSongEnd = () => {
    if (repeat) {
      audioRef.current?.play();
    } else {
      playNext();
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const createPlaylist = async () => {
    if (!newPlaylistName.trim()) return;
    try {
      await axiosInstance.post(`/playlists?name=${encodeURIComponent(newPlaylistName)}`);
      toast.success('Playlist created!');
      setNewPlaylistName('');
      setShowCreatePlaylist(false);
      loadPlaylists();
    } catch (e) {
      toast.error('Failed to create playlist');
    }
  };

  const addToPlaylist = async (playlistId, songId) => {
    try {
      await axiosInstance.post(`/playlists/${playlistId}/songs/${songId}`);
      toast.success('Added to playlist!');
      loadPlaylists();
    } catch (e) {
      toast.error('Failed to add to playlist');
    }
  };

  const removeFromPlaylist = async (playlistId, songId) => {
    try {
      await axiosInstance.delete(`/playlists/${playlistId}/songs/${songId}`);
      toast.success('Removed from playlist');
      loadPlaylists();
    } catch (e) {
      toast.error('Failed to remove from playlist');
    }
  };

  const deletePlaylist = async (playlistId) => {
    if (!window.confirm('Delete this playlist?')) return;
    try {
      await axiosInstance.delete(`/playlists/${playlistId}`);
      toast.success('Playlist deleted');
      loadPlaylists();
    } catch (e) {
      toast.error('Failed to delete playlist');
    }
  };

  const purchaseSong = async (songId) => {
    if (!user) {
      toast.error('Please login to purchase songs');
      navigate('/login');
      return;
    }
    try {
      const res = await axiosInstance.post(`/songs/${songId}/purchase`);
      if (res.data.free) {
        toast.success(res.data.message || 'Song added to your library!');
        loadPurchasedSongs();
      } else {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to initiate purchase');
    }
  };

  const downloadSong = async (songId) => {
    try {
      const response = await axiosInstance.get(`/songs/${songId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const song = songs.find(s => s.song_id === songId);
      link.setAttribute('download', `${song?.title || 'song'}.mp3`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started!');
    } catch (e) {
      toast.error('Failed to download song');
    }
  };

  const filteredSongs = songs.filter(song => {
    const matchesSearch = song.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         song.artist.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesGenre = selectedGenre === 'all' || song.genre === selectedGenre;
    return matchesSearch && matchesGenre;
  });

  const genres = ['all', ...new Set(songs.map(s => s.genre).filter(Boolean))];

  const getPlaylistSongs = (playlist) => {
    return playlist.songs.map(songId => songs.find(s => s.song_id === songId)).filter(Boolean);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 pb-32">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="font-heading text-2xl font-bold text-primary cursor-pointer" onClick={() => navigate('/')}>
                TheFeedingFaith
              </div>
              <span className="text-muted-foreground">|</span>
              <div className="flex items-center gap-2">
                <Music className="h-5 w-5 text-accent" />
                <span className="font-semibold">Worship Music</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {user ? (
                <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="worship-dashboard-btn">
                  Dashboard
                </Button>
              ) : (
                <Button onClick={() => navigate('/login')} data-testid="worship-login-btn">
                  Login
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-4xl font-bold text-primary mb-4">Worship Songs</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Listen to uplifting worship music. Create playlists and download your favorites to support our ministry.
          </p>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Search songs or artists..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="worship-search-input"
            />
          </div>
          <Select value={selectedGenre} onValueChange={setSelectedGenre}>
            <SelectTrigger className="w-full md:w-[180px]" data-testid="worship-genre-select">
              <SelectValue placeholder="All Genres" />
            </SelectTrigger>
            <SelectContent>
              {genres.map(genre => (
                <SelectItem key={genre} value={genre}>
                  {genre === 'all' ? 'All Genres' : genre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'songs' ? 'default' : 'outline'}
            onClick={() => setActiveTab('songs')}
            data-testid="worship-songs-tab"
          >
            <Music className="h-4 w-4 mr-2" />
            All Songs
          </Button>
          {user && (
            <Button
              variant={activeTab === 'playlists' ? 'default' : 'outline'}
              onClick={() => setActiveTab('playlists')}
              data-testid="worship-playlists-tab"
            >
              <ListMusic className="h-4 w-4 mr-2" />
              My Playlists
            </Button>
          )}
          {user && purchasedSongs.length > 0 && (
            <Button
              variant={activeTab === 'purchased' ? 'default' : 'outline'}
              onClick={() => setActiveTab('purchased')}
              data-testid="worship-purchased-tab"
            >
              <Download className="h-4 w-4 mr-2" />
              Purchased
            </Button>
          )}
        </div>

        {/* Songs Tab */}
        {activeTab === 'songs' && (
          <div className="grid gap-4">
            {filteredSongs.length === 0 ? (
              <Card className="p-8 text-center">
                <Music className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No songs found</p>
              </Card>
            ) : (
              filteredSongs.map((song, index) => (
                <motion.div
                  key={song.song_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className={`p-4 hover:shadow-md transition-all ${currentSong?.song_id === song.song_id ? 'ring-2 ring-primary bg-primary/5' : ''}`}>
                    <div className="flex items-center gap-4">
                      {/* Cover Image */}
                      <div 
                        className="w-16 h-16 rounded-lg bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center cursor-pointer group relative overflow-hidden"
                        onClick={() => playSong(song)}
                      >
                        {song.cover_image ? (
                          <img src={`${BACKEND_URL}${song.cover_image}`} alt={song.title} className="w-full h-full object-cover" />
                        ) : (
                          <Music className="h-8 w-8 text-primary" />
                        )}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          {currentSong?.song_id === song.song_id && isPlaying ? (
                            <Pause className="h-8 w-8 text-white" />
                          ) : (
                            <Play className="h-8 w-8 text-white" />
                          )}
                        </div>
                      </div>

                      {/* Song Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold truncate">{song.title}</h3>
                        <p className="text-sm text-muted-foreground truncate">{song.artist}</p>
                        {song.album && <p className="text-xs text-muted-foreground">{song.album}</p>}
                      </div>

                      {/* Genre Badge */}
                      {song.genre && (
                        <Badge variant="secondary" className="hidden md:block">{song.genre}</Badge>
                      )}

                      {/* Play Count */}
                      <div className="hidden md:flex items-center gap-1 text-sm text-muted-foreground">
                        <Play className="h-4 w-4" />
                        {song.play_count || 0}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {user && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`add-to-playlist-${song.song_id}`}>
                                <Plus className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuLabel>Add to Playlist</DropdownMenuLabel>
                              {playlists.length === 0 ? (
                                <DropdownMenuItem onClick={() => setShowCreatePlaylist(true)}>
                                  Create first playlist
                                </DropdownMenuItem>
                              ) : (
                                playlists.map(pl => (
                                  <DropdownMenuItem key={pl.playlist_id} onClick={() => addToPlaylist(pl.playlist_id, song.song_id)}>
                                    {pl.name}
                                  </DropdownMenuItem>
                                ))
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}

                        {purchasedSongs.includes(song.song_id) ? (
                          <Button variant="outline" size="sm" onClick={() => downloadSong(song.song_id)} data-testid={`download-${song.song_id}`}>
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </Button>
                        ) : (
                          <Button size="sm" onClick={() => purchaseSong(song.song_id)} data-testid={`purchase-${song.song_id}`}>
                            {(song.download_price === 0 || !song.download_price) ? (
                              <>
                                <Download className="h-4 w-4 mr-1" />
                                Free
                              </>
                            ) : (
                              <>
                                <DollarSign className="h-4 w-4 mr-1" />
                                ${song.download_price?.toFixed(2) || '2.99'}
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))
            )}
          </div>
        )}

        {/* Playlists Tab */}
        {activeTab === 'playlists' && user && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">My Playlists</h2>
              <Button onClick={() => setShowCreatePlaylist(true)} data-testid="create-playlist-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Playlist
              </Button>
            </div>

            {playlists.length === 0 ? (
              <Card className="p-8 text-center">
                <ListMusic className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">You haven't created any playlists yet</p>
                <Button onClick={() => setShowCreatePlaylist(true)}>Create Your First Playlist</Button>
              </Card>
            ) : (
              playlists.map(playlist => (
                <Card key={playlist.playlist_id} className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold">{playlist.name}</h3>
                      <p className="text-sm text-muted-foreground">{playlist.songs.length} songs</p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => deletePlaylist(playlist.playlist_id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  
                  {playlist.songs.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No songs in this playlist</p>
                  ) : (
                    <div className="space-y-2">
                      {getPlaylistSongs(playlist).map(song => (
                        <div key={song.song_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50">
                          <div 
                            className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center cursor-pointer"
                            onClick={() => playSong(song)}
                          >
                            {currentSong?.song_id === song.song_id && isPlaying ? (
                              <Pause className="h-4 w-4 text-primary" />
                            ) : (
                              <Play className="h-4 w-4 text-primary" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate text-sm">{song.title}</p>
                            <p className="text-xs text-muted-foreground">{song.artist}</p>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => removeFromPlaylist(playlist.playlist_id, song.song_id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>
        )}

        {/* Purchased Tab */}
        {activeTab === 'purchased' && user && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Purchased Songs</h2>
            {purchasedSongs.length === 0 ? (
              <Card className="p-8 text-center">
                <Download className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">You haven't purchased any songs yet</p>
              </Card>
            ) : (
              songs.filter(s => purchasedSongs.includes(s.song_id)).map(song => (
                <Card key={song.song_id} className="p-4">
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded bg-primary/10 flex items-center justify-center cursor-pointer"
                      onClick={() => playSong(song)}
                    >
                      {currentSong?.song_id === song.song_id && isPlaying ? (
                        <Pause className="h-5 w-5 text-primary" />
                      ) : (
                        <Play className="h-5 w-5 text-primary" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold">{song.title}</h3>
                      <p className="text-sm text-muted-foreground">{song.artist}</p>
                    </div>
                    <Button variant="outline" onClick={() => downloadSong(song.song_id)}>
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}
      </div>

      {/* Create Playlist Dialog */}
      <Dialog open={showCreatePlaylist} onOpenChange={setShowCreatePlaylist}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Playlist</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Input
              placeholder="Playlist name"
              value={newPlaylistName}
              onChange={(e) => setNewPlaylistName(e.target.value)}
              data-testid="new-playlist-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreatePlaylist(false)}>Cancel</Button>
            <Button onClick={createPlaylist} data-testid="create-playlist-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Limit Dialog */}
      <Dialog open={showPreviewMessage} onOpenChange={setShowPreviewMessage}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Music className="h-5 w-5 text-primary" />
              Preview Complete
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground mb-4">
              You've listened to the first minute of <strong>"{currentSong?.title}"</strong> by {currentSong?.artist}.
            </p>
            <p className="text-muted-foreground mb-4">
              {(currentSong?.download_price === 0 || !currentSong?.download_price) ? (
                <>Get this song for <strong>Free</strong> to:</>
              ) : (
                <>Purchase this song for <strong>${currentSong?.download_price?.toFixed(2) || '2.99'}</strong> to:</>
              )}
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1 mb-4">
              <li>Listen to the full song</li>
              <li>Download for offline listening</li>
              <li>Support our ministry</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewMessage(false)}>Maybe Later</Button>
            <Button onClick={() => { setShowPreviewMessage(false); purchaseSong(currentSong?.song_id); }}>
              {(currentSong?.download_price === 0 || !currentSong?.download_price) ? (
                <>
                  <Download className="h-4 w-4 mr-1" />
                  Get Free
                </>
              ) : (
                <>
                  <DollarSign className="h-4 w-4 mr-1" />
                  Purchase ${currentSong?.download_price?.toFixed(2) || '2.99'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audio Player (Fixed at bottom) */}
      {currentSong && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t shadow-lg z-50">
          <audio
            ref={audioRef}
            src={currentSong.audio_file ? `${BACKEND_URL}${currentSong.audio_file}` : ''}
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleSongEnd}
            onLoadedMetadata={handleTimeUpdate}
            onCanPlay={handleAudioCanPlay}
            onError={handleAudioError}
            preload="auto"
          />
          
          {/* Progress Bar */}
          <div className="w-full h-1 bg-muted">
            <input
              type="range"
              min="0"
              max={duration || 100}
              value={currentTime}
              onChange={handleSeek}
              className="w-full h-1 appearance-none bg-transparent cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-runnable-track]:bg-primary/20 [&::-webkit-slider-runnable-track]:h-1"
              style={{
                background: `linear-gradient(to right, hsl(var(--primary)) ${(currentTime / duration) * 100}%, hsl(var(--muted)) ${(currentTime / duration) * 100}%)`
              }}
            />
          </div>

          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center gap-4">
              {/* Song Info */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-12 h-12 rounded bg-primary/10 flex items-center justify-center">
                  {currentSong.cover_image ? (
                    <img src={`${BACKEND_URL}${currentSong.cover_image}`} alt="" className="w-full h-full object-cover rounded" />
                  ) : (
                    <Music className="h-6 w-6 text-primary" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="font-medium truncate">{currentSong.title}</p>
                  <p className="text-sm text-muted-foreground truncate">{currentSong.artist}</p>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShuffle(!shuffle)}
                  className={shuffle ? 'text-primary' : ''}
                >
                  <Shuffle className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={playPrevious}>
                  <SkipBack className="h-5 w-5" />
                </Button>
                <Button
                  size="icon"
                  className="h-10 w-10 rounded-full"
                  onClick={() => playSong(currentSong)}
                  data-testid="player-play-pause"
                >
                  {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                </Button>
                <Button variant="ghost" size="icon" onClick={playNext}>
                  <SkipForward className="h-5 w-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setRepeat(!repeat)}
                  className={repeat ? 'text-primary' : ''}
                >
                  <Repeat className="h-4 w-4" />
                </Button>
              </div>

              {/* Time & Volume */}
              <div className="hidden md:flex items-center gap-4 flex-1 justify-end">
                <span className="text-sm text-muted-foreground">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={toggleMute}>
                    {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                  </Button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="w-20 h-1 appearance-none bg-muted rounded cursor-pointer"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== ABOUT US PAGE ====================

function AboutPage() {
  const navigate = useNavigate();
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadContent = async () => {
      try {
        const res = await axiosInstance.get('/cms/content/about');
        // Convert array to object by section
        const contentObj = {};
        res.data.forEach(item => {
          if (!contentObj[item.section]) contentObj[item.section] = {};
          contentObj[item.section][item.key] = item.value;
        });
        setContent(contentObj);
      } catch (e) {
        console.log('Failed to load about content');
      }
      setLoading(false);
    };
    loadContent();
  }, []);

  const getContent = (section, key, defaultValue = '') => {
    return content[section]?.[key] || defaultValue;
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div onClick={() => navigate('/')} className="cursor-pointer">
              <SiteLogo className="h-10 max-w-[200px]" />
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => navigate('/about')} className="bg-primary/10">
                <Info className="h-4 w-4 mr-2" />
                About Us
              </Button>
              <Button variant="ghost" onClick={() => navigate('/worship')}>
                <Music className="h-4 w-4 mr-2" />
                Worship
              </Button>
              <Button variant="ghost" onClick={() => navigate('/sermons')}>
                Sermons
              </Button>
              <Button variant="ghost" onClick={() => navigate('/login')}>
                Login
              </Button>
              <Button onClick={() => navigate('/signup')} className="bg-accent hover:bg-accent/90">
                Join Us
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-20">
        <div className="relative h-[400px] bg-gradient-to-br from-primary/20 to-accent/20 overflow-hidden">
          {getContent('hero', 'image') && (
            <img 
              src={getContent('hero', 'image')} 
              alt="About Us" 
              className="absolute inset-0 w-full h-full object-cover opacity-40"
            />
          )}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
                {getContent('hero', 'title', 'About The Feeding Faith')}
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                {getContent('hero', 'subtitle', 'Learn about our mission, vision, and the heart behind what we do.')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Mission Section */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <h2 className="font-heading text-3xl font-bold mb-4">
              {getContent('mission', 'title', 'Our Mission')}
            </h2>
            <div className="prose prose-lg text-muted-foreground">
              <p>{getContent('mission', 'text', 'At The Feeding Faith, our mission is to spread the love of Christ through mentorship, community, and spiritual growth. We believe in nurturing every soul that walks through our doors.')}</p>
            </div>
          </div>
          {getContent('mission', 'image') && (
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src={getContent('mission', 'image')} 
                alt="Our Mission" 
                className="w-full h-80 object-cover"
              />
            </div>
          )}
        </div>

        {/* Vision Section */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
          {getContent('vision', 'image') && (
            <div className="rounded-2xl overflow-hidden shadow-xl order-2 md:order-1">
              <img 
                src={getContent('vision', 'image')} 
                alt="Our Vision" 
                className="w-full h-80 object-cover"
              />
            </div>
          )}
          <div className="order-1 md:order-2">
            <h2 className="font-heading text-3xl font-bold mb-4">
              {getContent('vision', 'title', 'Our Vision')}
            </h2>
            <div className="prose prose-lg text-muted-foreground">
              <p>{getContent('vision', 'text', 'We envision a world where every person has access to spiritual mentorship and a community that supports their growth in faith. Through technology and personal connection, we aim to reach souls worldwide.')}</p>
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="mb-20">
          <h2 className="font-heading text-3xl font-bold text-center mb-12">
            {getContent('values', 'title', 'Our Core Values')}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="glass-card text-center p-6">
              <Heart className="h-12 w-12 mx-auto mb-4 text-primary" />
              <h3 className="font-heading text-xl font-bold mb-2">
                {getContent('values', 'value1_title', 'Love')}
              </h3>
              <p className="text-muted-foreground">
                {getContent('values', 'value1_text', 'We lead with love in everything we do, reflecting Christ\'s unconditional love for all.')}
              </p>
            </Card>
            <Card className="glass-card text-center p-6">
              <Users className="h-12 w-12 mx-auto mb-4 text-primary" />
              <h3 className="font-heading text-xl font-bold mb-2">
                {getContent('values', 'value2_title', 'Community')}
              </h3>
              <p className="text-muted-foreground">
                {getContent('values', 'value2_text', 'We believe in the power of community and fellowship to transform lives.')}
              </p>
            </Card>
            <Card className="glass-card text-center p-6">
              <BookOpen className="h-12 w-12 mx-auto mb-4 text-primary" />
              <h3 className="font-heading text-xl font-bold mb-2">
                {getContent('values', 'value3_title', 'Growth')}
              </h3>
              <p className="text-muted-foreground">
                {getContent('values', 'value3_text', 'We are committed to continuous spiritual growth and learning through mentorship.')}
              </p>
            </Card>
          </div>
        </div>

        {/* Leadership Section */}
        <div className="mb-20">
          <h2 className="font-heading text-3xl font-bold text-center mb-12">
            {getContent('leadership', 'title', 'Our Leadership')}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Pastor/Leader 1 */}
            <Card className="glass-card overflow-hidden">
              {getContent('leadership', 'leader1_image') ? (
                <img 
                  src={getContent('leadership', 'leader1_image')} 
                  alt={getContent('leadership', 'leader1_name', 'Pastor')}
                  className="w-full h-64 object-cover"
                />
              ) : (
                <div className="w-full h-64 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                  <User className="h-24 w-24 text-primary/50" />
                </div>
              )}
              <CardContent className="p-6 text-center">
                <h3 className="font-heading text-xl font-bold">
                  {getContent('leadership', 'leader1_name', 'Senior Pastor')}
                </h3>
                <p className="text-primary font-medium">
                  {getContent('leadership', 'leader1_role', 'Lead Pastor')}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  {getContent('leadership', 'leader1_bio', '')}
                </p>
              </CardContent>
            </Card>

            {/* Leader 2 - Only show if name is set */}
            {getContent('leadership', 'leader2_name') && (
              <Card className="glass-card overflow-hidden">
                {getContent('leadership', 'leader2_image') ? (
                  <img 
                    src={getContent('leadership', 'leader2_image')} 
                    alt={getContent('leadership', 'leader2_name')}
                    className="w-full h-64 object-cover"
                  />
                ) : (
                  <div className="w-full h-64 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                    <User className="h-24 w-24 text-primary/50" />
                  </div>
                )}
                <CardContent className="p-6 text-center">
                  <h3 className="font-heading text-xl font-bold">
                    {getContent('leadership', 'leader2_name')}
                  </h3>
                  <p className="text-primary font-medium">
                    {getContent('leadership', 'leader2_role')}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {getContent('leadership', 'leader2_bio')}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Leader 3 - Only show if name is set */}
            {getContent('leadership', 'leader3_name') && (
              <Card className="glass-card overflow-hidden">
                {getContent('leadership', 'leader3_image') ? (
                  <img 
                    src={getContent('leadership', 'leader3_image')} 
                    alt={getContent('leadership', 'leader3_name')}
                    className="w-full h-64 object-cover"
                  />
                ) : (
                  <div className="w-full h-64 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                    <User className="h-24 w-24 text-primary/50" />
                  </div>
                )}
                <CardContent className="p-6 text-center">
                  <h3 className="font-heading text-xl font-bold">
                    {getContent('leadership', 'leader3_name')}
                  </h3>
                  <p className="text-primary font-medium">
                    {getContent('leadership', 'leader3_role')}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {getContent('leadership', 'leader3_bio')}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Contact/Location Section */}
        <Card className="glass-card p-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h2 className="font-heading text-2xl font-bold mb-4">
                {getContent('contact', 'title', 'Visit Us')}
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-primary mt-1" />
                  <p>{getContent('contact', 'address', '123 Faith Street, City, State 12345')}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-primary" />
                  <p>{getContent('contact', 'phone', '(555) 123-4567')}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-primary" />
                  <p>{getContent('contact', 'email', 'info@thefeedingfaith.org')}</p>
                </div>
              </div>
              <div className="mt-6">
                <h3 className="font-heading font-bold mb-2">Service Times</h3>
                <p className="text-muted-foreground">
                  {getContent('contact', 'service_times', 'Sunday: 9:00 AM & 11:00 AM\nWednesday: 7:00 PM')}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <Button onClick={() => navigate('/signup')} size="lg" className="bg-primary hover:bg-primary/90">
                <Heart className="h-5 w-5 mr-2" />
                Join Our Community
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-primary text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="opacity-80">© {new Date().getFullYear()} The Feeding Faith. All rights reserved.</p>
          <div className="mt-4 flex justify-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/login')} className="text-white/70 hover:text-white">
              Admin Portal
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ==================== CHURCH CRM PAGES ====================

function SermonsPage() {
  const navigate = useNavigate();
  const [sermons, setSermons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSermons = async () => {
      try {
        const res = await axiosInstance.get('/sermons');
        setSermons(res.data);
      } catch (e) {
        console.log('Failed to load sermons');
      }
      setLoading(false);
    };
    loadSermons();
  }, []);

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&\s]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div onClick={() => navigate('/')} className="cursor-pointer">
              <SiteLogo className="h-10 max-w-[200px]" />
            </div>
            <Button variant="ghost" onClick={() => navigate(-1)}>← Back</Button>
          </div>
        </div>
      </nav>
      <div className="pt-24 pb-12 px-4 max-w-7xl mx-auto">
        <h1 className="font-heading text-4xl font-bold mb-8">Sermons</h1>
        {loading ? (
          <p>Loading sermons...</p>
        ) : sermons.length === 0 ? (
          <Card className="glass-card text-center py-12">
            <CardContent>
              <p className="text-muted-foreground">No sermons available yet. Check back soon!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sermons.map(sermon => {
              const embedUrl = getYouTubeEmbedUrl(sermon.youtube_url);
              return (
                <Card key={sermon.sermon_id} className="glass-card overflow-hidden">
                  {embedUrl ? (
                    <div className="aspect-video">
                      <iframe
                        src={embedUrl}
                        title={sermon.title}
                        className="w-full h-full"
                        allowFullScreen
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      />
                    </div>
                  ) : sermon.thumbnail ? (
                    <img src={sermon.thumbnail} alt={sermon.title} className="w-full h-48 object-cover" />
                  ) : (
                    <div className="w-full h-48 bg-primary/10 flex items-center justify-center">
                      <Video className="h-12 w-12 text-primary/50" />
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle className="line-clamp-2">{sermon.title}</CardTitle>
                    <CardDescription>
                      {sermon.preacher} • {new Date(sermon.date).toLocaleDateString()}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {sermon.scripture_reference && (
                      <Badge variant="outline" className="mb-2">{sermon.scripture_reference}</Badge>
                    )}
                    <p className="text-sm text-muted-foreground line-clamp-2">{sermon.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StorePage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [songs, setSongs] = useState([]);
  const [purchasedSongs, setPurchasedSongs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [user, setUser] = useState(null);
  const [activeStoreTab, setActiveStoreTab] = useState('products');

  useEffect(() => {
    const loadData = async () => {
      try {
        const [productsRes, categoriesRes, songsRes] = await Promise.all([
          axiosInstance.get('/store/products'),
          axiosInstance.get('/store/categories'),
          axiosInstance.get('/songs')
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
        setSongs(songsRes.data || []);
        // Load user for checkout
        try {
          const userRes = await axiosInstance.get('/auth/me');
          setUser(userRes.data);
          // Load purchased songs
          const purchasedRes = await axiosInstance.get('/songs/purchased');
          setPurchasedSongs(purchasedRes.data?.map(s => s.song_id) || []);
        } catch (e) {
          // Not logged in
        }
        // Load saved cart from localStorage
        const savedCart = localStorage.getItem('churchStoreCart');
        if (savedCart) {
          setCart(JSON.parse(savedCart));
        }
      } catch (e) {
        console.log('Failed to load store');
      }
      setLoading(false);
    };
    loadData();
  }, []);

  // Save cart to localStorage
  useEffect(() => {
    localStorage.setItem('churchStoreCart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product) => {
    setCart(prevCart => {
      const existing = prevCart.find(item => item.product_id === product.product_id);
      if (existing) {
        return prevCart.map(item => 
          item.product_id === product.product_id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { ...product, quantity: 1 }];
    });
    toast.success(`${product.name} added to cart!`);
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.product_id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity < 1) {
      removeFromCart(productId);
      return;
    }
    setCart(prevCart => prevCart.map(item =>
      item.product_id === productId ? { ...item, quantity } : item
    ));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleCheckout = async () => {
    if (!user) {
      toast.error('Please log in to checkout');
      navigate('/login');
      return;
    }
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    
    setCheckingOut(true);
    try {
      const response = await axiosInstance.post('/store/checkout', {
        items: cart.map(item => ({
          product_id: item.product_id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        // Clear cart before redirecting
        localStorage.removeItem('churchStoreCart');
        setCart([]);
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed. Please try again.');
    } finally {
      setCheckingOut(false);
    }
  };

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen bg-background">
      <nav className="glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div onClick={() => navigate('/')} className="cursor-pointer">
              <SiteLogo className="h-10 max-w-[200px]" />
            </div>
            <div className="flex items-center gap-4">
              {/* Cart Button */}
              <Button 
                variant="outline" 
                onClick={() => setShowCart(true)}
                className="relative"
                data-testid="cart-button"
              >
                <ShoppingBag className="h-5 w-5" />
                {cartItemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-primary text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {cartItemCount}
                  </span>
                )}
              </Button>
              <Button variant="ghost" onClick={() => navigate(-1)}>← Back</Button>
            </div>
          </div>
        </div>
      </nav>
      <div className="pt-24 pb-12 px-4 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl font-bold mb-2">Church Store</h1>
            <p className="text-muted-foreground">Books, merchandise, and worship music</p>
          </div>
          <div className="mt-4 md:mt-0 flex gap-2">
            <Button variant="outline" onClick={() => navigate('/my-purchases')} data-testid="my-purchases-btn">
              <Package className="h-4 w-4 mr-2" />
              My Purchases
            </Button>
          </div>
        </div>
        
        {/* Store Tabs */}
        <Tabs defaultValue="products" className="w-full" onValueChange={(v) => setActiveStoreTab(v)}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="products" data-testid="products-tab">
              <ShoppingBag className="h-4 w-4 mr-2" />
              Products ({products.length})
            </TabsTrigger>
            <TabsTrigger value="music" data-testid="music-tab">
              <Music className="h-4 w-4 mr-2" />
              Worship Songs ({songs.length})
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            {/* Categories */}
            <div className="flex gap-2 flex-wrap mb-8">
              <Button 
                variant={selectedCategory === 'all' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setSelectedCategory('all')}
              >
                All
              </Button>
              {categories.map(cat => (
                <Button 
                  key={cat} 
                  variant={selectedCategory === cat ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </Button>
              ))}
            </div>

            {loading ? (
              <p>Loading products...</p>
            ) : filteredProducts.length === 0 ? (
              <Card className="glass-card text-center py-12">
                <CardContent>
                  <ShoppingBag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No products available yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {filteredProducts.map(product => (
                  <Card key={product.product_id} className="glass-card overflow-hidden hover:shadow-lg transition-shadow" data-testid={`product-card-${product.product_id}`}>
                    {product.image ? (
                      <img src={product.image} alt={product.name} className="w-full h-48 object-cover" />
                    ) : (
                      <div className="w-full h-48 bg-primary/10 flex items-center justify-center">
                        <ShoppingBag className="h-12 w-12 text-primary/50" />
                      </div>
                    )}
                    <CardHeader>
                      <CardTitle className="line-clamp-1">{product.name}</CardTitle>
                      <CardDescription>
                        <Badge variant="outline">{product.category}</Badge>
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{product.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xl font-bold">${product.price.toFixed(2)}</span>
                        <Button size="sm" onClick={() => addToCart(product)} data-testid={`add-to-cart-${product.product_id}`}>
                          <Plus className="h-4 w-4 mr-1" />Add
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Worship Songs Tab */}
          <TabsContent value="music">
            {loading ? (
              <p>Loading songs...</p>
            ) : songs.length === 0 ? (
              <Card className="glass-card text-center py-12">
                <CardContent>
                  <Music className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No worship songs available yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {songs.map(song => (
                  <Card key={song.song_id} className="glass-card overflow-hidden hover:shadow-lg transition-shadow" data-testid={`song-card-${song.song_id}`}>
                    <div className="relative">
                      {song.cover_image ? (
                        <img 
                          src={song.cover_image.startsWith('/') ? BACKEND_URL + song.cover_image : song.cover_image} 
                          alt={song.title} 
                          className="w-full h-40 object-cover" 
                        />
                      ) : (
                        <div className="w-full h-40 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                          <Music className="h-16 w-16 text-primary/50" />
                        </div>
                      )}
                      <Badge className="absolute top-2 right-2" variant="secondary">
                        {song.genre || 'Worship'}
                      </Badge>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-lg truncate">{song.title}</h3>
                      <p className="text-sm text-muted-foreground truncate">{song.artist}</p>
                      {song.album && (
                        <p className="text-xs text-muted-foreground truncate mt-1">{song.album}</p>
                      )}
                      <div className="flex items-center justify-between mt-4">
                        {purchasedSongs.includes(song.song_id) ? (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full"
                            onClick={() => navigate('/worship')}
                            data-testid={`listen-${song.song_id}`}
                          >
                            <Play className="h-4 w-4 mr-2" />
                            Listen / Download
                          </Button>
                        ) : (
                          <>
                            <span className="text-xl font-bold">
                              {song.download_price === 0 ? 'Free' : `$${song.download_price?.toFixed(2) || '2.99'}`}
                            </span>
                            <Button 
                              size="sm" 
                              onClick={async () => {
                                if (!user) {
                                  toast.error('Please log in to purchase');
                                  navigate('/login');
                                  return;
                                }
                                try {
                                  const res = await axiosInstance.post(`/songs/${song.song_id}/purchase`);
                                  if (res.data.free) {
                                    toast.success(res.data.message);
                                    setPurchasedSongs([...purchasedSongs, song.song_id]);
                                  } else {
                                    window.location.href = res.data.checkout_url;
                                  }
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || 'Failed to purchase');
                                }
                              }}
                              data-testid={`buy-song-${song.song_id}`}
                            >
                              {song.download_price === 0 ? (
                                <>
                                  <Download className="h-4 w-4 mr-1" />
                                  Get Free
                                </>
                              ) : (
                                <>
                                  <DollarSign className="h-4 w-4 mr-1" />
                                  Buy
                                </>
                              )}
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Cart Sidebar */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" />
              Shopping Cart
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {cart.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Your cart is empty</p>
            ) : (
              cart.map(item => (
                <div key={item.product_id} className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                  {item.image ? (
                    <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded" />
                  ) : (
                    <div className="w-16 h-16 bg-primary/10 rounded flex items-center justify-center">
                      <ShoppingBag className="h-6 w-6 text-primary/50" />
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-medium line-clamp-1">{item.name}</p>
                    <p className="text-sm text-muted-foreground">${item.price.toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => updateQuantity(item.product_id, item.quantity - 1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="w-8 text-center">{item.quantity}</span>
                    <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => updateQuantity(item.product_id, item.quantity + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => removeFromCart(item.product_id)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
          {cart.length > 0 && (
            <div className="border-t pt-4 space-y-4">
              <div className="flex justify-between text-lg font-bold">
                <span>Total:</span>
                <span>${cartTotal.toFixed(2)}</span>
              </div>
              <Button 
                className="w-full" 
                onClick={handleCheckout}
                disabled={checkingOut}
                data-testid="checkout-button"
              >
                {checkingOut ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Checkout (${cartTotal.toFixed(2)})
                  </>
                )}
              </Button>
              {!user && (
                <p className="text-xs text-center text-muted-foreground">
                  You'll need to <span className="text-primary cursor-pointer" onClick={() => navigate('/login')}>log in</span> to complete your purchase
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Store Success Page
function StoreSuccessPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      checkOrder(sessionId);
    } else {
      setLoading(false);
    }
  }, []);

  const checkOrder = async (sessionId) => {
    try {
      const response = await axiosInstance.get(`/store/order/${sessionId}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Failed to verify order:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Verifying your order...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center">
      <Card className="glass-card max-w-md text-center p-8">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle className="h-12 w-12 text-green-600" />
        </div>
        <h1 className="font-heading text-3xl font-bold mb-2">Order Complete!</h1>
        <p className="text-muted-foreground mb-6">
          Thank you for your purchase! Your order has been confirmed.
        </p>
        {order && (
          <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold mb-2">Order Details</h3>
            <div className="space-y-2 text-sm">
              {order.items?.map((item, idx) => (
                <div key={idx} className="flex justify-between">
                  <span>{item.name} x{item.quantity}</span>
                  <span>${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t pt-2 mt-2 font-bold flex justify-between">
                <span>Total</span>
                <span>${order.total_amount?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}
        <div className="space-y-3">
          <Button onClick={() => navigate('/store')} className="w-full">
            Continue Shopping
          </Button>
          <Button variant="outline" onClick={() => navigate('/dashboard')} className="w-full">
            Go to Dashboard
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ==================== MY PURCHASES PAGE ====================

function MyPurchasesPage() {
  const navigate = useNavigate();
  const [storeOrders, setStoreOrders] = useState([]);
  const [songPurchases, setSongPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('store');

  useEffect(() => {
    loadPurchases();
  }, []);

  const loadPurchases = async () => {
    try {
      const [ordersRes, songsRes] = await Promise.all([
        axiosInstance.get('/store/my-orders'),
        axiosInstance.get('/songs/purchased')
      ]);
      setStoreOrders(ordersRes.data || []);
      setSongPurchases(songsRes.data || []);
    } catch (error) {
      console.error('Failed to load purchases:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/10 to-accent/10 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to Dashboard
          </Button>
          <h1 className="font-heading text-4xl font-bold text-primary mb-2">My Purchases</h1>
          <p className="text-muted-foreground">View your purchase history and download your items</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Tabs */}
        <Tabs defaultValue="store" className="w-full" onValueChange={(v) => setActiveTab(v)}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="store" data-testid="store-purchases-tab">
              <ShoppingBag className="h-4 w-4 mr-2" />
              Store Orders ({storeOrders.length})
            </TabsTrigger>
            <TabsTrigger value="songs" data-testid="songs-purchases-tab">
              <Music className="h-4 w-4 mr-2" />
              Songs ({songPurchases.length})
            </TabsTrigger>
          </TabsList>

          {/* Store Orders Tab */}
          <TabsContent value="store">
            {storeOrders.length === 0 ? (
              <Card className="glass-card text-center py-12">
                <ShoppingBag className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Store Orders Yet</h3>
                <p className="text-muted-foreground mb-4">Start shopping to see your orders here</p>
                <Button onClick={() => navigate('/store')}>
                  Browse Store
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {storeOrders.map((order) => (
                  <Card key={order.order_id} className="glass-card" data-testid={`order-${order.order_id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg">Order #{order.order_id?.slice(-8)}</CardTitle>
                          <CardDescription>
                            {new Date(order.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </CardDescription>
                        </div>
                        <Badge variant={order.status === 'completed' ? 'default' : 'secondary'}>
                          {order.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center text-sm">
                            <div className="flex items-center gap-2">
                              <span>{item.name}</span>
                              <span className="text-muted-foreground">x{item.quantity}</span>
                            </div>
                            <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                        ))}
                        <div className="border-t pt-2 mt-2 flex justify-between font-bold">
                          <span>Total</span>
                          <span className="text-primary">${order.total_amount?.toFixed(2)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Songs Tab */}
          <TabsContent value="songs">
            {songPurchases.length === 0 ? (
              <Card className="glass-card text-center py-12">
                <Music className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Songs Purchased Yet</h3>
                <p className="text-muted-foreground mb-4">Purchase worship songs to download them anytime</p>
                <Button onClick={() => navigate('/worship')}>
                  Browse Worship Songs
                </Button>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {songPurchases.map((song) => (
                  <Card key={song.song_id} className="glass-card" data-testid={`song-${song.song_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        {song.cover_image ? (
                          <img 
                            src={song.cover_image.startsWith('/') ? BACKEND_URL + song.cover_image : song.cover_image} 
                            alt={song.title}
                            className="w-16 h-16 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Music className="h-8 w-8 text-primary" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold truncate">{song.title}</h4>
                          <p className="text-sm text-muted-foreground truncate">{song.artist}</p>
                          {song.album && (
                            <p className="text-xs text-muted-foreground truncate">{song.album}</p>
                          )}
                        </div>
                        <Button 
                          size="sm" 
                          onClick={async () => {
                            try {
                              const response = await axiosInstance.get(`/songs/${song.song_id}/download`, {
                                responseType: 'blob'
                              });
                              const url = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', `${song.title} - ${song.artist}.mp3`);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              window.URL.revokeObjectURL(url);
                              toast.success('Download started!');
                            } catch (e) {
                              toast.error('Failed to download');
                            }
                          }}
                          data-testid={`download-song-${song.song_id}`}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function GivingPage() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [offeringType, setOfferingType] = useState('tithe');
  const [loading, setLoading] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState('one_time');
  const [user, setUser] = useState(null);
  
  // Guest donor fields
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const res = await axiosInstance.get('/auth/me');
        setUser(res.data);
      } catch (e) {
        // Not logged in - that's OK for guest giving
      }
    };
    loadUser();
  }, []);

  const handleGive = async () => {
    const finalAmount = amount || customAmount;
    if (!finalAmount || parseFloat(finalAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      
      // Use different endpoint based on login status
      if (user) {
        // Logged-in user donation
        const response = await axiosInstance.post('/donations/checkout', {
          amount: parseFloat(finalAmount),
          offering_type: offeringType,
          frequency: frequency,
          origin_url: originUrl
        });
        
        if (response.data.url) {
          window.location.href = response.data.url;
        }
      } else {
        // Guest donation
        const response = await axiosInstance.post('/donations/guest-checkout', {
          amount: parseFloat(finalAmount),
          offering_type: offeringType,
          frequency: frequency,
          origin_url: originUrl,
          donor_name: isAnonymous ? null : donorName,
          donor_email: isAnonymous ? null : donorEmail,
          is_anonymous: isAnonymous
        });
        
        if (response.data.url) {
          window.location.href = response.data.url;
        }
      }
    } catch (e) {
      if (e.response?.data?.detail?.includes('Stripe')) {
        toast.error('Payment system not configured. Please contact admin.');
      } else {
        toast.error(e.response?.data?.detail || 'Failed to process donation');
      }
    }
    setLoading(false);
  };

  const quickAmounts = [25, 50, 100, 250, 500, 1000];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <nav className="glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div onClick={() => navigate('/')} className="cursor-pointer">
              <SiteLogo className="h-10 max-w-[200px]" />
            </div>
            <Button variant="ghost" onClick={() => navigate(-1)}>← Back</Button>
          </div>
        </div>
      </nav>
      
      <div className="pt-24 pb-12 px-4 max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="font-heading text-4xl font-bold mb-4">Give Online</h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            "Each of you should give what you have decided in your heart to give, not reluctantly or under compulsion, for God loves a cheerful giver." - 2 Corinthians 9:7
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          {/* Donation Form */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-primary" />
                Make a Donation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Offering Type</Label>
                <Select value={offeringType} onValueChange={setOfferingType}>
                  <SelectTrigger data-testid="offering-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tithe">Tithe (10%)</SelectItem>
                    <SelectItem value="offering">General Offering</SelectItem>
                    <SelectItem value="special_offering">Special Offering</SelectItem>
                    <SelectItem value="building_fund">Building Fund</SelectItem>
                    <SelectItem value="missions">Missions</SelectItem>
                    <SelectItem value="youth_ministry">Youth Ministry</SelectItem>
                    <SelectItem value="community_outreach">Community Outreach</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Quick Amounts */}
              <div>
                <Label>Select Amount</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {quickAmounts.map(a => (
                    <Button 
                      key={a} 
                      variant={amount === a.toString() ? "default" : "outline"}
                      className={amount === a.toString() ? "bg-primary" : ""}
                      onClick={() => { setAmount(a.toString()); setCustomAmount(''); }}
                      data-testid={`amount-${a}-btn`}
                    >
                      ${a}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Custom Amount */}
              <div>
                <Label>Or Enter Custom Amount ($)</Label>
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={customAmount}
                  onChange={(e) => { setCustomAmount(e.target.value); setAmount(''); }}
                  min="1"
                  step="0.01"
                  data-testid="custom-amount-input"
                />
              </div>

              {/* Recurring Option */}
              <div className="bg-primary/5 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <Label className="cursor-pointer">Make this recurring?</Label>
                  <input 
                    type="checkbox" 
                    checked={isRecurring}
                    onChange={(e) => {
                      setIsRecurring(e.target.checked);
                      setFrequency(e.target.checked ? 'monthly' : 'one_time');
                    }}
                    className="w-5 h-5 accent-primary"
                    data-testid="recurring-checkbox"
                  />
                </div>
                {isRecurring && (
                  <Select value={frequency} onValueChange={setFrequency}>
                    <SelectTrigger data-testid="frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Guest Donor Info (only shown when not logged in) */}
              {!user && (
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="font-medium">Donor Information (for receipt)</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="anonymous-check"
                        checked={isAnonymous}
                        onChange={(e) => setIsAnonymous(e.target.checked)}
                        className="rounded"
                      />
                      <Label htmlFor="anonymous-check" className="text-sm font-normal cursor-pointer">Give anonymously</Label>
                    </div>
                  </div>
                  {!isAnonymous && (
                    <>
                      <div>
                        <Label>Your Name</Label>
                        <Input
                          placeholder="Enter your name"
                          value={donorName}
                          onChange={(e) => setDonorName(e.target.value)}
                          data-testid="donor-name-input"
                        />
                      </div>
                      <div>
                        <Label>Email (for receipt)</Label>
                        <Input
                          type="email"
                          placeholder="Enter your email"
                          value={donorEmail}
                          onChange={(e) => setDonorEmail(e.target.value)}
                          data-testid="donor-email-input"
                        />
                      </div>
                    </>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {isAnonymous 
                      ? "Your donation will be recorded as anonymous."
                      : "We'll send a receipt to your email for your records."}
                  </p>
                </div>
              )}

              <Button 
                className="w-full bg-primary hover:bg-primary/90" 
                size="lg" 
                onClick={handleGive} 
                disabled={loading || (!amount && !customAmount)}
                data-testid="give-now-btn"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    Processing...
                  </div>
                ) : (
                  `Give $${amount || customAmount || '0.00'}${isRecurring ? ` / ${frequency}` : ''}`
                )}
              </Button>
              
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <CreditCard className="h-4 w-4" />
                <span>Secure payment powered by Stripe</span>
              </div>
            </CardContent>
          </Card>

          {/* Info Card */}
          <div className="space-y-6">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="h-5 w-5 text-red-500" />
                  Your Giving Matters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Your generous contributions help us continue our mission of spreading faith, building community, and fostering growth.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <div className="font-medium">100% Secure</div>
                      <p className="text-sm text-muted-foreground">Your payment information is encrypted</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-medium">Tax Deductible</div>
                      <p className="text-sm text-muted-foreground">Receive a receipt for your records</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Users className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Community Impact</div>
                      <p className="text-sm text-muted-foreground">Support local and global missions</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card bg-gradient-to-br from-primary/10 to-accent/10">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="font-heading text-2xl font-bold text-primary mb-2">Other Ways to Give</div>
                  <p className="text-sm text-muted-foreground mb-4">
                    In addition to online giving, you can also:
                  </p>
                  <ul className="text-left text-sm space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Give during Sunday service
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Mail a check to the church office
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Set up automatic bank transfers
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

// Nested Comment Component with Reply Support
function CommentWithReplies({ comment, postId, user, onReply }) {
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showReplies, setShowReplies] = useState(false);

  const handleSubmitReply = async () => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    try {
      const res = await axiosInstance.post(`/community/posts/${postId}/comment`, {
        content: replyText,
        parent_comment_id: comment.comment_id
      });
      onReply({
        ...res.data,
        user: { name: user?.name, picture: user?.picture }
      });
      setReplyText('');
      setShowReplyInput(false);
      setShowReplies(true);
      toast.success('Reply added!');
    } catch (e) {
      toast.error('Failed to add reply');
    }
    setSubmitting(false);
  };

  return (
    <div className="bg-muted/30 rounded-lg p-3">
      <div className="flex gap-2">
        <Avatar className="h-7 w-7">
          <AvatarImage src={comment.user_picture || comment.user?.picture} />
          <AvatarFallback>{(comment.user_name || comment.user?.name)?.[0] || 'U'}</AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{comment.user_name || comment.user?.name || 'Member'}</span>
            <span className="text-xs text-muted-foreground">
              {new Date(comment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          </div>
          <p className="text-sm mt-1">{comment.content}</p>
          
          {/* Reply Actions */}
          <div className="flex items-center gap-3 mt-2">
            {user && (
              <button 
                onClick={() => setShowReplyInput(!showReplyInput)}
                className="text-xs text-primary hover:underline"
                data-testid={`reply-btn-${comment.comment_id}`}
              >
                Reply
              </button>
            )}
            {(comment.replies?.length > 0 || comment.reply_count > 0) && (
              <button 
                onClick={() => setShowReplies(!showReplies)}
                className="text-xs text-muted-foreground hover:text-primary"
              >
                {showReplies ? 'Hide' : 'Show'} {comment.reply_count || comment.replies?.length || 0} replies
              </button>
            )}
          </div>

          {/* Reply Input */}
          {showReplyInput && (
            <div className="mt-2 flex gap-2">
              <Input
                placeholder={`Reply to ${comment.user_name || comment.user?.name || 'comment'}...`}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSubmitReply()}
                className="flex-1 h-8 text-sm"
                data-testid={`reply-input-${comment.comment_id}`}
              />
              <Button 
                size="sm" 
                onClick={handleSubmitReply}
                disabled={submitting || !replyText.trim()}
                className="h-8"
              >
                {submitting ? '...' : 'Reply'}
              </Button>
            </div>
          )}

          {/* Nested Replies */}
          {showReplies && comment.replies?.length > 0 && (
            <div className="mt-3 pl-4 border-l-2 border-muted space-y-2">
              {comment.replies.map((reply, idx) => (
                <div key={reply.comment_id || idx} className="flex gap-2">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={reply.user_picture || reply.user?.picture} />
                    <AvatarFallback>{(reply.user_name || reply.user?.name)?.[0] || 'U'}</AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-xs">{reply.user_name || reply.user?.name || 'Member'}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(reply.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                    <p className="text-xs mt-0.5">{reply.content}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CommunityPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = React.useRef(null);
  const [expandedComments, setExpandedComments] = useState({});
  const [commentText, setCommentText] = useState({});
  const [submittingComment, setSubmittingComment] = useState({});

  useEffect(() => {
    const loadData = async () => {
      try {
        const [postsRes, userRes] = await Promise.all([
          axiosInstance.get('/community/posts'),
          axiosInstance.get('/auth/me').catch(() => ({ data: null }))
        ]);
        setPosts(postsRes.data);
        setUser(userRes.data);
      } catch (e) {
        console.log('Failed to load community');
      }
      setLoading(false);
    };
    loadData();
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) { // 50MB limit
        toast.error('File size must be under 50MB');
        return;
      }
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePost = async () => {
    if (!newPost.trim() && !selectedFile) return;
    setUploading(true);
    
    try {
      let mediaUrl = null;
      let mediaType = null;
      
      // Upload file if selected
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        
        const uploadRes = await axiosInstance.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        mediaUrl = uploadRes.data.url;
        mediaType = selectedFile.type.startsWith('video/') ? 'video' : 'image';
      }
      
      const res = await axiosInstance.post('/community/posts', { 
        content: newPost,
        media_url: mediaUrl,
        media_type: mediaType
      });
      
      setPosts([{ 
        ...res.data, 
        author: { name: user?.name, picture: user?.picture }, 
        like_count: 0, 
        comments: [] 
      }, ...posts]);
      
      setNewPost('');
      clearFile();
      toast.success('Post shared!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Please login to post');
    }
    setUploading(false);
  };

  const handleLike = async (postId) => {
    try {
      const res = await axiosInstance.post(`/community/posts/${postId}/like`);
      setPosts(posts.map(p => p.post_id === postId ? {
        ...p, 
        like_count: res.data.liked ? p.like_count + 1 : p.like_count - 1,
        is_liked: res.data.liked
      } : p));
    } catch (e) {
      toast.error('Please login to like posts');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axiosInstance.delete(`/community/posts/${postId}`);
      setPosts(posts.filter(p => p.post_id !== postId));
      toast.success('Post deleted');
    } catch (e) {
      toast.error('Failed to delete post');
    }
  };

  const toggleComments = (postId) => {
    setExpandedComments(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const handleCommentSubmit = async (postId) => {
    const text = commentText[postId]?.trim();
    if (!text) return;
    
    setSubmittingComment(prev => ({ ...prev, [postId]: true }));
    try {
      const res = await axiosInstance.post(`/community/posts/${postId}/comment`, { content: text });
      
      // Add new comment to post
      setPosts(posts.map(p => {
        if (p.post_id === postId) {
          return {
            ...p,
            comments: [...(p.comments || []), {
              ...res.data,
              user: { name: user?.name, picture: user?.picture }
            }],
            comment_count: (p.comment_count || 0) + 1
          };
        }
        return p;
      }));
      
      setCommentText(prev => ({ ...prev, [postId]: '' }));
      toast.success('Comment added!');
    } catch (e) {
      toast.error('Please login to comment');
    }
    setSubmittingComment(prev => ({ ...prev, [postId]: false }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-primary/5">
      <nav className="glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div onClick={() => navigate('/')} className="cursor-pointer">
              <SiteLogo className="h-10 max-w-[200px]" />
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => navigate('/members')}>Members</Button>
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>Dashboard</Button>
            </div>
          </div>
        </div>
      </nav>
      <div className="pt-24 pb-12 px-4 max-w-2xl mx-auto">
        <h1 className="font-heading text-4xl font-bold mb-2">Community</h1>
        <p className="text-muted-foreground mb-8">Share updates, prayer requests, and connect with your church family</p>
        
        {/* New Post */}
        {user ? (
          <Card className="glass-card mb-6" data-testid="new-post-card">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3 mb-3">
                <Avatar>
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback>{user?.name?.charAt(0) || 'U'}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <textarea
                    className="w-full p-3 rounded-lg bg-secondary/20 resize-none border border-transparent focus:border-primary focus:outline-none"
                    placeholder="What's on your heart today?"
                    rows={3}
                    value={newPost}
                    onChange={(e) => setNewPost(e.target.value)}
                    data-testid="post-content-input"
                  />
                </div>
              </div>
              
              {/* Preview */}
              {previewUrl && (
                <div className="relative mb-3 mx-12">
                  {selectedFile?.type.startsWith('video/') ? (
                    <video src={previewUrl} controls className="w-full rounded-lg max-h-64" />
                  ) : (
                    <img src={previewUrl} alt="Preview" className="w-full rounded-lg max-h-64 object-cover" />
                  )}
                  <button 
                    onClick={clearFile}
                    className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1 hover:bg-black/70"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
              
              <div className="flex items-center justify-between ml-12">
                <div className="flex gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/*,video/*"
                    className="hidden"
                  />
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="add-media-btn"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Photo/Video
                  </Button>
                </div>
                <Button 
                  onClick={handlePost} 
                  disabled={(!newPost.trim() && !selectedFile) || uploading}
                  data-testid="share-post-btn"
                >
                  {uploading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      Posting...
                    </div>
                  ) : 'Share'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="glass-card mb-6 bg-primary/5">
            <CardContent className="py-6 text-center">
              <p className="text-muted-foreground mb-3">Join the conversation!</p>
              <Button onClick={() => navigate('/login')}>Login to Post</Button>
            </CardContent>
          </Card>
        )}

        {/* Posts Feed */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        ) : posts.length === 0 ? (
          <Card className="glass-card text-center py-12">
            <CardContent>
              <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No posts yet. Be the first to share!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {posts.map(post => (
              <Card key={post.post_id} className="glass-card" data-testid={`post-${post.post_id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarImage src={post.author?.picture} />
                        <AvatarFallback>{post.author?.name?.charAt(0) || 'U'}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-semibold">{post.author?.name || 'Member'}</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(post.created_at).toLocaleDateString('en-US', { 
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </div>
                      </div>
                    </div>
                    {(user?.user_id === post.user_id || ['admin', 'pastor'].includes(user?.role)) && (
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(post.post_id)}>
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="mb-4 whitespace-pre-wrap">{post.content}</p>
                  {post.media_url && (
                    post.media_type === 'video' ? (
                      <video src={post.media_url} controls className="w-full rounded-lg mb-4 max-h-96" />
                    ) : (
                      <img src={post.media_url} alt="" className="w-full rounded-lg mb-4 max-h-96 object-cover" />
                    )
                  )}
                  <div className="flex items-center gap-4 pt-2 border-t">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleLike(post.post_id)}
                      className={post.is_liked ? 'text-red-500' : ''}
                      data-testid={`like-btn-${post.post_id}`}
                    >
                      <Heart className={`h-4 w-4 mr-1 ${post.is_liked ? 'fill-red-500' : ''}`} />
                      {post.like_count || 0} {post.like_count === 1 ? 'Like' : 'Likes'}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => toggleComments(post.post_id)}
                      data-testid={`comment-btn-${post.post_id}`}
                    >
                      <MessageCircle className="h-4 w-4 mr-1" />
                      {post.comment_count || post.comments?.length || 0} Comments
                    </Button>
                  </div>
                  
                  {/* Comments Section */}
                  {expandedComments[post.post_id] && (
                    <div className="mt-4 pt-4 border-t space-y-3">
                      {/* Comment Input */}
                      {user && (
                        <div className="flex gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user.picture} />
                            <AvatarFallback>{user.name?.[0]}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 flex gap-2">
                            <Input
                              placeholder="Write a comment..."
                              value={commentText[post.post_id] || ''}
                              onChange={(e) => setCommentText(prev => ({ ...prev, [post.post_id]: e.target.value }))}
                              onKeyPress={(e) => e.key === 'Enter' && handleCommentSubmit(post.post_id)}
                              className="flex-1"
                              data-testid={`comment-input-${post.post_id}`}
                            />
                            <Button 
                              size="sm"
                              onClick={() => handleCommentSubmit(post.post_id)}
                              disabled={submittingComment[post.post_id] || !commentText[post.post_id]?.trim()}
                              data-testid={`submit-comment-${post.post_id}`}
                            >
                              {submittingComment[post.post_id] ? '...' : 'Post'}
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* Comments List */}
                      <div className="space-y-3 mt-3">
                        {(post.comments || []).length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-2">No comments yet. Be the first!</p>
                        ) : (
                          post.comments.map((comment, idx) => (
                            <CommentWithReplies 
                              key={comment.comment_id || idx}
                              comment={comment}
                              postId={post.post_id}
                              user={user}
                              onReply={(newReply) => {
                                // Add reply to comment
                                setPosts(posts.map(p => {
                                  if (p.post_id === post.post_id) {
                                    return {
                                      ...p,
                                      comments: p.comments.map(c => {
                                        if (c.comment_id === comment.comment_id) {
                                          return {
                                            ...c,
                                            replies: [...(c.replies || []), newReply],
                                            reply_count: (c.reply_count || 0) + 1
                                          };
                                        }
                                        return c;
                                      }),
                                      comment_count: (p.comment_count || 0) + 1
                                    };
                                  }
                                  return p;
                                }));
                              }}
                            />
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PrayerRequestsPage() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', is_anonymous: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const res = await axiosInstance.get('/prayer-requests');
      setRequests(res.data);
    } catch (e) {
      console.log('Failed to load prayer requests');
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.description) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await axiosInstance.post('/prayer-requests', formData);
      toast.success('Prayer request submitted');
      setShowForm(false);
      setFormData({ title: '', description: '', is_anonymous: false });
      loadRequests();
    } catch (e) {
      toast.error('Please login to submit prayer requests');
    }
  };

  const handlePray = async (requestId) => {
    try {
      const res = await axiosInstance.post(`/prayer-requests/${requestId}/pray`);
      setRequests(requests.map(r => r.request_id === requestId ? { ...r, prayer_count: res.data.prayer_count, has_prayed: true } : r));
      toast.success('Prayer recorded');
    } catch (e) {
      toast.error('Please login to pray for requests');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div onClick={() => navigate('/')} className="cursor-pointer">
              <SiteLogo className="h-10 max-w-[200px]" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowForm(true)}>Submit Request</Button>
              <Button variant="ghost" onClick={() => navigate(-1)}>← Back</Button>
            </div>
          </div>
        </div>
      </nav>
      <div className="pt-24 pb-12 px-4 max-w-3xl mx-auto">
        <h1 className="font-heading text-4xl font-bold mb-4">Prayer Requests</h1>
        <p className="text-muted-foreground mb-8">"The prayer of a righteous person is powerful and effective." - James 5:16</p>

        {/* Submit Form */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Submit Prayer Request</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input
                  placeholder="Brief title for your request"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </div>
              <div>
                <Label>Description</Label>
                <textarea
                  className="w-full p-3 rounded-lg bg-secondary/20 resize-none"
                  placeholder="Share your prayer request..."
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={formData.is_anonymous}
                  onCheckedChange={(c) => setFormData({ ...formData, is_anonymous: c })}
                />
                <Label>Submit anonymously</Label>
              </div>
              <Button className="w-full" onClick={handleSubmit}>Submit Request</Button>
            </div>
          </DialogContent>
        </Dialog>

        {loading ? (
          <p>Loading prayer requests...</p>
        ) : requests.length === 0 ? (
          <Card className="glass-card text-center py-12">
            <CardContent>
              <p className="text-muted-foreground">No prayer requests yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {requests.map(req => (
              <Card key={req.request_id} className={`glass-card ${req.is_answered ? 'border-green-500' : ''}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {req.title}
                        {req.is_answered && <Badge className="bg-green-600">Answered!</Badge>}
                      </CardTitle>
                      <CardDescription>
                        {req.author?.name || 'Anonymous'} • {new Date(req.created_at).toLocaleDateString()}
                      </CardDescription>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">{req.prayer_count}</div>
                      <div className="text-xs text-muted-foreground">prayers</div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">{req.description}</p>
                  <Button 
                    variant={req.has_prayed ? 'secondary' : 'default'}
                    onClick={() => handlePray(req.request_id)}
                    disabled={req.has_prayed}
                  >
                    {req.has_prayed ? '🙏 Prayed' : 'I Prayed'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== COUNSELING PAGE ====================

function CounselingPage() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [user, setUser] = useState(null);
  const [newRequest, setNewRequest] = useState({
    topic: '',
    message: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [requestsRes, userRes] = await Promise.all([
        axiosInstance.get('/counseling-requests'),
        axiosInstance.get('/auth/me')
      ]);
      setRequests(requestsRes.data);
      setUser(userRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!newRequest.topic || !newRequest.message) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await axiosInstance.post(`/counseling-requests?topic=${encodeURIComponent(newRequest.topic)}&message=${encodeURIComponent(newRequest.message)}`);
      toast.success('Counseling request submitted!');
      setShowNewRequest(false);
      setNewRequest({ topic: '', message: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const handleUpdateStatus = async (requestId, status, scheduledDate = null, notes = null) => {
    try {
      let url = `/counseling-requests/${requestId}?status=${status}`;
      if (scheduledDate) url += `&scheduled_date=${scheduledDate}`;
      if (notes) url += `&pastor_notes=${encodeURIComponent(notes)}`;
      
      await axiosInstance.put(url);
      toast.success('Request updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update request');
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;
    try {
      await axiosInstance.delete(`/counseling-requests/${requestId}`);
      toast.success('Request deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete request');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isPastorOrAdmin = user?.role === 'pastor' || user?.role === 'admin';

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="font-heading text-xl font-bold">
                  {isPastorOrAdmin ? 'Counseling Requests' : 'Connect with Pastor'}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {isPastorOrAdmin ? 'Manage counseling requests' : 'Request pastoral counseling'}
                </p>
              </div>
            </div>
            {!isPastorOrAdmin && (
              <Button onClick={() => setShowNewRequest(true)} data-testid="new-counseling-request-btn">
                <Plus className="h-4 w-4 mr-2" />
                Request Counseling
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Info Card for Members */}
        {!isPastorOrAdmin && (
          <Card className="mb-8 bg-gradient-to-r from-primary/10 to-accent/10">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <MessageCircle className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h2 className="font-semibold text-lg mb-2">Need to talk?</h2>
                  <p className="text-muted-foreground mb-4">
                    Our pastor is here to listen and provide spiritual guidance. Submit a counseling request
                    and we'll get back to you to schedule a confidential session.
                  </p>
                  <Button onClick={() => setShowNewRequest(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Request Counseling Session
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Requests List */}
        <div className="space-y-4">
          <h2 className="font-heading text-xl font-semibold">
            {isPastorOrAdmin ? 'All Requests' : 'Your Requests'}
          </h2>
          
          {requests.length === 0 ? (
            <Card className="p-8 text-center">
              <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {isPastorOrAdmin ? 'No counseling requests yet' : "You haven't submitted any requests yet"}
              </p>
            </Card>
          ) : (
            requests.map(request => (
              <Card key={request.request_id} className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{request.topic}</h3>
                      <Badge className={getStatusColor(request.status)}>
                        {request.status}
                      </Badge>
                    </div>
                    {isPastorOrAdmin && (
                      <p className="text-sm text-muted-foreground">
                        From: {request.user_name} ({request.user_email})
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {new Date(request.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  {(isPastorOrAdmin || request.user_id === user?.user_id) && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleDeleteRequest(request.request_id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
                
                <p className="text-sm mb-3">{request.message}</p>
                
                {request.scheduled_date && (
                  <div className="flex items-center gap-2 text-sm text-blue-600 mb-3">
                    <CalendarIcon className="h-4 w-4" />
                    Scheduled: {new Date(request.scheduled_date).toLocaleString()}
                  </div>
                )}
                
                {request.pastor_notes && (
                  <div className="bg-muted/50 rounded p-3 text-sm">
                    <strong>Pastor's Notes:</strong> {request.pastor_notes}
                  </div>
                )}
                
                {/* Pastor/Admin Actions */}
                {isPastorOrAdmin && request.status === 'pending' && (
                  <div className="flex gap-2 mt-4">
                    <Button 
                      size="sm" 
                      onClick={() => handleUpdateStatus(request.request_id, 'scheduled')}
                    >
                      Schedule
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleUpdateStatus(request.request_id, 'completed')}
                    >
                      Mark Completed
                    </Button>
                  </div>
                )}
              </Card>
            ))
          )}
        </div>
      </div>

      {/* New Request Dialog */}
      <Dialog open={showNewRequest} onOpenChange={setShowNewRequest}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Counseling</DialogTitle>
            <DialogDescription>
              Share what you'd like to discuss. All requests are kept confidential.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Topic</Label>
              <Select value={newRequest.topic} onValueChange={(v) => setNewRequest({ ...newRequest, topic: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a topic" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Spiritual Growth">Spiritual Growth</SelectItem>
                  <SelectItem value="Marriage & Family">Marriage & Family</SelectItem>
                  <SelectItem value="Career & Purpose">Career & Purpose</SelectItem>
                  <SelectItem value="Grief & Loss">Grief & Loss</SelectItem>
                  <SelectItem value="Relationships">Relationships</SelectItem>
                  <SelectItem value="Faith Questions">Faith Questions</SelectItem>
                  <SelectItem value="Personal Struggles">Personal Struggles</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                value={newRequest.message}
                onChange={(e) => setNewRequest({ ...newRequest, message: e.target.value })}
                placeholder="Tell us a bit about what you'd like to discuss..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewRequest(false)}>Cancel</Button>
            <Button onClick={handleSubmitRequest}>Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PastorCornerPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const res = await axiosInstance.get('/pastor-corner');
        setPosts(res.data);
      } catch (e) {
        console.log('Failed to load pastor posts');
      }
      setLoading(false);
    };
    loadPosts();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <nav className="glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div onClick={() => navigate('/')} className="cursor-pointer">
              <SiteLogo className="h-10 max-w-[200px]" />
            </div>
            <Button variant="ghost" onClick={() => navigate(-1)}>← Back</Button>
          </div>
        </div>
      </nav>
      <div className="pt-24 pb-12 px-4 max-w-4xl mx-auto">
        <h1 className="font-heading text-4xl font-bold mb-8">Pastor's Corner</h1>
        
        {loading ? (
          <p>Loading...</p>
        ) : posts.length === 0 ? (
          <Card className="glass-card text-center py-12">
            <CardContent>
              <p className="text-muted-foreground">No messages yet. Check back soon!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {posts.map(post => (
              <Card key={post.post_id} className="glass-card overflow-hidden">
                {post.image && (
                  <img src={post.image} alt={post.title} className="w-full h-64 object-cover" />
                )}
                <CardHeader>
                  <CardTitle className="text-2xl">{post.title}</CardTitle>
                  <CardDescription>
                    {post.author?.name} • {new Date(post.created_at).toLocaleDateString()}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="whitespace-pre-wrap">{post.content}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== WEEKLY FOCUS MANAGEMENT (Pastor/Admin) ====================

function WeeklyFocusManagement({ user }) {
  const navigate = useNavigate();
  const [focusList, setFocusList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingFocus, setEditingFocus] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    scripture: '',
    scripture_reference: '',
    message: '',
    prayer_points: [''],
    action_items: ['']
  });

  useEffect(() => {
    loadFocusList();
  }, []);

  const loadFocusList = async () => {
    try {
      const res = await axiosInstance.get('/weekly-focus/all');
      setFocusList(res.data);
    } catch (error) {
      toast.error('Failed to load weekly focus entries');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      prayer_points: formData.prayer_points.filter(p => p.trim()),
      action_items: formData.action_items.filter(a => a.trim())
    };

    try {
      if (editingFocus) {
        await axiosInstance.put(`/weekly-focus/${editingFocus.focus_id}`, submitData);
        toast.success('Weekly focus updated!');
      } else {
        await axiosInstance.post('/weekly-focus', submitData);
        toast.success('Weekly focus created!');
      }
      setShowCreate(false);
      setEditingFocus(null);
      setFormData({ title: '', scripture: '', scripture_reference: '', message: '', prayer_points: [''], action_items: [''] });
      loadFocusList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save weekly focus');
    }
  };

  const handleEdit = (focus) => {
    setEditingFocus(focus);
    setFormData({
      title: focus.title || '',
      scripture: focus.scripture || '',
      scripture_reference: focus.scripture_reference || '',
      message: focus.message || '',
      prayer_points: focus.prayer_points?.length ? focus.prayer_points : [''],
      action_items: focus.action_items?.length ? focus.action_items : ['']
    });
    setShowCreate(true);
  };

  const handleDelete = async (focusId) => {
    if (!window.confirm('Delete this weekly focus?')) return;
    try {
      await axiosInstance.delete(`/weekly-focus/${focusId}`);
      toast.success('Weekly focus deleted');
      loadFocusList();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleSetActive = async (focusId) => {
    try {
      await axiosInstance.put(`/weekly-focus/${focusId}`, { is_active: true });
      toast.success('Weekly focus activated!');
      loadFocusList();
    } catch (error) {
      toast.error('Failed to activate');
    }
  };

  const addPrayerPoint = () => setFormData({ ...formData, prayer_points: [...formData.prayer_points, ''] });
  const addActionItem = () => setFormData({ ...formData, action_items: [...formData.action_items, ''] });
  const updatePrayerPoint = (idx, value) => {
    const updated = [...formData.prayer_points];
    updated[idx] = value;
    setFormData({ ...formData, prayer_points: updated });
  };
  const updateActionItem = (idx, value) => {
    const updated = [...formData.action_items];
    updated[idx] = value;
    setFormData({ ...formData, action_items: updated });
  };
  const removePrayerPoint = (idx) => setFormData({ ...formData, prayer_points: formData.prayer_points.filter((_, i) => i !== idx) });
  const removeActionItem = (idx) => setFormData({ ...formData, action_items: formData.action_items.filter((_, i) => i !== idx) });

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-primary/5">
      <nav className="glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div onClick={() => navigate('/')} className="cursor-pointer">
              <SiteLogo className="h-10 max-w-[200px]" />
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>Dashboard</Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-4 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold">Weekly Focus Management</h1>
            <p className="text-muted-foreground">Create and manage the weekly focus message for the church</p>
          </div>
          <Button onClick={() => { setShowCreate(true); setEditingFocus(null); setFormData({ title: '', scripture: '', scripture_reference: '', message: '', prayer_points: [''], action_items: [''] }); }} data-testid="create-focus-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Weekly Focus
          </Button>
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingFocus ? 'Edit Weekly Focus' : 'Create Weekly Focus'}</DialogTitle>
              <DialogDescription>Set the focus message, scripture, prayer points, and action items for the week</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Walking in Faith"
                  required
                  data-testid="focus-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Scripture Text *</Label>
                  <textarea
                    className="w-full p-2 rounded-md border bg-background resize-none"
                    rows={3}
                    value={formData.scripture}
                    onChange={(e) => setFormData({ ...formData, scripture: e.target.value })}
                    placeholder="For we walk by faith, not by sight."
                    required
                    data-testid="focus-scripture-input"
                  />
                </div>
                <div>
                  <Label>Scripture Reference *</Label>
                  <Input
                    value={formData.scripture_reference}
                    onChange={(e) => setFormData({ ...formData, scripture_reference: e.target.value })}
                    placeholder="e.g., 2 Corinthians 5:7"
                    required
                    data-testid="focus-reference-input"
                  />
                </div>
              </div>
              <div>
                <Label>Message *</Label>
                <textarea
                  className="w-full p-2 rounded-md border bg-background resize-none"
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="This week, let us focus on strengthening our faith..."
                  required
                  data-testid="focus-message-input"
                />
              </div>
              <div>
                <Label>Prayer Points</Label>
                {formData.prayer_points.map((point, idx) => (
                  <div key={idx} className="flex gap-2 mt-2">
                    <Input
                      value={point}
                      onChange={(e) => updatePrayerPoint(idx, e.target.value)}
                      placeholder="Prayer point..."
                    />
                    <Button type="button" variant="outline" size="icon" onClick={() => removePrayerPoint(idx)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" className="mt-2" onClick={addPrayerPoint}>
                  <Plus className="h-4 w-4 mr-1" /> Add Prayer Point
                </Button>
              </div>
              <div>
                <Label>Action Items</Label>
                {formData.action_items.map((item, idx) => (
                  <div key={idx} className="flex gap-2 mt-2">
                    <Input
                      value={item}
                      onChange={(e) => updateActionItem(idx, e.target.value)}
                      placeholder="Action item..."
                    />
                    <Button type="button" variant="outline" size="icon" onClick={() => removeActionItem(idx)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" className="mt-2" onClick={addActionItem}>
                  <Plus className="h-4 w-4 mr-1" /> Add Action Item
                </Button>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-focus-btn">{editingFocus ? 'Update' : 'Create'} Weekly Focus</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Focus List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        ) : focusList.length === 0 ? (
          <Card className="glass-card text-center py-12">
            <CardContent>
              <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No weekly focus entries yet. Create the first one!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {focusList.map(focus => (
              <Card key={focus.focus_id} className={`glass-card ${focus.is_active ? 'border-2 border-primary' : ''}`} data-testid={`focus-card-${focus.focus_id}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <CardTitle>{focus.title}</CardTitle>
                        {focus.is_active && <Badge className="bg-primary">Active</Badge>}
                      </div>
                      <CardDescription>
                        {focus.scripture_reference} • Created {new Date(focus.created_at).toLocaleDateString()}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      {!focus.is_active && (
                        <Button variant="outline" size="sm" onClick={() => handleSetActive(focus.focus_id)}>
                          Set Active
                        </Button>
                      )}
                      <Button variant="outline" size="icon" onClick={() => handleEdit(focus)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="icon" onClick={() => handleDelete(focus.focus_id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="italic text-sm mb-2">"{focus.scripture}"</p>
                  <p className="text-muted-foreground text-sm">{focus.message}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MembersPage() {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadMembers = async () => {
      try {
        const res = await axiosInstance.get('/members');
        setMembers(res.data);
      } catch (e) {
        toast.error('Please login to view members');
        navigate('/login');
      }
      setLoading(false);
    };
    loadMembers();
  }, [navigate]);

  const handleFriendRequest = async (memberId) => {
    try {
      await axiosInstance.post(`/friends/request/${memberId}`);
      toast.success('Friend request sent!');
      setMembers(members.map(m => m.user_id === memberId ? { ...m, friendship_status: 'pending' } : m));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send request');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div onClick={() => navigate('/')} className="cursor-pointer">
              <SiteLogo className="h-10 max-w-[200px]" />
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => navigate('/community')}>Community</Button>
              <Button variant="ghost" onClick={() => navigate(-1)}>← Back</Button>
            </div>
          </div>
        </div>
      </nav>
      <div className="pt-24 pb-12 px-4 max-w-6xl mx-auto">
        <h1 className="font-heading text-4xl font-bold mb-8">Church Members</h1>
        
        {loading ? (
          <p>Loading members...</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {members.filter(m => !m.is_self).map(member => (
              <Card key={member.user_id} className="glass-card text-center">
                <CardContent className="pt-6">
                  <Avatar className="h-20 w-20 mx-auto mb-4">
                    <AvatarImage src={member.picture} />
                    <AvatarFallback className="text-2xl">{member.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <h3 className="font-semibold mb-1">{member.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{member.bio || 'Church member'}</p>
                  {member.friendship_status === 'accepted' ? (
                    <Badge className="bg-green-600">Friend</Badge>
                  ) : member.friendship_status === 'pending' ? (
                    <Badge variant="secondary">Pending</Badge>
                  ) : (
                    <Button size="sm" onClick={() => handleFriendRequest(member.user_id)}>
                      <Plus className="h-4 w-4 mr-1" /> Add Friend
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MentorBrowse({ user }) {
  const navigate = useNavigate();
  const [mentors, setMentors] = useState([]);
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [membership, setMembership] = useState(null);

  useEffect(() => {
    loadMentors();
    loadMembership();
  }, []);

  const loadMentors = async () => {
    try {
      const response = await axiosInstance.get('/mentors');
      setMentors(response.data);
    } catch (error) {
      console.error('Error loading mentors:', error);
    }
  };

  const loadMembership = async () => {
    try {
      const response = await axiosInstance.get('/membership/my');
      setMembership(response.data);
    } catch (error) {
      console.error('Error loading membership:', error);
    }
  };

  const handleRequestMentorship = async () => {
    // Check if user has an active mentorship plan
    if (!membership?.has_mentorship) {
      toast.info('Please subscribe to a mentorship plan first');
      navigate('/subscribe');
      return;
    }
    
    if (!requestMessage.trim()) {
      toast.error('Please write a message');
      return;
    }
    try {
      await axiosInstance.post('/mentorship/request', {
        mentor_id: selectedMentor.user_id,
        message: requestMessage,
      });
      toast.success('Request sent! The mentor will review your request.');
      setShowDialog(false);
      setRequestMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    }
  };

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-bold mb-2" data-testid="browse-mentors-heading">Find Your Mentor</h1>
          <p className="text-muted-foreground">Connect with experienced professionals ready to guide you</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {mentors.map((mentor) => (
            <Card key={mentor.user_id} className="glass-card hover:shadow-lg transition-shadow" data-testid="mentor-card">
              <CardHeader>
                <div className="flex items-center gap-4 mb-4">
                  <Avatar className="h-16 w-16">
                    <AvatarImage src={mentor.picture} />
                    <AvatarFallback>{mentor.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <CardTitle className="font-heading">{mentor.name}</CardTitle>
                    <Badge className="mt-1 bg-primary">Mentor</Badge>
                  </div>
                </div>
                <CardDescription className="line-clamp-3">{mentor.bio}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {mentor.expertise_areas && mentor.expertise_areas.length > 0 && (
                    <div>
                      <div className="text-sm font-semibold mb-2">Expertise</div>
                      <div className="flex flex-wrap gap-2">
                        {mentor.expertise_areas.slice(0, 3).map((area, idx) => (
                          <Badge key={idx} variant="secondary">
                            {area}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  <Button
                    className="w-full bg-primary hover:bg-primary/90"
                    onClick={() => {
                      if (!membership?.has_mentorship) {
                        toast.info('Subscribe to a mentorship plan to connect with mentors');
                        navigate('/subscribe');
                        return;
                      }
                      setSelectedMentor(mentor);
                      setShowDialog(true);
                    }}
                    data-testid="connect-mentor-btn"
                  >
                    Connect
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        {/* Upgrade Banner for non-subscribers */}
        {!membership?.has_mentorship && (
          <Card className="mt-8 bg-gradient-to-r from-primary/10 to-primary/5 border-primary/30">
            <CardContent className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-heading text-xl font-bold">Unlock Mentorship</h3>
                <p className="text-muted-foreground">Subscribe to a mentorship plan to connect with our experienced mentors</p>
              </div>
              <Button onClick={() => navigate('/subscribe')} className="bg-primary hover:bg-primary/90" data-testid="upgrade-cta-btn">
                View Plans
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg" data-testid="request-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Request Mentorship with {selectedMentor?.name}</DialogTitle>
            <DialogDescription>Introduce yourself and explain what you hope to achieve</DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Mentor Info */}
            {selectedMentor && (
              <div className="flex items-center gap-4 p-4 bg-secondary/10 rounded-lg">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={selectedMentor.picture} />
                  <AvatarFallback>{selectedMentor.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-semibold">{selectedMentor.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {selectedMentor.expertise_areas?.slice(0, 2).join(', ')}
                  </div>
                </div>
              </div>
            )}
            
            {/* Your Plan */}
            {membership?.has_mentorship && (
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Your Plan: <Badge variant="secondary">{membership.mentorship_plan_name}</Badge></span>
              </div>
            )}

            {/* Message */}
            <div>
              <Label className="mb-2 block">Your Message</Label>
              <Textarea
                placeholder="Tell the mentor about yourself and what you hope to achieve..."
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                rows={4}
                data-testid="request-message-input"
              />
            </div>

            <div className="flex gap-2">
              <Button 
                className="flex-1 bg-primary hover:bg-primary/90" 
                onClick={handleRequestMentorship}
                disabled={!requestMessage.trim()}
                data-testid="send-request-btn"
              >
                Send Request
              </Button>
              <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="cancel-request-btn">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MentorshipDetail({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const pathParts = location.pathname.split('/');
  const id = pathParts[pathParts.length - 1];
  
  const [mentorship, setMentorship] = useState(null);
  const [mentorInfo, setMentorInfo] = useState(null);
  const [menteeInfo, setMenteeInfo] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [goals, setGoals] = useState([]);
  const [messages, setMessages] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [files, setFiles] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [videoSessions, setVideoSessions] = useState([]);
  const [activeVideoCall, setActiveVideoCall] = useState(null);
  const [isInCall, setIsInCall] = useState(false);
  const videoContainerRef = useRef(null);
  const callFrameRef = useRef(null);
  const [newMessage, setNewMessage] = useState('');
  const [showNewSession, setShowNewSession] = useState(false);
  const [showNewGoal, setShowNewGoal] = useState(false);
  const [showNewTask, setShowNewTask] = useState(false);
  const [showNewDocument, setShowNewDocument] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [goalTemplates, setGoalTemplates] = useState([]);
  const [showTemplateGoals, setShowTemplateGoals] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const messagesEndRef = useRef(null);

  const [newSession, setNewSession] = useState({
    title: '',
    description: '',
    scheduled_at: '',
    duration_minutes: 60
  });

  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    target_date: ''
  });

  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    task_type: 'standard'
  });

  const [newDocumentData, setNewDocumentData] = useState({
    title: '',
    content: ''
  });

  useEffect(() => {
    if (id) {
      loadMentorshipData();
    }
  }, [id]);

  // Task type helper
  const getTaskTypeInfo = (taskType) => {
    const types = {
      standard: { label: 'Task', color: 'bg-secondary', icon: CheckCircle },
      budget: { label: 'Budget', color: 'bg-green-600', icon: DollarSign },
      resume_review: { label: 'Resume Review', color: 'bg-blue-600', icon: FileText },
      leadership_quiz: { label: 'Leadership', color: 'bg-purple-600', icon: Award },
      business_plan: { label: 'Business Plan', color: 'bg-orange-600', icon: Briefcase }
    };
    return types[taskType] || types.standard;
  };

  const loadMentorshipData = async () => {
    try {
      const [mentorshipsRes, sessionsRes, goalsRes, messagesRes, tasksRes, filesRes, documentsRes, videoSessionsRes] = await Promise.all([
        axiosInstance.get('/mentorships'),
        axiosInstance.get(`/sessions?mentorship_id=${id}`),
        axiosInstance.get(`/goals?mentorship_id=${id}`),
        axiosInstance.get(`/messages?mentorship_id=${id}`),
        axiosInstance.get(`/tasks?mentorship_id=${id}`),
        axiosInstance.get(`/files?mentorship_id=${id}`),
        axiosInstance.get(`/documents?mentorship_id=${id}`),
        axiosInstance.get(`/video/sessions?mentorship_id=${id}`)
      ]);
      
      const currentMentorship = mentorshipsRes.data.find((m) => m.mentorship_id === id);
      setMentorship(currentMentorship);
      
      if (currentMentorship) {
        const [mentorRes, menteeRes] = await Promise.all([
          axiosInstance.get('/users/profile'),
          axiosInstance.get('/users/profile')
        ]);
        
        const allUsers = await axiosInstance.get('/mentors');
        const mentor = allUsers.data.find(u => u.user_id === currentMentorship.mentor_id);
        const mentee = allUsers.data.find(u => u.user_id === currentMentorship.mentee_id);
        
        setMentorInfo(mentor || { name: 'Mentor', email: '' });
        setMenteeInfo(mentee || { name: 'Mentee', email: '' });
      }
      
      setSessions(sessionsRes.data);
      setGoals(goalsRes.data);
      setMessages(messagesRes.data);
      setTasks(tasksRes.data);
      setFiles(filesRes.data);
      setDocuments(documentsRes.data);
      setVideoSessions(videoSessionsRes.data);
      
      // Load goal templates
      try {
        const templatesRes = await axiosInstance.get('/mentorship-templates');
        setGoalTemplates(templatesRes.data);
      } catch (e) {
        console.log('Could not load templates');
      }
    } catch (error) {
      console.error('Error loading mentorship data:', error);
    }
  };

  const handleApplyTemplate = async (template) => {
    if (!template || !template.goals || template.goals.length === 0) {
      toast.error('Template has no goals');
      return;
    }
    
    setApplyingTemplate(true);
    try {
      // Calculate target dates based on template duration
      const durationWeeks = template.duration_weeks || 12;
      const weeksPerGoal = Math.floor(durationWeeks / template.goals.length);
      
      // Create each goal from the template
      for (let i = 0; i < template.goals.length; i++) {
        const goalTitle = template.goals[i];
        const targetDate = new Date();
        targetDate.setDate(targetDate.getDate() + (weeksPerGoal * (i + 1) * 7));
        
        await axiosInstance.post('/goals', {
          mentorship_id: id,
          title: goalTitle,
          description: `Goal from "${template.name}" template`,
          target_date: targetDate.toISOString()
        });
      }
      
      toast.success(`${template.goals.length} goals added from "${template.name}" template!`);
      setShowTemplateGoals(false);
      setSelectedTemplate(null);
      loadMentorshipData();
    } catch (error) {
      toast.error('Failed to apply template');
    }
    setApplyingTemplate(false);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      await axiosInstance.post('/messages', {
        mentorship_id: id,
        content: newMessage,
      });
      setNewMessage('');
      loadMentorshipData();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/sessions', {
        mentorship_id: id,
        ...newSession,
        scheduled_at: new Date(newSession.scheduled_at).toISOString()
      });
      toast.success('Session created!');
      setShowNewSession(false);
      setNewSession({ title: '', description: '', scheduled_at: '', duration_minutes: 60 });
      loadMentorshipData();
    } catch (error) {
      toast.error('Failed to create session');
    }
  };

  const handleCreateGoal = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/goals', {
        mentorship_id: id,
        ...newGoal,
        target_date: newGoal.target_date ? new Date(newGoal.target_date).toISOString() : null
      });
      toast.success('Goal created!');
      setShowNewGoal(false);
      setNewGoal({ title: '', description: '', target_date: '' });
      loadMentorshipData();
    } catch (error) {
      toast.error('Failed to create goal');
    }
  };

  const handleUpdateGoalProgress = async (goalId, newProgress) => {
    try {
      await axiosInstance.put(`/goals/${goalId}`, { 
        progress: newProgress,
        status: newProgress === 100 ? 'completed' : 'in_progress'
      });
      toast.success('Progress updated!');
      loadMentorshipData();
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/tasks', {
        mentorship_id: id,
        ...newTask,
        due_date: newTask.due_date ? new Date(newTask.due_date).toISOString() : null
      });
      toast.success('Task created!');
      setShowNewTask(false);
      setNewTask({ title: '', description: '', priority: 'medium', due_date: '', task_type: 'standard' });
      loadMentorshipData();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      await axiosInstance.put(`/tasks/${taskId}`, { status: newStatus });
      toast.success('Task updated!');
      loadMentorshipData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axiosInstance.delete(`/tasks/${taskId}`);
      toast.success('Task deleted!');
      loadMentorshipData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axiosInstance.post(`/files/upload?mentorship_id=${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('File uploaded!');
      loadMentorshipData();
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await axiosInstance.delete(`/files/${fileId}`);
      toast.success('File deleted!');
      loadMentorshipData();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handleCreateDocument = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/documents', {
        mentorship_id: id,
        ...newDocumentData
      });
      toast.success('Document created!');
      setShowNewDocument(false);
      setNewDocumentData({ title: '', content: '' });
      loadMentorshipData();
    } catch (error) {
      toast.error('Failed to create document');
    }
  };

  const handleUpdateDocument = async (documentId, content) => {
    try {
      await axiosInstance.put(`/documents/${documentId}`, { content });
      toast.success('Document saved!');
      loadMentorshipData();
    } catch (error) {
      toast.error('Failed to save document');
    }
  };

  const handleStartVideoCall = async () => {
    try {
      const response = await axiosInstance.post(`/video/session?mentorship_id=${id}`);
      setActiveVideoCall(response.data);
      toast.success('Video call started! Share the link with your partner.');
    } catch (error) {
      toast.error('Failed to start video call');
    }
  };

  const handleJoinVideoCall = (roomUrl) => {
    window.open(roomUrl, '_blank', 'width=1200,height=800');
  };

  const handleEndVideoCall = async () => {
    if (!activeVideoCall) return;
    try {
      await axiosInstance.put(`/video/session/${activeVideoCall.session_id}/end`);
      setActiveVideoCall(null);
      toast.success('Video call ended');
      loadMentorshipData();
    } catch (error) {
      toast.error('Failed to end call');
    }
  };

  const tasksByStatus = {
    todo: tasks.filter(t => t.status === 'todo'),
    in_progress: tasks.filter(t => t.status === 'in_progress'),
    done: tasks.filter(t => t.status === 'done')
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!mentorship) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6" data-testid="back-btn">
          ← Back to Dashboard
        </Button>

        {/* Header */}
        <Card className="glass-card mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-3xl">Mentorship Workspace</CardTitle>
                <CardDescription className="mt-2">
                  <div className="flex items-center gap-4">
                    <div>
                      <span className="font-semibold">Mentor:</span> {mentorInfo?.name || 'Loading...'}
                    </div>
                    <div>
                      <span className="font-semibold">Mentee:</span> {menteeInfo?.name || 'Loading...'}
                    </div>
                  </div>
                </CardDescription>
              </div>
              <Badge className="bg-primary text-lg px-4 py-2">{mentorship.status}</Badge>
            </div>
          </CardHeader>
        </Card>

        {/* Main Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks</TabsTrigger>
            <TabsTrigger value="goals" data-testid="tab-goals">Goals</TabsTrigger>
            <TabsTrigger value="sessions" data-testid="tab-sessions">Sessions</TabsTrigger>
            <TabsTrigger value="video" data-testid="tab-video">Video Calls</TabsTrigger>
            <TabsTrigger value="files" data-testid="tab-files">Files</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle className="font-heading">Quick Stats</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-3xl font-bold font-heading text-primary">{tasks.length}</div>
                        <div className="text-sm text-muted-foreground">Tasks</div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold font-heading text-accent">{goals.length}</div>
                        <div className="text-sm text-muted-foreground">Goals</div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold font-heading text-primary">{sessions.length}</div>
                        <div className="text-sm text-muted-foreground">Sessions</div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold font-heading text-accent">{files.length}</div>
                        <div className="text-sm text-muted-foreground">Files</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle className="font-heading">Upcoming Sessions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {sessions.filter(s => s.status === 'scheduled').length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">No upcoming sessions</p>
                    ) : (
                      <div className="space-y-3">
                        {sessions
                          .filter(s => s.status === 'scheduled')
                          .slice(0, 3)
                          .map((session) => (
                            <div key={session.session_id} className="p-3 rounded-lg bg-secondary/20">
                              <div className="font-semibold">{session.title}</div>
                              <div className="text-sm text-muted-foreground">
                                {new Date(session.scheduled_at).toLocaleString()} • {session.duration_minutes} min
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Messages */}
              <Card className="glass-card h-[600px] flex flex-col">
                <CardHeader>
                  <CardTitle className="font-heading">Messages</CardTitle>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <div className="flex-1 overflow-y-auto space-y-3 mb-4" data-testid="messages-container">
                    {messages.map((message) => (
                      <div
                        key={message.message_id}
                        className={`p-3 rounded-lg ${
                          message.sender_id === user.user_id ? 'bg-primary text-primary-foreground ml-8' : 'bg-secondary mr-8'
                        }`}
                        data-testid="message-item"
                      >
                        <p className="text-sm">{message.content}</p>
                        <div className="text-xs opacity-70 mt-1">{new Date(message.created_at).toLocaleTimeString()}</div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      data-testid="message-input"
                    />
                    <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="send-message-btn">
                      Send
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <Card className="glass-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading">Task Board</CardTitle>
                  <Button size="sm" className="bg-accent hover:bg-accent/90" onClick={() => setShowNewTask(true)} data-testid="add-task-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add Task
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* To Do Column */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Circle className="h-4 w-4 text-muted-foreground" />
                      To Do ({tasksByStatus.todo.length})
                    </h3>
                    <div className="space-y-2">
                      {tasksByStatus.todo.map(task => {
                        const typeInfo = getTaskTypeInfo(task.task_type);
                        const TypeIcon = typeInfo.icon;
                        return (
                        <Card key={task.task_id} className="p-3" data-testid="task-card">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="font-semibold text-sm">{task.title}</div>
                              {task.task_type && task.task_type !== 'standard' && (
                                <Badge className={`${typeInfo.color} text-xs mt-1`}>
                                  <TypeIcon className="h-3 w-3 mr-1" />
                                  {typeInfo.label}
                                </Badge>
                              )}
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="ghost">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Move to</DropdownMenuLabel>
                                <DropdownMenuItem onClick={() => handleUpdateTaskStatus(task.task_id, 'in_progress')}>
                                  <Clock className="h-4 w-4 mr-2 text-accent" /> In Progress
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleUpdateTaskStatus(task.task_id, 'done')}>
                                  <CheckCircle className="h-4 w-4 mr-2 text-primary" /> Done
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteTask(task.task_id)} className="text-destructive">
                                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          {task.description && <p className="text-xs text-muted-foreground mb-2">{task.description}</p>}
                          <div className="flex items-center justify-between">
                            <Badge className={getPriorityColor(task.priority)}>{task.priority}</Badge>
                            <div className="flex gap-1">
                              {task.task_type === 'budget' && (
                                <Button size="sm" variant="outline" onClick={() => navigate(`/budget/${id}`)}>
                                  <DollarSign className="h-3 w-3 mr-1" /> Open Budget
                                </Button>
                              )}
                              {task.task_type === 'resume_review' && (
                                <Button size="sm" variant="outline" onClick={() => navigate('/ai-tools')}>
                                  <FileText className="h-3 w-3 mr-1" /> Review
                                </Button>
                              )}
                              {task.task_type === 'leadership_quiz' && (
                                <Button size="sm" variant="outline" onClick={() => navigate('/leadership-assessment')}>
                                  <Award className="h-3 w-3 mr-1" /> Take Quiz
                                </Button>
                              )}
                              {task.task_type === 'business_plan' && (
                                <Button size="sm" variant="outline" onClick={() => navigate('/ai-tools')}>
                                  <Briefcase className="h-3 w-3 mr-1" /> Plan
                                </Button>
                              )}
                              <Button size="sm" onClick={() => handleUpdateTaskStatus(task.task_id, 'in_progress')}>
                                Start
                              </Button>
                            </div>
                          </div>
                        </Card>
                      )})}
                    </div>
                  </div>

                  {/* In Progress Column */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Clock className="h-4 w-4 text-accent" />
                      In Progress ({tasksByStatus.in_progress.length})
                    </h3>
                    <div className="space-y-2">
                      {tasksByStatus.in_progress.map(task => {
                        const typeInfo = getTaskTypeInfo(task.task_type);
                        const TypeIcon = typeInfo.icon;
                        return (
                        <Card key={task.task_id} className="p-3 border-l-4 border-accent" data-testid="task-card">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="font-semibold text-sm">{task.title}</div>
                              {task.task_type && task.task_type !== 'standard' && (
                                <Badge className={`${typeInfo.color} text-xs mt-1`}>
                                  <TypeIcon className="h-3 w-3 mr-1" />
                                  {typeInfo.label}
                                </Badge>
                              )}
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="ghost">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Move to</DropdownMenuLabel>
                                <DropdownMenuItem onClick={() => handleUpdateTaskStatus(task.task_id, 'todo')}>
                                  <Circle className="h-4 w-4 mr-2" /> To Do
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleUpdateTaskStatus(task.task_id, 'done')}>
                                  <CheckCircle className="h-4 w-4 mr-2 text-primary" /> Done
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteTask(task.task_id)} className="text-destructive">
                                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          {task.description && <p className="text-xs text-muted-foreground mb-2">{task.description}</p>}
                          <div className="flex items-center justify-between">
                            <Badge className={getPriorityColor(task.priority)}>{task.priority}</Badge>
                            <div className="flex gap-1">
                              {task.task_type === 'budget' && (
                                <Button size="sm" variant="outline" onClick={() => navigate(`/budget/${id}`)}>
                                  <DollarSign className="h-3 w-3 mr-1" /> Budget
                                </Button>
                              )}
                              {task.task_type === 'resume_review' && (
                                <Button size="sm" variant="outline" onClick={() => navigate('/ai-tools')}>
                                  <FileText className="h-3 w-3 mr-1" /> Review
                                </Button>
                              )}
                              {task.task_type === 'leadership_quiz' && (
                                <Button size="sm" variant="outline" onClick={() => navigate('/leadership-assessment')}>
                                  <Award className="h-3 w-3 mr-1" /> Quiz
                                </Button>
                              )}
                              {task.task_type === 'business_plan' && (
                                <Button size="sm" variant="outline" onClick={() => navigate('/ai-tools')}>
                                  <Briefcase className="h-3 w-3 mr-1" /> Plan
                                </Button>
                              )}
                              <Button size="sm" onClick={() => handleUpdateTaskStatus(task.task_id, 'done')}>
                                Complete
                              </Button>
                            </div>
                          </div>
                        </Card>
                      )})}
                    </div>
                  </div>

                  {/* Done Column */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Done ({tasksByStatus.done.length})
                    </h3>
                    <div className="space-y-2">
                      {tasksByStatus.done.map(task => {
                        const typeInfo = getTaskTypeInfo(task.task_type);
                        const TypeIcon = typeInfo.icon;
                        return (
                        <Card key={task.task_id} className="p-3 bg-primary/5" data-testid="task-card">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="font-semibold text-sm line-through text-muted-foreground">{task.title}</div>
                              {task.task_type && task.task_type !== 'standard' && (
                                <Badge className={`${typeInfo.color} text-xs mt-1 opacity-60`}>
                                  <TypeIcon className="h-3 w-3 mr-1" />
                                  {typeInfo.label}
                                </Badge>
                              )}
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="ghost">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Move to</DropdownMenuLabel>
                                <DropdownMenuItem onClick={() => handleUpdateTaskStatus(task.task_id, 'todo')}>
                                  <Circle className="h-4 w-4 mr-2" /> To Do
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleUpdateTaskStatus(task.task_id, 'in_progress')}>
                                  <Clock className="h-4 w-4 mr-2 text-accent" /> In Progress
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteTask(task.task_id)} className="text-destructive">
                                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          {task.description && <p className="text-xs text-muted-foreground mb-2">{task.description}</p>}
                          <Badge className={getPriorityColor(task.priority)}>{task.priority}</Badge>
                        </Card>
                      )})}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Goals Tab */}
          <TabsContent value="goals">
            <Card className="glass-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading">Goals & Progress</CardTitle>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setShowTemplateGoals(true)} data-testid="use-template-btn">
                      <FileText className="h-4 w-4 mr-1" /> Use Template
                    </Button>
                    <Button size="sm" className="bg-accent hover:bg-accent/90" onClick={() => setShowNewGoal(true)} data-testid="add-goal-btn">
                      <Plus className="h-4 w-4 mr-1" /> Add Goal
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {goals.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground mb-4">No goals set yet</p>
                    <Button variant="outline" onClick={() => setShowTemplateGoals(true)}>
                      <FileText className="h-4 w-4 mr-2" /> Start with a Template
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {goals.map((goal) => (
                      <Card key={goal.goal_id} className="glass-card" data-testid="goal-card">
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <CardTitle className="font-heading text-lg">{goal.title}</CardTitle>
                            <Badge variant={goal.status === 'completed' ? 'default' : 'secondary'} className={goal.progress === 100 ? 'bg-green-600' : ''}>
                              {goal.progress === 100 ? 'Completed' : goal.status}
                            </Badge>
                          </div>
                          {goal.description && <CardDescription>{goal.description}</CardDescription>}
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            <div>
                              <div className="flex justify-between text-sm mb-2">
                                <span className="text-muted-foreground">Progress</span>
                                <span className="font-semibold">{goal.progress}%</span>
                              </div>
                              <Progress value={goal.progress} className="h-3" />
                            </div>
                            <div className="flex gap-2">
                              {[0, 25, 50, 75, 100].map(val => (
                                <Button
                                  key={val}
                                  size="sm"
                                  variant={goal.progress === val ? 'default' : 'outline'}
                                  onClick={() => handleUpdateGoalProgress(goal.goal_id, val)}
                                  className="flex-1"
                                >
                                  {val}%
                                </Button>
                              ))}
                            </div>
                            {goal.target_date && (
                              <div className="text-sm text-muted-foreground flex items-center gap-1">
                                <CalendarIcon className="h-3 w-3" />
                                Target: {new Date(goal.target_date).toLocaleDateString()}
                                {new Date(goal.target_date) < new Date() && goal.progress < 100 && (
                                  <Badge className="bg-red-500 text-xs ml-2">Overdue</Badge>
                                )}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sessions Tab */}
          <TabsContent value="sessions">
            <Card className="glass-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading">Sessions</CardTitle>
                  <Button size="sm" className="bg-accent hover:bg-accent/90" onClick={() => setShowNewSession(true)} data-testid="add-session-btn">
                    <Plus className="h-4 w-4 mr-1" /> Schedule Session
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {sessions.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No sessions yet</p>
                ) : (
                  <div className="space-y-3">
                    {sessions.map((session) => (
                      <div key={session.session_id} className="p-4 rounded-lg bg-secondary/20" data-testid="session-detail-item">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="font-semibold text-lg">{session.title}</div>
                            <div className="text-sm text-muted-foreground mt-1">
                              {new Date(session.scheduled_at).toLocaleString()} • {session.duration_minutes} min
                            </div>
                            {session.description && <p className="text-sm mt-2">{session.description}</p>}
                          </div>
                          <Badge className={session.status === 'scheduled' ? 'bg-accent' : 'bg-primary'}>
                            {session.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Video Calls Tab */}
          <TabsContent value="video">
            <Card className="glass-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading">Video Calls</CardTitle>
                  {!activeVideoCall ? (
                    <Button
                      size="sm"
                      className="bg-accent hover:bg-accent/90"
                      onClick={handleStartVideoCall}
                      data-testid="start-video-call-btn"
                    >
                      <Video className="h-4 w-4 mr-1" /> Start Video Call
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={handleEndVideoCall}
                      data-testid="end-video-call-btn"
                    >
                      <PhoneOff className="h-4 w-4 mr-1" /> End Call
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {activeVideoCall && (
                  <Card className="p-6 mb-6 bg-green-50 border-green-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-lg mb-2 flex items-center gap-2">
                          <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse"></div>
                          Active Call
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">
                          Share this link with your partner to join the call
                        </p>
                        <div className="flex gap-2">
                          <Input
                            value={activeVideoCall.room_url}
                            readOnly
                            className="font-mono text-sm"
                            data-testid="video-room-url"
                          />
                          <Button
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(activeVideoCall.room_url);
                              toast.success('Link copied!');
                            }}
                            data-testid="copy-video-link-btn"
                          >
                            Copy
                          </Button>
                        </div>
                      </div>
                      <Button
                        size="lg"
                        className="bg-primary hover:bg-primary/90"
                        onClick={() => handleJoinVideoCall(activeVideoCall.room_url)}
                        data-testid="join-video-call-btn"
                      >
                        <Phone className="h-5 w-5 mr-2" /> Join Call
                      </Button>
                    </div>
                  </Card>
                )}

                <div className="mb-4">
                  <h3 className="font-semibold mb-3">Call History</h3>
                  {videoSessions.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No video calls yet</p>
                  ) : (
                    <div className="space-y-3">
                      {videoSessions.map((session) => (
                        <div
                          key={session.session_id}
                          className="p-4 rounded-lg bg-secondary/20 flex items-center justify-between"
                          data-testid="video-session-item"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                              session.status === 'active' ? 'bg-green-100' : 'bg-gray-100'
                            }`}>
                              <Video className={`h-5 w-5 ${
                                session.status === 'active' ? 'text-green-600' : 'text-gray-600'
                              }`} />
                            </div>
                            <div>
                              <div className="font-semibold">
                                {session.status === 'active' ? 'Active Call' : 'Past Call'}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Started {new Date(session.started_at).toLocaleString()}
                                {session.ended_at && (
                                  <> • Ended {new Date(session.ended_at).toLocaleString()}</>
                                )}
                              </div>
                            </div>
                          </div>
                          {session.status === 'active' && session.room_url && (
                            <Button
                              size="sm"
                              className="bg-primary hover:bg-primary/90"
                              onClick={() => handleJoinVideoCall(session.room_url)}
                              data-testid="rejoin-video-call-btn"
                            >
                              <Phone className="h-4 w-4 mr-1" /> Join
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Card className="p-4 bg-blue-50 border-blue-200 mt-6">
                  <div className="flex gap-3">
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Video className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-semibold mb-1">How Video Calls Work</div>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Click "Start Video Call" to create a new room</li>
                        <li>• Share the link with your mentor/mentee</li>
                        <li>• Click "Join Call" to enter the video room</li>
                        <li>• Video calls open in a new window</li>
                        <li>• End the call when you're finished</li>
                      </ul>
                    </div>
                  </div>
                </Card>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Files Tab */}
          <TabsContent value="files">
            <Card className="glass-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading">Files</CardTitle>
                  <div>
                    <input
                      type="file"
                      id="file-upload"
                      className="hidden"
                      onChange={handleFileUpload}
                      disabled={uploadingFile}
                    />
                    <Button
                      size="sm"
                      className="bg-accent hover:bg-accent/90"
                      onClick={() => document.getElementById('file-upload').click()}
                      disabled={uploadingFile}
                      data-testid="upload-file-btn"
                    >
                      {uploadingFile ? 'Uploading...' : <><Plus className="h-4 w-4 mr-1" /> Upload File</>}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {files.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No files uploaded yet</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {files.map((file) => (
                      <Card key={file.file_id} className="p-4" data-testid="file-card">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">{file.filename}</div>
                            <div className="text-xs text-muted-foreground">
                              {(file.file_size / 1024).toFixed(2)} KB • {new Date(file.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(`${API}/files/${file.file_id}/download`, '_blank')}
                              data-testid="download-file-btn"
                            >
                              Download
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteFile(file.file_id)}
                              data-testid="delete-file-btn"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card className="glass-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading">Collaborative Documents</CardTitle>
                  <Button size="sm" className="bg-accent hover:bg-accent/90" onClick={() => setShowNewDocument(true)} data-testid="add-document-btn">
                    <Plus className="h-4 w-4 mr-1" /> New Document
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {documents.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No documents yet</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {documents.map((doc) => (
                      <Card
                        key={doc.document_id}
                        className="p-4 cursor-pointer hover:shadow-lg transition-shadow"
                        onClick={() => setSelectedDocument(doc)}
                        data-testid="document-card"
                      >
                        <div className="font-semibold mb-2">{doc.title}</div>
                        <div className="text-xs text-muted-foreground">
                          Updated {new Date(doc.updated_at).toLocaleDateString()}
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialogs */}
        <Dialog open={showNewSession} onOpenChange={setShowNewSession}>
          <DialogContent data-testid="new-session-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Schedule New Session</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateSession} className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input value={newSession.title} onChange={(e) => setNewSession({...newSession, title: e.target.value})} required />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={newSession.description} onChange={(e) => setNewSession({...newSession, description: e.target.value})} rows={3} />
              </div>
              <div>
                <Label>Date & Time</Label>
                <Input type="datetime-local" value={newSession.scheduled_at} onChange={(e) => setNewSession({...newSession, scheduled_at: e.target.value})} required />
              </div>
              <div>
                <Label>Duration (minutes)</Label>
                <Input type="number" value={newSession.duration_minutes} onChange={(e) => setNewSession({...newSession, duration_minutes: parseInt(e.target.value)})} />
              </div>
              <Button type="submit" className="w-full bg-primary hover:bg-primary/90">Create Session</Button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={showNewGoal} onOpenChange={setShowNewGoal}>
          <DialogContent data-testid="new-goal-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Create New Goal</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateGoal} className="space-y-4">
              <div>
                <Label>Goal Title</Label>
                <Input value={newGoal.title} onChange={(e) => setNewGoal({...newGoal, title: e.target.value})} required />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={newGoal.description} onChange={(e) => setNewGoal({...newGoal, description: e.target.value})} rows={3} />
              </div>
              <div>
                <Label>Target Date (Optional)</Label>
                <Input type="date" value={newGoal.target_date} onChange={(e) => setNewGoal({...newGoal, target_date: e.target.value})} />
              </div>
              <Button type="submit" className="w-full bg-accent hover:bg-accent/90">Create Goal</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Template Goals Dialog */}
        <Dialog open={showTemplateGoals} onOpenChange={setShowTemplateGoals}>
          <DialogContent className="max-w-2xl" data-testid="template-goals-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Choose a Goal Template</DialogTitle>
              <DialogDescription>Select a pre-built template to add structured goals to this mentorship</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {goalTemplates.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No templates available. Ask an admin to create templates.</p>
              ) : (
                goalTemplates.map((template) => (
                  <Card 
                    key={template.template_id} 
                    className={`cursor-pointer transition-all hover:border-primary ${selectedTemplate?.template_id === template.template_id ? 'border-primary border-2 bg-primary/5' : ''}`}
                    onClick={() => setSelectedTemplate(template)}
                    data-testid={`template-${template.template_id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{template.name}</CardTitle>
                        <Badge variant="outline">{template.duration_weeks} weeks</Badge>
                      </div>
                      <CardDescription>{template.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-muted-foreground">Goals ({template.goals?.length || 0}):</p>
                        <div className="flex flex-wrap gap-2">
                          {template.goals?.slice(0, 5).map((goal, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {idx + 1}. {goal.length > 30 ? goal.substring(0, 30) + '...' : goal}
                            </Badge>
                          ))}
                          {template.goals?.length > 5 && (
                            <Badge variant="outline" className="text-xs">+{template.goals.length - 5} more</Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => { setShowTemplateGoals(false); setSelectedTemplate(null); }}>
                Cancel
              </Button>
              <Button 
                onClick={() => handleApplyTemplate(selectedTemplate)} 
                disabled={!selectedTemplate || applyingTemplate}
                data-testid="apply-template-btn"
              >
                {applyingTemplate ? 'Applying...' : `Apply ${selectedTemplate?.goals?.length || 0} Goals`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showNewTask} onOpenChange={setShowNewTask}>
          <DialogContent data-testid="new-task-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Create New Task</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTask} className="space-y-4">
              <div>
                <Label>Task Title</Label>
                <Input value={newTask.title} onChange={(e) => setNewTask({...newTask, title: e.target.value})} required data-testid="new-task-title" />
              </div>
              <div>
                <Label>Task Type</Label>
                <Select value={newTask.task_type} onValueChange={(value) => setNewTask({...newTask, task_type: value})} data-testid="new-task-type-select">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard Task</SelectItem>
                    <SelectItem value="budget">Budget Tracking</SelectItem>
                    <SelectItem value="resume_review">Resume Review (AI)</SelectItem>
                    <SelectItem value="leadership_quiz">Leadership Assessment</SelectItem>
                    <SelectItem value="business_plan">Business Plan (AI)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={newTask.description} onChange={(e) => setNewTask({...newTask, description: e.target.value})} rows={3} data-testid="new-task-description" />
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={newTask.priority} onValueChange={(value) => setNewTask({...newTask, priority: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Due Date (Optional)</Label>
                <Input type="date" value={newTask.due_date} onChange={(e) => setNewTask({...newTask, due_date: e.target.value})} />
              </div>
              <Button type="submit" className="w-full bg-primary hover:bg-primary/90" data-testid="create-task-submit-btn">Create Task</Button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={showNewDocument} onOpenChange={setShowNewDocument}>
          <DialogContent data-testid="new-document-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Create New Document</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateDocument} className="space-y-4">
              <div>
                <Label>Document Title</Label>
                <Input value={newDocumentData.title} onChange={(e) => setNewDocumentData({...newDocumentData, title: e.target.value})} required />
              </div>
              <div>
                <Label>Content</Label>
                <Textarea value={newDocumentData.content} onChange={(e) => setNewDocumentData({...newDocumentData, content: e.target.value})} rows={8} placeholder="Start writing..." />
              </div>
              <Button type="submit" className="w-full bg-accent hover:bg-accent/90">Create Document</Button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={selectedDocument !== null} onOpenChange={() => setSelectedDocument(null)}>
          <DialogContent className="max-w-4xl" data-testid="document-editor-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">{selectedDocument?.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Textarea
                value={selectedDocument?.content || ''}
                onChange={(e) => setSelectedDocument({...selectedDocument, content: e.target.value})}
                rows={15}
                className="font-mono"
              />
              <Button
                onClick={() => {
                  handleUpdateDocument(selectedDocument.document_id, selectedDocument.content);
                  setSelectedDocument(null);
                }}
                className="w-full bg-primary hover:bg-primary/90"
              >
                Save Document
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

// ==================== PERSONAL BUDGET ====================

function PersonalBudgetV2() {
  const [showShare, setShowShare] = useState(false);
  return (
    <>
      <BudgetTracker onShare={() => setShowShare(true)} />
      <ShareDialog
        open={showShare}
        onOpenChange={setShowShare}
        resourceType="budget"
        resourceId="personal"
        resourceName="Personal Budget"
      />
    </>
  );
}

function PersonalBudget() {
  const navigate = useNavigate();
  const [budgetItems, setBudgetItems] = useState([]);
  const [summary, setSummary] = useState({ total_income: 0, total_expenses: 0, total_savings: 0, balance: 0 });
  const [loading, setLoading] = useState(true);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({
    category: 'income',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    frequency: 'one-time'
  });

  useEffect(() => {
    loadBudgetData();
  }, []);

  const loadBudgetData = async () => {
    try {
      const [itemsRes, summaryRes] = await Promise.all([
        axiosInstance.get('/personal-budget'),
        axiosInstance.get('/personal-budget/summary')
      ]);
      setBudgetItems(itemsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error loading budget:', error);
      toast.error('Failed to load budget data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.description || !newItem.amount) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await axiosInstance.post('/personal-budget', {
        ...newItem,
        amount: parseFloat(newItem.amount)
      });
      toast.success('Budget item added');
      setShowAddItem(false);
      setNewItem({ category: 'income', description: '', amount: '', date: new Date().toISOString().split('T')[0], frequency: 'one-time' });
      loadBudgetData();
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const handleUpdateItem = async () => {
    if (!editingItem) return;
    try {
      await axiosInstance.put(`/personal-budget/${editingItem.budget_id}`, {
        category: editingItem.category,
        description: editingItem.description,
        amount: parseFloat(editingItem.amount),
        date: editingItem.date,
        frequency: editingItem.frequency
      });
      toast.success('Budget item updated');
      setEditingItem(null);
      loadBudgetData();
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const handleDeleteItem = async (budgetId) => {
    if (!confirm('Delete this budget item?')) return;
    try {
      await axiosInstance.delete(`/personal-budget/${budgetId}`);
      toast.success('Item deleted');
      loadBudgetData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const incomeItems = budgetItems.filter(i => i.category === 'income');
  const expenseItems = budgetItems.filter(i => i.category === 'expense');
  const savingsItems = budgetItems.filter(i => i.category === 'savings');

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div onClick={() => navigate('/')} className="cursor-pointer">
              <SiteLogo className="h-10 max-w-[200px]" />
            </div>
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>← Back to Dashboard</Button>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-4 max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-4xl font-bold">Personal Budget</h1>
            <p className="text-muted-foreground">Track your income, expenses, and savings</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowShare(true)} data-testid="share-budget-btn">
              <Users className="h-4 w-4 mr-2" /> Share
            </Button>
            <Button onClick={() => setShowAddItem(true)} data-testid="add-budget-item-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Item
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="glass-card bg-gradient-to-br from-green-500/10 to-green-500/5">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Income</p>
                  <p className="text-2xl font-bold text-green-600">${summary.total_income.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card bg-gradient-to-br from-red-500/10 to-red-500/5">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                  <TrendingDown className="h-5 w-5 text-red-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Expenses</p>
                  <p className="text-2xl font-bold text-red-600">${summary.total_expenses.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card bg-gradient-to-br from-blue-500/10 to-blue-500/5">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <PiggyBank className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Savings</p>
                  <p className="text-2xl font-bold text-blue-600">${summary.total_savings.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card bg-gradient-to-br from-purple-500/10 to-purple-500/5">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Balance</p>
                  <p className={`text-2xl font-bold ${summary.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ${summary.balance.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Budget Items */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Income */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-600">
                <TrendingUp className="h-5 w-5" /> Income
              </CardTitle>
            </CardHeader>
            <CardContent>
              {incomeItems.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No income items yet</p>
              ) : (
                <div className="space-y-3">
                  {incomeItems.map(item => (
                    <div key={item.budget_id} className="flex items-center justify-between p-3 rounded-lg bg-green-500/5 border border-green-500/20">
                      <div>
                        <p className="font-medium">{item.description}</p>
                        <p className="text-xs text-muted-foreground">{item.date} • {item.frequency}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-green-600">+${item.amount.toLocaleString()}</span>
                        <Button variant="ghost" size="sm" onClick={() => setEditingItem(item)}>
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteItem(item.budget_id)}>
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Expenses */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <TrendingDown className="h-5 w-5" /> Expenses
              </CardTitle>
            </CardHeader>
            <CardContent>
              {expenseItems.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No expense items yet</p>
              ) : (
                <div className="space-y-3">
                  {expenseItems.map(item => (
                    <div key={item.budget_id} className="flex items-center justify-between p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                      <div>
                        <p className="font-medium">{item.description}</p>
                        <p className="text-xs text-muted-foreground">{item.date} • {item.frequency}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-red-600">-${item.amount.toLocaleString()}</span>
                        <Button variant="ghost" size="sm" onClick={() => setEditingItem(item)}>
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteItem(item.budget_id)}>
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Savings */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-600">
                <PiggyBank className="h-5 w-5" /> Savings
              </CardTitle>
            </CardHeader>
            <CardContent>
              {savingsItems.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No savings items yet</p>
              ) : (
                <div className="space-y-3">
                  {savingsItems.map(item => (
                    <div key={item.budget_id} className="flex items-center justify-between p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                      <div>
                        <p className="font-medium">{item.description}</p>
                        <p className="text-xs text-muted-foreground">{item.date} • {item.frequency}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-blue-600">${item.amount.toLocaleString()}</span>
                        <Button variant="ghost" size="sm" onClick={() => setEditingItem(item)}>
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteItem(item.budget_id)}>
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Add Item Dialog */}
        <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Budget Item</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Category</Label>
                <Select value={newItem.category} onValueChange={(v) => setNewItem({...newItem, category: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="savings">Savings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Input 
                  value={newItem.description}
                  onChange={(e) => setNewItem({...newItem, description: e.target.value})}
                  placeholder="e.g., Salary, Rent, Emergency Fund"
                />
              </div>
              <div>
                <Label>Amount ($)</Label>
                <Input 
                  type="number"
                  value={newItem.amount}
                  onChange={(e) => setNewItem({...newItem, amount: e.target.value})}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input 
                  type="date"
                  value={newItem.date}
                  onChange={(e) => setNewItem({...newItem, date: e.target.value})}
                />
              </div>
              <div>
                <Label>Frequency</Label>
                <Select value={newItem.frequency} onValueChange={(v) => setNewItem({...newItem, frequency: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one-time">One-time</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddItem(false)}>Cancel</Button>
              <Button onClick={handleAddItem}>Add Item</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Item Dialog */}
        <Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Budget Item</DialogTitle>
            </DialogHeader>
            {editingItem && (
              <div className="space-y-4">
                <div>
                  <Label>Category</Label>
                  <Select value={editingItem.category} onValueChange={(v) => setEditingItem({...editingItem, category: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="income">Income</SelectItem>
                      <SelectItem value="expense">Expense</SelectItem>
                      <SelectItem value="savings">Savings</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Description</Label>
                  <Input 
                    value={editingItem.description}
                    onChange={(e) => setEditingItem({...editingItem, description: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Amount ($)</Label>
                  <Input 
                    type="number"
                    value={editingItem.amount}
                    onChange={(e) => setEditingItem({...editingItem, amount: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Date</Label>
                  <Input 
                    type="date"
                    value={editingItem.date}
                    onChange={(e) => setEditingItem({...editingItem, date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Frequency</Label>
                  <Select value={editingItem.frequency} onValueChange={(v) => setEditingItem({...editingItem, frequency: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="one-time">One-time</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingItem(null)}>Cancel</Button>
              <Button onClick={handleUpdateItem}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Share Dialog */}
        <ShareDialog
          open={showShare}
          onOpenChange={setShowShare}
          resourceType="budget"
          resourceId="personal"
          resourceName="Personal Budget"
        />
      </div>
    </div>
  );
}

// ==================== BUDGET PAGE ====================

function BudgetPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [budgetItems, setBudgetItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mentorship, setMentorship] = useState(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [moduleAccess, setModuleAccess] = useState({ budget_tracker: true });
  const [newItem, setNewItem] = useState({
    category: 'income',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    // Check module access first
    axiosInstance.get('/users/modules')
      .then(res => {
        setModuleAccess(res.data.modules || {});
        if (res.data.modules?.budget_tracker !== false) {
          loadData();
        } else {
          setLoading(false);
        }
      })
      .catch(() => {
        loadData(); // Default to loading if check fails
      });
  }, [id]);

  const loadData = async () => {
    try {
      const [budgetRes, mentorshipRes] = await Promise.all([
        axiosInstance.get(`/budget/${id}`),
        axiosInstance.get(`/mentorships/${id}`)
      ]);
      // Handle both array response and object with items
      const items = Array.isArray(budgetRes.data) ? budgetRes.data : (budgetRes.data.items || []);
      setBudgetItems(items);
      setMentorship(mentorshipRes.data);
    } catch (error) {
      console.error('Error loading budget data:', error);
      toast.error('Failed to load budget data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const response = await axiosInstance.get(`/my-exports/my_budget?format=${format}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `budget_report_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Budget exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(`Failed to export budget as ${format.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.description || !newItem.amount) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await axiosInstance.post('/budget', {
        mentorship_id: id,
        category: newItem.category,
        description: newItem.description,
        amount: parseFloat(newItem.amount),
        date: newItem.date
      });
      toast.success('Budget item added');
      setShowAddItem(false);
      setNewItem({ category: 'income', description: '', amount: '', date: new Date().toISOString().split('T')[0] });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add item');
    }
  };

  const handleDeleteItem = async (budgetId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await axiosInstance.delete(`/budget/${budgetId}`);
      toast.success('Item deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const incomeItems = budgetItems.filter(i => i.category === 'income');
  const expenseItems = budgetItems.filter(i => i.category === 'expense');
  const savingsItems = budgetItems.filter(i => i.category === 'savings');

  const totalIncome = incomeItems.reduce((sum, i) => sum + i.amount, 0);
  const totalExpenses = expenseItems.reduce((sum, i) => sum + i.amount, 0);
  const totalSavings = savingsItems.reduce((sum, i) => sum + i.amount, 0);
  const netBalance = totalIncome - totalExpenses - totalSavings;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show access denied if budget_tracker is disabled
  if (moduleAccess.budget_tracker === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center">
        <Card className="glass-card text-center py-12 max-w-md mx-4">
          <CardContent>
            <Lock className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">Budget Tracker Unavailable</h3>
            <p className="text-muted-foreground mb-4">This feature is currently not available for your account. Contact an administrator for access.</p>
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(`/mentorship/${id}`)}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="font-heading text-xl font-bold">Budget Tracker</h1>
                <p className="text-sm text-muted-foreground">
                  {mentorship?.mentee_name || 'Mentorship'} - Financial Planning
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleExport('excel')}
                disabled={exporting === 'excel'}
                data-testid="export-budget-excel-btn"
              >
                {exporting === 'excel' ? (
                  <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full mr-2" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Excel
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleExport('pdf')}
                disabled={exporting === 'pdf'}
                data-testid="export-budget-pdf-btn"
              >
                {exporting === 'pdf' ? (
                  <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full mr-2" />
                ) : (
                  <FileText className="h-4 w-4 mr-2" />
                )}
                PDF
              </Button>
              <Button onClick={() => setShowAddItem(true)} data-testid="add-budget-item-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-green-50 border-green-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-green-700">Total Income</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">${totalIncome.toFixed(2)}</div>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-red-700">Total Expenses</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">${totalExpenses.toFixed(2)}</div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-blue-700">Total Savings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">${totalSavings.toFixed(2)}</div>
            </CardContent>
          </Card>
          <Card className={`${netBalance >= 0 ? 'bg-primary/10 border-primary/30' : 'bg-amber-50 border-amber-200'}`}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm font-medium ${netBalance >= 0 ? 'text-primary' : 'text-amber-700'}`}>Net Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${netBalance >= 0 ? 'text-primary' : 'text-amber-600'}`}>
                ${netBalance.toFixed(2)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Budget Items by Category */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Income Column */}
          <Card>
            <CardHeader>
              <CardTitle className="text-green-600 flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Income
              </CardTitle>
            </CardHeader>
            <CardContent>
              {incomeItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">No income items</p>
              ) : (
                <div className="space-y-2">
                  {incomeItems.map(item => (
                    <div key={item.budget_id} className="flex items-center justify-between p-2 rounded bg-green-50">
                      <div>
                        <div className="font-medium text-sm">{item.description}</div>
                        <div className="text-xs text-muted-foreground">{item.date}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-green-600">${item.amount.toFixed(2)}</span>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteItem(item.budget_id)}>
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Expenses Column */}
          <Card>
            <CardHeader>
              <CardTitle className="text-red-600 flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Expenses
              </CardTitle>
            </CardHeader>
            <CardContent>
              {expenseItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">No expense items</p>
              ) : (
                <div className="space-y-2">
                  {expenseItems.map(item => (
                    <div key={item.budget_id} className="flex items-center justify-between p-2 rounded bg-red-50">
                      <div>
                        <div className="font-medium text-sm">{item.description}</div>
                        <div className="text-xs text-muted-foreground">{item.date}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-red-600">${item.amount.toFixed(2)}</span>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteItem(item.budget_id)}>
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Savings Column */}
          <Card>
            <CardHeader>
              <CardTitle className="text-blue-600 flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Savings
              </CardTitle>
            </CardHeader>
            <CardContent>
              {savingsItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">No savings items</p>
              ) : (
                <div className="space-y-2">
                  {savingsItems.map(item => (
                    <div key={item.budget_id} className="flex items-center justify-between p-2 rounded bg-blue-50">
                      <div>
                        <div className="font-medium text-sm">{item.description}</div>
                        <div className="text-xs text-muted-foreground">{item.date}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-blue-600">${item.amount.toFixed(2)}</span>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteItem(item.budget_id)}>
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Item Dialog */}
      <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Budget Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Category</Label>
              <Select value={newItem.category} onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                  <SelectItem value="savings">Savings</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="e.g., Salary, Rent, Emergency Fund"
              />
            </div>
            <div>
              <Label>Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={newItem.amount}
                onChange={(e) => setNewItem({ ...newItem, amount: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={newItem.date}
                onChange={(e) => setNewItem({ ...newItem, date: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItem(false)}>Cancel</Button>
            <Button onClick={handleAddItem}>Add Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Profile({ user: initialUser }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(initialUser);
  const [editing, setEditing] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState(null);
  const [savingLanguage, setSavingLanguage] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);
  const [reminderPrefs, setReminderPrefs] = useState({
    goal_reminders: true,
    session_reminders: true,
    weekly_summary: true,
    email_notifications: true
  });
  const [savingReminders, setSavingReminders] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    skills: '',
    expertise_areas: ''
  });

  // Preset avatar icons
  const presetAvatars = [
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Felix&backgroundColor=b6e3f4',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Aneka&backgroundColor=c0aede',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Bailey&backgroundColor=d1d4f9',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Max&backgroundColor=ffd5dc',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Sam&backgroundColor=ffdfbf',
    'https://api.dicebear.com/7.x/lorelei/svg?seed=Felix&backgroundColor=b6e3f4',
    'https://api.dicebear.com/7.x/lorelei/svg?seed=Aneka&backgroundColor=c0aede',
    'https://api.dicebear.com/7.x/lorelei/svg?seed=Bailey&backgroundColor=d1d4f9',
    'https://api.dicebear.com/7.x/bottts/svg?seed=Felix&backgroundColor=b6e3f4',
    'https://api.dicebear.com/7.x/bottts/svg?seed=Aneka&backgroundColor=c0aede',
    'https://api.dicebear.com/7.x/bottts/svg?seed=Bailey&backgroundColor=d1d4f9',
    'https://api.dicebear.com/7.x/bottts/svg?seed=Max&backgroundColor=ffd5dc',
    'https://api.dicebear.com/7.x/fun-emoji/svg?seed=Felix&backgroundColor=b6e3f4',
    'https://api.dicebear.com/7.x/fun-emoji/svg?seed=Aneka&backgroundColor=c0aede',
    'https://api.dicebear.com/7.x/fun-emoji/svg?seed=Bailey&backgroundColor=d1d4f9',
    'https://api.dicebear.com/7.x/fun-emoji/svg?seed=Max&backgroundColor=ffd5dc',
  ];

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        bio: user.bio || '',
        skills: user.skills?.join(', ') || '',
        expertise_areas: user.expertise_areas?.join(', ') || ''
      });
    }
    loadLanguages();
    loadReminderPreferences();
  }, [user]);

  const loadLanguages = async () => {
    try {
      const [langRes, currentRes] = await Promise.all([
        axiosInstance.get('/languages'),
        axiosInstance.get('/users/language')
      ]);
      setLanguages(langRes.data);
      setCurrentLanguage(currentRes.data.current_language);
    } catch (error) {
      console.error('Failed to load languages');
    }
  };

  const loadReminderPreferences = async () => {
    try {
      const res = await axiosInstance.get('/user/reminder-preferences');
      setReminderPrefs(res.data);
    } catch (error) {
      console.error('Failed to load reminder preferences');
    }
  };

  const handleReminderPrefChange = async (key, value) => {
    const newPrefs = { ...reminderPrefs, [key]: value };
    setReminderPrefs(newPrefs);
    
    setSavingReminders(true);
    try {
      await axiosInstance.put('/user/reminder-preferences', newPrefs);
      toast.success('Notification preferences updated');
    } catch (error) {
      toast.error('Failed to update preferences');
      // Revert on error
      setReminderPrefs(reminderPrefs);
    } finally {
      setSavingReminders(false);
    }
  };

  const handleLanguageChange = async (langCode) => {
    setSavingLanguage(true);
    try {
      await axiosInstance.put(`/users/language?language_code=${langCode}`);
      const langInfo = languages.find(l => l.code === langCode);
      setCurrentLanguage(langInfo);
      toast.success(`Language changed to ${langInfo?.name || langCode}`);
    } catch (error) {
      toast.error('Failed to update language preference');
    } finally {
      setSavingLanguage(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axiosInstance.post('/users/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUser({ ...user, picture: response.data.picture });
      setShowAvatarPicker(false);
      toast.success('Avatar updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSelectPresetAvatar = async (iconUrl) => {
    setUploadingAvatar(true);
    try {
      const response = await axiosInstance.put('/users/avatar/icon', { icon_url: iconUrl });
      setUser({ ...user, picture: response.data.picture });
      setShowAvatarPicker(false);
      toast.success('Avatar updated!');
    } catch (error) {
      toast.error('Failed to update avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        name: formData.name,
        bio: formData.bio,
        skills: formData.skills.split(',').map((s) => s.trim()).filter(Boolean),
        expertise_areas: formData.expertise_areas.split(',').map((s) => s.trim()).filter(Boolean),
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
      };
      const response = await axiosInstance.put('/users/profile', updateData);
      setUser(response.data);
      setEditing(false);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Admin Link for admins/pastors */}
        {(user.role === 'admin' || user.role === 'pastor') && (
          <div className="mb-4">
            <Button 
              variant="outline" 
              onClick={() => navigate('/admin')}
              className="gap-2"
              data-testid="admin-dashboard-link"
            >
              <Settings className="h-4 w-4" />
              Go to Admin Dashboard
            </Button>
          </div>
        )}
        
        <Card className="glass-card" data-testid="profile-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="relative group">
                  <Avatar className="h-20 w-20 cursor-pointer" onClick={() => setShowAvatarPicker(true)}>
                    <AvatarImage src={user.picture?.startsWith('/') ? BACKEND_URL + user.picture : user.picture} />
                    <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div 
                    className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    onClick={() => setShowAvatarPicker(true)}
                  >
                    <Edit className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <CardTitle className="font-heading text-2xl">{user.name}</CardTitle>
                  <CardDescription>{user.email}</CardDescription>
                  <Badge className="mt-2 bg-primary">{user.role}</Badge>
                </div>
              </div>
              <Button onClick={() => setEditing(!editing)} variant={editing ? 'outline' : 'default'} data-testid="edit-profile-btn">
                {editing ? 'Cancel' : 'Edit Profile'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {editing ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="profile-name-input" />
                </div>
                <div>
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea id="bio" value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} rows={4} data-testid="profile-bio-input" />
                </div>
                <div>
                  <Label htmlFor="skills">Skills (comma-separated)</Label>
                  <Input
                    id="skills"
                    value={formData.skills}
                    onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                    data-testid="profile-skills-input"
                  />
                </div>
                {user.role === 'mentor' && (
                  <>
                    <div>
                      <Label htmlFor="expertise">Expertise Areas (comma-separated)</Label>
                      <Input
                        id="expertise"
                        value={formData.expertise_areas}
                        onChange={(e) => setFormData({ ...formData, expertise_areas: e.target.value })}
                        data-testid="profile-expertise-input"
                      />
                    </div>
                  </>
                )}
                <Button type="submit" className="w-full bg-accent hover:bg-accent/90" data-testid="save-profile-btn">
                  Save Changes
                </Button>
              </form>
            ) : (
              <div className="space-y-6">
                <div>
                  <div className="text-sm font-semibold text-muted-foreground mb-2">Bio</div>
                  <p>{user.bio || 'No bio added yet'}</p>
                </div>
                {user.skills && user.skills.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-muted-foreground mb-2">Skills</div>
                    <div className="flex flex-wrap gap-2">
                      {user.skills.map((skill, idx) => (
                        <Badge key={idx} variant="secondary">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {user.role === 'mentor' && user.expertise_areas && user.expertise_areas.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-muted-foreground mb-2">Expertise Areas</div>
                    <div className="flex flex-wrap gap-2">
                      {user.expertise_areas.map((area, idx) => (
                        <Badge key={idx} variant="secondary">
                          {area}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Language Preference Card */}
        <Card className="glass-card mt-6" data-testid="language-settings-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <span className="text-2xl">🌐</span> Language Preference
            </CardTitle>
            <CardDescription>Choose your preferred language for the application</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {currentLanguage && (
                <div className="flex items-center gap-2 mb-4 p-3 bg-primary/10 rounded-lg">
                  <span className="font-semibold">Current:</span>
                  <span>{currentLanguage.native} ({currentLanguage.name})</span>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {languages.map((lang) => (
                  <Button
                    key={lang.code}
                    variant={currentLanguage?.code === lang.code ? "default" : "outline"}
                    className={`flex flex-col h-auto py-3 ${currentLanguage?.code === lang.code ? 'bg-primary' : ''}`}
                    onClick={() => handleLanguageChange(lang.code)}
                    disabled={savingLanguage}
                    data-testid={`lang-${lang.code}-btn`}
                  >
                    <span className="font-semibold">{lang.native}</span>
                    <span className="text-xs opacity-70">{lang.name}</span>
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notification & Reminder Preferences */}
        <Card className="glass-card" data-testid="reminder-preferences-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" /> Notification Preferences
            </CardTitle>
            <CardDescription>Manage your email notifications and reminders</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <p className="font-medium">Email Notifications</p>
                  <p className="text-sm text-muted-foreground">Receive notifications via email</p>
                </div>
                <Button
                  variant={reminderPrefs.email_notifications ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleReminderPrefChange('email_notifications', !reminderPrefs.email_notifications)}
                  disabled={savingReminders}
                  data-testid="toggle-email-notifications"
                >
                  {reminderPrefs.email_notifications ? 'On' : 'Off'}
                </Button>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <p className="font-medium">Goal Reminders</p>
                  <p className="text-sm text-muted-foreground">Get reminded about goals due soon or overdue</p>
                </div>
                <Button
                  variant={reminderPrefs.goal_reminders ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleReminderPrefChange('goal_reminders', !reminderPrefs.goal_reminders)}
                  disabled={savingReminders}
                  data-testid="toggle-goal-reminders"
                >
                  {reminderPrefs.goal_reminders ? 'On' : 'Off'}
                </Button>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <p className="font-medium">Session Reminders</p>
                  <p className="text-sm text-muted-foreground">Get reminded about upcoming mentorship sessions</p>
                </div>
                <Button
                  variant={reminderPrefs.session_reminders ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleReminderPrefChange('session_reminders', !reminderPrefs.session_reminders)}
                  disabled={savingReminders}
                  data-testid="toggle-session-reminders"
                >
                  {reminderPrefs.session_reminders ? 'On' : 'Off'}
                </Button>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <p className="font-medium">Weekly Progress Summary</p>
                  <p className="text-sm text-muted-foreground">Receive a weekly email with your progress summary</p>
                </div>
                <Button
                  variant={reminderPrefs.weekly_summary ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleReminderPrefChange('weekly_summary', !reminderPrefs.weekly_summary)}
                  disabled={savingReminders}
                  data-testid="toggle-weekly-summary"
                >
                  {reminderPrefs.weekly_summary ? 'On' : 'Off'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Avatar Picker Dialog */}
      <Dialog open={showAvatarPicker} onOpenChange={setShowAvatarPicker}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Change Your Avatar</DialogTitle>
            <DialogDescription>Upload a photo or choose from our preset avatars</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Upload Custom Photo */}
            <div>
              <Label className="mb-2 block">Upload Your Photo</Label>
              <div className="flex gap-2">
                <input
                  type="file"
                  ref={avatarInputRef}
                  onChange={handleAvatarUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button 
                  variant="outline" 
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  className="w-full gap-2"
                  data-testid="upload-avatar-btn"
                >
                  <Upload className="h-4 w-4" />
                  {uploadingAvatar ? 'Uploading...' : 'Choose Photo'}
                </Button>
              </div>
            </div>

            {/* Preset Avatars */}
            <div>
              <Label className="mb-2 block">Or Choose an Avatar</Label>
              <div className="grid grid-cols-4 gap-3 max-h-[250px] overflow-y-auto p-1">
                {presetAvatars.map((avatar, idx) => (
                  <div
                    key={idx}
                    className={`cursor-pointer rounded-full p-1 border-2 transition-all hover:border-primary ${user.picture === avatar ? 'border-primary ring-2 ring-primary/50' : 'border-transparent'}`}
                    onClick={() => handleSelectPresetAvatar(avatar)}
                    data-testid={`preset-avatar-${idx}`}
                  >
                    <Avatar className="h-14 w-14">
                      <AvatarImage src={avatar} />
                      <AvatarFallback>?</AvatarFallback>
                    </Avatar>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Subscription({ user }) {
  const [loading, setLoading] = useState(false);

  const handleCheckout = async (packageId) => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axiosInstance.post('/payments/checkout', {
        package_id: packageId,
        origin_url: originUrl,
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to create checkout session');
      setLoading(false);
    }
  };

  const packages = [
    { id: 'basic', name: 'Basic', price: 49.99, credits: 4, description: '1 month of mentorship access' },
    { id: 'pro', name: 'Pro', price: 99.99, credits: 12, description: '3 months of mentorship access', popular: true },
    { id: 'premium', name: 'Premium', price: 199.99, credits: 24, description: '6 months of mentorship access' },
  ];

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl font-bold mb-2" data-testid="subscription-heading">Choose Your Plan</h1>
          <p className="text-muted-foreground">Select the perfect plan for your mentorship journey</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {packages.map((pkg) => (
            <Card
              key={pkg.id}
              className={`glass-card relative hover:shadow-xl transition-shadow ${
                pkg.popular ? 'ring-2 ring-accent' : ''
              }`}
              data-testid={`package-${pkg.id}-card`}
            >
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <Badge className="bg-accent text-white">Most Popular</Badge>
                </div>
              )}
              <CardHeader>
                <CardTitle className="font-heading text-2xl">{pkg.name}</CardTitle>
                <CardDescription>{pkg.description}</CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold font-heading">${pkg.price}</span>
                  <span className="text-muted-foreground">/package</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    <span>{pkg.credits} mentorship sessions</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    <span>Access to all mentors</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    <span>Goal tracking & progress reports</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    <span>Unlimited messaging</span>
                  </li>
                </ul>
                <Button
                  className={`w-full ${
                    pkg.popular ? 'bg-accent hover:bg-accent/90' : 'bg-primary hover:bg-primary/90'
                  }`}
                  onClick={() => handleCheckout(pkg.id)}
                  disabled={loading}
                  data-testid={`checkout-${pkg.id}-btn`}
                >
                  {loading ? 'Loading...' : 'Get Started'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

function PaymentSuccess({ user }) {
  const [checking, setChecking] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');

    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [location]);

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    try {
      const response = await axiosInstance.get(`/payments/status/${sessionId}`);
      const status = response.data;

      if (status.payment_status === 'paid') {
        setPaymentStatus('success');
        setChecking(false);
        toast.success('Payment successful!');
      } else if (status.status === 'expired') {
        setPaymentStatus('failed');
        setChecking(false);
        toast.error('Payment session expired');
      } else if (attempts < maxAttempts) {
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setPaymentStatus('pending');
        setChecking(false);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="glass-card max-w-md w-full" data-testid="payment-success-card">
        <CardHeader className="text-center">
          {checking ? (
            <>
              <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
              <CardTitle className="font-heading text-2xl">Processing Payment...</CardTitle>
              <CardDescription>Please wait while we confirm your payment</CardDescription>
            </>
          ) : paymentStatus === 'success' ? (
            <>
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="font-heading text-2xl text-primary">Payment Successful!</CardTitle>
              <CardDescription>Your subscription is now active</CardDescription>
            </>
          ) : (
            <>
              <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
                <X className="h-8 w-8 text-destructive" />
              </div>
              <CardTitle className="font-heading text-2xl text-destructive">Payment Failed</CardTitle>
              <CardDescription>There was an issue processing your payment</CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {!checking && (
            <div className="flex gap-2">
              <Button className="flex-1 bg-primary hover:bg-primary/90" onClick={() => (window.location.href = '/dashboard')} data-testid="go-dashboard-btn">
                Go to Dashboard
              </Button>
              {paymentStatus !== 'success' && (
                <Button variant="outline" className="flex-1" onClick={() => (window.location.href = '/subscription')} data-testid="try-again-btn">
                  Try Again
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== DONATION SUCCESS PAGE ====================

function DonationSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
      <Card className="glass-card max-w-md w-full text-center" data-testid="donation-success-card">
        <CardContent className="pt-8 pb-6">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-green-800 mb-3">Thank You!</h1>
          <p className="text-muted-foreground mb-6">
            Your generous donation has been received. May God bless you abundantly for your cheerful giving.
          </p>
          <div className="bg-green-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-green-700">
              A confirmation email will be sent to your registered email address with your donation receipt.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Button onClick={() => navigate('/dashboard')} className="bg-green-600 hover:bg-green-700">
              <Home className="h-4 w-4 mr-2" />
              Return to Dashboard
            </Button>
            <Button variant="outline" onClick={() => navigate('/giving')}>
              <Heart className="h-4 w-4 mr-2" />
              Make Another Donation
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== MINISTRY GROUPS PAGE ====================

function MinistryGroupsPage() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showMembersDialog, setShowMembersDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [newGroup, setNewGroup] = useState({ name: '', description: '', group_type: 'ministry' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [groupsRes, userRes, usersRes] = await Promise.all([
        axiosInstance.get('/ministry-groups'),
        axiosInstance.get('/auth/me'),
        axiosInstance.get('/admin/users').catch(() => ({ data: [] }))
      ]);
      setGroups(groupsRes.data);
      setUser(userRes.data);
      setAllUsers(usersRes.data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    try {
      await axiosInstance.post('/ministry-groups', newGroup);
      toast.success('Group created!');
      setShowCreateDialog(false);
      setNewGroup({ name: '', description: '', group_type: 'ministry' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create group');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Delete this group?')) return;
    try {
      await axiosInstance.delete(`/ministry-groups/${groupId}`);
      toast.success('Group deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete group');
    }
  };

  const openMembersDialog = async (group) => {
    setSelectedGroup(group);
    try {
      const res = await axiosInstance.get(`/ministry-groups/${group.group_id}/members`);
      setMembers(res.data);
      setShowMembersDialog(true);
    } catch (error) {
      toast.error('Failed to load members');
    }
  };

  const addMemberToGroup = async (userId) => {
    try {
      await axiosInstance.post(`/ministry-groups/${selectedGroup.group_id}/members/${userId}`);
      toast.success('Member added');
      const res = await axiosInstance.get(`/ministry-groups/${selectedGroup.group_id}/members`);
      setMembers(res.data);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const removeMemberFromGroup = async (userId) => {
    try {
      await axiosInstance.delete(`/ministry-groups/${selectedGroup.group_id}/members/${userId}`);
      toast.success('Member removed');
      const res = await axiosInstance.get(`/ministry-groups/${selectedGroup.group_id}/members`);
      setMembers(res.data);
      loadData();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const canManage = user?.role === 'admin' || user?.role === 'pastor';

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}><ChevronLeft className="h-5 w-5" /></Button>
            <div>
              <h1 className="font-heading text-xl font-bold">Ministry Groups</h1>
              <p className="text-sm text-muted-foreground">Manage church ministry groups</p>
            </div>
          </div>
          {canManage && (
            <Button onClick={() => setShowCreateDialog(true)} data-testid="create-group-btn">
              <Plus className="h-4 w-4 mr-2" />Create Group
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {groups.map(group => (
            <Card key={group.group_id} className="glass-card hover:shadow-lg transition-all">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading">{group.name}</CardTitle>
                  <Badge variant="outline">{group.group_type}</Badge>
                </div>
                <CardDescription>{group.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                  <Users className="h-4 w-4" />
                  <span>{group.member_count || 0} members</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openMembersDialog(group)}>
                    <Users className="h-4 w-4 mr-1" />View Members
                  </Button>
                  {canManage && (
                    <Button variant="destructive" size="sm" onClick={() => handleDeleteGroup(group.group_id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {groups.length === 0 && (
          <Card className="glass-card text-center p-12">
            <Users className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="font-heading text-xl mb-2">No Ministry Groups Yet</h2>
            <p className="text-muted-foreground mb-4">Create groups to organize your church members</p>
            {canManage && <Button onClick={() => setShowCreateDialog(true)}>Create First Group</Button>}
          </Card>
        )}
      </main>

      {/* Create Group Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Ministry Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Group Name</Label>
              <Input value={newGroup.name} onChange={(e) => setNewGroup({...newGroup, name: e.target.value})} placeholder="e.g., Youth Ministry" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={newGroup.description} onChange={(e) => setNewGroup({...newGroup, description: e.target.value})} placeholder="Group description..." />
            </div>
            <div>
              <Label>Group Type</Label>
              <Select value={newGroup.group_type} onValueChange={(v) => setNewGroup({...newGroup, group_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ministry">Ministry</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="team">Team</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateGroup}>Create Group</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Members Dialog */}
      <Dialog open={showMembersDialog} onOpenChange={setShowMembersDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedGroup?.name} - Members</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {canManage && (
              <div>
                <Label>Add Member</Label>
                <Select onValueChange={addMemberToGroup}>
                  <SelectTrigger><SelectValue placeholder="Select a member to add" /></SelectTrigger>
                  <SelectContent>
                    {allUsers.filter(u => !members.find(m => m.user_id === u.user_id)).map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {members.map(member => (
                <div key={member.user_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Avatar><AvatarImage src={member.user?.picture} /><AvatarFallback>{member.user?.name?.[0]}</AvatarFallback></Avatar>
                    <div>
                      <p className="font-medium">{member.user?.name}</p>
                      <p className="text-sm text-muted-foreground">{member.user?.email}</p>
                    </div>
                  </div>
                  {canManage && (
                    <Button variant="ghost" size="sm" onClick={() => removeMemberFromGroup(member.user_id)}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              {members.length === 0 && <p className="text-center text-muted-foreground py-4">No members in this group</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== TEAMS PAGE ====================

function TeamsPage() {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showMembersDialog, setShowMembersDialog] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [newTeam, setNewTeam] = useState({ name: '', description: '', team_type: 'staff' });
  const [newMember, setNewMember] = useState({ user_id: '', team_role: 'member', permissions: [] });

  const availablePermissions = [
    'can_manage_content', 'can_respond_tickets', 'can_edit_articles', 
    'can_approve_articles', 'can_view_reports', 'can_manage_events'
  ];

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [teamsRes, userRes, usersRes] = await Promise.all([
        axiosInstance.get('/teams'),
        axiosInstance.get('/auth/me'),
        axiosInstance.get('/admin/users').catch(() => ({ data: [] }))
      ]);
      setTeams(teamsRes.data);
      setUser(userRes.data);
      setAllUsers(usersRes.data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async () => {
    try {
      await axiosInstance.post('/teams', newTeam);
      toast.success('Team created!');
      setShowCreateDialog(false);
      setNewTeam({ name: '', description: '', team_type: 'staff' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team');
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Delete this team?')) return;
    try {
      await axiosInstance.delete(`/teams/${teamId}`);
      toast.success('Team deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete team');
    }
  };

  const openMembersDialog = async (team) => {
    setSelectedTeam(team);
    try {
      const res = await axiosInstance.get(`/teams/${team.team_id}/members`);
      setMembers(res.data);
      setShowMembersDialog(true);
    } catch (error) {
      toast.error('Failed to load members');
    }
  };

  const addTeamMember = async () => {
    if (!newMember.user_id) return;
    try {
      await axiosInstance.post(`/teams/${selectedTeam.team_id}/members?user_id=${newMember.user_id}&team_role=${newMember.team_role}&permissions=${JSON.stringify(newMember.permissions)}`);
      toast.success('Member added');
      const res = await axiosInstance.get(`/teams/${selectedTeam.team_id}/members`);
      setMembers(res.data);
      setNewMember({ user_id: '', team_role: 'member', permissions: [] });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const removeTeamMember = async (userId) => {
    try {
      await axiosInstance.delete(`/teams/${selectedTeam.team_id}/members/${userId}`);
      toast.success('Member removed');
      const res = await axiosInstance.get(`/teams/${selectedTeam.team_id}/members`);
      setMembers(res.data);
      loadData();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const isAdmin = user?.role === 'admin';

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}><ChevronLeft className="h-5 w-5" /></Button>
            <div>
              <h1 className="font-heading text-xl font-bold">Teams</h1>
              <p className="text-sm text-muted-foreground">Manage teams with roles and permissions</p>
            </div>
          </div>
          {isAdmin && (
            <Button onClick={() => setShowCreateDialog(true)} data-testid="create-team-btn">
              <Plus className="h-4 w-4 mr-2" />Create Team
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {teams.map(team => (
            <Card key={team.team_id} className="glass-card hover:shadow-lg transition-all">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading">{team.name}</CardTitle>
                  <Badge variant="outline">{team.team_type}</Badge>
                </div>
                <CardDescription>{team.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                  <Users className="h-4 w-4" /><span>{team.member_count || 0} members</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openMembersDialog(team)}>
                    <Users className="h-4 w-4 mr-1" />Manage
                  </Button>
                  {isAdmin && (
                    <Button variant="destructive" size="sm" onClick={() => handleDeleteTeam(team.team_id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {teams.length === 0 && (
          <Card className="glass-card text-center p-12">
            <Users className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="font-heading text-xl mb-2">No Teams Yet</h2>
            <p className="text-muted-foreground mb-4">Create teams to organize staff with specific roles</p>
            {isAdmin && <Button onClick={() => setShowCreateDialog(true)}>Create First Team</Button>}
          </Card>
        )}
      </main>

      {/* Create Team Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Team</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Team Name</Label><Input value={newTeam.name} onChange={(e) => setNewTeam({...newTeam, name: e.target.value})} placeholder="e.g., Support Team" /></div>
            <div><Label>Description</Label><Textarea value={newTeam.description} onChange={(e) => setNewTeam({...newTeam, description: e.target.value})} /></div>
            <div>
              <Label>Team Type</Label>
              <Select value={newTeam.team_type} onValueChange={(v) => setNewTeam({...newTeam, team_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="ministry">Ministry</SelectItem>
                  <SelectItem value="project">Project</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateTeam}>Create Team</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Members Dialog */}
      <Dialog open={showMembersDialog} onOpenChange={setShowMembersDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{selectedTeam?.name} - Team Members</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {isAdmin && (
              <Card className="p-4">
                <h4 className="font-semibold mb-3">Add Team Member</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Select User</Label>
                    <Select value={newMember.user_id} onValueChange={(v) => setNewMember({...newMember, user_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Select user" /></SelectTrigger>
                      <SelectContent>
                        {allUsers.filter(u => !members.find(m => m.user_id === u.user_id)).map(u => (
                          <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Team Role</Label>
                    <Select value={newMember.team_role} onValueChange={(v) => setNewMember({...newMember, team_role: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="lead">Team Lead</SelectItem>
                        <SelectItem value="coordinator">Coordinator</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-3">
                  <Label>Permissions</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {availablePermissions.map(perm => (
                      <Badge 
                        key={perm} 
                        variant={newMember.permissions.includes(perm) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => {
                          if (newMember.permissions.includes(perm)) {
                            setNewMember({...newMember, permissions: newMember.permissions.filter(p => p !== perm)});
                          } else {
                            setNewMember({...newMember, permissions: [...newMember.permissions, perm]});
                          }
                        }}
                      >
                        {perm.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
                <Button className="mt-3" onClick={addTeamMember} disabled={!newMember.user_id}>Add to Team</Button>
              </Card>
            )}
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {members.map(member => (
                <div key={member.user_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Avatar><AvatarImage src={member.user?.picture} /><AvatarFallback>{member.user?.name?.[0]}</AvatarFallback></Avatar>
                    <div>
                      <p className="font-medium">{member.user?.name}</p>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{member.team_role}</Badge>
                        {member.permissions?.slice(0, 2).map(p => <Badge key={p} variant="outline" className="text-xs">{p.replace(/_/g, ' ')}</Badge>)}
                        {member.permissions?.length > 2 && <span className="text-xs text-muted-foreground">+{member.permissions.length - 2}</span>}
                      </div>
                    </div>
                  </div>
                  {isAdmin && <Button variant="ghost" size="sm" onClick={() => removeTeamMember(member.user_id)}><X className="h-4 w-4" /></Button>}
                </div>
              ))}
              {members.length === 0 && <p className="text-center text-muted-foreground py-4">No team members yet</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== COURSE ASSIGNMENTS PAGE ====================

function CourseAssignmentsPage() {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [mentees, setMentees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [newAssignment, setNewAssignment] = useState({ course_id: '', assigned_to: '', assignment_type: 'individual', due_date: '', notes: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [assignRes, courseRes, groupRes, userRes, usersRes] = await Promise.all([
        axiosInstance.get('/course-assignments'),
        axiosInstance.get('/courses'),
        axiosInstance.get('/ministry-groups'),
        axiosInstance.get('/auth/me'),
        axiosInstance.get('/admin/users').catch(() => ({ data: [] }))
      ]);
      setAssignments(assignRes.data);
      setCourses(courseRes.data);
      setGroups(groupRes.data);
      setUser(userRes.data);
      setUsers(usersRes.data || []);
      
      // If mentor, get their mentees from mentorships
      if (userRes.data.role === 'mentor') {
        try {
          const mentorshipsRes = await axiosInstance.get('/mentorships');
          const activeMentorships = mentorshipsRes.data.filter(m => m.mentor_id === userRes.data.user_id && m.status === 'active');
          const menteeIds = activeMentorships.map(m => m.mentee_id);
          
          // Fetch mentee info from mentorship details
          if (menteeIds.length > 0) {
            const menteePromises = activeMentorships.map(m => 
              axiosInstance.get(`/mentorships/${m.mentorship_id}`).catch(() => null)
            );
            const menteeResponses = await Promise.all(menteePromises);
            const menteeList = menteeResponses
              .filter(r => r && r.data && r.data.mentee)
              .map(r => ({
                user_id: r.data.mentee_id,
                name: r.data.mentee.name,
                email: r.data.mentee.email,
                picture: r.data.mentee.picture
              }));
            setMentees(menteeList);
          } else {
            setMentees([]);
          }
        } catch (error) {
          console.error('Error fetching mentorships:', error);
          setMentees([]);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAssignment = async () => {
    try {
      await axiosInstance.post('/course-assignments', newAssignment);
      toast.success('Course assigned!');
      setShowAssignDialog(false);
      setNewAssignment({ course_id: '', assigned_to: '', assignment_type: 'individual', due_date: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign course');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Remove this assignment?')) return;
    try {
      await axiosInstance.delete(`/course-assignments/${assignmentId}`);
      toast.success('Assignment removed');
      loadData();
    } catch (error) {
      toast.error('Failed to remove assignment');
    }
  };

  const canAssign = ['admin', 'pastor', 'mentor'].includes(user?.role);
  const canAssignToGroups = ['admin', 'pastor'].includes(user?.role);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}><ChevronLeft className="h-5 w-5" /></Button>
            <div>
              <h1 className="font-heading text-xl font-bold">Course Assignments</h1>
              <p className="text-sm text-muted-foreground">Assign courses to members and groups</p>
            </div>
          </div>
          {canAssign && (
            <Button onClick={() => setShowAssignDialog(true)} data-testid="assign-course-btn">
              <Plus className="h-4 w-4 mr-2" />Assign Course
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="space-y-4">
          {assignments.map(assignment => (
            <Card key={assignment.assignment_id} className="glass-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <BookOpen className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{assignment.course?.title || 'Unknown Course'}</h3>
                      <p className="text-sm text-muted-foreground">
                        Assigned to: {assignment.assigned_entity?.name || assignment.assigned_to}
                        {assignment.assignment_type === 'group' && <Badge className="ml-2" variant="outline">Group</Badge>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <Badge variant={assignment.status === 'completed' ? 'default' : 'secondary'}>{assignment.status}</Badge>
                      <p className="text-sm text-muted-foreground mt-1">{assignment.progress || 0}% complete</p>
                    </div>
                    {canAssign && (
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteAssignment(assignment.assignment_id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {assignments.length === 0 && (
            <Card className="glass-card text-center p-12">
              <BookOpen className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h2 className="font-heading text-xl mb-2">No Course Assignments</h2>
              <p className="text-muted-foreground mb-4">
                {canAssign ? 'Assign courses to members or groups' : 'No courses have been assigned to you yet'}
              </p>
              {canAssign && <Button onClick={() => setShowAssignDialog(true)}>Assign First Course</Button>}
            </Card>
          )}
        </div>
      </main>

      {/* Assign Course Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign Course</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Course</Label>
              <Select value={newAssignment.course_id} onValueChange={(v) => setNewAssignment({...newAssignment, course_id: v})}>
                <SelectTrigger><SelectValue placeholder="Choose a course" /></SelectTrigger>
                <SelectContent>
                  {courses.map(c => <SelectItem key={c.course_id} value={c.course_id}>{c.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {canAssignToGroups && (
              <div>
                <Label>Assignment Type</Label>
                <Select value={newAssignment.assignment_type} onValueChange={(v) => setNewAssignment({...newAssignment, assignment_type: v, assigned_to: ''})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="group">Group</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Assign To</Label>
              {user?.role === 'mentor' && mentees.length === 0 ? (
                <div className="p-3 border rounded-lg bg-amber-50 text-amber-800 text-sm">
                  <p className="font-medium">No mentees available</p>
                  <p className="text-xs mt-1">You need active mentorship assignments to assign courses. Contact an admin to be assigned mentees.</p>
                </div>
              ) : (
                <Select value={newAssignment.assigned_to} onValueChange={(v) => setNewAssignment({...newAssignment, assigned_to: v})}>
                  <SelectTrigger><SelectValue placeholder={newAssignment.assignment_type === 'group' ? 'Select group' : 'Select member'} /></SelectTrigger>
                  <SelectContent>
                    {newAssignment.assignment_type === 'group' 
                      ? groups.map(g => <SelectItem key={g.group_id} value={g.group_id}>{g.name}</SelectItem>)
                      : (user?.role === 'mentor' ? mentees : users).map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)
                    }
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label>Due Date (Optional)</Label>
              <Input type="date" value={newAssignment.due_date} onChange={(e) => setNewAssignment({...newAssignment, due_date: e.target.value})} />
            </div>
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea value={newAssignment.notes} onChange={(e) => setNewAssignment({...newAssignment, notes: e.target.value})} placeholder="Additional instructions..." />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateAssignment} disabled={!newAssignment.course_id || !newAssignment.assigned_to}>Assign</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== SUPPORT TICKETS PAGE ====================

function SupportTicketsPage() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [ticketsRes, userRes] = await Promise.all([
        axiosInstance.get('/support-tickets'),
        axiosInstance.get('/auth/me')
      ]);
      setTickets(ticketsRes.data);
      setUser(userRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const isStaff = ['admin', 'pastor', 'support'].includes(user?.role);

  const filteredTickets = tickets.filter(t => {
    if (filter === 'all') return true;
    return t.status === filter;
  });

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-green-100 text-green-800';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-purple-100 text-purple-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}><ChevronLeft className="h-5 w-5" /></Button>
            <div>
              <h1 className="font-heading text-xl font-bold">Support Tickets</h1>
              <p className="text-sm text-muted-foreground">{isStaff ? 'Manage support requests' : 'Your support requests'}</p>
            </div>
          </div>
          <Button onClick={() => navigate('/support/new')} data-testid="create-ticket-btn">
            <Plus className="h-4 w-4 mr-2" />New Ticket
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="flex gap-2 mb-6">
          {['all', 'open', 'in_progress', 'resolved'].map(status => (
            <Button key={status} variant={filter === status ? 'default' : 'outline'} size="sm" onClick={() => setFilter(status)}>
              {status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1)}
            </Button>
          ))}
        </div>

        <div className="space-y-4">
          {filteredTickets.map(ticket => (
            <Card key={ticket.ticket_id} className="glass-card cursor-pointer hover:shadow-lg transition-all" onClick={() => navigate(`/support/${ticket.ticket_id}`)}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{ticket.subject}</h3>
                      <Badge className={getPriorityColor(ticket.priority)}>{ticket.priority}</Badge>
                      <Badge className={getStatusColor(ticket.status)}>{ticket.status.replace('_', ' ')}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-1">{ticket.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      {isStaff && ticket.user && <span>By: {ticket.user.name}</span>}
                      <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                      <span>{ticket.reply_count || 0} replies</span>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))}

          {filteredTickets.length === 0 && (
            <Card className="glass-card text-center p-12">
              <MessageCircle className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h2 className="font-heading text-xl mb-2">No Tickets Found</h2>
              <p className="text-muted-foreground mb-4">
                {filter !== 'all' ? `No ${filter.replace('_', ' ')} tickets` : 'Create a support ticket to get help'}
              </p>
              <Button onClick={() => navigate('/support/new')}>Create Ticket</Button>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}

// ==================== CREATE TICKET PAGE ====================

function CreateTicketPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState({ subject: '', description: '', category: 'general', priority: 'medium' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ticket.subject || !ticket.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.post('/support-tickets', ticket);
      toast.success('Ticket created successfully!');
      navigate(`/support/${res.data.ticket_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/support')}><ChevronLeft className="h-5 w-5" /></Button>
          <div>
            <h1 className="font-heading text-xl font-bold">Create Support Ticket</h1>
            <p className="text-sm text-muted-foreground">Submit a new support request</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <Card className="glass-card">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label>Subject *</Label>
                <Input value={ticket.subject} onChange={(e) => setTicket({...ticket, subject: e.target.value})} placeholder="Brief description of your issue" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select value={ticket.category} onValueChange={(v) => setTicket({...ticket, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="technical">Technical</SelectItem>
                      <SelectItem value="billing">Billing</SelectItem>
                      <SelectItem value="feedback">Feedback</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={ticket.priority} onValueChange={(v) => setTicket({...ticket, priority: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Description *</Label>
                <Textarea value={ticket.description} onChange={(e) => setTicket({...ticket, description: e.target.value})} placeholder="Describe your issue in detail..." rows={6} />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => navigate('/support')}>Cancel</Button>
                <Button type="submit" disabled={loading}>{loading ? 'Submitting...' : 'Submit Ticket'}</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

// ==================== TICKET DETAIL PAGE ====================

function TicketDetailPage() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [newReply, setNewReply] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadData(); }, [ticketId]);

  const loadData = async () => {
    try {
      const [ticketRes, userRes] = await Promise.all([
        axiosInstance.get(`/support-tickets/${ticketId}`),
        axiosInstance.get('/auth/me')
      ]);
      setTicket(ticketRes.data);
      setUser(userRes.data);
    } catch (error) {
      toast.error('Failed to load ticket');
      navigate('/support');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReply = async () => {
    if (!newReply.trim()) return;
    setSubmitting(true);
    try {
      await axiosInstance.post(`/support-tickets/${ticketId}/replies`, { message: newReply });
      setNewReply('');
      loadData();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (status) => {
    try {
      await axiosInstance.put(`/support-tickets/${ticketId}?status=${status}`);
      toast.success('Status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const isStaff = ['admin', 'pastor', 'support'].includes(user?.role);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  if (!ticket) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/support')}><ChevronLeft className="h-5 w-5" /></Button>
            <div>
              <h1 className="font-heading text-xl font-bold">{ticket.subject}</h1>
              <p className="text-sm text-muted-foreground">Ticket #{ticketId.slice(-8)}</p>
            </div>
          </div>
          {isStaff && (
            <Select value={ticket.status} onValueChange={handleUpdateStatus}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Ticket Info */}
        <Card className="glass-card mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4 mb-4">
              <Avatar><AvatarImage src={ticket.user?.picture} /><AvatarFallback>{ticket.user?.name?.[0]}</AvatarFallback></Avatar>
              <div>
                <p className="font-medium">{ticket.user?.name}</p>
                <p className="text-sm text-muted-foreground">{new Date(ticket.created_at).toLocaleString()}</p>
              </div>
              <Badge variant="outline" className="ml-auto">{ticket.category}</Badge>
            </div>
            <p className="whitespace-pre-wrap">{ticket.description}</p>
          </CardContent>
        </Card>

        {/* Replies */}
        <div className="space-y-4 mb-6">
          {ticket.replies?.map(reply => (
            <Card key={reply.reply_id} className={`glass-card ${reply.is_staff ? 'border-l-4 border-l-primary' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Avatar className="h-8 w-8"><AvatarImage src={reply.user?.picture} /><AvatarFallback>{reply.user?.name?.[0]}</AvatarFallback></Avatar>
                  <div>
                    <p className="font-medium text-sm">{reply.user?.name} {reply.is_staff && <Badge className="ml-1" variant="secondary">Staff</Badge>}</p>
                    <p className="text-xs text-muted-foreground">{new Date(reply.created_at).toLocaleString()}</p>
                  </div>
                </div>
                <p className="whitespace-pre-wrap text-sm">{reply.message}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Reply Form */}
        {ticket.status !== 'resolved' && (
          <Card className="glass-card">
            <CardContent className="p-4">
              <Textarea value={newReply} onChange={(e) => setNewReply(e.target.value)} placeholder="Type your reply..." rows={3} />
              <div className="flex justify-end mt-3">
                <Button onClick={handleSubmitReply} disabled={submitting || !newReply.trim()}>
                  {submitting ? 'Sending...' : 'Send Reply'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

// ==================== KNOWLEDGE BASE PAGE ====================

function KnowledgeBasePage() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');

  useEffect(() => { loadData(); }, [search, category]);

  const loadData = async () => {
    try {
      let url = '/knowledge-base';
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      if (params.toString()) url += `?${params.toString()}`;
      
      const [articlesRes, userRes] = await Promise.all([
        axiosInstance.get(url),
        axiosInstance.get('/auth/me').catch(() => ({ data: null }))
      ]);
      setArticles(articlesRes.data);
      setUser(userRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const canCreate = user && ['admin', 'pastor', 'support', 'mentor', 'church_worker'].includes(user.role);

  const categories = ['general', 'getting-started', 'faq', 'tutorials', 'policies'];

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}><ChevronLeft className="h-5 w-5" /></Button>
            <div>
              <h1 className="font-heading text-xl font-bold">Knowledge Base</h1>
              <p className="text-sm text-muted-foreground">Find answers and resources</p>
            </div>
          </div>
          {canCreate && (
            <Button onClick={() => navigate('/knowledge-base/new')} data-testid="create-article-btn">
              <Plus className="h-4 w-4 mr-2" />New Article
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Search & Filters */}
        <div className="flex gap-4 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-10" placeholder="Search articles..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48"><SelectValue placeholder="All Categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => <SelectItem key={cat} value={cat}>{cat.replace('-', ' ').charAt(0).toUpperCase() + cat.slice(1)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map(article => (
            <Card key={article.article_id} className="glass-card cursor-pointer hover:shadow-lg transition-all" onClick={() => navigate(`/knowledge-base/${article.article_id}`)}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <Badge variant="outline">{article.category}</Badge>
                  {article.status === 'pending' && <Badge variant="secondary">Pending</Badge>}
                </div>
                <CardTitle className="font-heading text-lg line-clamp-2">{article.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground line-clamp-3">{article.content.substring(0, 150)}...</p>
                <div className="flex items-center gap-2 mt-4 text-xs text-muted-foreground">
                  <Avatar className="h-5 w-5"><AvatarImage src={article.author?.picture} /><AvatarFallback>{article.author?.name?.[0]}</AvatarFallback></Avatar>
                  <span>{article.author?.name}</span>
                  <span>•</span>
                  <span>{article.views || 0} views</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {articles.length === 0 && (
          <Card className="glass-card text-center p-12">
            <BookOpen className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="font-heading text-xl mb-2">No Articles Found</h2>
            <p className="text-muted-foreground mb-4">
              {search || category ? 'Try different search terms or filters' : 'Be the first to create an article'}
            </p>
            {canCreate && <Button onClick={() => navigate('/knowledge-base/new')}>Create Article</Button>}
          </Card>
        )}
      </main>
    </div>
  );
}

// ==================== CREATE ARTICLE PAGE ====================

function CreateArticlePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [article, setArticle] = useState({ title: '', content: '', category: 'general', tags: [] });
  const [tagInput, setTagInput] = useState('');

  const addTag = () => {
    if (tagInput && !article.tags.includes(tagInput)) {
      setArticle({...article, tags: [...article.tags, tagInput]});
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setArticle({...article, tags: article.tags.filter(t => t !== tag)});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!article.title || !article.content) {
      toast.error('Please fill in title and content');
      return;
    }
    setLoading(true);
    try {
      await axiosInstance.post('/knowledge-base', article);
      toast.success('Article submitted for approval!');
      navigate('/knowledge-base');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create article');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/knowledge-base')}><ChevronLeft className="h-5 w-5" /></Button>
          <div>
            <h1 className="font-heading text-xl font-bold">Create Article</h1>
            <p className="text-sm text-muted-foreground">Share knowledge with the community</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card className="glass-card">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label>Title *</Label>
                <Input value={article.title} onChange={(e) => setArticle({...article, title: e.target.value})} placeholder="Article title" />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={article.category} onValueChange={(v) => setArticle({...article, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="getting-started">Getting Started</SelectItem>
                    <SelectItem value="faq">FAQ</SelectItem>
                    <SelectItem value="tutorials">Tutorials</SelectItem>
                    <SelectItem value="policies">Policies</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tags</Label>
                <div className="flex gap-2">
                  <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add a tag" onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} />
                  <Button type="button" variant="outline" onClick={addTag}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {article.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                      {tag} <X className="h-3 w-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <Label>Content *</Label>
                <Textarea value={article.content} onChange={(e) => setArticle({...article, content: e.target.value})} placeholder="Write your article content..." rows={15} />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => navigate('/knowledge-base')}>Cancel</Button>
                <Button type="submit" disabled={loading}>{loading ? 'Submitting...' : 'Submit for Review'}</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

// ==================== KNOWLEDGE ARTICLE PAGE ====================

function KnowledgeArticlePage() {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => { loadData(); }, [articleId]);

  const loadData = async () => {
    try {
      const [articleRes, userRes] = await Promise.all([
        axiosInstance.get(`/knowledge-base/${articleId}`),
        axiosInstance.get('/auth/me').catch(() => ({ data: null }))
      ]);
      setArticle(articleRes.data);
      setUser(userRes.data);
    } catch (error) {
      toast.error('Article not found');
      navigate('/knowledge-base');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      await axiosInstance.put(`/knowledge-base/${articleId}?status=approved`);
      toast.success('Article approved!');
      loadData();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const handleReject = async () => {
    try {
      await axiosInstance.put(`/knowledge-base/${articleId}?status=rejected`);
      toast.success('Article rejected');
      loadData();
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  const isAdmin = user?.role === 'admin';

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  if (!article) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/knowledge-base')}><ChevronLeft className="h-5 w-5" /></Button>
            <div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">{article.category}</Badge>
                {article.status === 'pending' && <Badge variant="secondary">Pending Approval</Badge>}
              </div>
            </div>
          </div>
          {isAdmin && article.status === 'pending' && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleReject}>Reject</Button>
              <Button onClick={handleApprove}>Approve</Button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card className="glass-card">
          <CardContent className="p-8">
            <h1 className="font-heading text-3xl font-bold mb-4">{article.title}</h1>
            <div className="flex items-center gap-4 mb-8 pb-4 border-b">
              <Avatar><AvatarImage src={article.author?.picture} /><AvatarFallback>{article.author?.name?.[0]}</AvatarFallback></Avatar>
              <div>
                <p className="font-medium">{article.author?.name}</p>
                <p className="text-sm text-muted-foreground">{new Date(article.created_at).toLocaleDateString()} • {article.views || 0} views</p>
              </div>
            </div>
            {article.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {article.tags.map(tag => <Badge key={tag} variant="outline">{tag}</Badge>)}
              </div>
            )}
            <div className="prose prose-lg max-w-none whitespace-pre-wrap">{article.content}</div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

// ==================== REPORTS PAGE ====================

function ReportsPage() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);

  useEffect(() => { loadReports(); }, []);

  const loadReports = async () => {
    try {
      const response = await axiosInstance.get('/admin/reports');
      setReports(response.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (reportId, format) => {
    setExporting(`${reportId}-${format}`);
    try {
      const response = await axiosInstance.get(`/admin/reports/${reportId}?format=${format}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportId}_report.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${format.toUpperCase()} exported successfully!`);
    } catch (error) {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/dashboard')}><ChevronLeft className="h-5 w-5" /></Button>
          <div>
            <h1 className="font-heading text-xl font-bold">Reports & Exports</h1>
            <p className="text-sm text-muted-foreground">Generate and export data reports</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reports.map(report => (
            <Card key={report.id} className="glass-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg">{report.name}</CardTitle>
                <CardDescription>{report.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-muted-foreground">Records:</span>
                  <Badge variant="secondary">{report.record_count}</Badge>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleExport(report.id, 'excel')}
                    disabled={exporting === `${report.id}-excel`}
                    data-testid={`export-${report.id}-excel`}
                  >
                    {exporting === `${report.id}-excel` ? (
                      <span className="animate-spin">⏳</span>
                    ) : (
                      <>
                        <FileText className="h-4 w-4 mr-1" />
                        Excel
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleExport(report.id, 'pdf')}
                    disabled={exporting === `${report.id}-pdf`}
                    data-testid={`export-${report.id}-pdf`}
                  >
                    {exporting === `${report.id}-pdf` ? (
                      <span className="animate-spin">⏳</span>
                    ) : (
                      <>
                        <FileText className="h-4 w-4 mr-1" />
                        PDF
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}

// ==================== MY EXPORTS PAGE ====================

function MyExportsPage() {
  const navigate = useNavigate();
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);

  useEffect(() => { loadExports(); }, []);

  const loadExports = async () => {
    try {
      const response = await axiosInstance.get('/my-exports');
      setExports(response.data);
    } catch (error) {
      toast.error('Failed to load exports');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (exportId, format) => {
    setExporting(`${exportId}-${format}`);
    try {
      const response = await axiosInstance.get(`/my-exports/${exportId}?format=${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${exportId}_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${format.toUpperCase()} exported successfully!`);
    } catch (error) {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}><ChevronLeft className="h-5 w-5" /></Button>
          <div>
            <h1 className="font-heading text-xl font-bold">My Exports</h1>
            <p className="text-sm text-muted-foreground">Export your progress and data</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {exports.map(exp => (
            <Card key={exp.id} className="glass-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg">{exp.name}</CardTitle>
                <CardDescription>{exp.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-muted-foreground">Records:</span>
                  <Badge variant="secondary">{exp.record_count}</Badge>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleExport(exp.id, 'excel')}
                    disabled={exporting === `${exp.id}-excel` || exp.record_count === 0}
                  >
                    {exporting === `${exp.id}-excel` ? '⏳' : <><FileText className="h-4 w-4 mr-1" />Excel</>}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleExport(exp.id, 'pdf')}
                    disabled={exporting === `${exp.id}-pdf` || exp.record_count === 0}
                  >
                    {exporting === `${exp.id}-pdf` ? '⏳' : <><FileText className="h-4 w-4 mr-1" />PDF</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}

// ==================== BADGES PAGE ====================

function BadgesPage() {
  const navigate = useNavigate();
  const [badgeData, setBadgeData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadBadges(); }, []);

  const loadBadges = async () => {
    try {
      const response = await axiosInstance.get('/gamification/my-badges');
      setBadgeData(response.data);
    } catch (error) {
      toast.error('Failed to load badges');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  if (!badgeData) return null;

  const progressToNext = badgeData.next_rank 
    ? ((badgeData.total_points - badgeData.current_rank.min_points) / (badgeData.next_rank.min_points - badgeData.current_rank.min_points)) * 100
    : 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}><ChevronLeft className="h-5 w-5" /></Button>
            <div>
              <h1 className="font-heading text-xl font-bold">My Badges & Achievements</h1>
              <p className="text-sm text-muted-foreground">Track your progress and earn rewards</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate('/leaderboard')}>
            <Award className="h-4 w-4 mr-2" />View Leaderboard
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Rank Card */}
        <Card className="glass-card mb-8 bg-gradient-to-r from-primary/10 to-primary/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-6">
              <div className="text-6xl">{badgeData.current_rank.icon}</div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="font-heading text-2xl font-bold">{badgeData.current_rank.name}</h2>
                  <Badge variant="secondary">Level {badgeData.current_rank.level}</Badge>
                </div>
                <p className="text-3xl font-bold text-primary mb-2">{badgeData.total_points} points</p>
                {badgeData.next_rank && (
                  <div>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span>Progress to {badgeData.next_rank.name}</span>
                      <span>{badgeData.points_to_next_rank} points needed</span>
                    </div>
                    <div className="bg-muted rounded-full h-3">
                      <div className="bg-primary h-3 rounded-full transition-all" style={{width: `${progressToNext}%`}}></div>
                    </div>
                  </div>
                )}
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Badges Earned</p>
                <p className="text-4xl font-bold text-primary">{badgeData.earned_badges.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Earned Badges */}
        <h3 className="font-heading text-xl font-bold mb-4">Earned Badges ({badgeData.earned_badges.length})</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          {badgeData.earned_badges.map(badge => (
            <Card key={badge.badge_id} className="glass-card text-center p-4 bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200">
              <div className="text-4xl mb-2">{badge.icon}</div>
              <h4 className="font-semibold text-sm">{badge.name}</h4>
              <p className="text-xs text-muted-foreground mt-1">{badge.description}</p>
              <Badge className="mt-2 bg-green-600">✓ Earned</Badge>
            </Card>
          ))}
          {badgeData.earned_badges.length === 0 && (
            <Card className="glass-card text-center p-8 col-span-full">
              <Award className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground">No badges earned yet. Keep going!</p>
            </Card>
          )}
        </div>

        {/* Available Badges */}
        <h3 className="font-heading text-xl font-bold mb-4">Badges to Earn ({badgeData.available_badges.length})</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {badgeData.available_badges.map(badge => (
            <Card key={badge.badge_id} className="glass-card text-center p-4 opacity-75">
              <div className="text-4xl mb-2 grayscale">{badge.icon}</div>
              <h4 className="font-semibold text-sm">{badge.name}</h4>
              <p className="text-xs text-muted-foreground mt-1">{badge.description}</p>
              <div className="mt-2">
                <div className="bg-muted rounded-full h-2 mb-1">
                  <div className="bg-primary h-2 rounded-full" style={{width: `${badge.progress_percent}%`}}></div>
                </div>
                <span className="text-xs">{badge.current_progress}/{badge.requirement}</span>
              </div>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}

// ==================== LEADERBOARD PAGE ====================

function LeaderboardPage() {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [leaderboardRes, userRes] = await Promise.all([
        axiosInstance.get('/gamification/leaderboard?limit=50'),
        axiosInstance.get('/auth/me')
      ]);
      setLeaderboard(leaderboardRes.data);
      setCurrentUserId(userRes.data.user_id);
    } catch (error) {
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getPositionBadge = (position) => {
    if (position === 1) return '🥇';
    if (position === 2) return '🥈';
    if (position === 3) return '🥉';
    return `#${position}`;
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}><ChevronLeft className="h-5 w-5" /></Button>
            <div>
              <h1 className="font-heading text-xl font-bold">Leaderboard</h1>
              <p className="text-sm text-muted-foreground">Top achievers in our community</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate('/badges')}>
            <Award className="h-4 w-4 mr-2" />My Badges
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Top 3 Podium */}
        <div className="flex justify-center items-end gap-4 mb-8">
          {leaderboard.slice(0, 3).map((entry, idx) => {
            const order = [1, 0, 2]; // Silver, Gold, Bronze positioning
            const actualEntry = leaderboard[order[idx]];
            if (!actualEntry) return null;
            const heights = ['h-32', 'h-40', 'h-24'];
            const bgColors = ['bg-gray-200', 'bg-yellow-100', 'bg-orange-100'];
            
            return (
              <div key={actualEntry.user_id} className={`flex flex-col items-center ${idx === 1 ? 'order-first' : ''}`}>
                <div className="text-3xl mb-2">{getPositionBadge(actualEntry.position)}</div>
                <Avatar className="h-16 w-16 border-4 border-white shadow-lg mb-2">
                  <AvatarImage src={actualEntry.picture} />
                  <AvatarFallback>{actualEntry.name?.[0]}</AvatarFallback>
                </Avatar>
                <p className="font-semibold text-center">{actualEntry.name}</p>
                <p className="text-lg font-bold text-primary">{actualEntry.total_points} pts</p>
                <div className={`${heights[idx]} w-24 ${bgColors[idx]} rounded-t-lg mt-2 flex items-end justify-center pb-2`}>
                  <span className="text-2xl">{actualEntry.rank.icon}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Full List */}
        <Card className="glass-card">
          <CardContent className="p-0">
            <div className="divide-y">
              {leaderboard.map(entry => (
                <div 
                  key={entry.user_id} 
                  className={`flex items-center gap-4 p-4 ${entry.user_id === currentUserId ? 'bg-primary/10' : ''}`}
                >
                  <div className="w-12 text-center font-bold text-lg">
                    {entry.position <= 3 ? getPositionBadge(entry.position) : `#${entry.position}`}
                  </div>
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={entry.picture} />
                    <AvatarFallback>{entry.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-semibold">{entry.name} {entry.user_id === currentUserId && <Badge variant="outline" className="ml-2">You</Badge>}</p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{entry.rank.icon} {entry.rank.name}</span>
                      <span>•</span>
                      <span>{entry.badges_earned} badges</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-primary">{entry.total_points}</p>
                    <p className="text-xs text-muted-foreground">points</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

// ==================== SUBSCRIPTION PAGE ====================

function SubscriptionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [currentMembership, setCurrentMembership] = useState(null);
  const preSelectedPlan = location.state?.selectedPlan;

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [plansRes, membershipRes] = await Promise.all([
        axiosInstance.get('/mentorship/plans'),
        axiosInstance.get('/membership/my')
      ]);
      setPlans(plansRes.data);
      setCurrentMembership(membershipRes.data);
    } catch (error) {
      console.error('Error:', error);
      setPlans({
        starter: { name: "Starter", price: 29.99, interval: "month", features: ["AI Resume Builder", "Basic career guidance"] },
        growth: { name: "Growth", price: 49.99, interval: "month", features: ["1-on-1 Mentorship", "Leadership assessment"] },
        elite: { name: "Elite", price: 99.99, interval: "month", features: ["All features", "Business plan"] },
        lifetime: { name: "Lifetime", original_price: 500, price: 350, features: ["All features forever"] }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (plan) => {
    setProcessing(plan);
    try {
      const response = await axiosInstance.post(`/membership/subscribe?plan=${plan}&origin_url=${encodeURIComponent(window.location.origin)}`);
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start subscription');
    } finally {
      setProcessing(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your mentorship plan? It will remain active until the end of the billing period.')) return;
    try {
      await axiosInstance.post('/membership/cancel');
      toast.success('Plan will be canceled at the end of the billing period');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  const planOrder = ['starter', 'growth', 'elite', 'lifetime'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <header className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}><ChevronLeft className="h-5 w-5" /></Button>
          <div>
            <h1 className="font-heading text-xl font-bold">Mentorship Plans</h1>
            <p className="text-sm text-muted-foreground">Invest in your growth with professional mentorship</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Active Plan Banner */}
        {currentMembership?.has_mentorship && (
          <Card className="glass-card mb-8 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                    <h3 className="font-heading text-xl font-bold text-green-800 dark:text-green-300">
                      Active {currentMembership.mentorship_plan_name} Plan
                    </h3>
                  </div>
                  {currentMembership.is_lifetime ? (
                    <p className="text-green-600 dark:text-green-400 mt-1">Lifetime access - Never expires!</p>
                  ) : currentMembership.expires_at && (
                    <p className="text-green-600 dark:text-green-400 mt-1">
                      Renews on {new Date(currentMembership.expires_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                {!currentMembership.is_lifetime && (
                  <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={handleCancel}>
                    Cancel Plan
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Church Member Benefits */}
        <Card className="glass-card mb-8 bg-gradient-to-r from-primary/5 to-primary/10">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                <Home className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Church Membership (Free)</h3>
                <p className="text-sm text-muted-foreground">All members already have access to: Sermons, Worship, Community, Calendar, Prayer Requests, Giving & Store</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <h2 className="font-heading text-2xl font-bold text-center mb-6">Add Mentorship to Your Journey</h2>

        {/* Mentorship Plans */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {planOrder.map((planKey) => {
            const plan = plans[planKey];
            if (!plan) return null;
            
            const isCurrentPlan = currentMembership?.mentorship_plan === planKey;
            const isPopular = planKey === 'elite';
            const isLifetime = planKey === 'lifetime';
            const isPreSelected = preSelectedPlan === planKey;
            
            return (
              <Card 
                key={planKey} 
                className={`glass-card relative overflow-hidden transition-all hover:shadow-xl ${
                  isPopular ? 'border-primary ring-2 ring-primary/20' : ''
                } ${isCurrentPlan ? 'border-green-500' : ''} ${isPreSelected ? 'ring-2 ring-accent' : ''}`}
                data-testid={`plan-${planKey}`}
              >
                {isPopular && (
                  <div className="absolute top-0 right-0 bg-primary text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                    Popular
                  </div>
                )}
                {isLifetime && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                    Best Value
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <div className="mt-3">
                    {plan.original_price && (
                      <span className="text-sm text-muted-foreground line-through mr-2">${plan.original_price}</span>
                    )}
                    <span className="text-3xl font-bold">${plan.price}</span>
                    {plan.interval ? (
                      <span className="text-muted-foreground">/{plan.interval}</span>
                    ) : (
                      <span className="text-muted-foreground block text-sm mt-1">one-time</span>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pt-2">
                  <ul className="space-y-2 mb-4 text-sm">
                    {plan.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {isCurrentPlan ? (
                    <Button className="w-full" disabled variant="outline">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Current Plan
                    </Button>
                  ) : currentMembership?.has_mentorship && !isLifetime ? (
                    <Button className="w-full" variant="outline" disabled>
                      Already Subscribed
                    </Button>
                  ) : (
                    <Button 
                      className={`w-full ${isPopular ? 'bg-primary hover:bg-primary/90' : ''} ${isLifetime ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600' : ''}`}
                      onClick={() => handleSubscribe(planKey)}
                      disabled={processing === planKey}
                      data-testid={`subscribe-${planKey}`}
                    >
                      {processing === planKey ? 'Processing...' : isLifetime ? 'Get Lifetime' : 'Subscribe'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-8">
          Secure payment via Stripe. Monthly plans can be canceled anytime.
        </p>
      </main>
    </div>
  );
}

// ==================== MEMBERSHIP SUCCESS PAGE ====================

function MembershipSuccessPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [membership, setMembership] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      verifyMembership(sessionId);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyMembership = async (sessionId) => {
    try {
      const response = await axiosInstance.get(`/membership/verify/${sessionId}`);
      setMembership(response.data);
    } catch (error) {
      console.error('Failed to verify membership:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Activating your membership...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center">
      <Card className="glass-card max-w-md text-center p-8">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle className="h-12 w-12 text-green-600" />
        </div>
        <h1 className="font-heading text-3xl font-bold mb-2">Welcome to {membership?.tier_name || 'Your Plan'}!</h1>
        <p className="text-muted-foreground mb-6">
          {membership?.is_lifetime 
            ? "You now have lifetime access to all features. Thank you for your support!"
            : "Your membership is now active. Enjoy all your new features!"
          }
        </p>
        {membership?.modules && (
          <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold mb-2">Your Access Includes:</h3>
            <div className="flex flex-wrap gap-2">
              {membership.modules.slice(0, 8).map((mod) => (
                <Badge key={mod} variant="secondary">
                  {mod.replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>
          </div>
        )}
        <div className="space-y-3">
          <Button onClick={() => navigate('/dashboard')} className="w-full">
            Go to Dashboard
          </Button>
          <Button variant="outline" onClick={() => navigate('/courses')} className="w-full">
            Browse Courses
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ==================== MENTORSHIP PRICING PAGE (DEPRECATED - Redirects to Subscribe) ====================
// This component is kept for backwards compatibility but redirects to the main subscription page

function MentorshipPricingPage() {
  const navigate = useNavigate();
  
  useEffect(() => {
    navigate('/subscribe', { replace: true });
  }, [navigate]);
  
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
  );
}

// ==================== SUBSCRIPTION SUCCESS PAGE ====================

function SubscriptionSuccessPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      checkSubscription(sessionId);
    } else {
      setLoading(false);
    }
  }, []);

  const checkSubscription = async (sessionId) => {
    try {
      const response = await axiosInstance.get(`/subscriptions/status/${sessionId}`);
      setSubscription(response.data);
    } catch (error) {
      toast.error('Failed to verify subscription');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Verifying your subscription...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center">
      <Card className="glass-card max-w-md text-center p-8">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle className="h-12 w-12 text-green-600" />
        </div>
        <h1 className="font-heading text-3xl font-bold mb-2">Subscription Active!</h1>
        <p className="text-muted-foreground mb-6">
          Thank you for subscribing! Your mentorship journey begins now.
        </p>
        {subscription && (
          <div className="bg-muted/50 rounded-lg p-4 mb-6">
            <p className="font-medium">{subscription.package_id} Plan</p>
            {subscription.current_period_end && (
              <p className="text-sm text-muted-foreground">
                Next billing: {new Date(subscription.current_period_end).toLocaleDateString()}
              </p>
            )}
          </div>
        )}
        <Button onClick={() => navigate('/dashboard')} className="w-full">
          Go to Dashboard
        </Button>
      </Card>
    </div>
  );
}

// ==================== MENTORSHIP AUDIT PAGE ====================

function MentorshipAuditPage() {
  const { mentorshipId } = useParams();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadAudit(); }, [mentorshipId]);

  const loadAudit = async () => {
    try {
      const response = await axiosInstance.get(`/admin/mentorships/${mentorshipId}/audit`);
      setAudit(response.data);
    } catch (error) {
      toast.error('Failed to load mentorship audit');
      navigate('/admin/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handlePermanentDelete = async () => {
    setDeleting(true);
    try {
      await axiosInstance.delete(`/admin/mentorships/${mentorshipId}/permanent?confirm=true`);
      toast.success('Mentorship permanently deleted');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete mentorship');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  if (!audit) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/dashboard')}><ChevronLeft className="h-5 w-5" /></Button>
            <div>
              <h1 className="font-heading text-xl font-bold">Mentorship Audit</h1>
              <p className="text-sm text-muted-foreground">ID: {mentorshipId}</p>
            </div>
          </div>
          <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)} data-testid="delete-mentorship-btn">
            <Trash2 className="h-4 w-4 mr-2" />Delete Mentorship
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Mentor & Mentee Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Mentor</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={audit.mentor?.picture} />
                  <AvatarFallback>{audit.mentor?.name?.[0]}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-lg">{audit.mentor?.name}</p>
                  <p className="text-sm text-muted-foreground">{audit.mentor?.email}</p>
                  <Badge variant="outline" className="mt-1">{audit.mentor?.role}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Mentee</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={audit.mentee?.picture} />
                  <AvatarFallback>{audit.mentee?.name?.[0]}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-lg">{audit.mentee?.name}</p>
                  <p className="text-sm text-muted-foreground">{audit.mentee?.email}</p>
                  <Badge variant="outline" className="mt-1">{audit.mentee?.role}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Statistics */}
        <Card className="glass-card mb-8">
          <CardHeader>
            <CardTitle className="font-heading">Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="p-4 bg-muted/30 rounded-lg text-center">
                <p className="text-2xl font-bold">{audit.statistics.total_goals}</p>
                <p className="text-xs text-muted-foreground">Total Tasks</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-600">{audit.statistics.completed_goals}</p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-600">{audit.statistics.in_progress_goals}</p>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-gray-600">{audit.statistics.not_started_goals}</p>
                <p className="text-xs text-muted-foreground">Not Started</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-purple-600">{audit.statistics.completion_rate}%</p>
                <p className="text-xs text-muted-foreground">Completion Rate</p>
              </div>
              <div className="p-4 bg-orange-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-orange-600">{audit.statistics.total_sessions}</p>
                <p className="text-xs text-muted-foreground">Sessions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tasks/Goals */}
        <Card className="glass-card mb-8">
          <CardHeader>
            <CardTitle className="font-heading">Tasks & Goals</CardTitle>
            <CardDescription>{audit.goals.length} total tasks assigned</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {audit.goals.map(goal => (
                <div key={goal.goal_id} className="p-4 border rounded-lg bg-card">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">{goal.title}</h4>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{goal.task_type}</Badge>
                      <Badge className={getStatusColor(goal.status)}>{goal.status}</Badge>
                    </div>
                  </div>
                  {goal.description && <p className="text-sm text-muted-foreground mb-2">{goal.description}</p>}
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Progress: {goal.progress}%</span>
                    {goal.due_date && <span>Due: {new Date(goal.due_date).toLocaleDateString()}</span>}
                    {goal.completed_at && <span>Completed: {new Date(goal.completed_at).toLocaleDateString()}</span>}
                  </div>
                  <div className="mt-2 bg-muted/30 rounded-full h-2">
                    <div className="bg-primary h-2 rounded-full" style={{width: `${goal.progress}%`}}></div>
                  </div>
                </div>
              ))}
              {audit.goals.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No tasks assigned yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Sessions */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="font-heading">Sessions</CardTitle>
            <CardDescription>{audit.sessions.length} sessions scheduled</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {audit.sessions.map(session => (
                <div key={session.session_id} className="p-3 border rounded-lg flex items-center justify-between">
                  <div>
                    <p className="font-medium">{session.title || 'Untitled Session'}</p>
                    <p className="text-sm text-muted-foreground">
                      {session.scheduled_at ? new Date(session.scheduled_at).toLocaleString() : 'Not scheduled'}
                    </p>
                  </div>
                  <Badge variant="outline">{session.status || 'scheduled'}</Badge>
                </div>
              ))}
              {audit.sessions.length === 0 && (
                <p className="text-center text-muted-foreground py-4">No sessions recorded</p>
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Permanently Delete Mentorship?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete:
            </DialogDescription>
          </DialogHeader>
          <div className="bg-destructive/10 p-4 rounded-lg text-sm">
            <ul className="list-disc list-inside space-y-1">
              <li>The mentorship relationship</li>
              <li>All {audit.statistics.total_goals} tasks/goals</li>
              <li>All {audit.statistics.total_sessions} sessions</li>
              <li>All messages between mentor and mentee</li>
              <li>All uploaded files</li>
            </ul>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handlePermanentDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Yes, Delete Permanently'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </ThemeProvider>
  );
}

export default App;